import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { 
  FaUser, FaUtensils, FaDumbbell, FaDownload, FaPlus, FaTrash,
  FaSave, FaFolder, FaEdit, FaPills, FaFileAlt, FaCog, FaMagic, FaSync, FaExchangeAlt,
  FaArrowUp, FaArrowDown, FaClipboardList, FaPaperPlane, FaSpinner, FaFilePdf, FaBalanceScale,
  FaCopy, FaArrowsAlt, FaBrain, FaEnvelope, FaTimes, FaImage, FaClock, FaBan, FaHistory, FaUndo, FaRuler
} from 'react-icons/fa';
import { toast } from 'sonner';
import WorkoutGeneratorOffline from './WorkoutGeneratorOffline';
import MacroCalculator, { generateSmartMeal } from './MacroCalculator';
import ClinicalEditPanel from './ClinicalEditPanel';
import HormonalCycleAdminPanel from './HormonalCycleAdminPanel';
import PharmacologyCycleTab from './PharmacologyCycleTab';
import WorkoutProgressionSection from './WorkoutProgressionSection';
import AdminDataProgresionModal from './AdminDataProgresionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_TYPES = [
  { id: 'basic', name: 'Fit Evolution', color: 'bg-gray-500', icon: '📋' },
  { id: 'premium', name: 'Plan Premium Estético', color: 'bg-purple-500', icon: '✨' },
  { id: 'gold', name: 'Plan Gold VIP', color: 'bg-yellow-500', icon: '👑' },
  { id: 'competitive', name: 'Plan Competitivo', color: 'bg-red-500', icon: '🏆' }
];

const GOALS = [
  { id: 'perder_grasa', name: 'Perder Grasa', icon: '🔥' },
  { id: 'ganar_musculo', name: 'Ganar Músculo', icon: '💪' },
  { id: 'recomposicion', name: 'Recomposición Corporal', icon: '🔄' },
  { id: 'mantener', name: 'Mantener', icon: '⚖️' },
  { id: 'definicion', name: 'Definición', icon: '✨' },
  { id: 'competencia', name: 'Competencia', icon: '🏆' }
];

const ACTIVITY_LEVELS = [
  { id: 'sedentario', name: 'Sedentario', multiplier: 1.2 },
  { id: 'ligero', name: 'Ligero (1-2 días/sem)', multiplier: 1.375 },
  { id: 'moderado', name: 'Moderado (3 días/sem)', multiplier: 1.46 },
  { id: 'activo_moderado', name: 'Activo (4-5 días/sem)', multiplier: 1.55 },
  { id: 'activo', name: 'Activo (6-7 días/sem)', multiplier: 1.725 },
  { id: 'muy_activo', name: 'Muy Activo (2x día)', multiplier: 1.9 }
];

// Preferencias de distribución de macros
const DIET_PREFERENCES = [
  { 
    id: 'equilibrada', 
    name: 'Equilibrada', 
    description: 'Distribución balanceada de macros',
    icon: '⚖️',
    ratios: { protein: 0.30, carbs: 0.40, fat: 0.30 }
  },
  { 
    id: 'alta_proteina', 
    name: 'Alta en Proteína', 
    description: 'Mayor proteína para desarrollo muscular',
    icon: '🥩',
    ratios: { protein: 0.40, carbs: 0.35, fat: 0.25 }
  },
  { 
    id: 'baja_carbos', 
    name: 'Baja en Carbos (Low Carb)', 
    description: 'Menos carbohidratos, más grasas saludables',
    icon: '🥑',
    ratios: { protein: 0.35, carbs: 0.25, fat: 0.40 }
  },
  { 
    id: 'alta_carbos', 
    name: 'Alta en Carbos', 
    description: 'Más energía para entrenamientos intensos',
    icon: '🍚',
    ratios: { protein: 0.25, carbs: 0.50, fat: 0.25 }
  },
  { 
    id: 'cetogenica', 
    name: 'Cetogénica (Keto)', 
    description: 'Muy baja en carbos, alta en grasas',
    icon: '🥓',
    ratios: { protein: 0.30, carbs: 0.10, fat: 0.60 }
  },
  { 
    id: 'alta_grasa', 
    name: 'Alta en Grasas Saludables', 
    description: 'Enfoque en grasas saludables y proteína',
    icon: '🫒',
    ratios: { protein: 0.30, carbs: 0.30, fat: 0.40 }
  }
];

// Ajustes de calorías según objetivo
const GOAL_CALORIE_ADJUSTMENTS = {
  perder_grasa: { adjustment: -400, description: 'Déficit calórico moderado' },
  ganar_musculo: { adjustment: 300, description: 'Superávit para crecimiento' },
  recomposicion: { adjustment: -100, description: 'Déficit leve + alto proteína para ganar músculo y perder grasa' },
  mantener: { adjustment: 0, description: 'Mantenimiento' },
  definicion: { adjustment: -300, description: 'Déficit suave para definir' },
  competencia: { adjustment: -500, description: 'Déficit agresivo' }
};

// 2026-02 · Composición Corporal y Proyecciones
// Fórmulas: masa libre = peso × (1 − %grasa); masa grasa = peso × %grasa;
// pérdida grasa/semana = déficit diario × 7 / 7700; semanas al objetivo =
// (masa grasa actual − masa grasa objetivo) / pérdida semanal; aumento
// muscular/semana = 0.1 kg si objetivo=ganar_musculo/recomposicion, si no 0.
function bmiCategory(bmi) {
  if (!bmi || bmi <= 0) return null;
  if (bmi < 18.5) return { label: 'Bajo peso', color: '#60A5FA' };
  if (bmi < 25) return { label: 'Normal', color: '#34D399' };
  if (bmi < 30) return { label: 'Sobrepeso', color: '#FBBF24' };
  return { label: 'Obesidad', color: '#EF4444' };
}

function bfInterpretation(bf, gender) {
  if (!bf || bf <= 0) return null;
  const g = String(gender || 'femenino').toLowerCase();
  const isMale = g === 'masculino' || g === 'hombre' || g === 'male' || g === 'm';
  if (isMale) {
    if (bf <= 13) return { label: 'Atlético', color: '#00F0FF' };
    if (bf <= 17) return { label: 'Saludable', color: '#34D399' };
    if (bf <= 24) return { label: 'Aceptable', color: '#FBBF24' };
    return { label: 'Obesidad', color: '#EF4444' };
  }
  if (bf <= 20) return { label: 'Atlética', color: '#00F0FF' };
  if (bf <= 24) return { label: 'Saludable', color: '#34D399' };
  if (bf <= 31) return { label: 'Aceptable', color: '#FBBF24' };
  return { label: 'Obesidad', color: '#EF4444' };
}

// 2026-02 · Cálculos Metabólicos y Proyecciones (SOLO admin - no se persisten,
// se calculan reactivamente al vuelo desde peso, altura, edad, género, %grasa,
// %grasa objetivo, nivel de actividad y objetivo). Devuelve null si faltan
// entradas mínimas (peso/altura/edad).
function computeMetabolicProjections(clientData, overrides = {}, projWeeks = 20) {
  const w = parseFloat(clientData?.weight) || 0;
  const h = parseFloat(clientData?.height) || 0;
  const a = parseInt(clientData?.age) || 0;
  const bf = parseFloat(clientData?.body_fat_pct) || 0;
  const bfTarget = parseFloat(clientData?.body_fat_pct_target) || 0;
  const gender = String(clientData?.gender || 'femenino').toLowerCase();
  const isMale = gender === 'masculino' || gender === 'hombre' || gender === 'male' || gender === 'm';
  const activityMult = ACTIVITY_LEVELS.find(l => l.id === (clientData?.activity_level || 'moderado'))?.multiplier || 1.55;
  const goal = clientData?.goal || '';
  const goalAdj = GOAL_CALORIE_ADJUSTMENTS[goal]?.adjustment || 0;

  if (w <= 0 || h <= 0 || a <= 0) return null;

  const ov = (k) => {
    const v = overrides?.[k];
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  // BMR (Katch-McArdle con %grasa, Mifflin-St Jeor de respaldo)
  let rawBmr;
  if (bf > 0) rawBmr = 370 + 21.6 * (w * (1 - bf / 100));
  else if (isMale) rawBmr = 10 * w + 6.25 * h - 5 * a + 5;
  else rawBmr = 10 * w + 6.25 * h - 5 * a - 161;
  const bmr = ov('bmr') ?? Math.round(rawBmr);

  const tdee = ov('tdee') ?? Math.round(bmr * activityMult);
  const rawAdjPct = tdee > 0 ? +((goalAdj / tdee) * 100).toFixed(1) : 0;
  const adjustmentPct = ov('adjustmentPct') ?? rawAdjPct;
  const adjustedKcal = ov('adjustedKcal') ?? Math.round(tdee * (1 + adjustmentPct / 100));
  const dailyDeficit = ov('dailyDeficit') ?? (tdee - adjustedKcal);
  const weeklyAdjustedKcal = ov('weeklyAdjustedKcal') ?? (adjustedKcal * 7);
  const weeklyAdjustmentKcal = ov('weeklyAdjustmentKcal') ?? (dailyDeficit * 7);

  // Proyección pérdida de grasa
  const fatMass = bf > 0 ? +(w * (bf / 100)).toFixed(1) : null;
  const fatMassTarget = bfTarget > 0 ? +(w * (bfTarget / 100)).toFixed(1) : null;
  const rawKgToLose = (fatMass != null && fatMassTarget != null && fatMass > fatMassTarget)
    ? +(fatMass - fatMassTarget).toFixed(1) : null;
  const kgToLose = ov('kgToLose') ?? rawKgToLose;
  const weeklyLossLow = ov('weeklyLossLow') ?? +(w * 0.005).toFixed(2);
  const weeklyLossHigh = ov('weeklyLossHigh') ?? +(w * 0.01).toFixed(2);
  const rawWeeklyLossDeficit = dailyDeficit > 0 ? +((dailyDeficit * 7) / 7700).toFixed(2) : 0;
  const weeklyLossFromDeficit = ov('weeklyLossFromDeficit') ?? rawWeeklyLossDeficit;
  const rawLossPct = w > 0 && weeklyLossFromDeficit > 0 ? +((weeklyLossFromDeficit / w) * 100).toFixed(2) : 0;
  const weeklyLossPctFromDeficit = ov('weeklyLossPctFromDeficit') ?? rawLossPct;
  const rawDaysToTarget = (kgToLose != null && weeklyLossFromDeficit > 0)
    ? Math.ceil((kgToLose / weeklyLossFromDeficit) * 7) : null;
  const daysToTargetFat = ov('daysToTargetFat') ?? rawDaysToTarget;

  // Proyección aumento de músculo
  const isGain = goal === 'ganar_musculo' || goal === 'recomposicion';
  const rawWeeklyGain = isGain ? 0.25 : 0;
  const weeklyGain = ov('weeklyGain') ?? rawWeeklyGain;
  const muscleGain21 = ov('muscleGain21') ?? +(weeklyGain * (2 / 3)).toFixed(3);
  const muscleGain11 = ov('muscleGain11') ?? +(weeklyGain * 0.5).toFixed(3);
  const fatGain21 = ov('fatGain21') ?? +(weeklyGain * (1 / 3)).toFixed(3);
  const fatGain11 = ov('fatGain11') ?? +(weeklyGain * 0.5).toFixed(3);
  const rawMonthlyGainPct = w > 0 && weeklyGain > 0 ? +(((weeklyGain * 4) / w) * 100).toFixed(2) : 0;
  const monthlyGainPct = ov('monthlyGainPct') ?? rawMonthlyGainPct;
  const totalWeightGain = ov('totalWeightGain') ?? +(weeklyGain * projWeeks).toFixed(2);
  const totalMuscle21 = ov('totalMuscle21') ?? +(muscleGain21 * projWeeks).toFixed(2);
  const totalMuscle11 = ov('totalMuscle11') ?? +(muscleGain11 * projWeeks).toFixed(2);
  const totalFat21 = ov('totalFat21') ?? +(fatGain21 * projWeeks).toFixed(2);
  const totalFat11 = ov('totalFat11') ?? +(fatGain11 * projWeeks).toFixed(2);

  return {
    bmr, tdee, adjustedKcal, adjustmentPct, dailyDeficit,
    weeklyAdjustedKcal, weeklyAdjustmentKcal,
    fatMass, fatMassTarget, kgToLose,
    weeklyLossLow, weeklyLossHigh, weeklyLossFromDeficit, weeklyLossPctFromDeficit,
    daysToTargetFat,
    isGain, weeklyGain, muscleGain21, muscleGain11, fatGain21, fatGain11, monthlyGainPct,
    projWeeks, totalWeightGain, totalMuscle21, totalMuscle11, totalFat21, totalFat11
  };
}

function computeBodyComposition(clientData) {
  const w = parseFloat(clientData?.weight) || 0;
  const h = parseFloat(clientData?.height) || 0;
  const bf = parseFloat(clientData?.body_fat_pct) || 0;
  const bfTarget = parseFloat(clientData?.body_fat_pct_target) || 0;
  const adj = GOAL_CALORIE_ADJUSTMENTS[clientData?.goal]?.adjustment || 0;
  const dailyDeficit = adj < 0 ? Math.abs(adj) : 0;
  const goal = clientData?.goal || '';
  const gender = clientData?.gender || 'femenino';

  const bmi = (w > 0 && h > 0) ? +(w / Math.pow(h / 100, 2)).toFixed(1) : null;
  const bmiCat = bmiCategory(bmi);
  const bfCat = bfInterpretation(bf, gender);

  const leanMass = (w > 0 && bf > 0) ? +(w * (1 - bf / 100)).toFixed(1) : null;
  const fatMass = (w > 0 && bf > 0) ? +(w * (bf / 100)).toFixed(1) : null;
  const fatMassTarget = (w > 0 && bfTarget > 0) ? +(w * (bfTarget / 100)).toFixed(1) : null;

  // 2026-02 · Overrides manuales del admin (si están vacíos, se calcula)
  const manualWfl = parseFloat(clientData?.manual_weekly_fat_loss_kg);
  const computedWeeklyFatLoss = dailyDeficit > 0 ? +((dailyDeficit * 7) / 7700).toFixed(2) : 0;
  const weeklyFatLoss = (!isNaN(manualWfl) && manualWfl > 0) ? manualWfl : computedWeeklyFatLoss;

  const manualWtt = parseInt(clientData?.manual_weeks_to_target);
  let weeksToTarget = null;
  if (!isNaN(manualWtt) && manualWtt > 0) {
    weeksToTarget = manualWtt;
  } else if (fatMass != null && fatMassTarget != null && fatMass > fatMassTarget && weeklyFatLoss > 0) {
    weeksToTarget = Math.ceil((fatMass - fatMassTarget) / weeklyFatLoss);
  }

  const weeklyMuscleGain = (goal === 'ganar_musculo' || goal === 'recomposicion') ? 0.1 : 0;

  // Fecha estimada de llegada al objetivo (día/mes)
  let target_date_iso = null;
  let target_date_label = null;
  if (weeksToTarget != null && weeksToTarget > 0) {
    const d = new Date();
    d.setDate(d.getDate() + weeksToTarget * 7);
    target_date_iso = d.toISOString();
    target_date_label = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  return {
    body_fat_pct: bf > 0 ? bf : null,
    body_fat_pct_target: bfTarget > 0 ? bfTarget : null,
    bmi,
    bmi_category: bmiCat ? bmiCat.label : null,
    bmi_category_color: bmiCat ? bmiCat.color : null,
    bf_interpretation: bfCat ? bfCat.label : null,
    bf_interpretation_color: bfCat ? bfCat.color : null,
    lean_mass_kg: leanMass,
    fat_mass_kg: fatMass,
    fat_mass_target_kg: fatMassTarget,
    weekly_fat_loss_kg: weeklyFatLoss,
    weeks_to_target: weeksToTarget,
    weekly_muscle_gain_kg: weeklyMuscleGain,
    daily_calorie_deficit_kcal: dailyDeficit,
    target_date_iso,
    target_date_label,
    goal,
    gender
  };
}

const PAGE_POSITIONS = [
  { id: 1, name: '📄 Página 1 (Antes de todo)' },
  { id: 2, name: '📄 Página 2 (Después de las comidas)' },
  { id: 3, name: '📄 Página 3 (Al final del PDF)' }
];

// Acompañamientos opcionales (no afectan macros)
const ACOMP_OPTIONS = [
  { group: 'Dulce Bajo Impacto', items: [
    { id: 'gelatina_sin_azucar', label: 'Gelatina sin azúcar' },
    { id: 'gelatina_sin_sabor_edulcorante', label: 'Gelatina sin sabor con edulcorante' },
    { id: 'pudin_light', label: 'Pudín light / sin azúcar' },
    { id: 'flan_light', label: 'Flan light / sin azúcar' },
  ]},
  { group: 'Bebidas', items: [
    { id: 'cafe_negro', label: 'Café negro' },
    { id: 'te_verde', label: 'Té verde' },
    { id: 'te_negro', label: 'Té negro' },
    { id: 'infusiones', label: 'Infusiones / aromáticas' },
    { id: 'agua_con_gas', label: 'Agua con gas' },
    { id: 'bebida_cero', label: 'Bebida cero azúcar' },
  ]},
  { group: 'Condimentos / Otros', items: [
    { id: 'salsa_cero', label: 'Salsa cero calorías' },
    { id: 'mostaza_sin_azucar', label: 'Mostaza sin azúcar añadida' },
    { id: 'vinagre_blanco', label: 'Vinagre blanco' },
    { id: 'vinagre_manzana', label: 'Vinagre de manzana' },
    { id: 'vinagre_balsamico_moderado', label: 'Vinagre balsámico (moderado)' },
    { id: 'palomitas_aire_controlado', label: 'Palomitas (aire caliente, porción controlada)' },
    { id: 'chicle_sin_azucar', label: 'Chicle sin azúcar' },
  ]},
];

// Categorías de emojis para el panel - TECLADO COMPLETO
const EMOJI_CATEGORIES = {
  '😀 Caras': [
    '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '☺️', '😚', '😙', '🥲',
    '😋', '😛', '😜', '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥', '😌', '😔',
    '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐', '😕', '😟',
    '🙁', '☹️', '😮', '😯', '😲', '😳', '🥺', '😦', '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖', '😣', '😞', '😓', '😩', '😫', '🥱',
    '😤', '😡', '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👺', '👻', '👽', '👾', '🤖'
  ],
  '👋 Manos': [
    '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊',
    '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦵', '🦿', '🦶', '👂', '🦻', '👃', '🧠', '🫀',
    '🫁', '🦷', '🦴', '👀', '👁️', '👅', '👄'
  ],
  '❤️ Amor': [
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '❤️‍🔥', '❤️‍🩹',
    '💋', '💌', '💐', '🌹', '🌷', '🌸', '💮', '🏵️', '🌺', '🌻', '🌼', '🥀', '💍', '👫', '👭', '👬', '💑', '👩‍❤️‍👨', '💏'
  ],
  '✅ Verificar': [
    '✅', '❌', '✓', '✔️', '☑️', '❎', '🚫', '⛔', '🔴', '🟠', '🟡', '🟢', '🔵', '🟣', '⚫', '⚪', '🟤', '🔘', '⭕', '✅', '☑️', '✔️',
    '❗', '❕', '❓', '❔', '‼️', '⁉️', '💯', '🆗', '🆙', '🆒', '🆕', '🆓', '🔤', '🔠', '🔡', '🔢', '🔣', '🔛', '🔝', '🔜', '🔚', '🔙'
  ],
  '💪 Fitness': [
    '💪', '🏋️', '🏋️‍♂️', '🏋️‍♀️', '🤸', '🤸‍♂️', '🤸‍♀️', '⛹️', '⛹️‍♂️', '⛹️‍♀️', '🤾', '🤾‍♂️', '🤾‍♀️', '🏃', '🏃‍♂️', '🏃‍♀️', '🚶', '🚶‍♂️', '🚶‍♀️',
    '🧘', '🧘‍♂️', '🧘‍♀️', '🧗', '🧗‍♂️', '🧗‍♀️', '🚴', '🚴‍♂️', '🚴‍♀️', '🏊', '🏊‍♂️', '🏊‍♀️', '🤽', '🤽‍♂️', '🤽‍♀️', '🏄', '🏄‍♂️', '🏄‍♀️',
    '🔥', '⚡', '💥', '✨', '⭐', '🌟', '🎯', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🏵️', '🎪', '🎭', '🎨', '🎬'
  ],
  '🍎 Frutas': [
    '🍎', '🍏', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🫒', '🥑'
  ],
  '🥦 Verduras': [
    '🥬', '🥦', '🥒', '🌶️', '🫑', '🥕', '🧅', '🧄', '🥔', '🍆', '🌽', '🥗', '🥣', '🍲', '🫕', '🥘', '🍳', '🧆', '🥙', '🌯', '🌮'
  ],
  '🍖 Proteínas': [
    '🥩', '🍗', '🍖', '🦴', '🌭', '🍔', '🍟', '🍕', '🧀', '🥚', '🍳', '🥓', '🥪', '🥨', '🥐', '🥖', '🍞', '🥯', '🧇', '🥞', '🧈',
    '🐟', '🍣', '🍤', '🦪', '🍙', '🍚', '🍘', '🍛', '🍜', '🦞', '🦐', '🦑', '🦀', '🐙'
  ],
  '🍰 Postres': [
    '🍰', '🎂', '🧁', '🥧', '🍮', '🍭', '🍬', '🍫', '🍩', '🍪', '🍿', '🧂', '🥤', '🧃', '🧉', '🍵', '☕', '🍶', '🍺', '🍻', '🥂', '🍷',
    '🥃', '🍸', '🍹', '🧊', '🍼', '🫖'
  ],
  '💧 Salud': [
    '💧', '💦', '🌊', '🩺', '💉', '🩹', '🩼', '🩻', '💊', '🩸', '🧬', '🦠', '⚕️', '🏥', '🚑', '⚖️', '💤', '😴', '🛏️', '🛌', '🧘'
  ],
  '⭐ Estrellas': [
    '⭐', '🌟', '✨', '💫', '🌠', '🎇', '🎆', '🎉', '🎊', '🥳', '🎁', '🎀', '🎗️', '🏷️', '💡', '🔦', '💎', '💰', '💵', '💴', '💶', '💷',
    '💸', '💳', '🪙', '💹', '📈', '📉'
  ],
  '☀️ Clima': [
    '☀️', '🌤️', '⛅', '🌥️', '☁️', '🌦️', '🌧️', '⛈️', '🌩️', '🌨️', '❄️', '💨', '💦', '🌊', '🌅', '🌄', '🌇', '🌆', '🌃', '🌉', '🌌',
    '🌙', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗', '🌘', '🌑', '🌒', '🌓', '🌔', '🪐', '🌍', '🌎', '🌏', '🌐', '🗺️'
  ],
  '⏰ Tiempo': [
    '⏰', '⏱️', '⏲️', '🕰️', '⌚', '📅', '📆', '🗓️', '📇', '🕐', '🕑', '🕒', '🕓', '🕔', '🕕', '🕖', '🕗', '🕘', '🕙', '🕚', '🕛',
    '🕜', '🕝', '🕞', '🕟', '🕠', '🕡', '🕢', '🕣', '🕤', '🕥', '🕦', '🕧'
  ],
  '🐕 Animales': [
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐔', '🐧', '🐦', '🐤',
    '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🪲', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🕷️', '🦂', '🐢', '🐍', '🦎',
    '🦖', '🦕', '🐙', '🦑', '🦐', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓', '🦍', '🦧', '🐘', '🦛',
    '🦏', '🐪', '🐫', '🦒', '🦘', '🦬', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐', '🦌', '🐕', '🐩', '🦮', '🐕‍🦺', '🐈', '🐈‍⬛'
  ],
  '🌺 Plantas': [
    '🌵', '🎄', '🌲', '🌳', '🌴', '🪵', '🌱', '🌿', '☘️', '🍀', '🎍', '🎋', '🍃', '🍂', '🍁', '🪴', '🌾', '🌺', '🌻', '🌹', '🌷', '🌼',
    '🌸', '💐', '🌿', '🪻', '🪷'
  ],
  '🏠 Objetos': [
    '🏠', '🏡', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪', '🏫', '🏬', '🏭', '🏯', '🏰', '💒', '🗼', '🗽', '⛪', '🕌', '🛕', '🕍',
    '⛩️', '🕋', '⛲', '⛺', '🌁', '🌃', '🏙️', '🌄', '🌅', '🌆', '🌇', '🌉', '🎠', '🎡', '🎢', '💈', '🎪', '🚂', '🚃', '🚄', '🚅', '🚆',
    '🚇', '🚈', '🚉', '🚊', '🚝', '🚞', '🚋', '🚌', '🚍', '🚎', '🚐', '🚑', '🚒', '🚓', '🚔', '🚕', '🚖', '🚗', '🚘', '🚙', '🛻', '🚚'
  ],
  '➡️ Flechas': [
    '➡️', '⬅️', '⬆️', '⬇️', '↗️', '↘️', '↙️', '↖️', '↕️', '↔️', '↩️', '↪️', '⤴️', '⤵️', '🔃', '🔄', '🔙', '🔚', '🔛', '🔜', '🔝',
    '▶️', '⏩', '⏭️', '⏯️', '◀️', '⏪', '⏮️', '🔼', '⏫', '🔽', '⏬', '⏸️', '⏹️', '⏺️', '⏏️', '🔀', '🔁', '🔂'
  ],
  '📝 Oficina': [
    '📝', '📋', '📌', '📍', '🔗', '📎', '🖇️', '📐', '📏', '🗂️', '📂', '📁', '🗃️', '🗄️', '🗑️', '🔒', '🔓', '🔏', '🔐', '🔑', '🗝️',
    '📕', '📗', '📘', '📙', '📚', '📖', '🔖', '📰', '🗞️', '📄', '📃', '📑', '🧾', '📊', '📈', '📉', '🗒️', '🗓️', '📆', '📅', '📇',
    '✏️', '✒️', '🖋️', '🖊️', '🖌️', '🖍️', '📧', '📨', '📩', '📤', '📥', '📦', '📫', '📪', '📬', '📭', '📮', '🗳️', '✉️'
  ],
  '🔔 Símbolos': [
    '🔔', '🔕', '📣', '📢', '👁️‍🗨️', '💬', '💭', '🗯️', '♠️', '♣️', '♥️', '♦️', '🃏', '🎴', '🀄', '🎲', '🎯', '🪄', '🧿', '🪬', '🔮',
    '♻️', '⚜️', '🔱', '📛', '🔰', '⭕', '✅', '☑️', '✔️', '❌', '❎', '➕', '➖', '➗', '✖️', '♾️', '💲', '💱', '™️', '©️', '®️',
    '👁️', '🗣️', '👤', '👥', '🫂', '👣', '🧬', '🦷', '🦴', '❣️', '💔', '❤️‍🔥', '❤️‍🩹', '💋', '🫶'
  ],
  '🔢 Números': [
    '0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟', '#️⃣', '*️⃣', '🔢', '🔣', '🔤', '🔡', '🔠', '🆎', '🆑', '🆒',
    '🆓', '🆔', '🆕', '🆖', '🆗', '🆘', '🆙', '🆚', '🈁', '🈂️', '🈷️', '🈶', '🈯', '🈳', '🉐', '🉑', '🈴', '🈵', '🈲', '🈹', '🈺'
  ]
};

// Componente de barra de herramientas de texto enriquecido con teclado emoji completo
const RichTextToolbar = ({ pageId, content, onUpdate }) => {
  const [showEmojiPanel, setShowEmojiPanel] = useState(false);
  const [activeCategory, setActiveCategory] = useState('😀 Caras');
  const [emojiSearch, setEmojiSearch] = useState('');
  const [recentEmojis, setRecentEmojis] = useState(() => {
    const saved = localStorage.getItem('recentEmojis');
    return saved ? JSON.parse(saved) : ['✅', '❌', '💪', '🔥', '❤️', '⭐', '💧', '😊'];
  });

  // Aplicar formato al texto seleccionado
  const applyFormat = (formatType) => {
    const textarea = document.getElementById(`page-content-${pageId}`);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.substring(start, end);
    
    let newContent;
    switch(formatType) {
      case 'bold':
        newContent = content.substring(0, start) + `**${selected}**` + content.substring(end);
        break;
      case 'italic':
        newContent = content.substring(0, start) + `*${selected}*` + content.substring(end);
        break;
      case 'underline':
        newContent = content.substring(0, start) + `__${selected}__` + content.substring(end);
        break;
      default:
        newContent = content;
    }
    onUpdate(newContent);
  };

  // Insertar emoji en el cursor
  const insertEmoji = (emoji) => {
    const textarea = document.getElementById(`page-content-${pageId}`);
    if (!textarea) {
      onUpdate(content + emoji);
    } else {
      const start = textarea.selectionStart;
      const newContent = content.substring(0, start) + emoji + content.substring(start);
      onUpdate(newContent);
      
      // Mantener el foco y mover cursor
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + emoji.length, start + emoji.length);
      }, 0);
    }
    
    // Guardar en recientes
    setRecentEmojis(prev => {
      const updated = [emoji, ...prev.filter(e => e !== emoji)].slice(0, 16);
      localStorage.setItem('recentEmojis', JSON.stringify(updated));
      return updated;
    });
  };

  // Buscar emojis
  const getFilteredEmojis = () => {
    if (!emojiSearch.trim()) {
      return EMOJI_CATEGORIES[activeCategory] || [];
    }
    const searchLower = emojiSearch.toLowerCase();
    const allEmojis = Object.values(EMOJI_CATEGORIES).flat();
    // Buscar por categoría que contenga el término
    const matchingCategories = Object.keys(EMOJI_CATEGORIES).filter(cat => 
      cat.toLowerCase().includes(searchLower)
    );
    if (matchingCategories.length > 0) {
      return matchingCategories.flatMap(cat => EMOJI_CATEGORIES[cat]);
    }
    return allEmojis;
  };

  const categoryKeys = Object.keys(EMOJI_CATEGORIES);

  return (
    <div className="bg-gray-100 rounded-t-lg border border-b-0">
      {/* Barra de formato principal */}
      <div className="flex flex-wrap items-center gap-1 p-2">
        {/* Botones de formato */}
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-8 px-3 font-bold hover:bg-blue-100"
          onClick={() => applyFormat('bold')}
          title="Negrita (**texto**)"
        >
          B
        </Button>
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-8 px-3 italic hover:bg-blue-100"
          onClick={() => applyFormat('italic')}
          title="Cursiva (*texto*)"
        >
          I
        </Button>
        <Button 
          size="sm" 
          variant="ghost" 
          className="h-8 px-3 underline hover:bg-blue-100"
          onClick={() => applyFormat('underline')}
          title="Subrayado (__texto__)"
        >
          U
        </Button>
        
        <div className="w-px h-6 bg-gray-300 mx-2"></div>
        
        {/* Botón para abrir panel de emojis */}
        <Button 
          size="sm" 
          variant={showEmojiPanel ? "default" : "outline"}
          className={`h-8 px-3 ${showEmojiPanel ? 'bg-purple-500 hover:bg-purple-600 text-white' : ''}`}
          onClick={() => setShowEmojiPanel(!showEmojiPanel)}
          title="Teclado de emojis completo"
        >
          😊 Emojis
        </Button>
        
        <div className="w-px h-6 bg-gray-300 mx-2"></div>
        
        {/* Emojis recientes/rápidos */}
        <span className="text-xs text-gray-500 mr-1">Rápidos:</span>
        {recentEmojis.slice(0, 8).map((emoji, idx) => (
          <Button 
            key={`${emoji}-${idx}`}
            size="sm" 
            variant="ghost" 
            className="h-8 w-8 p-0 text-base hover:bg-purple-100 hover:scale-110 transition-transform"
            onClick={() => insertEmoji(emoji)}
            title={emoji}
          >
            {emoji}
          </Button>
        ))}
      </div>
      
      {/* Panel de emojis expandido - TECLADO COMPLETO */}
      {showEmojiPanel && (
        <div className="border-t bg-white">
          {/* Barra de búsqueda */}
          <div className="p-2 border-b bg-gray-50">
            <div className="relative">
              <input
                type="text"
                placeholder="🔍 Buscar emojis por categoría..."
                className="w-full px-3 py-2 pl-8 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                value={emojiSearch}
                onChange={(e) => setEmojiSearch(e.target.value)}
              />
              <span className="absolute left-2.5 top-2.5 text-gray-400">🔍</span>
              {emojiSearch && (
                <button
                  className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                  onClick={() => setEmojiSearch('')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex">
            {/* Sidebar de categorías */}
            <div className="w-20 bg-gray-50 border-r max-h-64 overflow-y-auto">
              {categoryKeys.map(category => (
                <button
                  key={category}
                  className={`w-full p-2 text-center hover:bg-purple-100 transition-colors ${
                    activeCategory === category ? 'bg-purple-200 border-r-2 border-purple-500' : ''
                  }`}
                  onClick={() => {
                    setActiveCategory(category);
                    setEmojiSearch('');
                  }}
                  title={category}
                >
                  <span className="text-xl">{category.split(' ')[0]}</span>
                  <p className="text-[10px] text-gray-500 truncate">{category.split(' ')[1] || ''}</p>
                </button>
              ))}
            </div>
            
            {/* Grid principal de emojis */}
            <div className="flex-1 p-2">
              {/* Título de categoría */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-300">
                  {emojiSearch ? `Resultados para "${emojiSearch}"` : activeCategory}
                </h4>
                <span className="text-xs text-gray-400">
                  {getFilteredEmojis().length} emojis
                </span>
              </div>
              
              {/* Grid de emojis */}
              <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-0.5 max-h-48 overflow-y-auto p-1 bg-gray-50 rounded-lg">
                {getFilteredEmojis().map((emoji, idx) => (
                  <button
                    key={`${emoji}-${idx}`}
                    className="h-9 w-9 flex items-center justify-center text-xl hover:bg-purple-200 rounded transition-all hover:scale-125 active:scale-100"
                    onClick={() => insertEmoji(emoji)}
                    title={`Insertar ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              
              {/* Recientes */}
              {recentEmojis.length > 0 && !emojiSearch && (
                <div className="mt-3 pt-2 border-t">
                  <h4 className="text-xs font-medium text-gray-500 mb-1">🕐 Usados recientemente</h4>
                  <div className="flex flex-wrap gap-0.5">
                    {recentEmojis.map((emoji, idx) => (
                      <button
                        key={`recent-${emoji}-${idx}`}
                        className="h-8 w-8 flex items-center justify-center text-lg hover:bg-purple-100 rounded transition-all hover:scale-110"
                        onClick={() => insertEmoji(emoji)}
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Footer informativo */}
          <div className="px-3 py-2 bg-gray-50 border-t text-xs text-gray-500 flex items-center justify-between">
            <span>💡 Haz clic en un emoji para insertarlo</span>
            <button 
              className="text-purple-500 hover:text-purple-700 hover:underline"
              onClick={() => setShowEmojiPanel(false)}
            >
              Cerrar panel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const OfflineGenerator = ({ onClose }) => {
  const [activeTab, setActiveTab] = useState('diet');
  const [generating, setGenerating] = useState(false);
  const [foodDatabase, setFoodDatabase] = useState({});
  
  // Estado para planes guardados
  const [savedPlans, setSavedPlans] = useState([]);
  const [loadingSavedPlans, setLoadingSavedPlans] = useState(false);
  const [showSavedPlans, setShowSavedPlans] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planName, setPlanName] = useState('');
  const [planDates, setPlanDates] = useState({
    diet_total_update_date: null,
    diet_progression_date: null,
    workout_total_update_date: null,
    workout_progression_date: null
  });
  const [matchingPlan, setMatchingPlan] = useState(null); // Plan encontrado por nombre de cliente
  const [planSearchTerm, setPlanSearchTerm] = useState(''); // Búsqueda de planes guardados
  
  // Helper para normalizar post_workout_meal
  const normalizePostWorkoutMeal = (value) => {
    if (!value || value === 'ninguna' || value === 'none' || value === 'null' || value === '') {
      return 'none';
    }
    // Si es número, convertir a string
    if (typeof value === 'number') {
      return value.toString();
    }
    return value;
  };
  
  // Estado para páginas por defecto
  const [defaultPages, setDefaultPages] = useState([]);
  const [showDefaultPagesConfig, setShowDefaultPagesConfig] = useState(false);
  const [newDefaultPage, setNewDefaultPage] = useState({ title: '', content: '', page_position: 1 });
  const [editingDefaultPage, setEditingDefaultPage] = useState(null); // Página por defecto en edición
  
  // Planes de suscripción cargados desde el API
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  
  // Cargar planes de suscripción al montar el componente
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        const response = await axios.get(`${API}/admin/subscription-plans`, { withCredentials: false });
        const plansData = response.data?.plans || response.data || [];
        
        if (Array.isArray(plansData) && plansData.length > 0) {
          // Mapear los planes al formato que necesitamos
          const plans = plansData.map(plan => ({
            id: plan.slug || plan.id,
            name: plan.name,
            color: plan.color || 'bg-gray-500',
            icon: plan.icon || '📋',
            background_image: plan.background_image
          }));
          setSubscriptionPlans(plans);
          
          // Si no hay plan seleccionado y hay planes disponibles, seleccionar el primero
          if (plans.length > 0 && !clientData.plan_type) {
            setClientData(prev => ({ ...prev, plan_type: plans[0].id }));
          }
        }
      } catch (error) {
        console.error('Error loading subscription plans:', error);
        // Usar planes por defecto si falla la carga
        setSubscriptionPlans(PLAN_TYPES);
      } finally {
        setLoadingPlans(false);
      }
    };
    
    fetchSubscriptionPlans();
  }, []);
  
  // Usar planes cargados o los por defecto
  const availablePlanTypes = subscriptionPlans.length > 0 ? subscriptionPlans : PLAN_TYPES;
  
  // Control de branding en PDFs
  const [showBranding, setShowBranding] = useState(true); // Por defecto muestra el logo/nombre DeisyCFit
  // 2026-02 · Overrides de Cálculos Metabólicos y Proyecciones (admin-only, no persisten)
  const [metabolicOverrides, setMetabolicOverrides] = useState({});
  const [projWeeks, setProjWeeks] = useState(20);
  // 2026-02 · Distribución de macros (%) - por defecto 43/30/27, editable
  const [macroSplitPct, setMacroSplitPct] = useState({ carbs: 43, protein: 30, fats: 27 });
  
  // Datos del cliente
  const [clientData, setClientData] = useState({
    name: '',
    age: '',
    weight: '',
    height: '',
    body_fat_pct: '', // Opcional. Si > 0 → BMR con Katch-McArdle; si vacío/0 → Harris-Benedict de respaldo
    body_fat_pct_target: '', // Opcional. Objetivo de % de grasa (solo referencia visual)
    manual_weekly_fat_loss_kg: '', // 2026-02 · Override manual del admin (vacío = calculado)
    manual_weeks_to_target: '',    // 2026-02 · Override manual del admin (vacío = calculado)
    gender: 'femenino', // 2026-02 · Género para interpretación de % grasa
    goal: 'perder_grasa',
    activity_level: 'moderado',
    plan_type: 'basic',
    num_meals: 5, // Número de comidas al día
    diet_preference: 'equilibrada' // Preferencia de distribución de macros
  });
  
  // Último formulario de actualización enviado por la usuaria (foods_to_remove, foods_to_add, etc.)
  const [latestFollowup, setLatestFollowup] = useState(null);
  
  // Estado para formularios clínicos
  const [clinicalForms, setClinicalForms] = useState([]);
  const [showClinicalFormModal, setShowClinicalFormModal] = useState(false);
  const [sendingClinicalForm, setSendingClinicalForm] = useState(false);
  
  // Cargar formularios clínicos disponibles
  useEffect(() => {
    const fetchClinicalForms = async () => {
      try {
        const response = await axios.get(`${API}/clinical-forms/templates`);
        if (response.data?.templates) {
          setClinicalForms(response.data.templates);
        }
      } catch (error) {
        console.error('Error loading clinical forms:', error);
      }
    };
    fetchClinicalForms();
  }, []);
  
  // Función para enviar formulario clínico al usuario
  const sendClinicalFormToUser = async (formTemplateId) => {
    if (!clientData.email) {
      toast.error('Primero carga un usuario para enviarle el formulario');
      return;
    }
    
    setSendingClinicalForm(true);
    try {
      const response = await axios.post(`${API}/clinical-forms/assign`, {
        form_template_id: formTemplateId,
        user_email: clientData.email,
        notes: `Enviado desde Generador Offline para ${clientData.name}`
      });
      
      if (response.data?.success) {
        toast.success(`Formulario enviado a ${clientData.name}`);
        setShowClinicalFormModal(false);
        // Recargar formularios clínicos del usuario
        fetchUserClinicalForms(clientData.email);
      }
    } catch (error) {
      console.error('Error sending clinical form:', error);
      const msg = error.response?.data?.detail || 'Error al enviar el formulario';
      toast.error(msg);
    } finally {
      setSendingClinicalForm(false);
    }
  };
  
  // Estado para formularios clínicos del usuario cargado
  const [userClinicalForms, setUserClinicalForms] = useState([]);
  const [expandedClinicalFormId, setExpandedClinicalFormId] = useState(null);
  
  // Función para cargar formularios clínicos del usuario
  const fetchUserClinicalForms = async (email) => {
    if (!email) return;
    try {
      const response = await axios.get(`${API}/clinical-forms/user/${email}/all`);
      if (response.data?.forms) {
        setUserClinicalForms(response.data.forms);
      }
    } catch (error) {
      console.error('Error fetching user clinical forms:', error);
    }
  };
  
  // ===== SISTEMA DE DIAGNÓSTICO POR PATOLOGÍAS =====
  const [userPathologyCases, setUserPathologyCases] = useState([]);
  // 2026-02 · Modal con Data Progresión impersonando a la clienta
  const [adminDataProgOpen, setAdminDataProgOpen] = useState(false);
  const [showDiagnosisModal, setShowDiagnosisModal] = useState(false);
  const [selectedPathologyCase, setSelectedPathologyCase] = useState(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState({ subject: '', body: '' });
  const [sendingDiagnosis, setSendingDiagnosis] = useState(false);
  const [processingPathologies, setProcessingPathologies] = useState(false);
  const [emailImageUrls, setEmailImageUrls] = useState([]);
  const [uploadingEmailImage, setUploadingEmailImage] = useState(false);
  const [generatingClinicalEmail, setGeneratingClinicalEmail] = useState(false);
  const [protocolStatus, setProtocolStatus] = useState(null);
  
  // ===== ASIGNACIÓN MANUAL DE PATOLOGÍAS =====
  const [showManualPathologyModal, setShowManualPathologyModal] = useState(false);
  const [selectedManualPathologies, setSelectedManualPathologies] = useState([]);
  
  // Lista completa de patologías disponibles
  const AVAILABLE_PATHOLOGIES = [
    // RENAL
    { id: 'enfermedad_renal', name: 'Enfermedad renal crónica', category: 'RENAL', tag: 'SHOW_FORM_RENAL' },
    // HEPÁTICO
    { id: 'higado_graso', name: 'Hígado graso', category: 'HEPATIC', tag: 'SHOW_FORM_HEPATIC' },
    // TIROIDES
    { id: 'hipotiroidismo', name: 'Hipotiroidismo', category: 'THYROID', tag: 'SHOW_FORM_THYROID' },
    { id: 'hipertiroidismo', name: 'Hipertiroidismo', category: 'THYROID', tag: 'SHOW_FORM_THYROID' },
    // GLUCOSA / METABÓLICO
    { id: 'diabetes_tipo1', name: 'Diabetes Tipo 1', category: 'GLUCOSE', tag: 'SHOW_FORM_GLUCOSE' },
    { id: 'diabetes_tipo2', name: 'Diabetes Tipo 2', category: 'GLUCOSE', tag: 'SHOW_FORM_GLUCOSE' },
    { id: 'resistencia_insulina', name: 'Resistencia a la insulina', category: 'GLUCOSE', tag: 'SHOW_FORM_GLUCOSE' },
    { id: 'hipoglucemia', name: 'Hipoglucemia reactiva', category: 'GLUCOSE', tag: 'SHOW_FORM_GLUCOSE' },
    // DIGESTIVO
    { id: 'colon_irritable', name: 'Síndrome de colon irritable', category: 'DIGESTIVE', tag: 'SHOW_FORM_DIGESTIVE' },
    { id: 'intolerancia_lactosa', name: 'Intolerancia a la lactosa', category: 'DIGESTIVE', tag: 'SHOW_FORM_DIGESTIVE' },
    { id: 'gastritis', name: 'Gastritis', category: 'DIGESTIVE', tag: 'SHOW_FORM_DIGESTIVE' },
    { id: 'reflujo', name: 'Reflujo gastroesofágico', category: 'DIGESTIVE', tag: 'SHOW_FORM_DIGESTIVE' },
    { id: 'celiaquia', name: 'Enfermedad celíaca', category: 'DIGESTIVE', tag: 'SHOW_FORM_DIGESTIVE' },
    // LÍPIDOS
    { id: 'colesterol_alto', name: 'Colesterol alto', category: 'LIPIDS', tag: 'SHOW_FORM_LIPIDS' },
    { id: 'trigliceridos_altos', name: 'Triglicéridos altos', category: 'LIPIDS', tag: 'SHOW_FORM_LIPIDS' },
    // CARDIOVASCULAR
    { id: 'hipertension', name: 'Hipertensión arterial', category: 'CARDIO', tag: 'SHOW_FORM_CARDIO' },
    // HEMATOLÓGICO
    { id: 'anemia', name: 'Anemia', category: 'HEMATO', tag: 'SHOW_FORM_HEMATO' },
    // ÓSEO
    { id: 'osteoporosis', name: 'Osteoporosis', category: 'BONE', tag: 'SHOW_FORM_BONE' },
    // INFLAMATORIO
    { id: 'artritis', name: 'Artritis', category: 'INFLAMMATORY', tag: 'SHOW_FORM_INFLAMMATORY' },
    { id: 'fibromialgia', name: 'Fibromialgia', category: 'INFLAMMATORY', tag: 'SHOW_FORM_INFLAMMATORY' },
    // GINECOLÓGICO
    { id: 'endometriosis', name: 'Endometriosis', category: 'GYNECO', tag: 'SHOW_FORM_GYNECO' },
    { id: 'sop', name: 'Síndrome de Ovario Poliquístico (SOP)', category: 'GYNECO', tag: 'SHOW_FORM_GYNECO' },
    // HORMONAL (NUEVO)
    { id: 'hormonal', name: 'Desbalance Hormonal', category: 'HORMONAL', tag: 'SHOW_FORM_HORMONAL' }
  ];
  
  // Función para asignar patologías manualmente
  const assignManualPathologies = async () => {
    if (!clientData.email || selectedManualPathologies.length === 0) {
      toast.error('Selecciona al menos una patología');
      return;
    }
    
    setProcessingPathologies(true);
    try {
      // Obtener los nombres de las patologías seleccionadas
      const pathologyNames = selectedManualPathologies.map(id => {
        const path = AVAILABLE_PATHOLOGIES.find(p => p.id === id);
        return path ? path.name : id;
      });
      
      // Actualizar el estado local del cliente con las nuevas patologías
      const currentPathologies = clientData.additional_info?.pathologies || [];
      const mergedPathologies = [...new Set([...currentPathologies, ...pathologyNames])];
      
      setClientData(prev => ({
        ...prev,
        additional_info: {
          ...prev.additional_info,
          pathologies: mergedPathologies
        }
      }));
      
      // Procesar las patologías en el backend
      const response = await axios.post(`${API}/pathology/process`, {
        user_id: clientData.id || clientData.email,
        user_email: clientData.email,
        user_name: clientData.name || 'Usuario',
        intake_source: 'manual_assignment',
        pathologies: pathologyNames
      });
      
      if (response.data?.success) {
        toast.success(`✅ ${pathologyNames.length} patología(s) asignadas y procesadas`);
        setShowManualPathologyModal(false);
        setSelectedManualPathologies([]);
        
        // Recargar casos y formularios
        await fetchUserPathologyCases(clientData.email);
        await fetchUserClinicalForms(clientData.email);
      }
    } catch (error) {
      console.error('Error assigning pathologies:', error);
      toast.error(error.response?.data?.detail || 'Error al asignar patologías');
    } finally {
      setProcessingPathologies(false);
    }
  };
  
  // Toggle selección de patología manual
  const toggleManualPathology = (pathId) => {
    setSelectedManualPathologies(prev => 
      prev.includes(pathId) 
        ? prev.filter(id => id !== pathId)
        : [...prev, pathId]
    );
  };
  
  // Cargar casos de patología del usuario
  const fetchUserPathologyCases = async (email) => {
    if (!email) return;
    try {
      const response = await axios.get(`${API}/pathology/user/${email}`);
      if (response.data?.cases) {
        setUserPathologyCases(response.data.cases);
      }
    } catch (error) {
      console.error('Error fetching user pathology cases:', error);
    }
  };
  
  // ===== PROCESAMIENTO CLÍNICO =====
  // Mapeo de nombres de patologías a IDs del módulo clínico
  const PATHOLOGY_NAME_TO_ID = {
    'enfermedad renal crónica': 'enfermedad_renal_cronica',
    'enfermedad renal': 'enfermedad_renal_cronica',
    'enfermedad renal cronica': 'enfermedad_renal_cronica',
    'diabetes tipo 1': 'diabetes_tipo_1',
    'diabetes tipo1': 'diabetes_tipo_1',
    'diabetes tipo 2': 'diabetes_tipo_2',
    'diabetes tipo2': 'diabetes_tipo_2',
    'diabetes_tipo1': 'diabetes_tipo_1',
    'diabetes_tipo2': 'diabetes_tipo_2',
    'resistencia a la insulina': 'resistencia_insulina',
    'resistencia insulina': 'resistencia_insulina',
    'resistencia_insulina': 'resistencia_insulina',
    'síndrome de ovario poliquístico (sop)': 'sop',
    'sop': 'sop',
    'hipotiroidismo': 'hipotiroidismo',
    'hipertiroidismo': 'hipertiroidismo',
    'hipoglucemia reactiva': 'hipoglucemia_reactiva',
    'hipoglucemia': 'hipoglucemia_reactiva',
    'gastritis': 'gastritis',
    'reflujo gastroesofágico': 'reflujo_gastroesofagico',
    'reflujo gastroesofagico': 'reflujo_gastroesofagico',
    'reflujo': 'reflujo_gastroesofagico',
    'síndrome de colon irritable': 'colon_irritable',
    'colon irritable': 'colon_irritable',
    'colon_irritable': 'colon_irritable',
    'enfermedad celíaca': 'enfermedad_celiaca',
    'enfermedad celiaca': 'enfermedad_celiaca',
    'celiaquía': 'enfermedad_celiaca',
    'celiaquia': 'enfermedad_celiaca',
    'hígado graso': 'higado_graso',
    'higado graso': 'higado_graso',
    'higado_graso': 'higado_graso',
    'hipertensión arterial': 'hipertension',
    'hipertensión': 'hipertension',
    'hipertension': 'hipertension',
    'colesterol alto': 'colesterol_alto',
    'colesterol_alto': 'colesterol_alto',
    'triglicéridos altos': 'trigliceridos_altos',
    'trigliceridos altos': 'trigliceridos_altos',
    'trigliceridos_altos': 'trigliceridos_altos',
    'anemia': 'anemia',
    'osteoporosis': 'osteoporosis',
    'artritis': 'artritis',
    'fibromialgia': 'fibromialgia',
    'endometriosis': 'endometriosis',
    'intolerancia a la lactosa': 'intolerancia_lactosa',
    'intolerancia lactosa': 'intolerancia_lactosa',
    'intolerancia_lactosa': 'intolerancia_lactosa',
    'sensibilidad al gluten': 'alergia_gluten',
    'gluten': 'alergia_gluten',
    'alergia al trigo': 'alergia_trigo',
    'trigo': 'alergia_trigo',
    'alergia al huevo': 'alergia_huevo',
    'huevo': 'alergia_huevo',
    'alergia a mariscos': 'alergia_mariscos',
    'mariscos': 'alergia_mariscos',
    'alergia a la soya': 'alergia_soya',
    'soya': 'alergia_soya',
    'alergia a frutos secos': 'alergia_frutos_secos',
    'frutos secos': 'alergia_frutos_secos',
    'maní': 'alergia_frutos_secos',
    'mani': 'alergia_frutos_secos',
    'desbalance hormonal': 'sop',
    'hormonal': 'sop',
    // Alergias alimentarias comunes (formato corto)
    'lactosa': 'intolerancia_lactosa',
    'leche': 'intolerancia_lactosa',
    'lácteos': 'intolerancia_lactosa',
    'lacteos': 'intolerancia_lactosa',
    'alergia a la lactosa': 'intolerancia_lactosa',
    'alergia lactosa': 'intolerancia_lactosa',
    'pescado': 'alergia_pescado',
    'alergia al pescado': 'alergia_pescado',
    'camarón': 'alergia_mariscos',
    'camaron': 'alergia_mariscos',
    'nueces': 'alergia_frutos_secos',
    'almendras': 'alergia_frutos_secos',
    'cacahuate': 'alergia_frutos_secos',
    'avena': 'sensibilidad_avena'
  };

  // Cargar datos del usuario (incluyendo patologías) cuando se busca por email
  // Busca en múltiples fuentes: users, offline_saved_plans, pending_personalized_plans
  const fetchUserDataByEmail = async (email) => {
    if (!email || !email.includes('@')) return;
    
    let pathologiesFound = [];
    let userIdFound = null;
    let userNameFound = '';
    
    // Fetch del último formulario de actualización (followup) por email
    try {
      const fuRes = await axios.get(`${API}/admin/followup/latest-by-email/${encodeURIComponent(email)}`);
      setLatestFollowup(fuRes.data?.followup || null);
    } catch (e) {
      setLatestFollowup(null);
    }
    
    // Helper: filtrar patologías inválidas (ninguna, vacías, etc.)
    const filterValidPathologies = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.filter(p => p && typeof p === 'string' && 
        !['ninguna', 'ninguna patología', 'ninguna patologia', 'no', 'no tengo', ''].includes(p.toLowerCase().trim())
      );
    };
    
    // 1. Intentar cargar desde la colección users
    try {
      const response = await axios.get(`${API}/users/by-email/${email}`);
      if (response.data?.user) {
        const userData = response.data.user;
        userIdFound = userData._id || userData.id;
        userNameFound = userData.name || '';
        pathologiesFound = filterValidPathologies(userData.additional_info?.pathologies);
      }
    } catch (error) {
      console.log('User not found in users collection');
    }
    
    // 2. Si no hay patologías válidas, buscar en los planes guardados offline
    if (pathologiesFound.length === 0) {
      try {
        const offlinePlans = await axios.get(`${API}/admin/offline-generator/saved-plans`);
        if (Array.isArray(offlinePlans.data)) {
          const userPlan = offlinePlans.data.find(p => p.email?.toLowerCase() === email.toLowerCase());
          if (userPlan) {
            const planPathologies = filterValidPathologies(userPlan.additional_info?.pathologies);
            if (planPathologies.length > 0) {
              pathologiesFound = planPathologies;
              userNameFound = userNameFound || userPlan.name || '';
              console.log('Pathologies found in offline_saved_plans:', planPathologies);
            }
          }
        }
      } catch (error) {
        console.log('Error fetching offline plans:', error);
      }
    }
    
    // 3. Si aún no hay patologías válidas, buscar en planes pendientes
    if (pathologiesFound.length === 0) {
      try {
        const pendingPlans = await axios.get(`${API}/personalized-plans/pending`);
        if (pendingPlans.data?.plans) {
          const userPlan = pendingPlans.data.plans.find(p => 
            p.client_data?.email?.toLowerCase() === email.toLowerCase()
          );
          if (userPlan?.client_data) {
            const planPathologies = filterValidPathologies(userPlan.client_data.pathologies);
            if (planPathologies.length > 0) {
              pathologiesFound = planPathologies;
              userNameFound = userNameFound || userPlan.client_data.full_name || '';
              console.log('Pathologies found in pending_personalized_plans:', planPathologies);
            }
          }
        }
      } catch (error) {
        console.log('Error fetching pending plans:', error);
      }
    }
    
    // 4. Buscar en pathology_cases (casos de diagnóstico ya procesados)
    if (pathologiesFound.length === 0) {
      try {
        const casesResponse = await axios.get(`${API}/pathology/cases/user/${email}`);
        if (casesResponse.data?.cases?.length > 0) {
          // Tomar las patologías del caso más reciente
          const latestCase = casesResponse.data.cases[0];
          const casePathologies = filterValidPathologies(latestCase.pathologies);
          if (casePathologies.length > 0) {
            pathologiesFound = casePathologies;
            userNameFound = userNameFound || latestCase.user_name || '';
            console.log('Pathologies found in pathology_cases:', casePathologies);
          }
        }
      } catch (error) {
        console.log('Error fetching pathology cases:', error);
      }
    }
    
    // Actualizar clientData con la info encontrada
    setClientData(prev => ({
      ...prev,
      name: prev.name || userNameFound,
      user_id: userIdFound || prev.user_id,
      followup_frequency_weeks: prev.followup_frequency_weeks,
      additional_info: {
        ...prev.additional_info,
        pathologies: pathologiesFound.length > 0 ? pathologiesFound : (prev.additional_info?.pathologies || [])
      }
    }));

    // Cargar frecuencia de followup del usuario
    try {
      const res = await axios.get(`${API}/users/by-email/${email}`);
      if (res.data?.user?.followup_frequency_weeks !== undefined) {
        setClientData(prev => ({ ...prev, followup_frequency_weeks: res.data.user.followup_frequency_weeks }));
      }
    } catch (e) { /* ignore */ }
  };
  
  // Procesar patologías de un usuario (genera caso de diagnóstico)
  const processUserPathologies = async () => {
    try {
      const pathologies = clientData.additional_info?.pathologies || [];
      const allergies = clientData.additional_info?.food_allergies || [];
      const intolerances = clientData.additional_info?.intolerances || [];
      const disliked = clientData.additional_info?.disliked_foods || [];
      
      // Filtrar patologías válidas
      const validPathologies = pathologies.filter(
        p => p && typeof p === 'string' && p.toLowerCase() !== 'ninguna' && p.toLowerCase() !== 'ninguna patología'
      );
      
      const hasValidPathologies = validPathologies.length > 0;
      const hasAllergies = allergies.length > 0;
      const hasIntolerances = intolerances.length > 0;
      
      if (!hasValidPathologies && !hasAllergies && !hasIntolerances) {
        toast.error('No hay patologías, alergias o intolerancias para procesar');
        return;
      }
      
      setProcessingPathologies(true);
      
      // 1. Si hay patologías válidas, procesar con el sistema de patologías (emails)
      if (hasValidPathologies && clientData.email) {
        const payload = {
          user_id: clientData.user_id || clientData.id || clientData.email,
          user_email: clientData.email,
          user_name: clientData.name || 'Usuario',
          intake_source: clientData.subscription_plan || 'premium_form',
          pathologies: validPathologies
        };
        
        try {
          const response = await axios.post(`${API}/pathology/process`, payload);
          if (response.data?.success) {
            toast.success(`Se procesaron ${validPathologies.length} patologías. Borrador de diagnóstico generado.`);
            await fetchUserPathologyCases(clientData.email);
            await fetchUserClinicalForms(clientData.email);
          }
        } catch (error) {
          console.error('Error processing pathology cases:', error);
        }
      }
      
      // 2. SIEMPRE activar el módulo clínico (con patologías + alergias + intolerancias)
      const allConditions = [...validPathologies, ...allergies, ...intolerances];
      await processClinicalConditions(allConditions, allergies, disliked);
      
    } catch (error) {
      console.error('Error processing pathologies:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar patologías');
    } finally {
      setProcessingPathologies(false);
    }
  };
  
  // Abrir modal de diagnóstico con un caso específico
  const openDiagnosisModal = (caseData) => {
    setSelectedPathologyCase(caseData);
    setEditingDiagnosis({
      subject: caseData.final_subject || caseData.draft_subject || '',
      body: caseData.final_body || caseData.draft_body || '',
      actionPlanTitle: caseData.action_plan_title_final || caseData.action_plan_title_auto || '',
      actionPlanBody: caseData.action_plan_body_final || caseData.action_plan_body_auto || ''
    });
    setEmailImageUrls(caseData.image_urls || []);
    setShowDiagnosisModal(true);
  };

  // Generar email clínico desde el módulo clínico (plantilla)
  const generateClinicalEmail = async () => {
    const uName = clientData?.name || clientData?.email || selectedPathologyCase?.user_name || '';
    const uEmail = clientData?.email || selectedPathologyCase?.user_email || '';
    const cIds = clinicalResult?.conditions_applied?.map(c => c.id) || selectedPathologyCase?.pathologies_selected || selectedPathologyCase?.pathologies || [];
    const cSummary = clinicalResult?.user_summary || selectedPathologyCase?.clinical_summary || null;
    if (!uName && !uEmail) return;
    setGeneratingClinicalEmail(true);
    try {
      const response = await axios.post(`${API}/pathology/generate-clinical-email`, {
        user_name: uName,
        user_email: uEmail,
        condition_ids: cIds,
        clinical_summary: cSummary
      });
      if (response.data?.success) {
        const content = response.data.email_content;
        setEditingDiagnosis(prev => ({
          ...prev,
          subject: content.subject,
          body: content.body,
          actionPlanTitle: content.action_plan_title,
          actionPlanBody: content.action_plan_body
        }));
        toast.success('Email clínico generado correctamente');
      }
    } catch (error) {
      console.error('Error generating clinical email:', error);
      toast.error('Error al generar el email clínico');
    } finally {
      setGeneratingClinicalEmail(false);
    }
  };

  // Generar email clínico con IA (GPT)
  const generateAIClinicalEmail = async () => {
    const uName = clientData?.name || clientData?.email || selectedPathologyCase?.user_name || '';
    const cIds = clinicalResult?.conditions_applied?.map(c => c.id) || selectedPathologyCase?.pathologies_selected || selectedPathologyCase?.pathologies || [];
    const cSummary = clinicalResult?.user_summary || selectedPathologyCase?.clinical_summary || null;
    if (!uName) return;
    setGeneratingClinicalEmail(true);
    try {
      const response = await axios.post(`${API}/pathology/generate-ai-email`, {
        user_name: uName,
        condition_ids: cIds,
        clinical_summary: cSummary,
        tone: "profesional y cálido"
      });
      if (response.data?.success) {
        const content = response.data.email_content;
        setEditingDiagnosis(prev => ({
          ...prev,
          subject: content.subject,
          body: content.body,
          actionPlanTitle: content.action_plan_title,
          actionPlanBody: content.action_plan_body
        }));
        toast.success('Email generado con IA exitosamente');
      }
    } catch (error) {
      console.error('Error generating AI email:', error);
      toast.error('Error al generar email con IA');
    } finally {
      setGeneratingClinicalEmail(false);
    }
  };

  // Subir imagen para adjuntar al email
  const uploadEmailImage = async (file) => {
    if (!file) return;
    setUploadingEmailImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.url) {
        setEmailImageUrls(prev => [...prev, response.data.url]);
        toast.success('Imagen adjuntada');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploadingEmailImage(false);
    }
  };

  // Manejar pegado de imagen desde portapapeles
  const handleEmailPaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) await uploadEmailImage(file);
      }
    }
  };

  // Eliminar imagen adjunta
  const removeEmailImage = (index) => {
    setEmailImageUrls(prev => prev.filter((_, i) => i !== index));
  };
  
  // Enviar email de diagnóstico
  const sendDiagnosisEmail = async () => {
    if (!selectedPathologyCase) return;
    
    if (!editingDiagnosis.body.trim()) {
      toast.error('El cuerpo del email no puede estar vacío');
      return;
    }
    
    setSendingDiagnosis(true);
    try {
      const response = await axios.post(`${API}/pathology/send-diagnosis`, {
        case_id: selectedPathologyCase.id,
        subject: editingDiagnosis.subject,
        body: editingDiagnosis.body,
        image_urls: emailImageUrls.length > 0 ? emailImageUrls : null
      });
      
      if (response.data?.success) {
        toast.success(`Email enviado exitosamente a ${selectedPathologyCase.user_email}`);
        setShowDiagnosisModal(false);
        setSelectedPathologyCase(null);
        // Recargar casos
        await fetchUserPathologyCases(clientData.email);
      }
    } catch (error) {
      console.error('Error sending diagnosis email:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar el email');
    } finally {
      setSendingDiagnosis(false);
    }
  };
  
  // Guardar borrador de diagnóstico
  const saveDiagnosisDraft = async () => {
    if (!selectedPathologyCase) return;
    
    try {
      await axios.put(`${API}/pathology/case/${selectedPathologyCase.id}`, {
        final_subject: editingDiagnosis.subject,
        final_body: editingDiagnosis.body,
        action_plan_title_final: editingDiagnosis.actionPlanTitle,
        action_plan_body_final: editingDiagnosis.actionPlanBody,
        image_urls: emailImageUrls
      });
      toast.success('Borrador y Plan de Acción guardados');
      // Actualizar caso en estado local
      setUserPathologyCases(prev => 
        prev.map(c => c.id === selectedPathologyCase.id 
          ? { 
              ...c, 
              final_subject: editingDiagnosis.subject, 
              final_body: editingDiagnosis.body,
              action_plan_title_final: editingDiagnosis.actionPlanTitle,
              action_plan_body_final: editingDiagnosis.actionPlanBody
            }
          : c
        )
      );
    } catch (error) {
      console.error('Error saving diagnosis draft:', error);
      toast.error('Error al guardar el borrador');
    }
  };
  
  // Eliminar caso de patología
  const deletePathologyCase = async (caseId) => {
    if (!caseId) return;
    
    // Confirmación antes de eliminar
    if (!window.confirm('¿Estás seguro de eliminar este caso de diagnóstico? Esta acción no se puede deshacer.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/pathology/case/${caseId}`);
      toast.success('✅ Caso eliminado correctamente');
      
      // Actualizar lista de casos
      setUserPathologyCases(prev => prev.filter(c => c.id !== caseId));
      
      // Cerrar modal si el caso eliminado es el seleccionado
      if (selectedPathologyCase?.id === caseId) {
        setShowDiagnosisModal(false);
        setSelectedPathologyCase(null);
      }
    } catch (error) {
      console.error('Error deleting pathology case:', error);
      toast.error('Error al eliminar el caso');
    }
  };
  
  // Eliminar formulario clínico asignado
  const deleteClinicalFormAssignment = async (assignmentId, formName) => {
    if (!assignmentId) return;
    
    // Confirmación antes de eliminar
    if (!window.confirm(`¿Eliminar el formulario "${formName}"?\n\nEsto eliminará la asignación y el usuario ya no verá este formulario en su dashboard.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/clinical-forms/admin/assignment/${assignmentId}`);
      toast.success('✅ Formulario eliminado correctamente');
      
      // Actualizar lista de formularios del usuario
      setUserClinicalForms(prev => prev.filter(f => f.id !== assignmentId));
    } catch (error) {
      console.error('Error deleting clinical form assignment:', error);
      toast.error('Error al eliminar el formulario');
    }
  };
  
  // ===== FIN SISTEMA DE DIAGNÓSTICO =====
  
  // Datos de dieta
  const [dietData, setDietData] = useState({
    diet_name: '',
    day_type: 'training_inferior',
    notes: '',
    target_calories: 0,
    target_protein: 0,
    target_carbs: 0,
    target_fat: 0,
    post_workout_meal: 'none',
    fasting_supplements: [], // Suplementos en ayunas (antes de comida 1)
    pre_workout_supplements: [], // Suplementos pre-entreno (antes de comida post-entreno)
    water_liters: '', // Litros de agua recomendados al día (string en state, se convierte a float al enviar)
    water_note: '', // Nota opcional de hidratación
    meals: [
      { meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [], target_protein: 0, target_carbs: 0, target_fat: 0, target_calories: 0 }
    ],
    custom_pages: []
  });

  // ═══ Recipe picker state (asignar receta de la biblioteca a una comida) ═══
  const [recipePickerOpen, setRecipePickerOpen] = useState(false);
  const [recipePickerMealIndex, setRecipePickerMealIndex] = useState(null);
  // 2026-02 · Modal de intercambio universal (para ingredientes de recetas
  // importadas donde la categoría no matchea con `foodDatabase` local). El
  // Select de intercambio existente sólo funciona cuando `item.category`
  // pertenece a `foodDatabase`; este picker permite buscar en TODA la
  // biblioteca de alimentos.
  const [swapPickerOpen, setSwapPickerOpen] = useState(false);
  const [swapPickerCtx, setSwapPickerCtx] = useState(null); // { mealIndex, itemIndex, currentName }
  const [swapPickerSearch, setSwapPickerSearch] = useState('');
  const [recipeSearch, setRecipeSearch] = useState('');
  const [recipeResults, setRecipeResults] = useState([]);
  const [recipeLoading, setRecipeLoading] = useState(false);
  // 2026-02 · Filtro por meal_slot en el picker del generador offline.
  // '' = todas · valores canónicos: 'Desayuno' | 'Snack AM' | 'Almuerzo' | 'Snack PM' | 'Cena' | 'Post-Entreno'.
  const [recipeSlotFilter, setRecipeSlotFilter] = useState('');

  const openRecipePicker = (mealIndex) => {
    setRecipePickerMealIndex(mealIndex);
    setRecipeSearch('');
    setRecipeSlotFilter('');
    setRecipeResults([]);
    setRecipePickerOpen(true);
    // 2026-02 · Cargar TODO el catálogo de una vez (rara vez >few hundreds).
    // El filtrado por meal_slot + texto se hace client-side para respuesta instantánea.
    void fetchRecipes('');
  };

  const fetchRecipes = async (query) => {
    setRecipeLoading(true);
    try {
      // Cargar hasta 1000 recetas (backend cap). Todo el filtrado es client-side.
      // 🔒 complete_only=true · Solo recetas con image_url y protein/carbs/fats > 0
      // (regla 2026-02). Las incompletas quedan ocultas en la asignación a planes.
      const params = { limit: 1000, active_only: true, complete_only: true };
      if (query && query.trim()) params.search = query.trim();
      // Auth header is injected by AdminWrapper's global axios interceptor.
      const res = await axios.get(`${BACKEND_URL}/api/admin/recipe-library`, { params });
      setRecipeResults(res.data?.items || res.data?.recipes || []);
    } catch (e) {
      setRecipeResults([]);
    } finally { setRecipeLoading(false); }
  };

  // 2026-09 · Match ingrediente de receta → alimento de la base (macros reales por 100 g).
  // Normaliza acentos/puntuación/plurales y exige que un nombre contenga TODAS las
  // palabras del otro: 'Pechuga de pollo' → 'Pollo (pechuga sin piel)', pero NO
  // 'Pavo (pechuga)' ni 'Pollo (muslo sin piel)'.
  // Conectores + palabras de preparación/porción que no cambian el alimento base.
  const _FOOD_STOPWORDS = new Set(['de', 'del', 'la', 'las', 'el', 'los', 'sin', 'con', 'en', 'al', 'y', 'a', 'o', 'para', 'tipo',
    'piel', 'cocido', 'cocida', 'picado', 'picada', 'fino', 'fina', 'rebanada', 'pequeno', 'pequena', 'mediano', 'mediana',
    'grande', 'lonchas', 'loncha', 'escurrido', 'escurrida', 'bastones', 'delgado', 'delgada', 'fresco', 'fresca', 'unidad', 'taza', 'cdta', 'cda',
    'peso', 'crudo', 'cruda', 'seco', 'seca', 'hojuela']);
  const _plain = (name) => String(name || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const _foodTokens = (name) => String(name || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9ñ]+/g, ' ').split(' ')
    .filter((t) => t && !_FOOD_STOPWORDS.has(t) && !/^\d+$/.test(t))
    .map((t) => (t.length > 3 && t.endsWith('es') && !t.endsWith('ies') ? t.slice(0, -2) : (t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : t)));
  const findFoodInDatabase = (ingredientName) => {
    const ingList = _foodTokens(ingredientName);
    const ingTokens = new Set(ingList);
    const ingHead = ingList[0];
    const ingRaw = _plain(ingredientName);
    const ingDry = /\b(crud[oa]s?|sec[oa]s?|hojuelas)\b/.test(ingRaw);
    const ingCooked = /cocid/.test(ingRaw);
    if (ingTokens.size === 0 || !foodDatabase) return null;
    let best = null;
    for (const foods of Object.values(foodDatabase)) {
      if (!Array.isArray(foods)) continue;
      for (const f of foods) {
        if (!f || !f.name) continue;
        const fTokens = new Set(_foodTokens(f.name));
        if (fTokens.size === 0) continue;
        const overlap = [...ingTokens].filter((t) => fTokens.has(t)).length;
        const ingInFood = overlap === ingTokens.size;   // 'pechuga pollo' ⊂ 'pollo pechuga'
        const foodInIng = overlap === fTokens.size;     // 'pavo pechuga' ⊂ 'pechuga pavo lonchas'
        if (!ingInFood && !foodInIng) continue;
        // La palabra principal de cada nombre debe estar en el otro: evita
        // 'Agua' → 'Atún (en agua)' o 'Leche de almendras' → 'Almendras'.
        if (!fTokens.has(ingHead) || !ingTokens.has(_foodTokens(f.name)[0])) continue;
        // Crudo/seco vs cocido cambia muchísimo los macros (avena, arroz, pasta): no mezclar.
        const fRaw = _plain(f.name);
        if ((ingDry && /cocid/.test(fRaw)) || (ingCooked && /(crud|seca|seco)/.test(fRaw))) continue;
        const extra = (fTokens.size - overlap) + (ingTokens.size - overlap);
        if (!best || overlap > best.overlap || (overlap === best.overlap && extra < best.extra)) {
          best = { food: f, overlap, extra, tie: false };
        } else if (overlap === best.overlap && extra === best.extra && best.food.name !== f.name) {
          best.tie = true; // ambiguo (ej. 'Arroz blanco' vs cocido/crudo) → no adivinar
        }
      }
    }
    if (!best || best.tie) return null;
    return best.food;
  };

  const assignRecipeToMeal = (recipe) => {
    if (recipePickerMealIndex === null) return;
    const newMeals = [...dietData.meals];
    const idx = recipePickerMealIndex;
    const p = Math.round(Number(recipe.protein) || 0);
    const c = Math.round(Number(recipe.carbs) || 0);
    const g = Math.round(Number(recipe.fat) || Number(recipe.fats) || 0);
    const kcal = Math.round(Number(recipe.calories) || (p * 4 + c * 4 + g * 9));

    // 2026-02 · ESCALADO A OBJETIVO DE LA COMIDA
    // La receta se ADAPTA a los macros target ya definidos en la comida
    // (NO los reemplaza). Escalamos los gramos de cada ingrediente por un
    // factor `scale` tal que kcal_receta * scale ≈ kcal_objetivo. Los macros
    // por 100 g quedan intactos → el admin puede seguir editando gramos y
    // los macros escalan correctamente.
    const meal = newMeals[idx] || {};
    const tgtP = Number(meal.target_protein) || 0;
    const tgtC = Number(meal.target_carbs) || 0;
    const tgtF = Number(meal.target_fat) || 0;
    const tgtKcal = Number(meal.target_calories) || (tgtP * 4 + tgtC * 4 + tgtF * 9);
    // Prioridad: calorías → proteína → 1 (sin escalar). Nunca dividir por 0.
    let scale = 1;
    if (tgtKcal > 0 && kcal > 0) scale = tgtKcal / kcal;
    else if (tgtP > 0 && p > 0) scale = tgtP / p;
    // Nunca escalar a algo absurdo (0.05× o 20×). Rango sensato: [0.25, 4].
    scale = Math.max(0.25, Math.min(4, scale));

    // 2026-02 · Desglosar los ingredientes de la receta en el listado `items`
    // de la comida, para que el admin pueda editar/reordenar cada uno igual
    // que los demás alimentos del plan. Preserva `items` previos añadidos
    // manualmente (los ingredientes de la receta se agregan al final).
    const parseAmount = (raw) => {
      const s = String(raw || '').trim();
      const m = s.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
      if (!m) return { qty: s || '', unit: '' };
      const num = parseFloat(m[1].replace(',', '.'));
      const unit = (m[2] || 'g').trim() || 'g';
      return { qty: Number.isFinite(num) ? num : m[1], unit };
    };
    const recipeIngredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
    // 2026-02 · Distribuir los macros TOTALES de la receta entre sus ingredientes
    // proporcionalmente al peso en gramos (si están disponibles). Si un
    // ingrediente no tiene gramos, se cae al reparto equitativo. Así, la suma
    // de macros de los items iguala los totales de la receta y el contador
    // "Agregado" de la comida refleja correctamente lo asignado.
    const parsed = recipeIngredients.map((ing) => {
      const raw = ing?.amount ?? ing?.quantity ?? '';
      const { qty, unit } = parseAmount(raw);
      return { ing, qty, unit };
    });
    // 2026-09 · Macros REALES por ingrediente desde la base de alimentos (por 100 g).
    // Sólo los ingredientes sin match en la base (o sin gramos) reciben un
    // reparto proporcional del REMANENTE de la receta, así la suma sigue
    // coincidiendo con la receta y el pollo ya no hereda carbos del arroz.
    const isWeightUnit = (u) => /^(g|gr|grs|gramos?|ml)\b/i.test(String(u || 'g').trim());
    const withFood = parsed.map((x) => {
      const name = (x.ing?.name || '').trim();
      const grams = typeof x.qty === 'number' && x.qty > 0 && isWeightUnit(x.unit) ? x.qty : null;
      const food = name && grams ? findFoodInDatabase(name) : null;
      return { ...x, name, grams, food };
    });
    const matchedTotals = withFood.reduce((acc, x) => {
      if (!x.food) return acc;
      const m = x.grams / 100;
      acc.kcal += (Number(x.food.calories) || 0) * m;
      acc.p += (Number(x.food.protein) || 0) * m;
      acc.c += (Number(x.food.carbs) || 0) * m;
      acc.g += (Number(x.food.fat ?? x.food.fats) || 0) * m;
      return acc;
    }, { kcal: 0, p: 0, c: 0, g: 0 });
    const rest = {
      kcal: Math.max(0, kcal - matchedTotals.kcal),
      p: Math.max(0, p - matchedTotals.p),
      c: Math.max(0, c - matchedTotals.c),
      g: Math.max(0, g - matchedTotals.g),
    };
    const unmatched = withFood.filter((x) => x.name && !x.food);
    const unmatchedGrams = unmatched.reduce((s, x) => s + (typeof x.qty === 'number' ? x.qty : 0), 0);
    const equalShare = 1 / (unmatched.length || 1);
    const derivedItems = withFood.map(({ ing, qty, unit, name, food }) => {
      if (!name) return null;
      const originalGrams = typeof qty === 'number' && qty > 0 ? qty : 100;
      let per100Kcal, per100P, per100C, per100G;
      if (food) {
        per100Kcal = Number(food.calories) || 0;
        per100P = Number(food.protein) || 0;
        per100C = Number(food.carbs) || 0;
        per100G = Number(food.fat ?? food.fats) || 0;
      } else {
        const share = unmatchedGrams > 0 && typeof qty === 'number' ? (qty / unmatchedGrams) : equalShare;
        const per100Divisor = originalGrams / 100;
        per100Kcal = (rest.kcal * share) / per100Divisor;
        per100P = (rest.p * share) / per100Divisor;
        per100C = (rest.c * share) / per100Divisor;
        per100G = (rest.g * share) / per100Divisor;
      }
      // Gramos escalados al objetivo de la comida (macros por 100 g no cambian).
      const scaledGrams = Math.max(1, Math.round(originalGrams * scale));
      return {
        food_name: name,
        quantity: qty !== '' ? `${scaledGrams}${unit ? (unit.startsWith('g') || unit.startsWith('ml') ? unit : ' ' + unit) : 'g'}` : '',
        grams: scaledGrams,
        // Macros por 100 g → al multiplicar por (grams/100) se recupera el total real.
        calories: Math.max(0, Math.round(per100Kcal)),
        protein: Math.max(0, +per100P.toFixed(1)),
        carbs: Math.max(0, +per100C.toFixed(1)),
        fat: Math.max(0, +per100G.toFixed(1)),
        fats: Math.max(0, +per100G.toFixed(1)), // alias por compatibilidad con lecturas legacy
        category: ing?.category || '',
        from_recipe_library_id: recipe.id,
        macro_source: food ? 'food_database' : 'recipe_estimate',
        matched_food_name: food ? food.name : undefined,
      };
    }).filter(Boolean);

    const prevItems = Array.isArray(newMeals[idx].items) ? newMeals[idx].items : [];
    // Totales mostrados = suma real de los ingredientes (lo mismo que suma "Agregado").
    const itemsTotals = derivedItems.reduce((acc, it) => {
      const m = (Number(it.grams) || 0) / 100;
      acc.p += it.protein * m; acc.c += it.carbs * m; acc.g += it.fat * m; acc.kcal += it.calories * m;
      return acc;
    }, { p: 0, c: 0, g: 0, kcal: 0 });
    const hasItems = derivedItems.length > 0;

    newMeals[idx] = {
      ...newMeals[idx],
      recipe_from_library: {
        id: recipe.id,
        name: recipe.name,  // Nombre EXACTO de la biblioteca
        // Totales de la receta YA ESCALADOS al objetivo de la comida.
        protein: Math.round(hasItems ? itemsTotals.p : p * scale),
        carbs: Math.round(hasItems ? itemsTotals.c : c * scale),
        fat: Math.round(hasItems ? itemsTotals.g : g * scale),
        calories: Math.round(hasItems ? itemsTotals.kcal : kcal * scale),
        image_url: recipe.image_url || '',
        scale_factor: +scale.toFixed(3),
        original_totals: { protein: p, carbs: c, fat: g, calories: kcal },
      },
      // Desglose de ingredientes editable + reordenable, escalado al objetivo.
      items: [...prevItems, ...derivedItems],
      // 2026-02 · NO tocar los macros objetivo: la receta se adaptó a ellos.
    };
    setDietData({ ...dietData, meals: newMeals });
    setRecipePickerOpen(false);
    setRecipePickerMealIndex(null);
    const addedCount = derivedItems.length;
    const dbCount = derivedItems.filter((it) => it.macro_source === 'food_database').length;
    const scalePct = Math.round(scale * 100);
    toast.success(
      `"${recipe.name}" asignada · ${addedCount} ingrediente(s) escalado(s) al ${scalePct}% para ajustarse a ${newMeals[idx].meal_name} · ${dbCount} con macros de la base de alimentos`
    );
  };
  
  // Estado para múltiples dietas (Inferior, Superior, Descanso, Lineal) en el mismo plan
  const [multiDiets, setMultiDiets] = useState({
    training_inferior: null,
    training_superior: null,
    descanso: null,
    lineal: null
  });
  const [activeDietTab, setActiveDietTab] = useState('training_inferior');
  
  // Guardar la dieta actual en multiDiets cuando se cambia de pestaña
  const saveDietToMulti = (dayType) => {
    const hasContent = (dietData.meals.length > 0 && dietData.meals.some(m => m.items?.length > 0))
      || (dietData.post_workout_meal && dietData.post_workout_meal !== 'none')
      || dietData.target_calories > 0;
    if (hasContent) {
      setMultiDiets(prev => ({
        ...prev,
        [dayType]: { ...dietData }
      }));
    }
  };
  
  // Cargar una dieta desde multiDiets
  const loadDietFromMulti = (dayType) => {
    const saved = multiDiets[dayType];
    if (saved) {
      setDietData({ 
        ...saved, 
        day_type: dayType,
        post_workout_meal: saved.post_workout_meal || 'none',
        fasting_supplements: saved.fasting_supplements || dietData.fasting_supplements || [],
        pre_workout_supplements: saved.pre_workout_supplements || dietData.pre_workout_supplements || [],
        water_liters: saved.water_liters ?? dietData.water_liters ?? '',
        water_note: saved.water_note ?? dietData.water_note ?? ''
      });
    } else {
      // Crear una dieta vacía para este tipo de día con macros independientes (empezar en 0)
      // PERO preservar los suplementos en ayunas y pre-entreno que ya existen
      setDietData({
        diet_name: '',
        day_type: dayType,
        notes: '',
        target_calories: 0,
        target_protein: 0,
        target_carbs: 0,
        target_fat: 0,
        post_workout_meal: 'none',
        fasting_supplements: dietData.fasting_supplements || [],
        pre_workout_supplements: dietData.pre_workout_supplements || [],
        water_liters: dietData.water_liters ?? '',
        water_note: dietData.water_note ?? '',
        meals: [
          { meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [], target_protein: 0, target_carbs: 0, target_fat: 0, target_calories: 0 }
        ],
        custom_pages: []
      });
    }
  };
  
  // Cambiar de pestaña de dieta
  const handleDietTabChange = (newTab) => {
    // Guardar la dieta actual antes de cambiar
    saveDietToMulti(activeDietTab);
    // Cargar la nueva dieta
    loadDietFromMulti(newTab);
    setActiveDietTab(newTab);
  };
  
  // Función para eliminar una dieta completa del plan
  const handleDeleteDiet = (dietType) => {
    const dietLabels = {
      training_inferior: 'Día Tren Inferior',
      training_superior: 'Día Tren Superior',
      descanso: 'Día de Descanso',
      lineal: 'Dieta Lineal'
    };
    
    if (!window.confirm(`¿Eliminar la dieta "${dietLabels[dietType]}" del plan?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    // Eliminar del estado multiDiets
    const updatedMultiDiets = { ...multiDiets };
    updatedMultiDiets[dietType] = null;
    setMultiDiets(updatedMultiDiets);
    
    // Si la dieta eliminada era la activa, cambiar a otra disponible
    if (activeDietTab === dietType) {
      const availableDiets = Object.keys(updatedMultiDiets).filter(key => updatedMultiDiets[key] !== null);
      if (availableDiets.length > 0) {
        loadDietFromMulti(availableDiets[0]);
        setActiveDietTab(availableDiets[0]);
      } else {
        // No quedan dietas, resetear al estado inicial
        setDietData({
          diet_name: dietData.diet_name,
          target_calories: 0,
          target_protein: 0,
          target_carbs: 0,
          target_fat: 0,
          meals: [],
          supplements: [],
          notes: '',
          day_type: 'training_inferior',
          post_workout_meal: 'none'
        });
        setActiveDietTab('training_inferior');
      }
    }
    
    toast.success(`✅ Dieta "${dietLabels[dietType]}" eliminada`);
  };
  
  // Estado para macros restantes por comida
  const [mealMacrosRemaining, setMealMacrosRemaining] = useState({});
  
  // Estado para el calculador de macros (Plan Básico)
  const [calculatedMacros, setCalculatedMacros] = useState(null);
  
  const handleMacrosCalculated = useCallback((macros) => {
    setCalculatedMacros(macros);
    // Auto-actualizar targets en dietData
    setDietData(prev => ({
      ...prev,
      target_calories: macros.target_calories,
      target_protein: macros.target_protein,
      target_carbs: macros.target_carbs,
      target_fat: macros.target_fat
    }));
  }, []);
  
  // Generar dieta con cálculo inteligente de gramos (Plan Básico)
  const handleGenerateSmartDiet = () => {
    if (!calculatedMacros && (!dietData.target_calories || !dietData.target_protein)) {
      toast.error('Configura los macros con el calculador primero');
      return;
    }
    
    setAutoDietGenerating(true);
    try {
      const macros = calculatedMacros || {
        target_calories: dietData.target_calories,
        target_protein: dietData.target_protein,
        target_carbs: dietData.target_carbs,
        target_fat: dietData.target_fat
      };
      const numMeals = parseInt(clientData.num_meals) || 5;
      const postWM = dietData.post_workout_meal || 'none';
      
      // Determinar comidas con fruta (máx 2: desayuno + post-entreno)
      const fruitMeals = [0]; // desayuno siempre puede tener fruta
      if (postWM !== 'none') {
        const postIdx = parseInt(postWM) - 1;
        if (postIdx > 0 && !fruitMeals.includes(postIdx)) {
          fruitMeals.push(postIdx);
        }
      }
      
      // Generar 3 opciones por comida
      const filteredDB = getFilteredFoodDatabase() || foodDatabase;
      const generatedMeals = [];
      for (let i = 0; i < numMeals; i++) {
        const meal = generateSmartMeal({
          mealIndex: i,
          totalMeals: numMeals,
          dailyMacros: macros,
          foodDatabase: filteredDB,
          postWorkoutMeal: postWM,
          fruitMeals
        });
        
        // Generar 2 opciones adicionales (total 3)
        const options = [];
        for (let opt = 0; opt < 2; opt++) {
          const altMeal = generateSmartMeal({
            mealIndex: i,
            totalMeals: numMeals,
            dailyMacros: macros,
            foodDatabase: filteredDB,
            postWorkoutMeal: postWM,
            fruitMeals
          });
          options.push(altMeal.items);
        }
        meal.options = options;
        generatedMeals.push(meal);
      }
      
      setDietData(prev => ({ ...prev, meals: generatedMeals }));
      
      // Calcular macros usados
      const remainingMacros = {};
      generatedMeals.forEach((meal, idx) => {
        remainingMacros[idx] = {
          protein: meal.target_protein - meal.actual_protein,
          carbs: meal.target_carbs - meal.actual_carbs,
          fat: meal.target_fat - meal.actual_fat,
          calories: meal.target_calories - meal.actual_calories,
          usedProtein: meal.actual_protein,
          usedCarbs: meal.actual_carbs,
          usedFat: meal.actual_fat,
          usedCalories: meal.actual_calories
        };
      });
      setMealMacrosRemaining(remainingMacros);
      setShowAutoDietGenerator(false);
      toast.success(`Dieta inteligente generada con ${numMeals} comidas y cálculo exacto de gramos.`);
    } catch (error) {
      console.error('Error generating smart diet:', error);
      toast.error('Error al generar la dieta');
    } finally {
      setAutoDietGenerating(false);
    }
  };
  
  // Estado para generación automática de dieta
  const [showAutoDietGenerator, setShowAutoDietGenerator] = useState(false);
  const [autoDietGenerating, setAutoDietGenerating] = useState(false);
  
  // Estado para modal de progresión de macros
  const [showCalorieProgressionModal, setShowCalorieProgressionModal] = useState(false);
  const [macroProgressionPercent, setMacroProgressionPercent] = useState({
    protein: 0,
    carbs: 0,
    fat: 0
  });
  const [applyToAllDiets, setApplyToAllDiets] = useState(true);

  // Estado para modal de redistribución de macros
  const [showRedistributeModal, setShowRedistributeModal] = useState(false);
  const [redistributionMode, setRedistributionMode] = useState('igualitaria');
  const [customMealPercents, setCustomMealPercents] = useState({});
  // Pila de snapshots para Deshacer redistribuciones (máx 5)
  const [redistributeHistory, setRedistributeHistory] = useState([]);
  
  // ========== FORMULARIOS DE ACTUALIZACIÓN (RENOVACIONES) ==========
  const [showUpdateForms, setShowUpdateForms] = useState(false);
  const [updateForms, setUpdateForms] = useState(null);
  const [loadingUpdateForms, setLoadingUpdateForms] = useState(false);
  
  // ========== MÓDULO CLÍNICO ==========
  const [clinicalResult, setClinicalResult] = useState(null);
  const [processingClinical, setProcessingClinical] = useState(false);
  const [showClinicalPanel, setShowClinicalPanel] = useState(false);
  const [clinicalNotes, setClinicalNotes] = useState(''); // Notas manuales del admin
  const [adminBlockedFoodsText, setAdminBlockedFoodsText] = useState(''); // Alimentos prohibidos manualmente por el admin (CSV)
  // true sólo si el admin vació la caja a mano → el backend sólo borra la lista con este flag.
  const adminBlockedClearedRef = useRef(false);
  const setAdminBlockedFoodsTextByUser = (v) => {
    adminBlockedClearedRef.current = !String(v || '').trim();
    setAdminBlockedFoodsText(v);
  };

  // ===== Historial de versiones del plan =====
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [manualSnapName, setManualSnapName] = useState('');
  const [manualSnapNote, setManualSnapNote] = useState('');
  const [clinicalEditedData, setClinicalEditedData] = useState(null); // Admin edits loaded from DB
  const [clinicalAutoSummary, setClinicalAutoSummary] = useState(null); // Auto-generated summary from DB
  
  // ========== SECCIONES COLAPSABLES ==========
  // 2026-02 · Persistir estado colapsado/expandido de cada sección en localStorage
  const COLLAPSED_SECTIONS_STORAGE_KEY = 'offline_generator_collapsed_sections_v1';
  const DEFAULT_COLLAPSED_SECTIONS = {
    patologias: true,
    hormonalQuestions: true,
    clinicalNotes: false,
    clinicalActions: true,
    clinicalAvoid: false,
    clinicalSubstitutions: true,
    formInfo: false,
    hormonalCycle: true
  };
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem(COLLAPSED_SECTIONS_STORAGE_KEY);
      if (raw) return { ...DEFAULT_COLLAPSED_SECTIONS, ...JSON.parse(raw) };
    } catch (e) { /* localStorage no disponible */ }
    return DEFAULT_COLLAPSED_SECTIONS;
  });

  // Persistir cambios de colapsado en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_SECTIONS_STORAGE_KEY, JSON.stringify(collapsedSections));
    } catch (e) { /* localStorage no disponible */ }
  }, [collapsedSections]);
  
  // Estado para almacenar las etiquetas de campos dinámicos
  const [fieldLabelsMap, setFieldLabelsMap] = useState({});
  
  // Función para toggle de secciones
  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };
  
  // ===== FUNCIONES DEL MÓDULO CLÍNICO =====
  const processClinicalConditions = async (pathologies, allergies, dislikedFoods) => {
    setProcessingClinical(true);
    try {
      const conditionIds = [];
      const allConditions = [...(pathologies || []), ...(allergies || [])];
      
      for (const name of allConditions) {
        if (!name) continue;
        const normalized = name.toLowerCase().trim().replace(/_/g, ' ');
        if (['ninguna', 'ninguna patología', 'ninguna patologia'].includes(normalized)) continue;
        
        const id = PATHOLOGY_NAME_TO_ID[normalized];
        if (id && !conditionIds.includes(id)) {
          conditionIds.push(id);
        }
      }
      
      if (conditionIds.length === 0) {
        setClinicalResult(null);
        setProtocolStatus(null);
        return;
      }
      
      const response = await axios.post(`${API}/clinical-module/process`, {
        condition_ids: conditionIds,
        disliked_foods: dislikedFoods || []
      });
      
      if (response.data?.success) {
        const clinData = response.data.data;
        // Solo activar si hay una condición dominante válida
        if (!clinData.dominant_condition) {
          setClinicalResult(null);
          return;
        }
        setClinicalResult(clinData);
        
        // Auto-agregar suplementos recomendados al plan
        if (clinData.recommended_supplements?.length > 0) {
          const supplementTexts = clinData.recommended_supplements.map(s => {
            let text = s.name;
            if (s.dose) text += ` ${s.dose}`;
            return text;
          });
          
          // Separar por timing
          const fastingSupps = clinData.recommended_supplements
            .filter(s => s.timing === 'ayunas' || s.timing === 'mañana')
            .map(s => `${s.name} ${s.dose || ''}`);
          
          const withMealSupps = clinData.recommended_supplements
            .filter(s => s.timing !== 'ayunas' && s.timing !== 'mañana' && s.timing !== 'noche')
            .map(s => `${s.name} ${s.dose || ''}`);
          
          setDietData(prev => ({
            ...prev,
            fasting_supplements: [
              ...new Set([...(prev.fasting_supplements || []), ...fastingSupps])
            ]
          }));
          
          toast.success(`${supplementTexts.length} suplementos clínicos agregados automáticamente`);
        }
        
        toast.success(`Módulo clínico activado: ${conditionIds.length} condición(es) procesadas`);
      }
    } catch (error) {
      console.error('Error procesando condiciones clínicas:', error);
      setClinicalResult(null);
      // Solo mostrar error si es un error real del servidor, no un 400
      if (error.response?.status >= 500) {
        toast.error('Error al procesar condiciones clínicas');
      }
    } finally {
      setProcessingClinical(false);
    }
  };

  // Obtener estado del protocolo clínico
  const fetchProtocolStatus = async (planId) => {
    if (!planId) return;
    try {
      const response = await axios.get(`${API}/clinical-module/protocol-status/${planId}`);
      if (response.data?.success) {
        setProtocolStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching protocol status:', error);
    }
  };

  // Sincronizar alimentos reautorizados al cargar un plan guardado
  useEffect(() => {
    if (!editingPlanId || !clinicalResult?.blocked_foods?.length) return;
    const syncReauthorized = async () => {
      try {
        const res = await axios.get(`${API}/clinical-module/blocked-foods/${editingPlanId}`);
        if (res.data?.success) {
          const reauthorizedNames = new Set(
            (res.data.blocked_foods || [])
              .filter(f => f.is_reauthorized)
              .map(f => f.food_name)
          );
          if (reauthorizedNames.size > 0) {
            setClinicalResult(prev => {
              if (!prev) return prev;
              const updated = (prev.blocked_foods || []).filter(f => !reauthorizedNames.has(f));
              if (updated.length === prev.blocked_foods.length) return prev;
              return { ...prev, blocked_foods: updated };
            });
          }
        }
      } catch (e) {
        // Silently fail - plan might not be saved yet
      }
    };
    syncReauthorized();
  }, [editingPlanId, clinicalResult?.conditions_applied]);

  const getFilteredFoodDatabase = useCallback(() => {
    if (!foodDatabase) return foodDatabase;

    // 1) Listas del módulo clínico (si está activo)
    const blockedSet = new Set(clinicalResult?.blocked_foods || []);
    const cautionSet = new Set(clinicalResult?.caution_foods || []);
    const clinicalDisliked = (clinicalResult?.disliked_foods || []);

    // 2) Listas directas del perfil del cliente (siempre se aplican,
    //    aunque NO haya patología/alergia clínica reconocida).
    const profileAllergies = clientData?.additional_info?.food_allergies || [];
    const profileDisliked = clientData?.additional_info?.disliked_foods || [];

    // 3) Lista manual del admin (textarea CSV en el panel clínico).
    const adminManualBlocked = (adminBlockedFoodsText || '')
      .split(/[,;\n]+/)
      .map(s => s.trim())
      .filter(Boolean);

    // Normalizar todos los términos a minúsculas para substring match
    const excludeTerms = [
      ...clinicalDisliked,
      ...profileAllergies,
      ...profileDisliked,
      ...adminManualBlocked
    ]
      .filter(Boolean)
      .map(t => String(t).toLowerCase().trim())
      .filter(t => t.length >= 3); // evitar coincidencias accidentales con "y", "o", etc.

    // Keywords que indican que un alimento es SIN LACTOSA o alternativa vegetal.
    // Si el nombre del alimento los contiene, NO debe excluirse por términos
    // relacionados a lactosa/lácteos (ej. si la clienta tiene "lactosa" o "leche"
    // como alergia, alimentos "sin lactosa" o "leche de almendra" NO se excluyen).
    const LACTOSE_FREE_KEYWORDS = [
      'sin lactosa', 'sin lactoso', 'deslactosad',
      'libre de lactosa', 'cero lactosa', '0 lactosa',
      'lactose free', 'lactose-free',
      'sin leche', 'sin lacteos', 'sin lacteo',
      'almendra', 'coco', 'soya', 'soja',
      'avena vegetal', 'arroz vegetal',
      'vegetal (arroz)', 'vegetal (guisante)', 'vegetal (soya)',
      'leche de almendra', 'leche de coco', 'leche de soya', 'leche vegetal',
      'yogur vegetal', 'yogurt vegetal', 'yogur de coco', 'yogurt de coco',
      'queso vegetal', 'queso de almendra'
    ];
    const LACTOSE_RELATED_TERMS = ['lactosa', 'lacteo', 'lacteos', 'leche', 'queso', 'yogur', 'yogurt', 'kefir', 'kéfir', 'requeson', 'requesón', 'ricotta', 'nata', 'crema de leche'];

    const isLactoseFreeByName = (name) => {
      const n = String(name || '').toLowerCase();
      return LACTOSE_FREE_KEYWORDS.some(kw => n.includes(kw));
    };
    const isLactoseRelatedTerm = (term) => {
      return LACTOSE_RELATED_TERMS.some(kw => term.includes(kw) || kw.includes(term));
    };

    const isExcluded = (foodName) => {
      const lower = String(foodName || '').toLowerCase();
      const nameIsLactoseFree = isLactoseFreeByName(lower);
      // Substring match en ambas direcciones: el término aparece en el alimento o viceversa
      return excludeTerms.some(term => {
        const matches = lower.includes(term) || term.includes(lower);
        if (!matches) return false;
        // Si el alimento es "sin lactosa" y el término de exclusión es sobre lactosa,
        // NO excluirlo (el alimento es una alternativa segura).
        if (nameIsLactoseFree && isLactoseRelatedTerm(term)) return false;
        return true;
      });
    };

    const filtered = {};
    for (const [category, foods] of Object.entries(foodDatabase)) {
      filtered[category] = foods.filter(food => {
        // Si el alimento es "sin lactosa" por nombre y está en blockedSet
        // por accidente (ej. admin manual), permitirlo pasar.
        if (blockedSet.has(food.name) && !isLactoseFreeByName(food.name)) return false;
        if (isExcluded(food.name)) return false;
        return true;
      }).map(food => ({
        ...food,
        _caution: cautionSet.has(food.name)
      }));
    }
    return filtered;
  }, [clinicalResult, foodDatabase, clientData?.additional_info?.food_allergies, clientData?.additional_info?.disliked_foods, adminBlockedFoodsText]);

  // Función para obtener formularios de actualización por email
  const fetchUpdateFormsByEmail = async (email) => {
    if (!email || email.trim() === '') {
      setUpdateForms(null);
      return;
    }
    
    setLoadingUpdateForms(true);
    try {
      const response = await axios.get(`${API}/admin/forms/by-email/${encodeURIComponent(email.trim())}`);
      if (response.data.success) {
        setUpdateForms(response.data);
        if (response.data.continuation_forms?.length > 0) {
          toast.success(`📋 ${response.data.continuation_forms.length} formulario(s) de actualización encontrado(s)`);
        }
      }
    } catch (error) {
      console.error('Error fetching update forms:', error);
      setUpdateForms(null);
    } finally {
      setLoadingUpdateForms(false);
    }
  };
  
  // Función para aplicar datos del formulario de actualización al cliente actual
  const applyUpdateFormData = (form) => {
    const formData = form.form_data || {};
    
    // Mapear campos del formulario a clientData
    const updates = {};
    
    if (formData.newWeight) updates.weight = formData.newWeight;
    if (formData.newGoal) updates.goal = formData.newGoal;
    if (formData.activityLevel) updates.activity_level = formData.activityLevel;
    if (formData.mealsPerDay) updates.num_meals = parseInt(formData.mealsPerDay) || 5;
    
    // Actualizar datos adicionales
    if (formData.newAllergies || formData.keepAllergies) {
      updates.additional_info = {
        ...clientData.additional_info,
        food_allergies: formData.newAllergies ? formData.newAllergies.split(',').map(a => a.trim()) : clientData.additional_info?.food_allergies || []
      };
    }
    
    if (Object.keys(updates).length > 0) {
      setClientData(prev => ({ ...prev, ...updates }));
      toast.success('✅ Datos del formulario aplicados');
    } else {
      toast.info('ℹ️ No hay datos nuevos para aplicar');
    }
  };
  
  // Efecto para buscar formularios cuando cambia el email del cliente
  useEffect(() => {
    if (clientData.email && clientData.email.includes('@')) {
      const debounceTimer = setTimeout(() => {
        fetchUpdateFormsByEmail(clientData.email);
        fetchUserClinicalForms(clientData.email);
        fetchUserPathologyCases(clientData.email);
        fetchUserDataByEmail(clientData.email);
      }, 500);
      return () => clearTimeout(debounceTimer);
    } else {
      setUserClinicalForms([]);
      setUserPathologyCases([]);
    }
  }, [clientData.email]);

  // Auto-procesar módulo clínico cuando hay patologías/alergias/intolerancias
  useEffect(() => {
    const pathologies = clientData.additional_info?.pathologies || [];
    const allergies = clientData.additional_info?.food_allergies || [];
    const intolerances = clientData.additional_info?.intolerances || [];
    const disliked = clientData.additional_info?.disliked_foods || [];
    const hasConditions = pathologies.some(p => p && !['ninguna', 'ninguna_patologia', 'ninguna patología'].includes(p.toLowerCase()));
    const hasAllergies = allergies.length > 0;
    const hasIntolerances = intolerances.length > 0;
    
    if (hasConditions || hasAllergies || hasIntolerances) {
      const allConditions = [
        ...pathologies.filter(p => p && !['ninguna', 'ninguna_patologia', 'ninguna patología'].includes(p.toLowerCase())),
        ...allergies,
        ...intolerances
      ];
      processClinicalConditions(allConditions, allergies, disliked);
    } else {
      setClinicalResult(null);
    }
  }, [clientData.additional_info?.pathologies, clientData.additional_info?.food_allergies, clientData.additional_info?.disliked_foods, clientData.additional_info?.intolerances]);
  
  // Cargar etiquetas de campos de TODOS los templates disponibles
  useEffect(() => {
    const fetchAllFieldLabels = async () => {
      try {
        // Cargar templates de todos los planes para tener todas las etiquetas
        const planTypes = ['basic', 'premium', 'vip', 'gold', 'competitive'];
        const allLabels = {};
        
        for (const planType of planTypes) {
          try {
            const response = await axios.get(`${API}/personalized-plans/form-template/diet?plan=${planType}`);
            if (response.data?.fields) {
              response.data.fields.forEach(field => {
                // Priorizar etiquetas más descriptivas
                if (!allLabels[field.id] || field.label.length > allLabels[field.id].length) {
                  allLabels[field.id] = field.label;
                }
              });
            }
          } catch (err) {
            // Ignorar errores de templates que no existen
          }
        }
        
        // También cargar de form_templates directamente
        try {
          const templatesRes = await axios.get(`${API}/admin/form-templates`, { withCredentials: false });
          if (templatesRes.data?.templates) {
            templatesRes.data.templates.forEach(template => {
              if (template.fields) {
                template.fields.forEach(field => {
                  if (!allLabels[field.id]) {
                    allLabels[field.id] = field.label;
                  }
                });
              }
            });
          }
        } catch (err) {
          // Ignorar errores
        }
        
        console.log('Loaded field labels:', Object.keys(allLabels).length);
        setFieldLabelsMap(allLabels);
      } catch (error) {
        console.error('Error loading field labels:', error);
      }
    };
    fetchAllFieldLabels();
  }, []); // Solo cargar una vez al montar
  
  // ========== FIN FORMULARIOS DE ACTUALIZACIÓN ==========
  
  // Función para aplicar progresión de calorías
  // Función para aplicar progresión por macro individual
  const applyMacroProgression = (macroPercents, applyToAll) => {
    const proteinMultiplier = 1 + (macroPercents.protein / 100);
    const carbsMultiplier = 1 + (macroPercents.carbs / 100);
    const fatMultiplier = 1 + (macroPercents.fat / 100);
    
    // Función para calcular nuevas calorías basadas en macros
    const calculateCalories = (protein, carbs, fat) => {
      return Math.round((protein * 4) + (carbs * 4) + (fat * 9));
    };
    
    // Función auxiliar para escalar items según el tipo de macro
    // Solo escalar GRAMOS del alimento. Los valores per-100g (protein, carbs, fat, calories)
    // son intrínsecos del alimento y NUNCA deben modificarse.
    const scaleItems = (items) => {
      if (!items || !Array.isArray(items)) return items;
      return items.map(item => {
        const itemProtein = item.protein || 0;
        const itemCarbs = item.carbs || 0;
        const itemFat = item.fat || 0;
        const totalMacros = itemProtein + itemCarbs + itemFat;
        
        if (totalMacros === 0) return item;
        
        // Multiplicador ponderado basado en la composición del alimento
        const proteinRatio = itemProtein / totalMacros;
        const carbsRatio = itemCarbs / totalMacros;
        const fatRatio = itemFat / totalMacros;
        
        const weightedMultiplier = 
          (proteinRatio * proteinMultiplier) + 
          (carbsRatio * carbsMultiplier) + 
          (fatRatio * fatMultiplier);
        
        const newGrams = item.grams ? Math.round(item.grams * weightedMultiplier) : item.grams;
        
        return {
          ...item,
          grams: newGrams,
          quantity: newGrams
          // protein, carbs, fat, calories per-100g NO se tocan
        };
      });
    };
    
    // Función auxiliar para escalar una dieta
    const scaleDiet = (diet) => {
      if (!diet) return null;
      
      // Escalar cada macro independientemente
      const newTargetProtein = Math.round(diet.target_protein * proteinMultiplier);
      const newTargetCarbs = Math.round(diet.target_carbs * carbsMultiplier);
      const newTargetFat = Math.round(diet.target_fat * fatMultiplier);
      const newTargetCalories = calculateCalories(newTargetProtein, newTargetCarbs, newTargetFat);
      
      // Escalar los items de cada comida Y redistribuir los targets
      const numMeals = diet.meals.length;
      const proteinPerMeal = Math.round(newTargetProtein / numMeals);
      const carbsPerMeal = Math.round(newTargetCarbs / numMeals);
      const fatPerMeal = Math.round(newTargetFat / numMeals);
      const caloriesPerMeal = Math.round(newTargetCalories / numMeals);
      
      const scaledMeals = diet.meals.map((meal, index) => {
        const mealProteinMultiplier = macroPercents.protein !== 0 ? proteinMultiplier : 1;
        const mealCarbsMultiplier = macroPercents.carbs !== 0 ? carbsMultiplier : 1;
        const mealFatMultiplier = macroPercents.fat !== 0 ? fatMultiplier : 1;
        
        // Si la comida tiene targets en 0, redistribuir equitativamente
        // Si tiene valores, escalarlos
        const currentProteinTarget = meal.target_protein || 0;
        const currentCarbsTarget = meal.target_carbs || 0;
        const currentFatTarget = meal.target_fat || 0;
        
        // Determinar si es la última comida para asignar el residuo
        const isLastMeal = index === numMeals - 1;
        const usedProtein = proteinPerMeal * index;
        const usedCarbs = carbsPerMeal * index;
        const usedFat = fatPerMeal * index;
        const usedCalories = caloriesPerMeal * index;
        
        const scaledMeal = {
          ...meal,
          // Si el target actual es 0, redistribuir. Si no, escalar.
          target_protein: currentProteinTarget === 0 
            ? (isLastMeal ? newTargetProtein - usedProtein - proteinPerMeal * (numMeals - 1 - index) : proteinPerMeal)
            : Math.round(currentProteinTarget * mealProteinMultiplier),
          target_carbs: currentCarbsTarget === 0 
            ? (isLastMeal ? newTargetCarbs - usedCarbs - carbsPerMeal * (numMeals - 1 - index) : carbsPerMeal)
            : Math.round(currentCarbsTarget * mealCarbsMultiplier),
          target_fat: currentFatTarget === 0 
            ? (isLastMeal ? newTargetFat - usedFat - fatPerMeal * (numMeals - 1 - index) : fatPerMeal)
            : Math.round(currentFatTarget * mealFatMultiplier),
          target_calories: calculateCalories(
            currentProteinTarget === 0 ? proteinPerMeal : Math.round(currentProteinTarget * mealProteinMultiplier),
            currentCarbsTarget === 0 ? carbsPerMeal : Math.round(currentCarbsTarget * mealCarbsMultiplier),
            currentFatTarget === 0 ? fatPerMeal : Math.round(currentFatTarget * mealFatMultiplier)
          ),
          items: scaleItems(meal.items)
        };
        
        // También escalar las opciones múltiples si existen
        if (meal.options && Array.isArray(meal.options)) {
          scaledMeal.options = meal.options.map(option => ({
            ...option,
            items: scaleItems(option.items)
          }));
        }
        
        return scaledMeal;
      });
      
      return {
        ...diet,
        target_calories: newTargetCalories,
        target_protein: newTargetProtein,
        target_carbs: newTargetCarbs,
        target_fat: newTargetFat,
        meals: scaledMeals
      };
    };
    
    if (applyToAll) {
      // Aplicar a todas las dietas
      const newMultiDiets = { ...multiDiets };
      Object.keys(newMultiDiets).forEach(key => {
        if (newMultiDiets[key]) {
          newMultiDiets[key] = scaleDiet(newMultiDiets[key]);
        }
      });
      setMultiDiets(newMultiDiets);
      
      // También escalar la dieta actual
      setDietData(scaleDiet(dietData));
    } else {
      // Solo aplicar a la dieta actual
      setDietData(scaleDiet(dietData));
    }
    
    // Construir mensaje de éxito
    const changes = [];
    if (macroPercents.protein !== 0) changes.push(`Proteína ${macroPercents.protein > 0 ? '+' : ''}${macroPercents.protein}%`);
    if (macroPercents.carbs !== 0) changes.push(`Carbos ${macroPercents.carbs > 0 ? '+' : ''}${macroPercents.carbs}%`);
    if (macroPercents.fat !== 0) changes.push(`Grasas ${macroPercents.fat > 0 ? '+' : ''}${macroPercents.fat}%`);
    
    if (changes.length > 0) {
      toast.success(`Macros ajustados: ${changes.join(', ')}${applyToAll ? ' en todas las dietas' : ''}`);
    } else {
      toast.info('No se realizaron cambios (todos los valores en 0%)');
    }
    
    setShowCalorieProgressionModal(false);
    // Reset values after applying
    setMacroProgressionPercent({ protein: 0, carbs: 0, fat: 0 });
  };
  
  // Calcular vista previa de nuevas calorías
  const calculateNewCaloriesPreview = () => {
    const newProtein = Math.round(dietData.target_protein * (1 + macroProgressionPercent.protein / 100));
    const newCarbs = Math.round(dietData.target_carbs * (1 + macroProgressionPercent.carbs / 100));
    const newFat = Math.round(dietData.target_fat * (1 + macroProgressionPercent.fat / 100));
    return Math.round((newProtein * 4) + (newCarbs * 4) + (newFat * 9));
  };

  // Abrir modal de redistribución
  const openRedistributeModal = () => {
    if (!dietData.meals || dietData.meals.length === 0) {
      toast.error('No hay comidas para redistribuir');
      return;
    }
    const numMeals = dietData.meals.length;

    // 1) Si la dieta tiene una distribución guardada previamente, recuperarla.
    const savedMode = dietData.distribution_mode;
    const savedPercents = dietData.custom_meal_percents;
    const savedValid =
      savedPercents &&
      typeof savedPercents === 'object' &&
      Object.keys(savedPercents).length === numMeals;

    if (savedValid) {
      // Recuperar valores previos tal cual los dejó el usuario
      setCustomMealPercents(savedPercents);
      setRedistributionMode(savedMode || 'personalizada');
      setShowRedistributeModal(true);
      return;
    }

    // 2) Fallback: inicializar porcentajes equitativos
    const equalPct = Math.floor(100 / numMeals);
    const remainder = 100 - (equalPct * numMeals);
    const initial = {};
    dietData.meals.forEach((_, i) => {
      initial[i] = {
        protein: i === numMeals - 1 ? equalPct + remainder : equalPct,
        carbs: i === numMeals - 1 ? equalPct + remainder : equalPct,
        fat: i === numMeals - 1 ? equalPct + remainder : equalPct
      };
    });
    setCustomMealPercents(initial);
    setRedistributionMode(savedMode || 'igualitaria');
    setShowRedistributeModal(true);
  };

  // Redistribución igualitaria (comportamiento original)
  const redistributeEqual = () => {
    const numMeals = dietData.meals.length;
    const totalProtein = dietData.target_protein || 0;
    const totalCarbs = dietData.target_carbs || 0;
    const totalFat = dietData.target_fat || 0;

    const updatedMeals = dietData.meals.map((meal, index) => {
      const isLast = index === numMeals - 1;
      const pPerMeal = Math.round(totalProtein / numMeals);
      const cPerMeal = Math.round(totalCarbs / numMeals);
      const fPerMeal = Math.round(totalFat / numMeals);
      return {
        ...meal,
        target_protein: isLast ? totalProtein - pPerMeal * (numMeals - 1) : pPerMeal,
        target_carbs: isLast ? totalCarbs - cPerMeal * (numMeals - 1) : cPerMeal,
        target_fat: isLast ? totalFat - fPerMeal * (numMeals - 1) : fPerMeal,
        target_calories: isLast
          ? Math.round(((totalProtein - pPerMeal * (numMeals - 1)) * 4) + ((totalCarbs - cPerMeal * (numMeals - 1)) * 4) + ((totalFat - fPerMeal * (numMeals - 1)) * 9))
          : Math.round((pPerMeal * 4) + (cPerMeal * 4) + (fPerMeal * 9))
      };
    });

    setDietData({ ...dietData, meals: updatedMeals, distribution_mode: 'igualitaria' });
    setShowRedistributeModal(false);
    toast.success(`Macros redistribuidos igualitariamente en ${numMeals} comidas`);
  };

  // Redistribución Peri-Entreno
  const redistributePeriWorkout = () => {
    const numMeals = dietData.meals.length;
    const postWorkoutIdx = parseInt(dietData.post_workout_meal) - 1;

    if (isNaN(postWorkoutIdx) || postWorkoutIdx < 0 || dietData.post_workout_meal === 'none') {
      toast.error('Selecciona una comida post-entreno primero');
      return;
    }

    const preWorkoutIdx = postWorkoutIdx > 0 ? postWorkoutIdx - 1 : null;
    const totalProtein = dietData.target_protein || 0;
    const totalCarbs = dietData.target_carbs || 0;
    const totalFat = dietData.target_fat || 0;

    // Carbos: 35% post, 25% pre, 40% resto
    const postCarbsPct = 0.35;
    const preCarbsPct = preWorkoutIdx !== null ? 0.25 : 0;
    const remainingCarbsPct = 1 - postCarbsPct - preCarbsPct;
    const otherMealsCount = numMeals - (preWorkoutIdx !== null ? 2 : 1);

    // Proteína: equitativa
    const proteinPerMeal = Math.round(totalProtein / numMeals);

    // Grasas: reducidas en peri-entreno, compensadas en el resto
    const periWorkoutFatPct = 0.08; // 8% de grasa por comida peri
    const periMealCount = preWorkoutIdx !== null ? 2 : 1;
    const periTotalFatPct = periWorkoutFatPct * periMealCount;
    const remainingFatPct = 1 - periTotalFatPct;

    const updatedMeals = dietData.meals.map((meal, index) => {
      let mealCarbs, mealFat, mealProtein;

      if (index === postWorkoutIdx) {
        mealCarbs = Math.round(totalCarbs * postCarbsPct);
        mealFat = Math.round(totalFat * periWorkoutFatPct);
      } else if (index === preWorkoutIdx) {
        mealCarbs = Math.round(totalCarbs * preCarbsPct);
        mealFat = Math.round(totalFat * periWorkoutFatPct);
      } else {
        mealCarbs = otherMealsCount > 0 ? Math.round((totalCarbs * remainingCarbsPct) / otherMealsCount) : 0;
        mealFat = otherMealsCount > 0 ? Math.round((totalFat * remainingFatPct) / otherMealsCount) : 0;
      }

      // Proteína equitativa, última comida absorbe residuo
      mealProtein = index === numMeals - 1 ? totalProtein - proteinPerMeal * (numMeals - 1) : proteinPerMeal;

      return {
        ...meal,
        target_protein: mealProtein,
        target_carbs: mealCarbs,
        target_fat: mealFat,
        target_calories: Math.round((mealProtein * 4) + (mealCarbs * 4) + (mealFat * 9))
      };
    });

    setDietData({ ...dietData, meals: updatedMeals, distribution_mode: 'peri_entreno' });
    setShowRedistributeModal(false);
    toast.success('Macros redistribuidos con enfoque peri-entreno');
  };

  // Redistribución personalizada por porcentajes
  const redistributeCustom = () => {
    const numMeals = dietData.meals.length;
    const totalProtein = dietData.target_protein || 0;
    const totalCarbs = dietData.target_carbs || 0;
    const totalFat = dietData.target_fat || 0;

    // Validar que sumen 100%
    let sumP = 0, sumC = 0, sumF = 0;
    for (let i = 0; i < numMeals; i++) {
      sumP += customMealPercents[i]?.protein || 0;
      sumC += customMealPercents[i]?.carbs || 0;
      sumF += customMealPercents[i]?.fat || 0;
    }

    if (Math.abs(sumP - 100) > 1 || Math.abs(sumC - 100) > 1 || Math.abs(sumF - 100) > 1) {
      toast.error(`Los porcentajes deben sumar 100%. Actual: P=${sumP}%, C=${sumC}%, G=${sumF}%`);
      return;
    }

    const updatedMeals = dietData.meals.map((meal, index) => {
      const pPct = (customMealPercents[index]?.protein || 0) / 100;
      const cPct = (customMealPercents[index]?.carbs || 0) / 100;
      const fPct = (customMealPercents[index]?.fat || 0) / 100;
      const mp = Math.round(totalProtein * pPct);
      const mc = Math.round(totalCarbs * cPct);
      const mf = Math.round(totalFat * fPct);
      return {
        ...meal,
        target_protein: mp,
        target_carbs: mc,
        target_fat: mf,
        target_calories: Math.round((mp * 4) + (mc * 4) + (mf * 9))
      };
    });

    setDietData({
      ...dietData,
      meals: updatedMeals,
      distribution_mode: 'personalizada',
      custom_meal_percents: customMealPercents
    });
    setShowRedistributeModal(false);
    toast.success('Macros redistribuidos con configuración personalizada');
  };

  // Aplicar redistribución según modo activo
  // Guarda un snapshot de las comidas actuales ANTES de aplicar, para poder deshacer
  const applyRedistribution = () => {
    // Guardar snapshot de los targets actuales (lo que tenía antes)
    const snapshot = {
      mode: redistributionMode,
      timestamp: Date.now(),
      meals: dietData.meals.map(m => ({
        meal_name: m.meal_name,
        target_protein: m.target_protein,
        target_carbs: m.target_carbs,
        target_fat: m.target_fat,
        target_calories: m.target_calories
      }))
    };
    // Mantener solo los últimos 5 snapshots para no consumir memoria
    setRedistributeHistory(prev => [...prev.slice(-4), snapshot]);

    if (redistributionMode === 'igualitaria') redistributeEqual();
    else if (redistributionMode === 'peri_entreno') redistributePeriWorkout();
    else if (redistributionMode === 'personalizada') redistributeCustom();
  };

  // Deshacer la última redistribución (restaura los targets previos)
  const undoLastRedistribution = () => {
    if (redistributeHistory.length === 0) {
      toast.info('No hay redistribuciones previas para deshacer');
      return;
    }
    const last = redistributeHistory[redistributeHistory.length - 1];
    const restoredMeals = dietData.meals.map((m, i) => {
      const prev = last.meals[i];
      if (!prev) return m;
      return {
        ...m,
        target_protein: prev.target_protein,
        target_carbs: prev.target_carbs,
        target_fat: prev.target_fat,
        target_calories: prev.target_calories
      };
    });
    setDietData({ ...dietData, meals: restoredMeals });
    setRedistributeHistory(prev => prev.slice(0, -1));
    const modeLabel = { igualitaria: 'Igualitaria', peri_entreno: 'Peri-Entreno', personalizada: 'Personalizada' }[last.mode] || last.mode;
    toast.success(`Restaurada redistribución previa (${modeLabel})`);
  };

  // Helper: calcular preview de distribución
  const getDistributionPreview = () => {
    const numMeals = dietData.meals?.length || 0;
    if (numMeals === 0) return [];
    const totalP = dietData.target_protein || 0;
    const totalC = dietData.target_carbs || 0;
    const totalF = dietData.target_fat || 0;

    if (redistributionMode === 'igualitaria') {
      return dietData.meals.map((meal, i) => {
        const isLast = i === numMeals - 1;
        const pp = Math.round(totalP / numMeals);
        const cp = Math.round(totalC / numMeals);
        const fp = Math.round(totalF / numMeals);
        return {
          name: meal.meal_name,
          protein: isLast ? totalP - pp * (numMeals - 1) : pp,
          carbs: isLast ? totalC - cp * (numMeals - 1) : cp,
          fat: isLast ? totalF - fp * (numMeals - 1) : fp
        };
      });
    }

    if (redistributionMode === 'peri_entreno') {
      const postIdx = parseInt(dietData.post_workout_meal) - 1;
      const preIdx = postIdx > 0 ? postIdx - 1 : null;
      const otherCount = numMeals - (preIdx !== null ? 2 : 1);
      const postCPct = 0.35;
      const preCPct = preIdx !== null ? 0.25 : 0;
      const restCPct = 1 - postCPct - preCPct;
      const periFatPct = 0.08;
      const periCount = preIdx !== null ? 2 : 1;
      const restFatPct = 1 - (periFatPct * periCount);
      const protPerMeal = Math.round(totalP / numMeals);

      return dietData.meals.map((meal, i) => {
        let c, f, p;
        if (i === postIdx) { c = Math.round(totalC * postCPct); f = Math.round(totalF * periFatPct); }
        else if (i === preIdx) { c = Math.round(totalC * preCPct); f = Math.round(totalF * periFatPct); }
        else { c = otherCount > 0 ? Math.round((totalC * restCPct) / otherCount) : 0; f = otherCount > 0 ? Math.round((totalF * restFatPct) / otherCount) : 0; }
        p = i === numMeals - 1 ? totalP - protPerMeal * (numMeals - 1) : protPerMeal;
        return { name: meal.meal_name, protein: p, carbs: c, fat: f };
      });
    }

    if (redistributionMode === 'personalizada') {
      return dietData.meals.map((meal, i) => ({
        name: meal.meal_name,
        protein: Math.round(totalP * ((customMealPercents[i]?.protein || 0) / 100)),
        carbs: Math.round(totalC * ((customMealPercents[i]?.carbs || 0) / 100)),
        fat: Math.round(totalF * ((customMealPercents[i]?.fat || 0) / 100))
      }));
    }
    return [];
  };

  // Helper: actualizar customMealPercents preset para peri-entreno
  const applyPeriWorkoutPresetToCustom = () => {
    const numMeals = dietData.meals?.length || 0;
    const postIdx = parseInt(dietData.post_workout_meal) - 1;
    const preIdx = postIdx > 0 ? postIdx - 1 : null;
    const otherCount = numMeals - (preIdx !== null ? 2 : 1);
    const equalP = Math.floor(100 / numMeals);
    const remainderP = 100 - (equalP * numMeals);
    const restCarbPct = otherCount > 0 ? Math.floor(40 / otherCount) : 0;
    const restCarbRemainder = otherCount > 0 ? 40 - (restCarbPct * otherCount) : 0;
    const restFatPct = otherCount > 0 ? Math.floor((100 - (preIdx !== null ? 16 : 8)) / otherCount) : 0;
    const restFatTotal = preIdx !== null ? (100 - 16) : (100 - 8);
    const restFatRemainder = otherCount > 0 ? restFatTotal - (restFatPct * otherCount) : 0;

    const newPercents = {};
    let otherIdx = 0;
    dietData.meals.forEach((_, i) => {
      if (i === postIdx) {
        newPercents[i] = { protein: i === numMeals - 1 ? equalP + remainderP : equalP, carbs: 35, fat: 8 };
      } else if (i === preIdx) {
        newPercents[i] = { protein: i === numMeals - 1 ? equalP + remainderP : equalP, carbs: 25, fat: 8 };
      } else {
        const isLastOther = otherIdx === otherCount - 1;
        newPercents[i] = {
          protein: i === numMeals - 1 ? equalP + remainderP : equalP,
          carbs: isLastOther ? restCarbPct + restCarbRemainder : restCarbPct,
          fat: isLastOther ? restFatPct + restFatRemainder : restFatPct
        };
        otherIdx++;
      }
    });
    setCustomMealPercents(newPercents);
  };

  // Presets de progresión
  const applyPresetVolumen = () => {
    setMacroProgressionPercent({
      protein: 5,   // +5% proteína
      carbs: 10,    // +10% carbohidratos
      fat: 5        // +5% grasas
    });
  };

  const applyPresetDefinicion = () => {
    setMacroProgressionPercent({
      protein: 5,    // +5% proteína (mantener músculo)
      carbs: -15,    // -15% carbohidratos
      fat: -10       // -10% grasas
    });
  };

  // Estado para modal de mover/copiar dieta
  const [showDietActionModal, setShowDietActionModal] = useState(false);
  const [dietActionType, setDietActionType] = useState('copy'); // 'copy' o 'move'
  const [dietActionTarget, setDietActionTarget] = useState(null);
  
  // Estado para dropdowns de suplementos
  const [showCopySuppsDropdown, setShowCopySuppsDropdown] = useState(false);
  const [showMoveSuppsDropdown, setShowMoveSuppsDropdown] = useState(false);
  
  // Estado para dropdowns de dietas
  const [showCopyDietDropdown, setShowCopyDietDropdown] = useState(false);
  const [showMoveDietDropdown, setShowMoveDietDropdown] = useState(false);

  // Nombres amigables de los tipos de día
  const dietTypeNames = {
    training_inferior: '🦵 Tren Inferior',
    training_superior: '💪 Tren Superior',
    descanso: '😴 Descanso',
    lineal: '📊 Lineal'
  };

  // Obtener otros tipos de día (excluyendo el actual)
  const getOtherDietTypes = () => {
    const allTypes = ['training_inferior', 'training_superior', 'descanso', 'lineal'];
    return allTypes.filter(t => t !== activeDietTab);
  };

  // Copiar dieta a otro tipo de día
  const copyDietTo = (targetType) => {
    try {
      setShowCopyDietDropdown(false);
      
      // Crear una copia segura de la dieta actual
      const dietCopy = createSafeDiet(dietData, targetType);
      
      // Guardar en el tipo de día destino
      setMultiDiets(prev => ({
        ...prev,
        [targetType]: JSON.parse(JSON.stringify(dietCopy))
      }));
      
      toast.success(`Dieta copiada de ${dietTypeNames[activeDietTab]} a ${dietTypeNames[targetType]}`);
    } catch (error) {
      console.error('Error copying diet:', error);
      toast.error('Error al copiar la dieta');
    }
  };

  // Helper para crear una dieta con todos los campos necesarios (deep copy)
  const createSafeDiet = (baseDiet, dayType) => {
    // Deep copy de los meals incluyendo sus supplements y opciones múltiples
    const deepCopyMeals = (meals) => {
      if (!Array.isArray(meals) || meals.length === 0) {
        return [{ meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [], options: [], num_options: 1, active_option: 0, target_protein: 0, target_carbs: 0, target_fat: 0, target_calories: 0 }];
      }
      return meals.map(meal => ({
        meal_name: meal.meal_name || '',
        meal_time: meal.meal_time || '07:00',
        items: Array.isArray(meal.items) ? JSON.parse(JSON.stringify(meal.items)) : [],
        supplements: Array.isArray(meal.supplements) ? JSON.parse(JSON.stringify(meal.supplements)) : [],
        // Copiar opciones múltiples
        options: Array.isArray(meal.options) ? JSON.parse(JSON.stringify(meal.options)) : [],
        num_options: meal.num_options || 1,
        active_option: meal.active_option || 0,
        target_protein: meal.target_protein || 0,
        target_carbs: meal.target_carbs || 0,
        target_fat: meal.target_fat || 0,
        target_calories: meal.target_calories || 0
      }));
    };

    return {
      diet_name: baseDiet?.diet_name || '',
      day_type: dayType,
      target_calories: baseDiet?.target_calories || 0,
      target_protein: baseDiet?.target_protein || 0,
      target_carbs: baseDiet?.target_carbs || 0,
      target_fat: baseDiet?.target_fat || 0,
      notes: baseDiet?.notes || '',
      fasting_supplements: Array.isArray(baseDiet?.fasting_supplements) ? JSON.parse(JSON.stringify(baseDiet.fasting_supplements)) : [],
      pre_workout_supplements: Array.isArray(baseDiet?.pre_workout_supplements) ? JSON.parse(JSON.stringify(baseDiet.pre_workout_supplements)) : [],
      meals: deepCopyMeals(baseDiet?.meals),
      custom_pages: Array.isArray(baseDiet?.custom_pages) ? JSON.parse(JSON.stringify(baseDiet.custom_pages)) : []
    };
  };

  // Mover dieta a otro tipo de día (intercambiar)
  const moveDietTo = (targetType) => {
    try {
      setShowMoveDietDropdown(false);
      
      // Crear copias seguras de las dietas
      const currentDietSafe = createSafeDiet(dietData, targetType);
      const targetDietExists = multiDiets[targetType] && multiDiets[targetType].meals?.length > 0;
      const targetDietSafe = targetDietExists 
        ? createSafeDiet(multiDiets[targetType], activeDietTab)
        : createSafeDiet(null, activeDietTab);
      
      // Preservar suplementos de la dieta actual si el destino no tiene
      if (!targetDietExists) {
        targetDietSafe.fasting_supplements = currentDietSafe.fasting_supplements;
        targetDietSafe.pre_workout_supplements = currentDietSafe.pre_workout_supplements;
      }
      
      // Actualizar multiDiets
      setMultiDiets(prev => ({
        ...prev,
        [targetType]: JSON.parse(JSON.stringify(currentDietSafe)),
        [activeDietTab]: targetDietExists ? JSON.parse(JSON.stringify(targetDietSafe)) : null
      }));
      
      // Actualizar la dieta actual en pantalla
      setDietData(JSON.parse(JSON.stringify(targetDietSafe)));
      
      if (targetDietExists) {
        toast.success(`Dietas intercambiadas: ${dietTypeNames[activeDietTab]} ↔ ${dietTypeNames[targetType]}`);
      } else {
        toast.success(`Dieta movida a ${dietTypeNames[targetType]}`);
      }
    } catch (error) {
      console.error('Error moving diet:', error);
      toast.error('Error al mover la dieta');
    }
  };

  // Copiar suplementos a otro tipo de día (incluye suplementos de comidas)
  const copySupplementsTo = (targetType) => {
    try {
      setShowCopySuppsDropdown(false);
      
      // Verificar si hay suplementos en ayunas o en las comidas
      const hasFastingSupps = (dietData.fasting_supplements?.length > 0) || (dietData.pre_workout_supplements?.length > 0);
      const hasMealSupps = dietData.meals?.some(meal => meal.supplements?.length > 0);
      
      if (!hasFastingSupps && !hasMealSupps) {
        toast.error('No hay suplementos para copiar');
        return;
      }
      
      // Copiar suplementos a la dieta destino
      setMultiDiets(prev => {
        const existingDiet = prev[targetType];
        
        // Si existe la dieta destino, copiar los suplementos a las comidas correspondientes
        if (existingDiet && existingDiet.meals) {
          const updatedMeals = existingDiet.meals.map((meal, index) => {
            // Si hay una comida correspondiente en la dieta origen, copiar sus suplementos
            const sourceMeal = dietData.meals?.[index];
            return {
              ...meal,
              supplements: sourceMeal?.supplements 
                ? JSON.parse(JSON.stringify(sourceMeal.supplements)) 
                : meal.supplements || []
            };
          });
          
          return {
            ...prev,
            [targetType]: {
              ...existingDiet,
              fasting_supplements: JSON.parse(JSON.stringify(dietData.fasting_supplements || [])),
              pre_workout_supplements: JSON.parse(JSON.stringify(dietData.pre_workout_supplements || [])),
              meals: updatedMeals
            }
          };
        } else {
          // Si no existe la dieta, crear una nueva con todos los suplementos
          const newDiet = createSafeDiet({
            fasting_supplements: dietData.fasting_supplements,
            pre_workout_supplements: dietData.pre_workout_supplements,
            meals: dietData.meals?.map(meal => ({
              ...meal,
              supplements: meal.supplements ? JSON.parse(JSON.stringify(meal.supplements)) : []
            }))
          }, targetType);
          
          return {
            ...prev,
            [targetType]: newDiet
          };
        }
      });
      
      toast.success(`Suplementos copiados a ${dietTypeNames[targetType]}`);
    } catch (error) {
      console.error('Error copying supplements:', error);
      toast.error('Error al copiar los suplementos');
    }
  };

  // Mover suplementos a otro tipo de día (intercambiar) - incluye suplementos de cada comida
  const moveSupplementsTo = (targetType) => {
    try {
      setShowMoveSuppsDropdown(false);
      
      // Obtener suplementos actuales (fasting, pre-workout y de cada comida)
      const currentFasting = JSON.parse(JSON.stringify(dietData.fasting_supplements || []));
      const currentPreWorkout = JSON.parse(JSON.stringify(dietData.pre_workout_supplements || []));
      const currentMealSupplements = dietData.meals?.map(meal => 
        JSON.parse(JSON.stringify(meal.supplements || []))
      ) || [];
      
      // Obtener suplementos del destino (si existe)
      const targetDiet = multiDiets[targetType];
      const targetFasting = targetDiet?.fasting_supplements ? JSON.parse(JSON.stringify(targetDiet.fasting_supplements)) : [];
      const targetPreWorkout = targetDiet?.pre_workout_supplements ? JSON.parse(JSON.stringify(targetDiet.pre_workout_supplements)) : [];
      const targetMealSupplements = targetDiet?.meals?.map(meal => 
        JSON.parse(JSON.stringify(meal.supplements || []))
      ) || [];
      
      // Actualizar dieta destino con suplementos actuales
      setMultiDiets(prev => {
        const existingDiet = prev[targetType];
        
        if (existingDiet && existingDiet.meals) {
          // Si existe la dieta, actualizar suplementos de cada comida
          const updatedMeals = existingDiet.meals.map((meal, index) => ({
            ...meal,
            supplements: currentMealSupplements[index] || []
          }));
          
          return {
            ...prev,
            [targetType]: {
              ...existingDiet,
              fasting_supplements: currentFasting,
              pre_workout_supplements: currentPreWorkout,
              meals: updatedMeals
            }
          };
        } else {
          // Si no existe, crear dieta nueva con los suplementos
          const newDiet = createSafeDiet({
            fasting_supplements: currentFasting,
            pre_workout_supplements: currentPreWorkout,
            meals: dietData.meals?.map((meal, index) => ({
              ...meal,
              supplements: currentMealSupplements[index] || []
            }))
          }, targetType);
          
          return {
            ...prev,
            [targetType]: newDiet
          };
        }
      });
      
      // Actualizar dieta actual con los suplementos del destino
      setDietData(prev => {
        const updatedMeals = prev.meals?.map((meal, index) => ({
          ...meal,
          supplements: targetMealSupplements[index] || []
        })) || [];
        
        return {
          ...prev,
          fasting_supplements: targetFasting,
          pre_workout_supplements: targetPreWorkout,
          meals: updatedMeals
        };
      });
      
      toast.success(`Suplementos intercambiados con ${dietTypeNames[targetType]} (incluye suplementos de comidas)`);
    } catch (error) {
      console.error('Error moving supplements:', error);
      toast.error('Error al mover los suplementos');
    }
  };
  
  // Recalcular macros de todas las comidas cuando cambian los items
  useEffect(() => {
    if (dietData.meals && dietData.meals.length > 0) {
      const newRemaining = {};
      dietData.meals.forEach((meal, idx) => {
        let usedProtein = 0, usedCarbs = 0, usedFat = 0, usedCalories = 0;
        
        if (meal.items && meal.items.length > 0) {
          meal.items.forEach(item => {
            if (!item.skip) {
              const mult = item.grams ? item.grams / 100 : (item.multiplier || 1);
              usedProtein += (item.protein || 0) * mult;
              usedCarbs += (item.carbs || 0) * mult;
              usedFat += ((item.fat || item.fats) || 0) * mult;
              usedCalories += (item.calories || 0) * mult;
            }
          });
        }
        
        newRemaining[idx] = {
          protein: (meal.target_protein || 0) - usedProtein,
          carbs: (meal.target_carbs || 0) - usedCarbs,
          fat: (meal.target_fat || 0) - usedFat,
          calories: (meal.target_calories || 0) - usedCalories,
          usedProtein: usedProtein,
          usedCarbs: usedCarbs,
          usedFat: usedFat,
          usedCalories: usedCalories
        };
      });
      setMealMacrosRemaining(newRemaining);
    }
  }, [dietData.meals]);
  
  // Datos de rutina
  const [workoutData, setWorkoutData] = useState({
    workout_name: '',
    level: 'intermedio',
    notes: '',
    days: [
      { day_name: 'Día 1 - Tren Superior', exercises: [] },
      { day_name: 'Día 2 - Tren Inferior', exercises: [] },
      { day_name: 'Día 3 - Push', exercises: [] },
      { day_name: 'Día 4 - Pull', exercises: [] }
    ]
  });

  useEffect(() => {
    fetchFoodDatabase();
    fetchSavedPlans();
    fetchDefaultPages();
  }, []);

  const fetchFoodDatabase = async () => {
    try {
      const response = await axios.get(`${API}/admin/offline-generator/food-database`, { withCredentials: false });
      setFoodDatabase(response.data);
    } catch (error) {
      console.error('Error fetching food database:', error);
    }
  };

  const fetchSavedPlans = async () => {
    setLoadingSavedPlans(true);
    try {
      const response = await axios.get(`${API}/admin/offline-generator/saved-plans`, { withCredentials: false });
      setSavedPlans(response.data);
    } catch (error) {
      console.error('Error fetching saved plans:', error);
    } finally {
      setLoadingSavedPlans(false);
    }
  };

  const fetchDefaultPages = async () => {
    try {
      const response = await axios.get(`${API}/admin/offline-generator/default-pages`, { withCredentials: false });
      setDefaultPages(response.data);
    } catch (error) {
      console.error('Error fetching default pages:', error);
    }
  };

  // ===== Historial de versiones del plan =====
  const fetchPlanHistory = async () => {
    if (!editingPlanId) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/offline-generator/saved-plans/${editingPlanId}/snapshots`,
        { withCredentials: false }
      );
      setHistoryList(res.data?.snapshots || []);
    } catch (error) {
      console.error('Error fetching plan history:', error);
      toast.error('No se pudo cargar el historial');
    } finally {
      setHistoryLoading(false);
    }
  };

  const createManualSnapshot = async () => {
    if (!editingPlanId) return;
    try {
      // Guardar primero el plan actual para que el snapshot refleje los cambios sin guardar
      await handleSavePlan(true, 'full');
      await axios.post(
        `${API}/admin/offline-generator/saved-plans/${editingPlanId}/snapshot`,
        { name: manualSnapName, note: manualSnapNote },
        { withCredentials: false }
      );
      toast.success('Versión guardada en el historial');
      setManualSnapName('');
      setManualSnapNote('');
      await fetchPlanHistory();
    } catch (error) {
      console.error('Error creating snapshot:', error);
      toast.error('No se pudo guardar la versión');
    }
  };

  const restoreSnapshot = async (snapshotId, snapshotName) => {
    if (!editingPlanId) return;
    if (!window.confirm(`¿Restaurar la versión "${snapshotName}"?\n\nEl estado actual se guardará automáticamente antes (red de seguridad).`)) return;
    try {
      await axios.post(
        `${API}/admin/offline-generator/saved-plans/${editingPlanId}/snapshots/${snapshotId}/restore`,
        {},
        { withCredentials: false }
      );
      toast.success('Versión restaurada. Recargando plan...');
      // Recargar el plan desde el servidor usando el listado y el id
      try {
        const allPlansRes = await axios.get(`${API}/admin/offline-generator/saved-plans`, { withCredentials: false });
        const restoredPlan = (allPlansRes.data || []).find(p => p.id === editingPlanId);
        if (restoredPlan) {
          await handleLoadPlan(restoredPlan);
        } else {
          // Fallback: refrescar página si no encontramos el plan
          window.location.reload();
        }
      } catch (e) {
        window.location.reload();
      }
      setShowHistoryModal(false);
      await fetchPlanHistory();
    } catch (error) {
      console.error('Error restoring snapshot:', error);
      toast.error('No se pudo restaurar la versión');
    }
  };

  const deleteSnapshot = async (snapshotId) => {
    if (!editingPlanId) return;
    if (!window.confirm('¿Eliminar esta versión del historial? No se puede deshacer.')) return;
    try {
      await axios.delete(
        `${API}/admin/offline-generator/saved-plans/${editingPlanId}/snapshots/${snapshotId}`,
        { withCredentials: false }
      );
      toast.success('Versión eliminada del historial');
      await fetchPlanHistory();
    } catch (error) {
      console.error('Error deleting snapshot:', error);
      toast.error('No se pudo eliminar la versión');
    }
  };

  // Cargar páginas por defecto en un nuevo plan
  const loadDefaultPagesIntoCurrentPlan = () => {
    if (defaultPages.length === 0) return;
    
    const pagesToAdd = defaultPages.map((dp, index) => ({
      id: Date.now() + index,
      title: dp.title,
      content: dp.content,
      page_position: dp.page_position
    }));
    
    setDietData(prev => ({
      ...prev,
      custom_pages: [...prev.custom_pages, ...pagesToAdd]
    }));
    toast.success(`${pagesToAdd.length} páginas por defecto cargadas`);
  };

  // Guardar página por defecto
  const handleSaveDefaultPage = async () => {
    if (!newDefaultPage.title.trim()) {
      toast.error('Por favor ingresa un título para la página');
      return;
    }

    try {
      await axios.post(`${API}/admin/offline-generator/default-pages`, newDefaultPage, { withCredentials: false });
      toast.success('Página por defecto guardada');
      fetchDefaultPages();
      setNewDefaultPage({ title: '', content: '', page_position: 1 });
    } catch (error) {
      console.error('Error saving default page:', error);
      toast.error('Error al guardar la página');
    }
  };

  // Actualizar página por defecto existente
  const handleUpdateDefaultPage = async (page) => {
    if (!page.title.trim()) {
      toast.error('Por favor ingresa un título para la página');
      return;
    }

    try {
      await axios.put(`${API}/admin/offline-generator/default-pages/${page.id}`, {
        title: page.title,
        content: page.content,
        page_position: page.page_position
      }, { withCredentials: false });
      toast.success('Página actualizada');
      fetchDefaultPages();
      setEditingDefaultPage(null);
    } catch (error) {
      console.error('Error updating default page:', error);
      toast.error('Error al actualizar la página');
    }
  };

  // Eliminar página por defecto
  const handleDeleteDefaultPage = async (pageId) => {
    if (!window.confirm('¿Eliminar esta página por defecto?')) return;
    
    try {
      await axios.delete(`${API}/admin/offline-generator/default-pages/${pageId}`, { withCredentials: false });
      toast.success('Página eliminada');
      fetchDefaultPages();
      if (editingDefaultPage?.id === pageId) {
        setEditingDefaultPage(null);
      }
    } catch (error) {
      console.error('Error deleting default page:', error);
      toast.error('Error al eliminar la página');
    }
  };

  // Calcular macros basado en datos del cliente
  // Solo afecta la dieta actualmente seleccionada (activeDietTab) - no toca otras dietas
  const calculateMacros = (resetMeals = true) => {
    const { weight, height, age, activity_level, goal } = clientData;
    // num_meals y diet_preference se leen PRIMERO de la dieta activa (per-tab)
    // y como fallback, del clientData global
    const num_meals = dietData.num_meals || clientData.num_meals;
    const diet_preference = dietData.diet_preference || clientData.diet_preference;
    if (!weight || !height || !age) {
      toast.error('Por favor ingresa peso, altura y edad');
      return;
    }

    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseInt(age);
    const bf = parseFloat(clientData.body_fat_pct) || 0;
    const numMeals = parseInt(num_meals) || 5;

    // 2026-02 · Fuente única de verdad para las calorías: reutilizamos
    // `computeMetabolicProjections(clientData, metabolicOverrides)` para que
    // los macros respeten el ajuste porcentual dinámico (déficit/superávit
    // calculado por objetivo + adherencia) Y los overrides manuales que el
    // admin haya escrito en la sección "Cálculos base". Antes se recalculaba
    // BMR/TDEE aquí con el ajuste fijo (-300/-400 kcal) y se ignoraba el
    // override manual → los macros no coincidían con `adjustedKcal`.
    const mp = computeMetabolicProjections(clientData, metabolicOverrides);
    let bmr, tdee, calories;
    if (mp) {
      bmr = mp.bmr;
      tdee = mp.tdee;
      calories = mp.adjustedKcal;
    } else {
      // Fallback defensivo (no debería entrar aquí porque ya validamos w/h/a arriba)
      if (bf > 0) {
        const leanMassKg = w * (1 - bf / 100);
        bmr = 370 + 21.6 * leanMassKg;
      } else {
        bmr = 655.1 + (9.563 * w) + (1.850 * h) - (4.676 * a);
      }
      const activityMultiplier = ACTIVITY_LEVELS.find(l => l.id === activity_level)?.multiplier || 1.55;
      tdee = bmr * activityMultiplier;
      const goalAdjustment = GOAL_CALORIE_ADJUSTMENTS[goal]?.adjustment || 0;
      calories = tdee + goalAdjustment;
    }
    
    // Obtener ratios según preferencia de dieta
    const dietPref = DIET_PREFERENCES.find(d => d.id === diet_preference) || DIET_PREFERENCES[0];
    let { protein: proteinRatio, carbs: carbsRatio, fat: fatRatio } = dietPref.ratios;
    
    // Ajustes adicionales según objetivo + preferencia
    // Si el objetivo es perder grasa y la preferencia es alta en carbos, reducir un poco los carbos
    if (goal === 'perder_grasa' && diet_preference === 'alta_carbos') {
      carbsRatio = 0.40;
      proteinRatio = 0.35;
      fatRatio = 0.25;
    }
    // Si el objetivo es ganar músculo, asegurar proteína mínima
    if ((goal === 'ganar_musculo' || goal === 'definicion') && proteinRatio < 0.30) {
      proteinRatio = 0.30;
      // Ajustar los otros para que sumen 1
      const remaining = 1 - proteinRatio;
      const carbFatRatio = carbsRatio / (carbsRatio + fatRatio);
      carbsRatio = remaining * carbFatRatio;
      fatRatio = remaining * (1 - carbFatRatio);
    }
    
    const totalProtein = Math.round((calories * proteinRatio) / 4);
    const totalCarbs = Math.round((calories * carbsRatio) / 4);
    const totalFat = Math.round((calories * fatRatio) / 9);
    const totalCalories = Math.round(calories);
    
    // Dividir macros entre el número de comidas
    const proteinPerMeal = Math.round(totalProtein / numMeals);
    const carbsPerMeal = Math.round(totalCarbs / numMeals);
    const fatPerMeal = Math.round(totalFat / numMeals);
    const caloriesPerMeal = Math.round(totalCalories / numMeals);
    
    // ============================================================
    // MODO 1: resetMeals = true  → Reinicia comidas (vacías)
    // MODO 2: resetMeals = false → Conserva comidas, solo actualiza macros objetivo
    // ============================================================
    const mealTimes = ['07:00', '10:00', '13:00', '16:00', '19:00', '21:00'];
    let newMeals;

    if (resetMeals) {
      // Crear comidas vacías nuevas (comportamiento original)
      newMeals = [];
      for (let i = 0; i < numMeals; i++) {
        newMeals.push({
          meal_name: `Comida ${i + 1}`,
          meal_time: mealTimes[i] || '',
          items: [],
          supplements: [],
          target_protein: proteinPerMeal,
          target_carbs: carbsPerMeal,
          target_fat: fatPerMeal,
          target_calories: caloriesPerMeal
        });
      }
    } else {
      // Conservar comidas existentes, solo actualizar macros objetivo
      const existing = dietData.meals || [];
      newMeals = [];
      for (let i = 0; i < numMeals; i++) {
        const prev = existing[i];
        if (prev) {
          newMeals.push({
            ...prev,
            target_protein: proteinPerMeal,
            target_carbs: carbsPerMeal,
            target_fat: fatPerMeal,
            target_calories: caloriesPerMeal
          });
        } else {
          // Si el usuario aumentó num_meals, agregar comidas vacías al final
          newMeals.push({
            meal_name: `Comida ${i + 1}`,
            meal_time: mealTimes[i] || '',
            items: [],
            supplements: [],
            target_protein: proteinPerMeal,
            target_carbs: carbsPerMeal,
            target_fat: fatPerMeal,
            target_calories: caloriesPerMeal
          });
        }
      }
    }
    
    setDietData(prev => ({
      ...prev,
      num_meals: num_meals,
      diet_preference: diet_preference,
      target_calories: totalCalories,
      target_protein: totalProtein,
      target_carbs: totalCarbs,
      target_fat: totalFat,
      meals: newMeals
    }));
    
    // Inicializar/actualizar macros restantes según items reales en cada comida
    const remaining = {};
    newMeals.forEach((meal, idx) => {
      let usedP = 0, usedC = 0, usedF = 0, usedKcal = 0;
      (meal.items || []).forEach((it) => {
        usedP += parseFloat(it.protein || 0);
        usedC += parseFloat(it.carbs || 0);
        usedF += parseFloat(it.fat || 0);
        usedKcal += parseFloat(it.calories || 0);
      });
      remaining[idx] = {
        protein: Math.max(0, proteinPerMeal - usedP),
        carbs: Math.max(0, carbsPerMeal - usedC),
        fat: Math.max(0, fatPerMeal - usedF),
        calories: Math.max(0, caloriesPerMeal - usedKcal)
      };
    });
    setMealMacrosRemaining(remaining);
    
    if (resetMeals) {
      toast.success(`Dieta "${dietTypeNames[activeDietTab] || activeDietTab}" reiniciada: ${totalCalories} kcal en ${num_meals} comidas (${dietPref.name})`);
    } else {
      toast.success(`Macros recalculados solo para "${dietTypeNames[activeDietTab] || activeDietTab}" (${dietPref.name})`);
    }
  };
  
  // Calcular gramos necesarios de un alimento para alcanzar macros objetivo
  const calculateFoodGrams = (food, targetMacro, macroType) => {
    const macroPer100g = food[macroType] || 0;
    if (macroPer100g <= 0) return 0;
    return Math.round((targetMacro / macroPer100g) * 100);
  };
  
  // Recalcular macros restantes de una comida - acepta items opcionales para cálculo inmediato
  const recalculateMealRemaining = (mealIndex, updatedMeals = null) => {
    const mealsToUse = updatedMeals || dietData.meals;
    const meal = mealsToUse[mealIndex];
    if (!meal) return;
    
    let usedProtein = 0, usedCarbs = 0, usedFat = 0, usedCalories = 0;
    
    meal.items.forEach(item => {
      if (!item.skip) {
        const mult = item.grams ? item.grams / 100 : (item.multiplier || 1);
        usedProtein += (item.protein || 0) * mult;
        usedCarbs += (item.carbs || 0) * mult;
        usedFat += ((item.fat || item.fats) || 0) * mult;
        usedCalories += (item.calories || 0) * mult;
      }
    });
    
    // Guardar valores usados y restantes (pueden ser negativos si te pasas)
    setMealMacrosRemaining(prev => ({
      ...prev,
      [mealIndex]: {
        protein: (meal.target_protein || 0) - usedProtein,
        carbs: (meal.target_carbs || 0) - usedCarbs,
        fat: (meal.target_fat || 0) - usedFat,
        calories: (meal.target_calories || 0) - usedCalories,
        // También guardar los totales usados
        usedProtein: usedProtein,
        usedCarbs: usedCarbs,
        usedFat: usedFat,
        usedCalories: usedCalories
      }
    }));
  };

  const handleAddFoodItem = (mealIndex, category, food) => {
    if (food.skip) return; // No agregar si es "NO AGREGAR"
    
    // Usar los gramos ya calculados del dropdown
    const grams = food.calculatedGrams || 100;
    const multiplier = grams / 100;
    
    const newMeals = [...dietData.meals];
    const currentMeal = newMeals[mealIndex];
    const newItem = {
      category,
      food_name: food.name,
      quantity: grams,
      unit: 'g',
      calories: food.calories,
      protein: food.protein,
      carbs: food.carbs,
      fat: food.fat,
      multiplier: multiplier,
      grams: grams,
      skip: food.skip || false
    };
    
    // Agregar a items principal
    const newItems = [...(currentMeal.items || []), newItem];
    newMeals[mealIndex] = {
      ...currentMeal,
      items: newItems
    };
    
    // Si tiene múltiples opciones, también actualizar la opción activa
    if (currentMeal.num_options > 1) {
      const activeOption = currentMeal.active_option || 0;
      if (!newMeals[mealIndex].options) {
        newMeals[mealIndex].options = [];
      }
      while (newMeals[mealIndex].options.length <= activeOption) {
        newMeals[mealIndex].options.push({ items: [] });
      }
      newMeals[mealIndex].options[activeOption] = {
        ...newMeals[mealIndex].options[activeOption],
        items: newItems
      };
    }
    
    setDietData({ ...dietData, meals: newMeals });
    recalculateMealRemaining(mealIndex, newMeals);
  };

  const handleRemoveFoodItem = (mealIndex, itemIndex) => {
    const newMeals = [...dietData.meals];
    const currentMeal = newMeals[mealIndex];
    const newItems = currentMeal.items.filter((_, i) => i !== itemIndex);
    
    newMeals[mealIndex] = {
      ...currentMeal,
      items: newItems
    };
    
    // Si tiene múltiples opciones, también actualizar la opción activa
    if (currentMeal.num_options > 1) {
      const activeOption = currentMeal.active_option || 0;
      if (newMeals[mealIndex].options && newMeals[mealIndex].options[activeOption]) {
        newMeals[mealIndex].options[activeOption] = {
          ...newMeals[mealIndex].options[activeOption],
          items: newItems
        };
      }
    }
    
    setDietData({ ...dietData, meals: newMeals });
    recalculateMealRemaining(mealIndex, newMeals);
  };

  // Mover un alimento hacia arriba o abajo dentro de una comida
  const handleMoveItem = (mealIndex, itemIndex, direction) => {
    const newMeals = [...dietData.meals];
    const currentMeal = newMeals[mealIndex];
    const items = [...currentMeal.items];
    
    const newIndex = direction === 'up' ? itemIndex - 1 : itemIndex + 1;
    
    // Verificar límites
    if (newIndex < 0 || newIndex >= items.length) return;
    
    // Intercambiar posiciones
    [items[itemIndex], items[newIndex]] = [items[newIndex], items[itemIndex]];
    
    newMeals[mealIndex] = {
      ...currentMeal,
      items: items
    };
    
    // Si tiene múltiples opciones, también actualizar la opción activa
    if (currentMeal.num_options > 1) {
      const activeOption = currentMeal.active_option || 0;
      if (newMeals[mealIndex].options && newMeals[mealIndex].options[activeOption]) {
        newMeals[mealIndex].options[activeOption] = {
          ...newMeals[mealIndex].options[activeOption],
          items: items
        };
      }
    }
    
    setDietData({ ...dietData, meals: newMeals });
  };

  // Intercambiar un alimento por otro de la misma categoría
  const handleSwapFoodItem = (mealIndex, itemIndex, newFood) => {
    const currentMeal = dietData.meals[mealIndex];
    const item = currentMeal.items[itemIndex];
    let newGrams = item.grams || 100;
    let measurementNote = item.measurement_note || '';
    
    // Recálculo inteligente de gramos (Plan Básico)
    if (clientData.plan_type === 'basic' && item.category && !item.skip) {
      const category = item.category;
      if (category === 'proteinas' && newFood.protein > 0) {
        // Recalcular gramos para misma meta de proteína
        const targetP = currentMeal.target_protein || 30;
        newGrams = Math.round((targetP / newFood.protein) * 100 / 5) * 5 || 5;
        measurementNote = 'crudo';
      } else if (category === 'carbohidratos' && newFood.carbs > 0) {
        // Recalcular para misma meta de carbos (restando fruta si aplica)
        let carbTarget = currentMeal.target_carbs || 50;
        const fruitItem = currentMeal.items.find(i => i.category === 'frutas' && !i.skip);
        if (fruitItem) {
          const fruitCarbs = (fruitItem.grams || 100) * (fruitItem.carbs || 0) / 100;
          carbTarget = Math.max(0, carbTarget - fruitCarbs);
        }
        newGrams = Math.round((carbTarget / newFood.carbs) * 100 / 5) * 5 || 5;
        const name = (newFood.name || '').toLowerCase();
        if (name.includes('avena') || name.includes('crema de arroz') || name.includes('hojuelas') || name.includes('cereal')) {
          measurementNote = 'crudo (polvo)';
        } else if (name.includes('pan') || name.includes('arepa')) {
          measurementNote = 'listo para consumir';
        } else { measurementNote = 'cocido'; }
      } else if (category === 'grasas' && newFood.fat > 0) {
        // Recalcular grasa faltante
        const protItem = currentMeal.items.find(i => i.category === 'proteinas' && !i.skip);
        let fatFromProtein = 0;
        if (protItem) { fatFromProtein = (protItem.grams || 0) * (protItem.fat || 0) / 100; }
        const fatRemaining = Math.max(0, (currentMeal.target_fat || 15) - fatFromProtein);
        newGrams = Math.max(1, Math.round((fatRemaining / newFood.fat) * 100));
        measurementNote = 'gramos';
      }
    }
    
    const newMeals = [...dietData.meals];
    const newItems = currentMeal.items.map((it, idx) => 
      idx === itemIndex 
        ? {
            ...it,
            food_name: newFood.name,
            calories: newFood.calories,
            protein: newFood.protein,
            carbs: newFood.carbs,
            fat: newFood.fat,
            grams: newGrams,
            quantity: newGrams,
            multiplier: newGrams / 100,
            measurement_note: measurementNote || it.measurement_note
          }
        : it
    );
    
    newMeals[mealIndex] = {
      ...currentMeal,
      items: newItems
    };
    
    // Si tiene múltiples opciones, también actualizar la opción activa
    if (currentMeal.num_options > 1) {
      const activeOption = currentMeal.active_option || 0;
      if (!newMeals[mealIndex].options) {
        newMeals[mealIndex].options = [];
      }
      while (newMeals[mealIndex].options.length <= activeOption) {
        newMeals[mealIndex].options.push({ items: [] });
      }
      newMeals[mealIndex].options[activeOption] = {
        ...newMeals[mealIndex].options[activeOption],
        items: newItems
      };
    }
    
    setDietData({ ...dietData, meals: newMeals });
    recalculateMealRemaining(mealIndex, newMeals);
    toast.success(`Alimento cambiado a "${newFood.name}"`);
  };

  // Cambiar número de opciones por comida
  const handleChangeNumOptions = (mealIndex, numOptions) => {
    const newMeals = [...dietData.meals];
    const currentMeal = newMeals[mealIndex];
    const activeOption = currentMeal.active_option || 0;
    
    // Si ya tiene opciones, usarlas. Si no, crear array vacío
    let currentOptions = [...(currentMeal.options || [])];
    
    // SIEMPRE guardar los items actuales en la opción activa antes de modificar
    if (currentMeal.items && currentMeal.items.length > 0) {
      // Asegurar que existe el array hasta la opción activa
      while (currentOptions.length <= activeOption) {
        currentOptions.push({ items: [] });
      }
      // Guardar items actuales en la opción activa
      currentOptions[activeOption] = { 
        ...currentOptions[activeOption],
        items: [...currentMeal.items] 
      };
    }
    
    // Si no hay opciones previas Y hay items, crear la primera opción
    if (currentOptions.length === 0 && currentMeal.items && currentMeal.items.length > 0) {
      currentOptions = [{ items: [...currentMeal.items] }];
    }
    
    // Ajustar el número de opciones
    while (currentOptions.length < numOptions) {
      currentOptions.push({ items: [] });
    }
    while (currentOptions.length > numOptions) {
      currentOptions.pop();
    }
    
    newMeals[mealIndex] = {
      ...currentMeal,
      num_options: numOptions,
      options: currentOptions,
      active_option: Math.min(activeOption, numOptions - 1) // Asegurar que active_option sea válido
    };
    
    setDietData({ ...dietData, meals: newMeals });
  };

  // 2026-02 · Eliminar una opción individual. Si es la activa, se cae a la
  // anterior. Nunca deja la comida sin opciones (min 1).
  const handleRemoveOption = (mealIndex, optionIndex) => {
    const newMeals = [...dietData.meals];
    const currentMeal = newMeals[mealIndex];
    let opts = [...(currentMeal.options || [])];
    // Si el array de opciones aún no está sincronizado, asegurar que refleja num_options + items actuales.
    while (opts.length < (currentMeal.num_options || 1)) opts.push({ items: [] });
    // Guardar items actuales en la opción activa antes de eliminar (por si se elimina otra).
    const activeOpt = currentMeal.active_option || 0;
    opts[activeOpt] = { ...(opts[activeOpt] || {}), items: [...(currentMeal.items || [])] };
    if (opts.length <= 1) {
      // No permitimos quedarnos sin opciones — pero sí vaciar la última.
      opts = [{ items: [] }];
      newMeals[mealIndex] = { ...currentMeal, num_options: 1, active_option: 0, options: opts, items: [] };
      setDietData({ ...dietData, meals: newMeals });
      return;
    }
    opts.splice(optionIndex, 1);
    let newActive = activeOpt;
    if (optionIndex === activeOpt) {
      newActive = Math.max(0, activeOpt - 1);
    } else if (optionIndex < activeOpt) {
      newActive = activeOpt - 1;
    }
    newMeals[mealIndex] = {
      ...currentMeal,
      num_options: opts.length,
      options: opts,
      active_option: newActive,
      items: opts[newActive]?.items || [],
    };
    setDietData({ ...dietData, meals: newMeals });
  };

  // Cambiar la opción activa que se está editando
  const handleChangeActiveOption = (mealIndex, optionIndex) => {
    const newMeals = [...dietData.meals];
    const currentMeal = newMeals[mealIndex];
    const previousActiveOption = currentMeal.active_option || 0;
    
    // PRIMERO: Guardar los items actuales en la opción activa ANTES de cambiar
    if (!newMeals[mealIndex].options) {
      newMeals[mealIndex].options = [];
    }
    // Asegurarse de que existe la opción anterior
    while (newMeals[mealIndex].options.length <= previousActiveOption) {
      newMeals[mealIndex].options.push({ items: [] });
    }
    // Guardar items actuales en la opción anterior
    newMeals[mealIndex].options[previousActiveOption] = {
      ...newMeals[mealIndex].options[previousActiveOption],
      items: [...(currentMeal.items || [])]
    };
    
    // SEGUNDO: Cambiar a la nueva opción activa y cargar sus items
    newMeals[mealIndex] = {
      ...newMeals[mealIndex],
      active_option: optionIndex,
      // Cargar los items de la opción seleccionada
      items: newMeals[mealIndex].options?.[optionIndex]?.items || []
    };
    setDietData({ ...dietData, meals: newMeals });
  };

  // Guardar items actuales en la opción activa
  const handleSaveCurrentOption = (mealIndex) => {
    const meal = dietData.meals[mealIndex];
    const activeOption = meal.active_option || 0;
    const newMeals = [...dietData.meals];
    
    if (!newMeals[mealIndex].options) {
      newMeals[mealIndex].options = [{ items: [] }];
    }
    
    newMeals[mealIndex].options[activeOption] = {
      items: [...meal.items]
    };
    
    setDietData({ ...dietData, meals: newMeals });
  };

  // Funciones para suplementos múltiples
  const handleAddSupplement = (mealIndex) => {
    const newMeals = [...dietData.meals];
    if (!newMeals[mealIndex].supplements) {
      newMeals[mealIndex].supplements = [];
    }
    newMeals[mealIndex].supplements.push('');
    setDietData({ ...dietData, meals: newMeals });
  };

  const handleUpdateSupplement = (mealIndex, suppIndex, value) => {
    const newMeals = [...dietData.meals];
    newMeals[mealIndex].supplements[suppIndex] = value;
    setDietData({ ...dietData, meals: newMeals });
  };

  const handleRemoveSupplement = (mealIndex, suppIndex) => {
    const newMeals = [...dietData.meals];
    newMeals[mealIndex].supplements.splice(suppIndex, 1);
    setDietData({ ...dietData, meals: newMeals });
  };

  // Funciones para suplementos en ayunas
  const handleAddFastingSupplement = () => {
    const newSupps = [...(dietData.fasting_supplements || []), ''];
    setDietData({ ...dietData, fasting_supplements: newSupps });
  };

  const handleUpdateFastingSupplement = (suppIndex, value) => {
    const newSupps = [...(dietData.fasting_supplements || [])];
    newSupps[suppIndex] = value;
    setDietData({ ...dietData, fasting_supplements: newSupps });
  };

  const handleRemoveFastingSupplement = (suppIndex) => {
    const newSupps = [...(dietData.fasting_supplements || [])];
    newSupps.splice(suppIndex, 1);
    setDietData({ ...dietData, fasting_supplements: newSupps });
  };

  // Funciones para suplementos pre-entreno
  const handleAddPreWorkoutSupplement = () => {
    const newSupps = [...(dietData.pre_workout_supplements || []), ''];
    setDietData({ ...dietData, pre_workout_supplements: newSupps });
  };

  const handleUpdatePreWorkoutSupplement = (suppIndex, value) => {
    const newSupps = [...(dietData.pre_workout_supplements || [])];
    newSupps[suppIndex] = value;
    setDietData({ ...dietData, pre_workout_supplements: newSupps });
  };

  const handleRemovePreWorkoutSupplement = (suppIndex) => {
    const newSupps = [...(dietData.pre_workout_supplements || [])];
    newSupps.splice(suppIndex, 1);
    setDietData({ ...dietData, pre_workout_supplements: newSupps });
  };

  // Funciones para acompañamientos (ACOMP) - multi-select, no afecta macros
  const handleToggleAcomp = (mealIndex, acompId, acompLabel) => {
    const newMeals = [...dietData.meals];
    const meal = newMeals[mealIndex];
    const current = meal.acompanamientos || [];
    const exists = current.find(a => a.id === acompId);
    if (exists) {
      newMeals[mealIndex] = { ...meal, acompanamientos: current.filter(a => a.id !== acompId) };
    } else {
      newMeals[mealIndex] = { ...meal, acompanamientos: [...current, { id: acompId, label: acompLabel }] };
    }
    setDietData({ ...dietData, meals: newMeals });
  };

  const handleClearAcomp = (mealIndex) => {
    const newMeals = [...dietData.meals];
    newMeals[mealIndex] = { ...newMeals[mealIndex], acompanamientos: [] };
    setDietData({ ...dietData, meals: newMeals });
  };

  // Mover comida hacia arriba
  const handleMoveMealUp = (mealIndex) => {
    if (mealIndex === 0) return; // Ya está en la primera posición
    const newMeals = [...dietData.meals];
    // Intercambiar posiciones
    [newMeals[mealIndex - 1], newMeals[mealIndex]] = [newMeals[mealIndex], newMeals[mealIndex - 1]];
    // Actualizar nombres de comida
    newMeals.forEach((meal, idx) => {
      meal.meal_name = `Comida ${idx + 1}`;
    });
    setDietData({ ...dietData, meals: newMeals });
  };

  // Mover comida hacia abajo
  const handleMoveMealDown = (mealIndex) => {
    if (mealIndex === dietData.meals.length - 1) return; // Ya está en la última posición
    const newMeals = [...dietData.meals];
    // Intercambiar posiciones
    [newMeals[mealIndex], newMeals[mealIndex + 1]] = [newMeals[mealIndex + 1], newMeals[mealIndex]];
    // Actualizar nombres de comida
    newMeals.forEach((meal, idx) => {
      meal.meal_name = `Comida ${idx + 1}`;
    });
    setDietData({ ...dietData, meals: newMeals });
  };

  const handleAddExercise = (dayIndex) => {
    const newDays = [...workoutData.days];
    newDays[dayIndex].exercises.push({
      name: '',
      sets: '4',
      reps: '10-12',
      rest: '90 seg'
    });
    setWorkoutData({ ...workoutData, days: newDays });
  };

  const handleRemoveExercise = (dayIndex, exIndex) => {
    const newDays = [...workoutData.days];
    newDays[dayIndex].exercises.splice(exIndex, 1);
    setWorkoutData({ ...workoutData, days: newDays });
  };

  // Funciones para páginas personalizadas
  const handleAddCustomPage = () => {
    const newPage = {
      id: Date.now(),
      title: '',
      content: '',
      page_position: 1
    };
    setDietData({
      ...dietData,
      custom_pages: [...dietData.custom_pages, newPage]
    });
  };

  const handleUpdateCustomPage = (pageId, field, value) => {
    setDietData({
      ...dietData,
      custom_pages: dietData.custom_pages.map(p => 
        p.id === pageId ? { ...p, [field]: value } : p
      )
    });
  };

  const handleRemoveCustomPage = (pageId) => {
    setDietData({
      ...dietData,
      custom_pages: dietData.custom_pages.filter(p => p.id !== pageId)
    });
  };

  // Buscar plan existente por nombre de cliente
  const findPlanByClientName = (name) => {
    if (!name || name.length < 2) {
      setMatchingPlan(null);
      return;
    }
    
    const normalizedName = name.toLowerCase().trim();
    const found = savedPlans.find(plan => 
      plan.client?.name?.toLowerCase().trim() === normalizedName
    );
    
    if (found && found.id !== editingPlanId) {
      setMatchingPlan(found);
    } else {
      setMatchingPlan(null);
    }
  };

  // Cargar plan existente de cliente
  const handleLoadMatchingPlan = () => {
    if (matchingPlan) {
      handleLoadPlan(matchingPlan);
      setMatchingPlan(null);
    }
  };

  // Vista previa del PDF
  const handlePreviewPDF = async () => {
    if (!clientData.name) {
      toast.error('Por favor ingresa el nombre del cliente antes de ver la vista previa');
      return;
    }
    
    // Guardar la dieta actual en multiDiets antes de generar preview
    const updatedMultiDiets = {
      ...multiDiets,
      [activeDietTab]: {
        ...dietData,
        fasting_supplements: dietData.fasting_supplements || [],
        pre_workout_supplements: dietData.pre_workout_supplements || []
      }
    };

    try {
      toast.loading('Generando vista previa del PDF...', { id: 'pdf-preview' });
      
      // Crear payload para el endpoint de preview
      const payload = {
        client: {
          name: clientData.name,
          age: parseInt(clientData.age) || 25,
          weight: parseFloat(clientData.weight) || 60,
          height: parseFloat(clientData.height) || 165,
          goal: clientData.goal || 'ganar_musculo',
          plan_type: clientData.plan_type || 'basic'
        },
        diet: dietData,
        multi_diets: updatedMultiDiets,
        workout: workoutData.workout_name ? workoutData : null,
        clinical_data: clinicalResult ? {
          recommended_supplements: clinicalResult.recommended_supplements || [],
          avoid_supplements: clinicalResult.avoid_supplements || []
        } : null
      };
      
      const response = await axios.post(`${API}/offline-generator/preview-pdf`, payload, {
        responseType: 'blob',
        withCredentials: false
      });
      
      // Crear URL del blob y abrir en nueva pestaña
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Intentar abrir en nueva pestaña
      const newWindow = window.open(url, '_blank');
      
      // Si el popup fue bloqueado, descargar el archivo
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Crear link de descarga como alternativa
        const link = document.createElement('a');
        link.href = url;
        link.download = `vista_previa_${clientData.name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('PDF descargado (popup bloqueado)', { id: 'pdf-preview' });
      } else {
        toast.success('Vista previa del PDF generada', { id: 'pdf-preview' });
      }
    } catch (error) {
      console.error('Error generando preview del PDF:', error);
      toast.error('Error al generar la vista previa del PDF', { id: 'pdf-preview' });
    }
  };

  // Guardar plan
  const handleSavePlan = async (forceUpdate = false, updateType = 'full') => {
    if (!planName.trim()) {
      toast.error('Por favor ingresa un nombre para el plan');
      return;
    }
    if (!clientData.name) {
      toast.error('Por favor ingresa el nombre del cliente');
      return;
    }

    // Guardar la dieta actual en multiDiets antes de guardar
    // IMPORTANTE: Incluir siempre los suplementos en la dieta actual
    const currentDietWithSupplements = {
      ...dietData,
      fasting_supplements: dietData.fasting_supplements || [],
      pre_workout_supplements: dietData.pre_workout_supplements || []
    };
    
    const updatedMultiDiets = {
      ...multiDiets,
      [activeDietTab]: currentDietWithSupplements
    };

    // Si hay un plan existente del mismo cliente y no estamos en modo edición
    // SIEMPRE actualizar el existente, no crear uno nuevo
    let planIdToUpdate = editingPlanId;
    
    if (!planIdToUpdate) {
      const normalizedName = clientData.name.toLowerCase().trim();
      const existingPlan = savedPlans.find(plan => 
        plan.client?.name?.toLowerCase().trim() === normalizedName
      );
      
      if (existingPlan) {
        // Actualizar automáticamente sin preguntar
        planIdToUpdate = existingPlan.id;
        toast.info(`Actualizando plan existente de "${clientData.name}"`);
      }
    }

    try {
      const payload = {
        plan_name: planName,
        client: {
          name: clientData.name,
          age: parseInt(clientData.age) || 25,
          weight: parseFloat(clientData.weight) || 60,
          height: parseFloat(clientData.height) || 165,
          goal: clientData.goal || 'ganar_musculo',
          activity_level: clientData.activity_level || 'moderado',
          plan_type: clientData.plan_type || 'basic',
          num_meals: clientData.num_meals || 5,
          diet_preference: clientData.diet_preference || 'equilibrada',
          // 2026-02 · Persistir composición corporal y overrides manuales del admin
          body_fat_pct: clientData.body_fat_pct !== '' && clientData.body_fat_pct != null
            ? parseFloat(clientData.body_fat_pct) : null,
          body_fat_pct_target: clientData.body_fat_pct_target !== '' && clientData.body_fat_pct_target != null
            ? parseFloat(clientData.body_fat_pct_target) : null,
          manual_weekly_fat_loss_kg: clientData.manual_weekly_fat_loss_kg !== '' && clientData.manual_weekly_fat_loss_kg != null
            ? parseFloat(clientData.manual_weekly_fat_loss_kg) : null,
          manual_weeks_to_target: clientData.manual_weeks_to_target !== '' && clientData.manual_weeks_to_target != null
            ? parseFloat(clientData.manual_weeks_to_target) : null,
          gender: clientData.gender || 'femenino',
          // IMPORTANTE: Preservar datos del formulario - NUNCA se deben perder
          email: clientData.email || null,
          additional_info: clientData.additional_info || null
        },
        diet_name: dietData.diet_name,
        day_type: dietData.day_type,
        meals: dietData.meals,
        notes: dietData.notes,
        target_calories: dietData.target_calories,
        target_protein: dietData.target_protein,
        target_carbs: dietData.target_carbs,
        target_fat: dietData.target_fat,
        custom_pages: dietData.custom_pages,
        // Suplementos especiales
        fasting_supplements: dietData.fasting_supplements || [],
        pre_workout_supplements: dietData.pre_workout_supplements || [],
        // Hidratación (litros de agua)
        water_liters: dietData.water_liters !== '' && dietData.water_liters != null
          ? parseFloat(dietData.water_liters) || null
          : null,
        water_note: (dietData.water_note || '').trim() || null,
        // Post-workout meal selection (normalizado)
        post_workout_meal: normalizePostWorkoutMeal(dietData.post_workout_meal),
        // Número de comidas y preferencia - guardar también a nivel superior como respaldo
        num_meals: clientData.num_meals || 5,
        diet_preference: clientData.diet_preference || 'equilibrada',
        // Múltiples dietas por tipo de día
        multi_diets: updatedMultiDiets,
        workout_name: workoutData.workout_name || '',
        workout_level: workoutData.level || 'intermedio',
        workout_days: workoutData.days || [],
        workout_notes: workoutData.notes || '',
        // Cardio
        has_cardio: workoutData.has_cardio || false,
        cardio_type: workoutData.cardio_type || 'liss',
        cardio_duration: workoutData.cardio_duration || 20,
        cardio_notes: workoutData.cardio_notes || '',
        // Tipo de actualización para fechas
        update_type: updateType,
        // Datos del módulo clínico
        clinical_data: clinicalResult ? {
          dominant_condition: clinicalResult.dominant_condition,
          conditions_applied: clinicalResult.conditions_applied,
          blocked_foods: clinicalResult.blocked_foods || [],
          blocked_foods_count: clinicalResult.blocked_foods?.length || 0,
          caution_foods_count: clinicalResult.caution_foods?.length || 0,
          avoid_foods_display: clinicalResult.avoid_foods_display,
          block_behavior_map: clinicalResult.block_behavior_map || {},
          manual_notes: clinicalNotes,
          admin_blocked_foods: adminBlockedFoodsText || '',
          active_notes: clinicalResult.active_notes || [],
          active_actions: clinicalResult.active_actions || [],
          substitution_map: clinicalResult.substitution_map || {},
          user_summary: clinicalResult.user_summary || null,
          recommended_supplements: clinicalResult.recommended_supplements || [],
          avoid_supplements: clinicalResult.avoid_supplements || []
        } : null,
        // 2026-02 · Composición Corporal y Proyecciones (calculado admin-side)
        body_composition: computeBodyComposition(clientData),
        // 2026-02 · Alimentos prohibidos manualmente por el admin en la RAÍZ
        // del payload — independiente de clinical_data para que no se pierda
        // cuando el plan no tiene patologías (clinicalResult=null).
        admin_blocked_foods: adminBlockedFoodsText || '',
        admin_blocked_foods_cleared: adminBlockedClearedRef.current === true
      };

      if (planIdToUpdate) {
        await axios.put(`${API}/admin/offline-generator/saved-plans/${planIdToUpdate}`, payload, { withCredentials: false });
        toast.success('Plan actualizado exitosamente');
        setEditingPlanId(planIdToUpdate);
      } else {
        const response = await axios.post(`${API}/admin/offline-generator/save-plan`, payload, { withCredentials: false });
        toast.success('Plan guardado exitosamente');
        if (response.data?.plan_id) {
          setEditingPlanId(response.data.plan_id);
        }
      }

      // Actualizar fechas locales
      const now = new Date().toISOString();
      setPlanDates(prev => {
        const updated = { ...prev };
        if (updateType === 'diet_total') updated.diet_total_update_date = now;
        else if (updateType === 'diet_progression') updated.diet_progression_date = now;
        else if (updateType === 'workout_total') updated.workout_total_update_date = now;
        else if (updateType === 'workout_progression') updated.workout_progression_date = now;
        else { updated.diet_total_update_date = now; updated.workout_total_update_date = now; }
        return updated;
      });

      // Actualizar el estado de multiDiets con la versión guardada
      setMultiDiets(updatedMultiDiets);
      
      fetchSavedPlans();
      setMatchingPlan(null);
    } catch (error) {
      console.error('Error saving plan:', error);
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Error al guardar el plan');
      }
    }
  };

  // Cargar plan guardado
  // 2026-09 · Planes guardados ANTES del fix de macros: los ingredientes de recetas de la
  // biblioteca tenían un reparto proporcional del total. Al cargar el plan se recalculan
  // (por receta y comida): con match en la base → macros reales; sin match → el remanente
  // del total original de esa receta. Se persiste cuando el admin guarda el plan.
  const refreshLegacyRecipeItemMacros = (plan) => {
    let fixed = 0;
    if (!plan || !foodDatabase || Object.keys(foodDatabase).length === 0) return { plan, fixed };
    const fixMeal = (meal) => {
      const items = Array.isArray(meal?.items) ? meal.items : null;
      if (!items) return meal;
      const groups = {};
      items.forEach((it, i) => {
        if (it && it.from_recipe_library_id && !it.macro_source) (groups[it.from_recipe_library_id] = groups[it.from_recipe_library_id] || []).push(i);
      });
      if (Object.keys(groups).length === 0) return meal;
      const newItems = [...items];
      for (const idxs of Object.values(groups)) {
        const tot = (it) => { const m = (Number(it.grams) || 0) / 100; return { k: (Number(it.calories) || 0) * m, p: (Number(it.protein) || 0) * m, c: (Number(it.carbs) || 0) * m, g: (Number(it.fat ?? it.fats) || 0) * m }; };
        const orig = idxs.reduce((a, i) => { const t = tot(items[i]); return { k: a.k + t.k, p: a.p + t.p, c: a.c + t.c, g: a.g + t.g }; }, { k: 0, p: 0, c: 0, g: 0 });
        const rows = idxs.map((i) => ({ i, it: items[i], food: Number(items[i].grams) > 0 ? findFoodInDatabase(items[i].food_name) : null }));
        if (!rows.some((r) => r.food)) continue;
        const matched = rows.filter((r) => r.food).reduce((a, r) => { const m = Number(r.it.grams) / 100; return { k: a.k + (Number(r.food.calories) || 0) * m, p: a.p + (Number(r.food.protein) || 0) * m, c: a.c + (Number(r.food.carbs) || 0) * m, g: a.g + (Number(r.food.fat ?? r.food.fats) || 0) * m }; }, { k: 0, p: 0, c: 0, g: 0 });
        const rest = { k: Math.max(0, orig.k - matched.k), p: Math.max(0, orig.p - matched.p), c: Math.max(0, orig.c - matched.c), g: Math.max(0, orig.g - matched.g) };
        const um = rows.filter((r) => !r.food);
        const umGrams = um.reduce((s2, r) => s2 + (Number(r.it.grams) || 0), 0);
        for (const r of rows) {
          const grams = Number(r.it.grams) || 100;
          let v;
          if (r.food) {
            v = { k: Number(r.food.calories) || 0, p: Number(r.food.protein) || 0, c: Number(r.food.carbs) || 0, g: Number(r.food.fat ?? r.food.fats) || 0 };
          } else {
            const share = umGrams > 0 ? (Number(r.it.grams) || 0) / umGrams : 1 / (um.length || 1);
            const d = grams / 100;
            v = { k: rest.k * share / d, p: rest.p * share / d, c: rest.c * share / d, g: rest.g * share / d };
          }
          newItems[r.i] = {
            ...r.it,
            calories: Math.max(0, Math.round(v.k)),
            protein: Math.max(0, +v.p.toFixed(1)),
            carbs: Math.max(0, +v.c.toFixed(1)),
            fat: Math.max(0, +v.g.toFixed(1)),
            fats: Math.max(0, +v.g.toFixed(1)),
            macro_source: r.food ? 'food_database' : 'recipe_estimate',
            matched_food_name: r.food ? r.food.name : undefined,
          };
          if (r.food) fixed += 1;
        }
      }
      return { ...meal, items: newItems };
    };
    const out = { ...plan };
    if (Array.isArray(plan.meals)) out.meals = plan.meals.map(fixMeal);
    if (plan.multi_diets && typeof plan.multi_diets === 'object') {
      out.multi_diets = Object.fromEntries(Object.entries(plan.multi_diets).map(([k, d]) => [k, d && Array.isArray(d.meals) ? { ...d, meals: d.meals.map(fixMeal) } : d]));
    }
    return { plan: out, fixed };
  };

  const handleLoadPlan = async (rawPlan) => {
    const { plan, fixed: fixedRecipeItems } = refreshLegacyRecipeItemMacros(rawPlan);
    if (fixedRecipeItems > 0) {
      toast.info(`Se actualizaron ${fixedRecipeItems} ingrediente(s) de recetas con los macros reales de la base de alimentos. Guarda el plan para conservar el cambio.`);
    }
    // Verificar si es un plan personalizado (del formulario)
    const isPersonalized = plan.is_personalized_plan;
    
    // Para planes personalizados, los datos vienen en formato diferente
    if (isPersonalized) {
      setClientData({
        name: plan.name || '',
        age: plan.age?.toString() || '',
        weight: plan.weight?.toString() || '',
        height: plan.height?.toString() || '',
        goal: plan.goal || 'perder_grasa',
        activity_level: plan.activity_level || 'moderado',
        plan_type: plan.plan_type || 'basic',
        num_meals: plan.num_meals || 5,
        diet_preference: 'equilibrada',
        // 2026-02 · Composición corporal: los planes personalizados nuevos aún no tienen estos campos,
        // dejar vacíos para que el admin los complete
        body_fat_pct: (plan.body_fat_pct ?? '') === null ? '' : (plan.body_fat_pct ?? '').toString?.() ?? '',
        body_fat_pct_target: (plan.body_fat_pct_target ?? '') === null ? '' : (plan.body_fat_pct_target ?? '').toString?.() ?? '',
        manual_weekly_fat_loss_kg: '',
        manual_weeks_to_target: '',
        gender: plan.gender || 'femenino',
        // Datos adicionales del formulario
        email: plan.email || '',
        additional_info: plan.additional_info || {}
      });
      
      // Cargar dieta vacía para que admin la complete
      setDietData({
        diet_name: plan.diet_name || `Plan ${plan.plan_type?.toUpperCase()} - ${plan.name}`,
        day_type: 'training_inferior',
        notes: '',
        target_calories: 0,
        target_protein: 0,
        target_carbs: 0,
        target_fat: 0,
        post_workout_meal: normalizePostWorkoutMeal(plan.post_workout_meal),
        fasting_supplements: plan.fasting_supplements || [],
        pre_workout_supplements: plan.pre_workout_supplements || [],
        water_liters: plan.water_liters ?? '',
        water_note: plan.water_note ?? '',
        meals: plan.meals?.length > 0 ? plan.meals : [{ meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [], target_protein: 0, target_carbs: 0, target_fat: 0, target_calories: 0 }],
        custom_pages: plan.custom_pages || []
      });
      setMultiDiets({
        training_inferior: null,
        training_superior: null,
        descanso: null
      });
      setActiveDietTab('training_inferior');
    } else {
      // Cargar plan normal (formato antiguo)
      // IMPORTANTE: Preservar additional_info y email del formulario
      const additionalInfo = plan.additional_info || plan.client?.additional_info || {};
      const email = plan.email || plan.client?.email || '';
      
      setClientData({
        name: plan.client?.name || '',
        age: plan.client?.age?.toString() || '',
        weight: plan.client?.weight?.toString() || '',
        height: plan.client?.height?.toString() || '',
        goal: plan.client?.goal || 'perder_grasa',
        activity_level: plan.client?.activity_level || 'moderado',
        plan_type: plan.client?.plan_type || 'basic',
        num_meals: plan.client?.num_meals || plan.num_meals || 5,
        diet_preference: plan.client?.diet_preference || plan.diet_preference || 'equilibrada',
        // 2026-02 · Restaurar composición corporal y overrides manuales guardados en el plan
        body_fat_pct: (plan.client?.body_fat_pct ?? '') === null ? '' : (plan.client?.body_fat_pct ?? '').toString?.() ?? '',
        body_fat_pct_target: (plan.client?.body_fat_pct_target ?? '') === null ? '' : (plan.client?.body_fat_pct_target ?? '').toString?.() ?? '',
        manual_weekly_fat_loss_kg: (plan.client?.manual_weekly_fat_loss_kg ?? '') === null ? '' : (plan.client?.manual_weekly_fat_loss_kg ?? '').toString?.() ?? '',
        manual_weeks_to_target: (plan.client?.manual_weeks_to_target ?? '') === null ? '' : (plan.client?.manual_weeks_to_target ?? '').toString?.() ?? '',
        gender: plan.client?.gender || 'femenino',
        // Datos del formulario - NUNCA perder
        email: email,
        additional_info: additionalInfo
      });

      // Cargar múltiples dietas si existen
      if (plan.multi_diets) {
        setMultiDiets(plan.multi_diets);
        // Cargar la primera dieta disponible
        const firstDietType = Object.keys(plan.multi_diets).find(key => plan.multi_diets[key]) || 'training_inferior';
        const firstDiet = plan.multi_diets[firstDietType];
        if (firstDiet) {
          setDietData({
            diet_name: firstDiet.diet_name || '',
            day_type: firstDietType,
            notes: firstDiet.notes || '',
            target_calories: firstDiet.target_calories || 0,
            target_protein: firstDiet.target_protein || 0,
            target_carbs: firstDiet.target_carbs || 0,
            target_fat: firstDiet.target_fat || 0,
            post_workout_meal: normalizePostWorkoutMeal(firstDiet.post_workout_meal || plan.post_workout_meal),
            fasting_supplements: firstDiet.fasting_supplements || plan.fasting_supplements || [],
            pre_workout_supplements: firstDiet.pre_workout_supplements || plan.pre_workout_supplements || [],
            water_liters: firstDiet.water_liters ?? plan.water_liters ?? '',
            water_note: firstDiet.water_note ?? plan.water_note ?? '',
            meals: firstDiet.meals?.length > 0 ? firstDiet.meals : [{ meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [] }],
            custom_pages: firstDiet.custom_pages || [],
            // Recordar la última distribución de macros aplicada a esta dieta
            distribution_mode: firstDiet.distribution_mode || null,
            custom_meal_percents: firstDiet.custom_meal_percents || null
          });
          setActiveDietTab(firstDietType);
        }
      } else {
        // Cargar dieta única (compatibilidad con planes antiguos)
        setDietData({
          diet_name: plan.diet_name || '',
          day_type: plan.day_type || 'training_inferior',
          notes: plan.notes || '',
          target_calories: plan.target_calories || 0,
          target_protein: plan.target_protein || 0,
          target_carbs: plan.target_carbs || 0,
          target_fat: plan.target_fat || 0,
          post_workout_meal: normalizePostWorkoutMeal(plan.post_workout_meal),
          fasting_supplements: plan.fasting_supplements || [],
          pre_workout_supplements: plan.pre_workout_supplements || [],
          water_liters: plan.water_liters ?? '',
          water_note: plan.water_note ?? '',
          meals: plan.meals?.length > 0 ? plan.meals : [{ meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [] }],
          custom_pages: plan.custom_pages || []
        });
        setActiveDietTab(plan.day_type || 'training_inferior');
        // Guardar en multiDiets para compatibilidad
        setMultiDiets({
          training_inferior: plan.day_type === 'training_inferior' ? { ...plan } : null,
          training_superior: plan.day_type === 'training_superior' ? { ...plan } : null,
          descanso: plan.day_type === 'descanso' || plan.day_type === 'rest' ? { ...plan } : null,
          lineal: plan.day_type === 'lineal' ? { ...plan } : null
        });
      }
    }

    setWorkoutData({
      workout_name: plan.workout_name || '',
      level: plan.workout_level || 'intermedio',
      notes: plan.workout_notes || '',
      days: plan.workout_days?.length > 0 ? plan.workout_days : [],
      // Cardio
      has_cardio: plan.has_cardio || false,
      cardio_type: plan.cardio_type || 'liss',
      cardio_duration: plan.cardio_duration || 20,
      cardio_notes: plan.cardio_notes || ''
    });

    setPlanName(plan.plan_name || '');
    setEditingPlanId(plan.id);
    setPlanDates({
      diet_total_update_date: plan.diet_total_update_date || null,
      diet_progression_date: plan.diet_progression_date || null,
      workout_total_update_date: plan.workout_total_update_date || null,
      workout_progression_date: plan.workout_progression_date || null
    });
    
    // Cargar notas clínicas manuales si existen
    if (plan.clinical_data?.manual_notes) {
      setClinicalNotes(plan.clinical_data.manual_notes);
    } else {
      setClinicalNotes('');
    }

    // Cargar lista manual de alimentos bloqueados por el admin
    adminBlockedClearedRef.current = false;
    setAdminBlockedFoodsText(plan.admin_blocked_foods ?? plan.clinical_data?.admin_blocked_foods ?? '');
    
    // Cargar ediciones clínicas del admin si existen
    if (plan.clinical_data_edited && Object.keys(plan.clinical_data_edited).length > 0) {
      setClinicalEditedData(plan.clinical_data_edited);
    } else {
      setClinicalEditedData(null);
    }
    
    // Cargar resumen clínico auto-generado
    if (plan.user_clinical_summary) {
      setClinicalAutoSummary(plan.user_clinical_summary);
    } else {
      setClinicalAutoSummary(null);
    }
    
    // Cargar estado del protocolo si hay datos clínicos
    if (plan.clinical_data && plan.id) {
      fetchProtocolStatus(plan.id);
    } else {
      setProtocolStatus(null);
    }
    
    setShowSavedPlans(false);
    toast.success(`Plan "${plan.plan_name}" cargado`);
  };

  // Eliminar plan guardado
  const handleDeletePlan = async (planId) => {
    if (!window.confirm('¿Estás segura de eliminar este plan?')) return;
    
    try {
      await axios.delete(`${API}/admin/offline-generator/saved-plans/${planId}`, { withCredentials: false });
      toast.success('Plan eliminado');
      fetchSavedPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error al eliminar el plan');
    }
  };

  // Nuevo plan (limpiar todo)
  const handleNewPlan = () => {
    setClientData({
      name: '',
      age: '',
      weight: '',
      height: '',
      body_fat_pct: '',
      body_fat_pct_target: '',
      manual_weekly_fat_loss_kg: '',
      manual_weeks_to_target: '',
      gender: 'femenino',
      goal: 'perder_grasa',
      activity_level: 'moderado',
      plan_type: 'basic',
      num_meals: 5,
      diet_preference: 'equilibrada'
    });
    setDietData({
      diet_name: '',
      day_type: 'training_inferior',
      notes: '',
      target_calories: 0,
      target_protein: 0,
      target_carbs: 0,
      target_fat: 0,
      post_workout_meal: 'none',
      fasting_supplements: [],
      pre_workout_supplements: [],
      meals: [{ meal_name: 'Comida 1', meal_time: '07:00', items: [], supplements: [] }],
      custom_pages: []
    });
    // Resetear múltiples dietas
    setMultiDiets({
      training_inferior: null,
      training_superior: null,
      descanso: null
    });
    setActiveDietTab('training_inferior');
    setWorkoutData({
      workout_name: '',
      level: 'intermedio',
      notes: '',
      days: [],
      // Cardio
      has_cardio: false,
      cardio_type: 'liss',
      cardio_duration: 20,
      cardio_notes: ''
    });
    setPlanName('');
    setEditingPlanId(null);
    setPlanDates({ diet_total_update_date: null, diet_progression_date: null, workout_total_update_date: null, workout_progression_date: null });
    setClinicalResult(null);
    setClinicalNotes('');
    adminBlockedClearedRef.current = false;
    setAdminBlockedFoodsText('');
    setClinicalEditedData(null);
    setClinicalAutoSummary(null);
    
    // Cargar páginas por defecto automáticamente
    if (defaultPages.length > 0) {
      const pagesToAdd = defaultPages.map((dp, index) => ({
        id: Date.now() + index,
        title: dp.title,
        content: dp.content,
        page_position: dp.page_position
      }));
      setDietData(prev => ({ ...prev, custom_pages: pagesToAdd }));
    }
  };

  // Generar dieta automáticamente basada en macros objetivo
  const handleGenerateAutoDiet = async () => {
    if (!dietData.target_calories || !dietData.target_protein) {
      toast.error('Por favor configura las calorías y proteína objetivo');
      return;
    }
    
    setAutoDietGenerating(true);
    
    try {
      const numMeals = parseInt(clientData.num_meals) || 5;
      const generatedMeals = [];
      
      for (let i = 0; i < numMeals; i++) {
        const meal = generateSingleMeal(i, numMeals);
        generatedMeals.push(meal);
      }
      
      setDietData(prev => ({
        ...prev,
        meals: generatedMeals
      }));
      
      // Calcular macros restantes para cada comida generada
      const remainingMacros = {};
      generatedMeals.forEach((meal, idx) => {
        let usedProtein = 0, usedCarbs = 0, usedFat = 0, usedCalories = 0;
        
        meal.items.forEach(item => {
          if (!item.skip) {
            const mult = item.grams ? item.grams / 100 : (item.multiplier || 1);
            usedProtein += (item.protein || 0) * mult;
            usedCarbs += (item.carbs || 0) * mult;
            usedFat += ((item.fat || item.fats) || 0) * mult;
            usedCalories += (item.calories || 0) * mult;
          }
        });
        
        remainingMacros[idx] = {
          protein: (meal.target_protein || 0) - usedProtein,
          carbs: (meal.target_carbs || 0) - usedCarbs,
          fat: (meal.target_fat || 0) - usedFat,
          calories: (meal.target_calories || 0) - usedCalories,
          usedProtein: usedProtein,
          usedCarbs: usedCarbs,
          usedFat: usedFat,
          usedCalories: usedCalories
        };
      });
      setMealMacrosRemaining(remainingMacros);
      
      setShowAutoDietGenerator(false);
      toast.success(`Dieta generada con ${numMeals} comidas. Usa 🔄 para regenerar comidas individuales.`);
      
    } catch (error) {
      console.error('Error generating auto diet:', error);
      toast.error('Error al generar la dieta automáticamente');
    } finally {
      setAutoDietGenerating(false);
    }
  };

  // Función para generar una sola comida (reutilizable para regenerar)
  const generateSingleMeal = (mealIndex, totalMeals) => {
    const numMeals = totalMeals || parseInt(clientData.num_meals) || 5;
    
    // Usar base de datos filtrada clínicamente
    const filteredDB = getFilteredFoodDatabase();
    
    // Usar macros del plan, o los macros de la comida actual, o valores por defecto
    const currentMeal = dietData.meals[mealIndex];
    let proteinPerMeal = Math.round((dietData.target_protein || 0) / numMeals);
    let carbsPerMeal = Math.round((dietData.target_carbs || 0) / numMeals);
    let fatPerMeal = Math.round((dietData.target_fat || 0) / numMeals);
    let caloriesPerMeal = Math.round((dietData.target_calories || 0) / numMeals);
    
    // Si los valores calculados son 0 o muy bajos, usar los targets de la comida actual
    if (proteinPerMeal < 10 && currentMeal?.target_protein > 0) {
      proteinPerMeal = currentMeal.target_protein;
    }
    if (carbsPerMeal < 10 && currentMeal?.target_carbs > 0) {
      carbsPerMeal = currentMeal.target_carbs;
    }
    if (fatPerMeal < 5 && currentMeal?.target_fat > 0) {
      fatPerMeal = currentMeal.target_fat;
    }
    if (caloriesPerMeal < 100 && currentMeal?.target_calories > 0) {
      caloriesPerMeal = currentMeal.target_calories;
    }
    
    // Si aún son 0, usar valores por defecto razonables
    if (proteinPerMeal < 10) proteinPerMeal = 30;
    if (carbsPerMeal < 10) carbsPerMeal = 40;
    if (fatPerMeal < 5) fatPerMeal = 15;
    if (caloriesPerMeal < 100) caloriesPerMeal = 400;
    
    const mealNames = {
      3: ['Desayuno', 'Almuerzo', 'Cena'],
      4: ['Desayuno', 'Almuerzo', 'Merienda', 'Cena'],
      5: ['Desayuno', 'Snack AM', 'Almuerzo', 'Merienda', 'Cena'],
      6: ['Desayuno', 'Snack AM', 'Almuerzo', 'Merienda', 'Cena', 'Pre-sueño']
    };
    
    const mealTimes = {
      3: ['07:00', '13:00', '20:00'],
      4: ['07:00', '13:00', '17:00', '20:00'],
      5: ['07:00', '10:00', '13:00', '17:00', '20:00'],
      6: ['07:00', '10:00', '13:00', '17:00', '20:00', '22:00']
    };
    
    const names = mealNames[numMeals] || mealNames[5];
    const times = mealTimes[numMeals] || mealTimes[5];
    
    // Ajustar macros según tipo de comida
    let mealProtein = proteinPerMeal;
    let mealCarbs = carbsPerMeal;
    let mealFat = fatPerMeal;
    
    // Desayuno: más carbos
    if (mealIndex === 0) {
      mealCarbs = Math.round(mealCarbs * 1.2);
      mealFat = Math.round(mealFat * 0.8);
    }
    // Cena: menos carbos, más proteína
    if (mealIndex === numMeals - 1) {
      mealCarbs = Math.round(mealCarbs * 0.7);
      mealProtein = Math.round(mealProtein * 1.2);
    }
    
    const items = [];
    
    // Función para seleccionar alimento aleatorio de toda la lista
    const getRandomFood = (foods) => {
      if (!foods || foods.length === 0) return null;
      const randomIndex = Math.floor(Math.random() * foods.length);
      return foods[randomIndex];
    };
    
    // Proteína principal
    const proteinFoods = filteredDB.proteinas || [];
    if (proteinFoods.length > 0) {
      const proteinFood = getRandomFood(proteinFoods);
      if (proteinFood) {
        const multiplier = Math.round((mealProtein / (proteinFood.protein || 25)) * 10) / 10;
        items.push({
          category: 'proteinas',
          food_name: proteinFood.name,
          quantity: Math.round(multiplier * 100),
          unit: 'g',
          calories: proteinFood.calories || 0,
          protein: proteinFood.protein || 0,
          carbs: proteinFood.carbs || 0,
          fat: proteinFood.fat || 0,
          multiplier: Math.max(0.5, Math.min(3, multiplier)),
          grams: Math.round(multiplier * 100)
        });
      }
    }
    
    // Carbohidratos
    const carbFoods = filteredDB.carbohidratos || [];
    if (carbFoods.length > 0 && mealCarbs > 10) {
      const carbFood = getRandomFood(carbFoods);
      if (carbFood) {
        const multiplier = Math.round((mealCarbs / (carbFood.carbs || 30)) * 10) / 10;
        items.push({
          category: 'carbohidratos',
          food_name: carbFood.name,
          quantity: Math.round(multiplier * 100),
          unit: 'g',
          calories: carbFood.calories || 0,
          protein: carbFood.protein || 0,
          carbs: carbFood.carbs || 0,
          fat: carbFood.fat || 0,
          multiplier: Math.max(0.5, Math.min(3, multiplier)),
          grams: Math.round(multiplier * 100)
        });
      }
    }
    
    // Vegetales (desde el almuerzo en adelante)
    const vegFoods = filteredDB.vegetales || [];
    if (vegFoods.length > 0 && mealIndex >= 1) {
      const vegFood = getRandomFood(vegFoods);
      if (vegFood) {
        items.push({
          category: 'vegetales',
          food_name: vegFood.name,
          quantity: 150,
          unit: 'g',
          calories: vegFood.calories || 0,
          protein: vegFood.protein || 0,
          carbs: vegFood.carbs || 0,
          fat: vegFood.fat || 0,
          multiplier: 1.5,
          grams: 150
        });
      }
    }
    
    // Grasas
    const fatFoods = filteredDB.grasas || [];
    if (fatFoods.length > 0 && mealFat > 5) {
      const fatFood = getRandomFood(fatFoods);
      if (fatFood) {
        const multiplier = Math.round((mealFat / (fatFood.fat || 10)) * 10) / 10;
        items.push({
          category: 'grasas',
          food_name: fatFood.name,
          quantity: Math.round(multiplier * 100),
          unit: 'g',
          calories: fatFood.calories || 0,
          protein: fatFood.protein || 0,
          carbs: fatFood.carbs || 0,
          fat: fatFood.fat || 0,
          multiplier: Math.max(0.3, Math.min(2, multiplier)),
          grams: Math.round(multiplier * 100)
        });
      }
    }
    
    // Frutas (opcional, agregar aleatoriamente en algunas comidas)
    const fruitFoods = filteredDB.frutas || [];
    if (fruitFoods.length > 0 && Math.random() > 0.5 && mealIndex < numMeals - 1) {
      const fruitFood = getRandomFood(fruitFoods);
      if (fruitFood) {
        items.push({
          category: 'frutas',
          food_name: fruitFood.name,
          quantity: 100,
          unit: 'g',
          calories: fruitFood.calories || 0,
          protein: fruitFood.protein || 0,
          carbs: fruitFood.carbs || 0,
          fat: fruitFood.fat || 0,
          multiplier: 1,
          grams: 100
        });
      }
    }
    
    return {
      meal_name: names[mealIndex] || `Comida ${mealIndex + 1}`,
      meal_time: times[mealIndex] || '',
      items: items,
      supplements: [],
      target_protein: mealProtein,
      target_carbs: mealCarbs,
      target_fat: mealFat,
      target_calories: caloriesPerMeal
    };
  };

  // Regenerar una comida específica (o solo la opción activa si tiene múltiples opciones)
  const handleRegenerateMeal = (mealIndex) => {
    // Verificar que tenemos la base de datos de alimentos
    if (!foodDatabase || Object.keys(foodDatabase).length === 0) {
      toast.error('Base de datos de alimentos no cargada. Recargando...');
      fetchFoodDatabase();
      return;
    }
    
    // Verificar que hay alimentos en las categorías principales
    if ((!foodDatabase.proteinas || foodDatabase.proteinas.length === 0) && 
        (!foodDatabase.carbohidratos || foodDatabase.carbohidratos.length === 0)) {
      toast.error('No hay alimentos disponibles para generar la comida');
      return;
    }
    
    const currentMeal = dietData.meals[mealIndex];
    const numOptions = currentMeal.num_options || 1;
    const activeOption = currentMeal.active_option || 0;
    
    let newMeal;
    
    // Usar generación inteligente para Plan Básico
    if (clientData.plan_type === 'basic' && calculatedMacros) {
      const postWM = dietData.post_workout_meal || 'none';
      const fruitMeals = [0];
      if (postWM !== 'none') {
        const postIdx = parseInt(postWM) - 1;
        if (postIdx > 0) fruitMeals.push(postIdx);
      }
      
      newMeal = generateSmartMeal({
        mealIndex,
        totalMeals: dietData.meals.length,
        dailyMacros: calculatedMacros,
        foodDatabase: getFilteredFoodDatabase() || foodDatabase,
        postWorkoutMeal: postWM,
        fruitMeals
      });
    } else {
      newMeal = generateSingleMeal(mealIndex, dietData.meals.length);
    }
    
    // Verificar que se generaron items
    if (!newMeal.items || newMeal.items.length === 0) {
      toast.warning('No se pudieron generar alimentos. Verifica los macros de la comida.');
      return;
    }
    
    // Mantener datos originales
    newMeal.meal_name = currentMeal.meal_name;
    newMeal.meal_time = currentMeal.meal_time;
    newMeal.target_protein = currentMeal.target_protein || newMeal.target_protein;
    newMeal.target_carbs = currentMeal.target_carbs || newMeal.target_carbs;
    newMeal.target_fat = currentMeal.target_fat || newMeal.target_fat;
    newMeal.target_calories = currentMeal.target_calories || newMeal.target_calories;
    
    // Preservar configuración de opciones
    newMeal.num_options = numOptions;
    newMeal.active_option = activeOption;
    
    if (numOptions > 1) {
      // Si tiene múltiples opciones, solo reemplazar la opción activa
      newMeal.options = [...(currentMeal.options || [])];
      if (!newMeal.options[activeOption]) {
        newMeal.options[activeOption] = { items: [] };
      }
      newMeal.options[activeOption].items = [...newMeal.items];
    }
    
    // Crear el array actualizado de comidas
    const updatedMeals = dietData.meals.map((meal, idx) => idx === mealIndex ? newMeal : meal);
    
    setDietData(prev => ({
      ...prev,
      meals: updatedMeals
    }));
    
    // Recalcular macros de la comida regenerada
    recalculateMealRemaining(mealIndex, updatedMeals);
    
    const optionText = numOptions > 1 ? ` (Opción ${activeOption + 1})` : '';
    toast.success(`Comida "${newMeal.meal_name}"${optionText} regenerada con nuevos alimentos`);
  };

  const handleDownloadDietPDF = async () => {
    if (!clientData.name) {
      toast.error('Por favor ingresa el nombre del cliente');
      return;
    }
    if (!dietData.diet_name) {
      toast.error('Por favor ingresa un nombre para la dieta');
      return;
    }

    setGenerating(true);
    try {
      // Preparar meals con todas las opciones guardadas
      const preparedMeals = dietData.meals.map((meal, mealIdx) => {
        const numOptions = meal.num_options || 1;
        
        if (numOptions > 1) {
          // Si tiene múltiples opciones, asegurarse de guardar la opción activa
          const activeOption = meal.active_option || 0;
          let options = [...(meal.options || [])];
          
          // Guardar items actuales en la opción activa
          if (!options[activeOption]) {
            options[activeOption] = { items: [] };
          }
          options[activeOption].items = [...meal.items];
          
          return {
            ...meal,
            options: options
          };
        }
        return meal;
      });

      // Filtrar páginas personalizadas vacías antes de enviar
      const filteredCustomPages = (dietData.custom_pages || []).filter(
        page => (page.title && page.title.trim()) || (page.content && page.content.trim())
      );

      const payload = {
        client: {
          ...clientData,
          age: parseInt(clientData.age) || 25,
          weight: parseFloat(clientData.weight) || 60,
          height: parseFloat(clientData.height) || 165,
          // 2026-02 · Normalizar overrides manuales: convertir '' → null y
          // string → float para evitar errores de validación en Pydantic.
          body_fat_pct: clientData.body_fat_pct !== '' && clientData.body_fat_pct != null
            ? parseFloat(clientData.body_fat_pct) : null,
          body_fat_pct_target: clientData.body_fat_pct_target !== '' && clientData.body_fat_pct_target != null
            ? parseFloat(clientData.body_fat_pct_target) : null,
          manual_weekly_fat_loss_kg: clientData.manual_weekly_fat_loss_kg !== '' && clientData.manual_weekly_fat_loss_kg != null
            ? parseFloat(clientData.manual_weekly_fat_loss_kg) : null,
          manual_weeks_to_target: clientData.manual_weeks_to_target !== '' && clientData.manual_weeks_to_target != null
            ? parseFloat(clientData.manual_weeks_to_target) : null
        },
        ...dietData,
        custom_pages: filteredCustomPages,
        meals: preparedMeals,
        show_branding: showBranding,  // Controla si mostrar logo/nombre DeisyCFit
        clinical_data: clinicalResult ? {
          dominant_condition: clinicalResult.dominant_condition,
          conditions_applied: clinicalResult.conditions_applied,
          avoid_foods_display: clinicalResult.avoid_foods_display,
          manual_notes: clinicalNotes,
          admin_blocked_foods: adminBlockedFoodsText || '',
          active_notes: clinicalResult.active_notes || [],
          active_actions: clinicalResult.active_actions || [],
          substitution_map: clinicalResult.substitution_map || {},
          user_summary: clinicalResult.user_summary || null,
          recommended_supplements: clinicalResult.recommended_supplements || [],
          avoid_supplements: clinicalResult.avoid_supplements || []
        } : null,
        // 2026-02 · admin_blocked_foods en la raíz del payload — independiente
        // del ternario de clinicalResult para no perderlo cuando no hay patologías.
        admin_blocked_foods: adminBlockedFoodsText || ''
      };

      const response = await axios.post(
        `${API}/admin/offline-generator/generate-diet-pdf`,
        payload,
        { withCredentials: false, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dieta_${clientData.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('¡PDF de dieta descargado!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Intentar extraer mensaje de error del blob si existe
      if (error.response?.data) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          // Manejar diferentes formatos de error
          let errorMessage = 'Error al generar el PDF';
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Errores de validación Pydantic vienen como array
            errorMessage = errorData.detail.map(err => {
              if (typeof err === 'string') return err;
              if (err.msg) return `${err.loc?.join(' → ') || ''}: ${err.msg}`;
              return JSON.stringify(err);
            }).join(', ');
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          toast.error(`Error: ${errorMessage}`);
        } catch {
          toast.error('Error al generar el PDF. Verifica que todos los campos estén completos.');
        }
      } else if (error.message) {
        toast.error(`Error: ${error.message}`);
      } else {
        toast.error('Error al generar el PDF. Intenta de nuevo.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadWorkoutPDF = async () => {
    if (!clientData.name) {
      toast.error('Por favor ingresa el nombre del cliente');
      return;
    }
    if (!workoutData.workout_name) {
      toast.error('Por favor ingresa un nombre para la rutina');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        client: {
          name: clientData.name,
          age: parseInt(clientData.age) || 25,
          weight: parseFloat(clientData.weight) || 60,
          height: parseFloat(clientData.height) || 165,
          goal: clientData.goal || 'ganar_musculo',
          activity_level: clientData.activity_level || 'moderado',
          plan_type: clientData.plan_type || 'basic'
        },
        workout_name: workoutData.workout_name,
        level: workoutData.level || 'intermedio',
        days: workoutData.days || [],
        notes: workoutData.notes || '',
        has_cardio: workoutData.has_cardio || false,
        cardio_type: workoutData.cardio_type || 'liss',
        cardio_duration: workoutData.cardio_duration || 20,
        cardio_notes: workoutData.cardio_notes || '',
        show_branding: showBranding  // Controla si mostrar logo/nombre DeisyCFit
      };

      const response = await axios.post(
        `${API}/admin/offline-generator/generate-workout-pdf`,
        payload,
        { withCredentials: false, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rutina_${clientData.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('¡PDF de rutina descargado!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (error.response?.data) {
        // Try to extract error message from blob
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          // Manejar diferentes formatos de error
          let errorMessage = 'Error al generar el PDF';
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Errores de validación Pydantic vienen como array
            errorMessage = errorData.detail.map(err => {
              if (typeof err === 'string') return err;
              if (err.msg) return `${err.loc?.join(' → ') || ''}: ${err.msg}`;
              return JSON.stringify(err);
            }).join(', ');
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
          toast.error(`Error: ${errorMessage}`);
        } catch {
          toast.error('Error al generar el PDF');
        }
      } else {
        toast.error('Error al generar el PDF');
      }
    } finally {
      setGenerating(false);
    }
  };

  // Estado para controlar envíos
  const [sendingDiet, setSendingDiet] = useState(false);
  const [sendingWorkout, setSendingWorkout] = useState(false);

  // Función para enviar la dieta al usuario
  const handleSendDietToUser = async () => {
    if (!clientData.email) {
      toast.error('El cliente no tiene email registrado');
      return;
    }
    
    if (!editingPlanId) {
      toast.error('Primero guarda el plan antes de enviarlo');
      return;
    }

    setSendingDiet(true);
    try {
      // Primero guardar el plan actualizado
      await handleSavePlan(true);
      
      // Luego enviar notificación al usuario
      const response = await axios.post(`${API}/personalized-plans/send-to-user`, {
        plan_id: editingPlanId,
        email: clientData.email,
        plan_type: 'diet',
        plan_name: planName || 'Plan de Dieta'
      });
      
      if (response.data.success) {
        toast.success('✅ ¡Dieta enviada al usuario! Se le notificó por email.');
      }
    } catch (error) {
      console.error('Error sending diet:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la dieta');
    } finally {
      setSendingDiet(false);
    }
  };

  // Función para enviar la rutina al usuario
  const handleSendWorkoutToUser = async () => {
    if (!clientData.email) {
      toast.error('El cliente no tiene email registrado');
      return;
    }
    
    if (!editingPlanId) {
      toast.error('Primero guarda el plan antes de enviarlo');
      return;
    }

    setSendingWorkout(true);
    try {
      // Primero guardar el plan actualizado
      await handleSavePlan(true);
      
      // Luego enviar notificación al usuario
      const response = await axios.post(`${API}/personalized-plans/send-to-user`, {
        plan_id: editingPlanId,
        email: clientData.email,
        plan_type: 'workout',
        plan_name: planName || 'Plan de Rutina'
      });
      
      if (response.data.success) {
        toast.success('✅ ¡Rutina enviada al usuario! Se le notificó por email.');
      }
    } catch (error) {
      console.error('Error sending workout:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar la rutina');
    } finally {
      setSendingWorkout(false);
    }
  };

  const selectedPlan = availablePlanTypes.find(p => p.id === clientData.plan_type) || availablePlanTypes[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold text-white">Generador Offline</h2>
          <p className="text-gray-400">Crea dietas y rutinas para personas NO registradas</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={() => setShowDefaultPagesConfig(!showDefaultPagesConfig)}
            className="flex items-center gap-2 border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <FaCog /> Páginas por Defecto ({defaultPages.length})
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowSavedPlans(!showSavedPlans)}
            className="flex items-center gap-2 border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            <FaFolder /> Planes Guardados ({savedPlans.length})
          </Button>
          {onClose && (
            <Button variant="outline" onClick={onClose} className="border-gray-600 text-gray-300 hover:bg-gray-800">Cerrar</Button>
          )}
        </div>
      </div>

      {/* Panel de configuración de páginas por defecto */}
      {showDefaultPagesConfig && (
        <Card className="border-2 border-yellow-500/50 bg-gray-900">
          <CardHeader className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30">
            <CardTitle className="flex items-center gap-2 text-yellow-400">
              <FaCog className="text-yellow-500" /> Configurar Páginas por Defecto
            </CardTitle>
            <CardDescription className="text-gray-400">
              Crea páginas que puedes reutilizar en cualquier plan. Usa el botón <span className="text-green-400 font-bold">"➕ Añadir"</span> para agregarlas individualmente al plan actual.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            {/* Lista de páginas por defecto existentes - EDITABLE */}
            {defaultPages.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-300">📄 Páginas guardadas:</h4>
                  <span className="text-xs text-gray-500">Haz clic en "➕ Añadir" para agregar una página al plan actual</span>
                </div>
                {defaultPages.map((page) => (
                  <div key={page.id} className={`border rounded-lg transition-all ${editingDefaultPage?.id === page.id ? 'border-yellow-500 bg-yellow-900/30' : 'border-gray-700 bg-gray-800/50'}`}>
                    {/* Header de la página */}
                    <div 
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-700/50"
                      onClick={() => setEditingDefaultPage(editingDefaultPage?.id === page.id ? null : { ...page })}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{editingDefaultPage?.id === page.id ? '📝' : '📄'}</span>
                        <span className="font-medium text-white">{page.title}</span>
                        <span className="text-sm text-gray-500">
                          ({PAGE_POSITIONS.find(p => p.id === page.page_position)?.name || 'Página ' + page.page_position})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Botón Añadir al Plan Actual */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-400 border-green-500/50 hover:bg-green-500/20 hover:text-green-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Añadir esta página individual al plan actual
                            const newPage = {
                              id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
                              title: page.title,
                              content: page.content,
                              page_position: page.page_position,
                              order: dietData.custom_pages?.length || 0
                            };
                            setDietData(prev => ({
                              ...prev,
                              custom_pages: [...(prev.custom_pages || []), newPage]
                            }));
                            toast.success(`✅ Página "${page.title}" añadida al plan`);
                          }}
                        >
                          ➕ Añadir
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-blue-400 hover:text-blue-300"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingDefaultPage(editingDefaultPage?.id === page.id ? null : { ...page });
                          }}
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-400 hover:text-red-300" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteDefaultPage(page.id);
                          }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Editor expandido cuando está seleccionada */}
                    {editingDefaultPage?.id === page.id && (
                      <div className="border-t border-gray-700 p-4 space-y-3 bg-gray-800/50">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Título</label>
                            <Input
                              value={editingDefaultPage.title}
                              onChange={(e) => setEditingDefaultPage({ ...editingDefaultPage, title: e.target.value })}
                              className="bg-gray-900 border-gray-600 text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Posición</label>
                            <Select 
                              value={editingDefaultPage.page_position.toString()} 
                              onValueChange={(v) => setEditingDefaultPage({ ...editingDefaultPage, page_position: parseInt(v) })}
                            >
                              <SelectTrigger className="bg-gray-900 border-gray-600 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PAGE_POSITIONS.map(pos => (
                                  <SelectItem key={pos.id} value={pos.id.toString()}>{pos.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Contenido</label>
                          <RichTextToolbar
                            pageId={`default-page-edit-${page.id}`}
                            content={editingDefaultPage.content || ''}
                            onUpdate={(newContent) => setEditingDefaultPage({ ...editingDefaultPage, content: newContent })}
                          />
                          <Textarea
                            id={`page-content-default-page-edit-${page.id}`}
                            value={editingDefaultPage.content || ''}
                            onChange={(e) => setEditingDefaultPage({ ...editingDefaultPage, content: e.target.value })}
                            rows={6}
                            className="rounded-t-none"
                            placeholder="Contenido de la página..."
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => handleUpdateDefaultPage(editingDefaultPage)}
                            className="bg-yellow-600 hover:bg-yellow-700"
                          >
                            <FaSave className="mr-2" /> Guardar Cambios
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setEditingDefaultPage(null)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Formulario para agregar nueva página por defecto */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-300 mb-3">➕ Agregar nueva página por defecto:</h4>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Título de la página"
                  value={newDefaultPage.title}
                  onChange={(e) => setNewDefaultPage({ ...newDefaultPage, title: e.target.value })}
                />
                <Select 
                  value={newDefaultPage.page_position.toString()} 
                  onValueChange={(v) => setNewDefaultPage({ ...newDefaultPage, page_position: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Posición" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_POSITIONS.map(pos => (
                      <SelectItem key={pos.id} value={pos.id.toString()}>{pos.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Barra de herramientas de formato para páginas por defecto */}
              <RichTextToolbar
                pageId="default-page-new"
                content={newDefaultPage.content}
                onUpdate={(newContent) => setNewDefaultPage({ ...newDefaultPage, content: newContent })}
              />
              <Textarea
                id="page-content-default-page-new"
                placeholder="Contenido de la página... Usa **texto** para negrita, *texto* para cursiva"
                value={newDefaultPage.content}
                onChange={(e) => setNewDefaultPage({ ...newDefaultPage, content: e.target.value })}
                rows={5}
                className="rounded-t-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Formato: **negrita**, *cursiva*, __subrayado__ | Los emojis se convertirán en símbolos con color en el PDF
              </p>
              <Button onClick={handleSaveDefaultPage} className="mt-2 bg-yellow-600 hover:bg-yellow-700">
                <FaSave className="mr-2" /> Guardar Página por Defecto
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Panel de planes guardados */}
      {showSavedPlans && (
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <CardTitle className="flex items-center gap-2">
              <FaFolder className="text-blue-500" /> Planes Guardados ({savedPlans.length})
            </CardTitle>
            <CardDescription>Selecciona un plan para editarlo o crear uno nuevo</CardDescription>
          </CardHeader>
          <CardContent className="p-4">
            {/* Barra de búsqueda */}
            <div className="mb-4">
              <Input
                type="text"
                placeholder="🔍 Buscar por nombre del cliente o plan..."
                value={planSearchTerm}
                onChange={(e) => setPlanSearchTerm(e.target.value)}
                className="w-full bg-gray-800 border-gray-600 text-white placeholder-gray-400"
              />
              {planSearchTerm && (
                <p className="text-xs text-gray-400 mt-1">
                  Mostrando {savedPlans.filter(plan => {
                    const searchLower = planSearchTerm.toLowerCase();
                    const clientName = (plan.client?.name || plan.name || '').toLowerCase();
                    const planNameStr = (plan.plan_name || plan.diet_name || '').toLowerCase();
                    const email = (plan.email || plan.client?.email || '').toLowerCase();
                    return clientName.includes(searchLower) || planNameStr.includes(searchLower) || email.includes(searchLower);
                  }).length} de {savedPlans.length} planes
                </p>
              )}
            </div>
            
            {loadingSavedPlans ? (
              <p className="text-center py-4 text-gray-500">Cargando planes...</p>
            ) : savedPlans.length === 0 ? (
              <p className="text-center py-4 text-gray-500">No hay planes guardados</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {/* Ordenar: planes personalizados primero (los nuevos arriba) */}
                {[...savedPlans]
                  .filter(plan => {
                    if (!planSearchTerm) return true;
                    const searchLower = planSearchTerm.toLowerCase();
                    const clientName = (plan.client?.name || plan.name || '').toLowerCase();
                    const planNameStr = (plan.plan_name || plan.diet_name || '').toLowerCase();
                    const email = (plan.email || plan.client?.email || '').toLowerCase();
                    return clientName.includes(searchLower) || planNameStr.includes(searchLower) || email.includes(searchLower);
                  })
                  .sort((a, b) => {
                    // Planes personalizados primero
                    if (a.is_personalized_plan && !b.is_personalized_plan) return -1;
                    if (!a.is_personalized_plan && b.is_personalized_plan) return 1;
                    // Más recientes primero
                    return new Date(b.created_at) - new Date(a.created_at);
                  })
                  .map((plan) => (
                  <div 
                    key={plan.id}
                    className={`p-3 border rounded-lg hover:bg-gray-800/50 cursor-pointer transition-all ${
                      plan.is_personalized_plan ? 'border-red-500/50 border-2 bg-red-900/20' : 'border-gray-700 bg-gray-800/30'
                    } ${editingPlanId === plan.id ? 'ring-2 ring-cyan-500 bg-cyan-900/20' : ''}`}
                  >
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
                      <h4 className="font-semibold text-white">{plan.plan_name || plan.diet_name}</h4>
                      <div className="flex gap-1 flex-wrap">
                        {plan.is_personalized_plan && (
                          <Badge className="bg-red-500 text-white animate-pulse">NUEVO</Badge>
                        )}
                        <Badge variant="outline" className="capitalize border-gray-600 text-gray-300">{plan.client?.plan_type || plan.plan_type || 'basic'}</Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-400 mb-1">
                      Cliente: {plan.client?.name || plan.name || 'Sin nombre'}
                    </p>
                    
                    {/* Email del cliente si es plan personalizado */}
                    {plan.email && (
                      <p className="text-xs text-cyan-400 mb-1">
                        📧 {plan.email}
                      </p>
                    )}
                    
                    {/* Info adicional del formulario personalizado */}
                    {plan.additional_info && (
                      <div className="mt-2 p-2 bg-yellow-900/20 rounded text-xs space-y-1 border border-yellow-500/30">
                        <p className="font-semibold text-yellow-400">📋 Datos del formulario:</p>
                        <p className="text-gray-300">🕐 Horario: {plan.additional_info.wake_up_time} - {plan.additional_info.sleep_time}</p>
                        <p className="text-gray-300">💼 Trabajo: {plan.additional_info.work_type === 'sedentario' ? 'Sedentario' : 'Activo'}</p>
                        {plan.additional_info.food_allergies?.length > 0 && (
                          <p className="text-gray-300">🚫 Alergias: {plan.additional_info.food_allergies.join(', ')}</p>
                        )}
                        {plan.additional_info.disliked_foods?.length > 0 && (
                          <p className="text-gray-300">👎 No le gusta: {plan.additional_info.disliked_foods.join(', ')}</p>
                        )}
                        {plan.additional_info.pathologies?.length > 0 && plan.additional_info.pathologies[0] !== 'ninguna' && (
                          <p className="text-gray-300">💊 Patologías: {plan.additional_info.pathologies.join(', ')}</p>
                        )}
                        {plan.additional_info.eating_disorder_option === 'videollamada' && (
                          <p className="text-purple-400 font-semibold">📞 Solicita videollamada</p>
                        )}
                        {/* PREGUNTAS PERSONALIZADAS / SALUD HORMONAL */}
                        {plan.additional_info.custom_fields && Object.keys(plan.additional_info.custom_fields).length > 0 && (
                          <div className="mt-2 pt-2 border-t border-pink-500/30">
                            <p className="text-pink-400 font-semibold">🩺 Salud Hormonal ({Object.keys(plan.additional_info.custom_fields).length} respuestas)</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-400 mt-2 mb-3 space-y-1">
                      {/* Fechas de Dieta */}
                      {(plan.diet_total_update_date || plan.diet_progression_date) && (
                        <div className="flex flex-wrap gap-x-4">
                          {plan.diet_total_update_date && (
                            <span title="Fecha de cambio total de dieta">
                              <span className="text-orange-400 font-semibold">Dieta cambio total:</span> {new Date(plan.diet_total_update_date).toLocaleDateString('es-CO')}
                            </span>
                          )}
                          {plan.diet_progression_date && (
                            <span title="Fecha de progresión de macros/calorías">
                              <span className="text-cyan-400 font-semibold">Progresion macros:</span> {new Date(plan.diet_progression_date).toLocaleDateString('es-CO')}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Fechas de Rutina */}
                      {(plan.workout_total_update_date || plan.workout_progression_date) && (
                        <div className="flex flex-wrap gap-x-4">
                          {plan.workout_total_update_date && (
                            <span title="Fecha de cambio total de rutina">
                              <span className="text-purple-400 font-semibold">Rutina cambio total:</span> {new Date(plan.workout_total_update_date).toLocaleDateString('es-CO')}
                            </span>
                          )}
                          {plan.workout_progression_date && (
                            <span title="Fecha de progresión de rutina">
                              <span className="text-green-400 font-semibold">Progresion rutina:</span> {new Date(plan.workout_progression_date).toLocaleDateString('es-CO')}
                            </span>
                          )}
                        </div>
                      )}
                      {!plan.diet_total_update_date && !plan.workout_total_update_date && (
                        <p>Actualizado: {new Date(plan.updated_at || plan.created_at).toLocaleDateString('es-CO')}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleLoadPlan(plan)}>
                        <FaEdit className="mr-1" /> Cargar
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeletePlan(plan.id)}>
                        <FaTrash />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sección para guardar plan */}
      <Card className="border-2 border-green-500/30" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
        <CardHeader className="py-3" style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.2))' }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <FaSave className="text-green-400" /> Guardar Plan
            </CardTitle>
            <div className="flex gap-2">
              {editingPlanId && (
                <Badge className="bg-blue-500">Editando: {planName}</Badge>
              )}
              {defaultPages.length > 0 && !editingPlanId && (
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={loadDefaultPagesIntoCurrentPlan}
                  className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                >
                  Cargar TODAS las Paginas por Defecto
                </Button>
              )}
            </div>
          </div>
          {/* Fechas de última actualización */}
          {editingPlanId && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs mt-2">
              <span className="text-gray-400">
                <span className="text-orange-400 font-semibold">Dieta cambio total:</span>{' '}
                {planDates.diet_total_update_date ? new Date(planDates.diet_total_update_date).toLocaleDateString('es-CO') : 'Sin registro'}
              </span>
              <span className="text-gray-400">
                <span className="text-cyan-400 font-semibold">Progresion macros:</span>{' '}
                {planDates.diet_progression_date ? new Date(planDates.diet_progression_date).toLocaleDateString('es-CO') : 'Sin registro'}
              </span>
              <span className="text-gray-400">
                <span className="text-purple-400 font-semibold">Rutina cambio total:</span>{' '}
                {planDates.workout_total_update_date ? new Date(planDates.workout_total_update_date).toLocaleDateString('es-CO') : 'Sin registro'}
              </span>
              <span className="text-gray-400">
                <span className="text-green-400 font-semibold">Progresion rutina:</span>{' '}
                {planDates.workout_progression_date ? new Date(planDates.workout_progression_date).toLocaleDateString('es-CO') : 'Sin registro'}
              </span>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap">
            <Input
              placeholder="Nombre del plan (ej: Maria - Mes 1)"
              value={planName}
              onChange={(e) => setPlanName(e.target.value)}
              className="flex-1 min-w-[200px] bg-gray-800 border-gray-600 text-white"
            />
            {!editingPlanId ? (
              <Button onClick={() => handleSavePlan(false, 'full')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-plan-btn">
                <FaSave className="mr-2" /> Guardar
              </Button>
            ) : (
              <Button onClick={() => handleSavePlan(true, 'full')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="update-plan-btn">
                <FaSave className="mr-2" /> Actualizar
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={handlePreviewPDF}
              className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
              title="Ver cómo quedará el PDF antes de enviarlo"
            >
              <FaFilePdf className="mr-2" /> Vista Previa PDF
            </Button>
            <Button variant="outline" onClick={handleNewPlan} className="border-gray-500 text-gray-300 hover:bg-gray-700">
              Nuevo Plan
            </Button>
            {editingPlanId && (
              <Button
                variant="outline"
                onClick={() => { setShowHistoryModal(true); fetchPlanHistory(); }}
                className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                title="Historial de versiones del plan"
                data-testid="open-history-btn"
              >
                <FaHistory className="mr-2" /> Historial
              </Button>
            )}
          </div>
          {/* Botones de guardado individual por tipo de actualización */}
          {editingPlanId && (
            <div className="flex gap-2 flex-wrap mt-3 pt-3 border-t border-gray-700">
              <span className="text-xs text-gray-500 self-center mr-1">Guardado individual:</span>
              <Button
                size="sm"
                onClick={() => handleSavePlan(true, 'diet_total')}
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
                data-testid="save-diet-total-btn"
              >
                <FaUtensils className="mr-1" /> Dieta - Cambio Total
              </Button>
              <Button
                size="sm"
                onClick={() => handleSavePlan(true, 'diet_progression')}
                className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
                data-testid="save-diet-progression-btn"
              >
                <FaSync className="mr-1" /> Dieta - Progresión
              </Button>
              <Button
                size="sm"
                onClick={() => handleSavePlan(true, 'workout_total')}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                data-testid="save-workout-total-btn"
              >
                <FaDumbbell className="mr-1" /> Rutina - Cambio Total
              </Button>
              <Button
                size="sm"
                onClick={() => handleSavePlan(true, 'workout_progression')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                data-testid="save-workout-progression-btn"
              >
                <FaArrowUp className="mr-1" /> Rutina - Progresión
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Datos del Cliente */}
      <Card style={{ backgroundColor: '#1a1a1a', border: '1px solid rgba(168, 85, 247, 0.3)' }}>
        <CardHeader style={{ background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(var(--brand-primary-glow), 0.2))' }}>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2 text-white">
                <FaUser className="text-purple-400" /> Datos del Cliente
              </CardTitle>
              <CardDescription className="text-gray-400">Información básica para crear el plan personalizado</CardDescription>
            </div>
            {/* Botón Formularios Clínicos */}
            {clientData.email && (
              <div className="flex gap-2 flex-wrap">
                {/* Botón para ASIGNAR PATOLOGÍAS MANUALMENTE */}
                <Button
                  size="sm"
                  onClick={() => setShowManualPathologyModal(true)}
                  className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white"
                  title="Asignar patologías manualmente al usuario"
                >
                  <FaPlus className="mr-1" /> Asignar Patologías
                </Button>
                
                {/* Botón para procesar patologías/alergias - visible si hay condiciones */}
                {(
                  (clientData.additional_info?.pathologies?.length > 0 && 
                   clientData.additional_info.pathologies.some(p => p && p.toLowerCase() !== 'ninguna' && p.toLowerCase() !== 'ninguna patología')) ||
                  (clientData.additional_info?.food_allergies?.length > 0) ||
                  (clientData.additional_info?.intolerances?.length > 0)
                ) && (
                  <Button
                    size="sm"
                    onClick={processUserPathologies}
                    disabled={processingPathologies}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
                    title="Procesar condiciones clínicas y activar módulo clínico"
                    data-testid="process-pathologies-btn"
                  >
                    {processingPathologies ? <FaSpinner className="animate-spin mr-1" /> : <FaBrain className="mr-1" />}
                    {clinicalResult 
                      ? 'Re-procesar Módulo Clínico' 
                      : 'Procesar Módulo Clínico'}
                  </Button>
                )}
                
                {/* Botón para enviar correo - SIEMPRE disponible */}
                <div className="relative group">
                  <Button
                    size="sm"
                    onClick={() => {
                      // Si hay un caso draft, abrir ese modal
                      if (Array.isArray(userPathologyCases) && userPathologyCases.length > 0) {
                        const draftCase = userPathologyCases.find(c => c.status === 'draft');
                        if (draftCase) {
                          openDiagnosisModal(draftCase);
                          return;
                        }
                      }
                      // Si no hay draft, abrir modal genérico para enviar email
                      openDiagnosisModal({
                        user_email: clientData.email,
                        user_name: clientData.name,
                        pathologies: clientData.additional_info?.pathologies || [],
                        status: 'new',
                        draft_subject: `Información importante - ${clientData.name || 'Cliente'}`,
                        draft_body: `Hola ${clientData.name || ''},\n\n[Escribe tu mensaje aquí]\n\nSaludos,\nDeisyCFit`
                      });
                    }}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                  >
                    <FaEnvelope className="mr-1" /> Enviar Email
                    {Array.isArray(userPathologyCases) && userPathologyCases.length > 0 && (
                      <Badge className="ml-2 bg-red-500 text-white text-xs">
                        {userPathologyCases.length}
                      </Badge>
                    )}
                  </Button>
                  
                  {/* Dropdown con lista de casos de diagnóstico */}
                  {Array.isArray(userPathologyCases) && userPathologyCases.length > 0 && (
                    <div className="absolute right-0 top-full mt-1 w-96 bg-gray-800 border border-emerald-500/50 rounded-lg shadow-xl z-50 p-3 hidden group-hover:block">
                      <p className="text-xs text-gray-400 mb-2 font-semibold">📋 Casos de Diagnóstico ({userPathologyCases.length}):</p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {userPathologyCases.map((caseItem, idx) => (
                          <div 
                            key={caseItem.id || idx}
                            className="flex items-center justify-between p-2 bg-gray-700/50 rounded-lg hover:bg-gray-700 group/item"
                          >
                            <div 
                              className="flex-1 cursor-pointer pr-2"
                              onClick={() => openDiagnosisModal(caseItem)}
                            >
                              <p className="text-white text-sm font-medium truncate max-w-[250px]">
                                {caseItem.draft_subject || 'Sin asunto'}
                              </p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge className={`text-xs ${
                                  caseItem.status === 'draft' ? 'bg-yellow-500/30 text-yellow-300' :
                                  caseItem.status === 'sent' ? 'bg-green-500/30 text-green-300' :
                                  'bg-gray-500/30 text-gray-300'
                                }`}>
                                  {caseItem.status === 'draft' ? '📝 Borrador' : 
                                   caseItem.status === 'sent' ? '✅ Enviado' : caseItem.status}
                                </Badge>
                                {caseItem.severity_global && (
                                  <Badge className={`text-xs ${
                                    caseItem.severity_global === 'RED' ? 'bg-red-500/30 text-red-300' :
                                    caseItem.severity_global === 'YELLOW' ? 'bg-yellow-500/30 text-yellow-300' :
                                    'bg-green-500/30 text-green-300'
                                  }`}>
                                    {caseItem.severity_global}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deletePathologyCase(caseItem.id);
                              }}
                              className="flex-shrink-0 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/50 rounded-lg transition-colors"
                              title="Eliminar este caso"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="relative">
                  <Button
                    size="sm"
                    onClick={() => setShowClinicalFormModal(!showClinicalFormModal)}
                    className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white"
                  >
                    <FaClipboardList className="mr-1" /> Enviar Formulario Clínico
                  </Button>
                  {/* Modal de formularios clínicos */}
                  {showClinicalFormModal && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 p-4">
                      <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                        <FaClipboardList className="text-red-400" /> Formularios Clínicos
                      </h4>
                      <p className="text-xs text-gray-400 mb-3">
                        Enviar a: <span className="text-pink-400">{clientData.name || clientData.email}</span>
                      </p>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {clinicalForms.map(form => (
                          <div 
                            key={form.id} 
                            className="p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-white text-sm">{form.name}</p>
                                <p className="text-xs text-gray-400 mt-1">{form.description}</p>
                                <Badge className="mt-2 text-xs" variant="outline">
                                  {form.category === 'renal' ? '🫘 Renal' : form.category}
                                </Badge>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => sendClinicalFormToUser(form.id)}
                                disabled={sendingClinicalForm}
                                className="ml-2 bg-pink-500 hover:bg-pink-600 text-white"
                              >
                                {sendingClinicalForm ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                              </Button>
                            </div>
                          </div>
                        ))}
                        {clinicalForms.length === 0 && (
                          <p className="text-gray-500 text-sm text-center py-4">No hay formularios clínicos disponibles</p>
                        )}
                      </div>
                      <button 
                        onClick={() => setShowClinicalFormModal(false)}
                        className="mt-3 w-full text-center text-xs text-gray-500 hover:text-gray-300"
                      >
                        Cerrar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6 flex flex-col gap-4">
          {/* Alerta de plan existente */}
          {matchingPlan && (
            <div style={{ backgroundColor: 'rgba(59, 130, 246, 0.15)', border: '2px solid rgba(59, 130, 246, 0.4)', order: 1 }} className="rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FaUser className="text-blue-400" />
                <span className="text-blue-300">
                  <strong className="text-white">Plan existente encontrado:</strong> "{matchingPlan.plan_name}"
                </span>
              </div>
              <Button size="sm" onClick={handleLoadMatchingPlan} className="bg-blue-500 hover:bg-blue-600 text-white">
                Cargar Plan
              </Button>
            </div>
          )}
          
          {/* Datos adicionales del formulario personalizado */}
          {clientData.additional_info && Object.keys(clientData.additional_info).length > 0 && (
            <div style={{ background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.15), rgba(245, 158, 11, 0.15))', border: '2px solid rgba(234, 179, 8, 0.4)', order: 40 }} className="rounded-lg p-4" data-testid="section-form-info">
              <button
                type="button"
                onClick={() => toggleSection('formInfo')}
                className="w-full font-bold text-yellow-400 mb-3 flex items-center justify-between gap-2 hover:text-yellow-300 transition-colors"
                data-testid="toggle-form-info"
              >
                <span className="flex items-center gap-2">
                  📋 Información del Formulario
                  {clientData.email && (
                    <span className="text-sm font-normal text-blue-400">({clientData.email})</span>
                  )}
                </span>
                <span className="text-sm">{collapsedSections.formInfo ? '▼ Expandir' : '▲ Colapsar'}</span>
              </button>
              {!collapsedSections.formInfo && (
              <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} className="rounded p-2">
                  <p className="text-gray-400 text-xs">Horario de levantarse</p>
                  <p className="font-semibold text-white">{clientData.additional_info.wake_up_time || '-'}</p>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} className="rounded p-2">
                  <p className="text-gray-400 text-xs">Hora de acostarse</p>
                  <p className="font-semibold text-white">{clientData.additional_info.sleep_time || '-'}</p>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} className="rounded p-2">
                  <p className="text-gray-400 text-xs">Tipo de trabajo</p>
                  <p className="font-semibold text-white">{clientData.additional_info.work_type === 'sedentario' ? '💻 Sedentario' : '🏃 Activo'}</p>
                </div>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.1)' }} className="rounded p-2">
                  <p className="text-gray-400 text-xs">Conducta alimentaria</p>
                  <p className="font-semibold text-white">
                    {clientData.additional_info.eating_disorder_option === 'videollamada' 
                      ? '📞 Solicita videollamada' 
                      : '✅ Sin problemas'}
                  </p>
                </div>
              </div>
              
              {/* Alergias */}
              {clientData.additional_info.food_allergies?.length > 0 && (
                <div className="mt-3 p-2 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.4)' }}>
                  <p className="text-red-400 font-semibold text-xs mb-1">🚫 ALERGIAS ALIMENTARIAS:</p>
                  <p className="text-red-300">{clientData.additional_info.food_allergies.join(', ')}</p>
                </div>
              )}
              
              {/* Alimentos que no le gustan */}
              {clientData.additional_info.disliked_foods?.length > 0 && (
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(249, 115, 22, 0.15)', border: '1px solid rgba(249, 115, 22, 0.4)' }}>
                  <p className="text-orange-400 font-semibold text-xs mb-1">👎 ALIMENTOS QUE NO LE GUSTAN:</p>
                  <p className="text-orange-300">{clientData.additional_info.disliked_foods.join(', ')}</p>
                </div>
              )}

              {/* Formulario de actualización: alimentos a quitar */}
              {latestFollowup?.foods_to_remove && latestFollowup.foods_to_remove.trim() && (
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(244, 63, 94, 0.15)', border: '1px solid rgba(244, 63, 94, 0.4)' }} data-testid="followup-foods-to-remove">
                  <p className="text-rose-400 font-semibold text-xs mb-1">🚫 ALIMENTOS A QUITAR (Formulario de actualización):</p>
                  <p className="text-rose-300">{latestFollowup.foods_to_remove}</p>
                </div>
              )}

              {/* Formulario de actualización: alimentos a agregar */}
              {latestFollowup?.foods_to_add && latestFollowup.foods_to_add.trim() && (
                <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.4)' }} data-testid="followup-foods-to-add">
                  <p className="text-green-400 font-semibold text-xs mb-1">➕ ALIMENTOS A AGREGAR (Formulario de actualización):</p>
                  <p className="text-green-300">{latestFollowup.foods_to_add}</p>
                </div>
              )}

              {/* Panel del ciclo hormonal se movió a sección propia colapsable al final */}

              {/* Patologías - COLAPSABLE */}
              {clientData.additional_info.pathologies?.length > 0 && 
               clientData.additional_info.pathologies.some(p => p && !['ninguna', 'ninguna_patologia', 'ninguna patología'].includes(p.toLowerCase())) && (
                <div className="mt-2 rounded-lg overflow-hidden" style={{ backgroundColor: 'rgba(168, 85, 247, 0.15)', border: '1px solid rgba(168, 85, 247, 0.4)' }}>
                  <button 
                    onClick={() => toggleSection('patologias')}
                    className="w-full p-2 flex items-center justify-between hover:bg-purple-500/10 transition-colors"
                  >
                    <p className="text-purple-400 font-semibold text-xs flex items-center gap-2">
                      💊 PATOLOGÍAS A CONSIDERAR
                      <span className="bg-purple-500/30 px-2 py-0.5 rounded text-xs">
                        {clientData.additional_info.pathologies.filter(p => p && !['ninguna', 'ninguna_patologia', 'ninguna patología'].includes(p.toLowerCase())).length}
                      </span>
                    </p>
                    <span className="text-purple-400 text-sm">
                      {collapsedSections.patologias ? '▼ Expandir' : '▲ Colapsar'}
                    </span>
                  </button>
                  {!collapsedSections.patologias && (
                    <div className="px-2 pb-2">
                      <div className="flex flex-wrap gap-1">
                        {clientData.additional_info.pathologies
                          .filter(p => p && !['ninguna', 'ninguna_patologia', 'ninguna patología'].includes(p.toLowerCase()))
                          .map((p, i) => (
                          <span key={i} className="bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded text-xs capitalize">
                            {p.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* PREGUNTAS PERSONALIZADAS / SALUD HORMONAL - COLAPSABLE */}
              {clientData.additional_info.custom_fields && Object.keys(clientData.additional_info.custom_fields).length > 0 && (
                <div className="mt-3 rounded-lg overflow-hidden" style={{ backgroundColor: 'rgba(var(--brand-primary-glow), 0.15)', border: '2px solid rgba(var(--brand-primary-glow), 0.4)' }}>
                  <button 
                    onClick={() => toggleSection('hormonalQuestions')}
                    className="w-full p-3 flex items-center justify-between hover:bg-pink-500/10 transition-colors"
                  >
                    <p className="text-pink-400 font-bold text-sm flex items-center gap-2">
                      🩺 SALUD HORMONAL Y PREGUNTAS PERSONALIZADAS
                      <span className="bg-pink-500/30 px-2 py-0.5 rounded text-xs">
                        {Object.keys(clientData.additional_info.custom_fields).length} respuestas
                      </span>
                    </p>
                    <span className="text-pink-400 text-sm font-medium">
                      {collapsedSections.hormonalQuestions ? '▼ Expandir' : '▲ Colapsar'}
                    </span>
                  </button>
                  {!collapsedSections.hormonalQuestions && (
                    <div className="px-3 pb-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(clientData.additional_info.custom_fields).map(([fieldId, value], i) => {
                          // Mapeo completo de etiquetas de todos los templates
                          const staticLabels = {
                            // Salud Hormonal (hm)
                            'hm1': '¿Tu ciclo menstrual es regular?',
                            'hm2': '¿Cada cuántos días ocurre tu ciclo?',
                            'hm3': '¿Síntomas antes de tu menstruación?',
                            'hm4': '¿Usas anticonceptivo hormonal?',
                            'hm5': 'Tiempo usando anticonceptivos',
                            'hm6': 'Nivel de estrés (1-10)',
                            'hm7': '¿Duermes al menos 7 horas?',
                            'hm8': '¿Te despiertas descansada?',
                            'hm9': '¿Agotada antes de entrenar?',
                            // Campos Competitivo Rutina (cf y Cf)
                            'cf1': 'Nombre completo', 'Cf1': 'Nombre completo',
                            'cf2': 'Edad', 'Cf2': 'Edad',
                            'cf3': 'Género', 'Cf3': 'Género',
                            'cf4': 'WhatsApp', 'Cf4': 'WhatsApp',
                            'cf5': 'Peso actual (kg)', 'Cf5': 'Peso actual (kg)',
                            'cf6': 'Altura (cm)', 'Cf6': 'Altura (cm)',
                            'cf7': '% Grasa corporal actual', 'Cf7': '% Grasa corporal actual',
                            'cf8': 'Medidas: Pecho (cm)', 'Cf8': 'Medidas: Pecho (cm)',
                            'cf9': 'Medidas: Cintura (cm)', 'Cf9': 'Medidas: Cintura (cm)',
                            'cf10': 'Medidas: Cadera (cm)', 'Cf10': 'Medidas: Cadera (cm)',
                            'cf11': 'Medidas: Brazo (cm)', 'Cf11': 'Medidas: Brazo (cm)',
                            'cf12': 'Medidas: Muslo (cm)', 'Cf12': 'Medidas: Muslo (cm)',
                            'cf13': '¿Has competido antes?', 'Cf13': '¿Has competido antes?',
                            'cf14': 'Categoría de competencia', 'Cf14': 'Categoría de competencia',
                            'cf15': 'Logros previos en competencias', 'Cf15': 'Logros previos en competencias',
                            'cf16': '¿En qué fase de preparación estás?', 'Cf16': '¿En qué fase de preparación estás?',
                            'cf17': 'Peso objetivo para competir (kg)', 'Cf17': 'Peso objetivo para competir (kg)',
                            'cf18': 'Días de entrenamiento disponibles', 'Cf18': 'Días de entrenamiento disponibles',
                            'cf19': 'Describe tu rutina actual', 'Cf19': 'Describe tu rutina actual',
                            'cf20': 'Fecha de la competencia', 'Cf20': 'Fecha de la competencia',
                            'cf21': 'Nombre del evento', 'Cf21': 'Nombre del evento',
                            'cf22': 'Ubicación del evento', 'Cf22': 'Ubicación del evento',
                            'cf23': 'Notas adicionales', 'Cf23': 'Notas adicionales',
                            // Campos Competitivo Dieta (dcf y Dcf)
                            'dcf1': 'Nombre completo', 'Dcf1': 'Nombre completo',
                            'dcf2': 'Género', 'Dcf2': 'Género',
                            'dcf3': 'Edad', 'Dcf3': 'Edad',
                            'dcf4': 'Peso actual (kg)', 'Dcf4': 'Peso actual (kg)',
                            'dcf5': 'Peso de competencia deseado (kg)', 'Dcf5': 'Peso de competencia deseado (kg)',
                            'dcf6': '% Grasa actual', 'Dcf6': '% Grasa actual',
                            'dcf7': '% Grasa objetivo', 'Dcf7': '% Grasa objetivo',
                            'dcf8': 'Semanas hasta la competencia', 'Dcf8': 'Semanas hasta la competencia',
                            'dcf9': 'Describe tu dieta actual', 'Dcf9': 'Describe tu dieta actual',
                            'dcf10': '¿Has hecho prep de competencia antes?', 'Dcf10': '¿Has hecho prep de competencia antes?',
                            'dcf11': '¿Cómo respondes a los carbos?', 'Dcf11': '¿Cómo respondes a los carbos?',
                            'dcf12': 'Fase actual de prep', 'Dcf12': 'Fase actual de prep',
                            'dcf13': 'Restricciones alimentarias', 'Dcf13': 'Restricciones alimentarias',
                            'dcf14': 'Suplementos que usas actualmente', 'Dcf14': 'Suplementos que usas actualmente',
                            'dcf15': 'Alimentos que toleras mejor en prep', 'Dcf15': 'Alimentos que toleras mejor en prep',
                            'dcf16': 'Notas adicionales para el coach', 'Dcf16': 'Notas adicionales para el coach',
                            // Campos de dieta básica (dpf)
                            'dpf1': 'Género', 'dpf2': 'Edad', 'dpf3': 'Peso actual (kg)',
                            'dpf4': 'Altura (cm)', 'dpf5': '% Grasa corporal',
                            'dpf6': 'Nivel de actividad', 'dpf7': 'Comidas al día',
                            'dpf8': '¿Cuándo entrenas?', 'dpf9': 'Objetivo nutricional',
                            'dpf10': 'Restricciones alimenticias', 'dpf11': 'Alimentos favoritos',
                            'dpf12': 'Alimentos que NO te gustan', 'dpf13': 'Alergias alimentarias'
                          };
                          
                          // Priorizar: etiqueta del template > etiqueta estática > formato del ID
                          const label = fieldLabelsMap[fieldId] || staticLabels[fieldId] || fieldId
                            .replace(/_/g, ' ')
                            .replace(/^\d+/, '') // Remover números al inicio
                            .replace(/^[a-z]/, (c) => c.toUpperCase())
                            .trim() || 'Pregunta';
                          
                          // Formatear el valor
                          let displayValue = value;
                          if (Array.isArray(value)) {
                            // Mapear valores de síntomas a etiquetas legibles
                            const symptomLabels = {
                              'dolor_abdominal': 'Dolor abdominal',
                              'hinchazon': 'Hinchazón',
                              'irritabilidad': 'Irritabilidad',
                              'fatiga': 'Fatiga',
                              'antojos': 'Antojos',
                              'ninguno': 'Ninguno',
                              'retencion_de_liquidos': 'Retención de líquidos',
                              'ntojos_intensos': 'Antojos intensos',
                              'dolor_de_cabeza': 'Dolor de cabeza',
                              'ansiedad': 'Ansiedad',
                              'inflamacion_abdominal': 'Inflamación abdominal',
                              'cambios_de_humor': 'Cambios de humor',
                              'sensibilidad_mamaria': 'Sensibilidad mamaria'
                            };
                            displayValue = value.map(v => symptomLabels[v] || v.replace(/_/g, ' ')).join(', ');
                          } else if (typeof value === 'boolean') {
                            displayValue = value ? 'Sí' : 'No';
                          } else if (typeof value === 'string') {
                            // Mapear valores comunes de respuestas
                            const valueMap = {
                              'si': 'Sí', 'yes': 'Sí', 's': 'Sí', 'S': 'Sí', 'v': 'Sí', 'V': 'Sí',
                              'no': 'No', 'n': 'No', 'N': 'No',
                              'true': 'Sí', 'false': 'No',
                              'ninguno': 'Ninguno', 'ninguna': 'Ninguna',
                              'multiple': 'Múltiple', 'normal': 'Normal',
                              'wellness': 'Wellness', 'bikini': 'Bikini', 'physique': 'Physique',
                              'retomando': 'Retomando', 'novato': 'Novato', 'avanzado': 'Avanzado'
                            };
                            
                            const lowerValue = value.toLowerCase();
                            if (valueMap[value] || valueMap[lowerValue]) {
                              displayValue = valueMap[value] || valueMap[lowerValue];
                            } else {
                              // Formatear valores snake_case y capitalizar
                              displayValue = value.replace(/_/g, ' ').replace(/^[a-z]/, c => c.toUpperCase());
                            }
                          }
                          
                          return (
                            <div key={i} className="p-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
                              <p className="text-pink-300 text-xs font-medium">{label}</p>
                              <p className="text-white text-sm mt-0.5">{displayValue || '-'}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
              )}
            </div>
          )}
          
          {/* FORMULARIOS CLÍNICOS DEL USUARIO */}
          {userClinicalForms.length > 0 && (
            <div className="p-4 rounded-lg" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', order: 34 }}>
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedClinicalFormId(expandedClinicalFormId === 'clinical-forms' ? null : 'clinical-forms')}
              >
                <p className="text-red-400 font-semibold flex items-center gap-2">
                  🏥 Formularios Clínicos ({userClinicalForms.length})
                  {userClinicalForms.some(f => f.has_red_flags) && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">⚠️ ALERTAS</span>
                  )}
                </p>
                <span className="text-gray-400">{expandedClinicalFormId === 'clinical-forms' ? '▼' : '▶'}</span>
              </div>
              
              {expandedClinicalFormId === 'clinical-forms' && (
                <div className="mt-3 space-y-3">
                  {userClinicalForms.map((form) => (
                    <div 
                      key={form.id} 
                      className={`p-3 rounded-lg ${form.has_red_flags ? 'bg-red-500/20 border border-red-500/40' : 'bg-gray-800/50'}`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-white">{form.form_name}</p>
                          <p className="text-xs text-gray-400">
                            Enviado: {new Date(form.assigned_at).toLocaleDateString('es-ES')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            form.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {form.status === 'completed' ? '✓ Completado' : '⏳ Pendiente'}
                          </span>
                          {form.has_red_flags && (
                            <span className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400">⚠️ ALERTA</span>
                          )}
                          {/* Botón eliminar formulario */}
                          <button
                            onClick={() => deleteClinicalFormAssignment(form.id, form.form_name)}
                            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/50 rounded-lg transition-colors"
                            title="Eliminar este formulario"
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Tags */}
                      {form.tags && form.tags.length > 0 && (
                        <div className="flex gap-1 mb-2">
                          {form.tags.map(tag => (
                            <span key={tag} className={`text-xs px-2 py-0.5 rounded ${
                              tag === 'RED_FLAG' ? 'bg-red-500/30 text-red-300' :
                              tag === 'RENAL_DIAGNOSED' ? 'bg-orange-500/30 text-orange-300' :
                              tag === 'RENAL_RISK_MODERATE' ? 'bg-yellow-500/30 text-yellow-300' :
                              'bg-green-500/30 text-green-300'
                            }`}>
                              {tag.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Respuestas */}
                      {form.status === 'completed' && form.responses && (
                        <div className="mt-3 pt-3 border-t border-gray-700">
                          <p className="text-sm font-medium text-pink-400 mb-2">Respuestas:</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                            {Object.entries(form.responses).map(([key, value]) => {
                              // Formatear el nombre del campo
                              const fieldLabel = key.replace(/_/g, ' ').replace(/^[a-z]/, c => c.toUpperCase());
                              // Formatear el valor
                              let displayValue = value;
                              let isFile = false;
                              let fileUrl = '';
                              
                              // Detectar si es un archivo (URL o nombre con extensión)
                              const fileExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.gif', '.webp'];
                              const isFileValue = typeof value === 'string' && (
                                value.includes('/api/uploads/') ||
                                value.includes('clinical_files/') ||
                                value.includes('/uploads/') ||
                                value.includes('cloudinary.com/') ||  // URLs de Cloudinary
                                value.includes('res.cloudinary.com/') ||  // URLs de Cloudinary
                                fileExtensions.some(ext => value.toLowerCase().endsWith(ext))
                              );
                              
                              if (Array.isArray(value)) {
                                displayValue = value.length > 0 ? value.join(', ') : 'Ninguno';
                              } else if (!value) {
                                displayValue = '-';
                              } else if (isFileValue) {
                                isFile = true;
                                // Extraer nombre del archivo de la URL
                                displayValue = value.split('/').pop() || value;
                                
                                // Construir URL completa de descarga
                                if (value.startsWith('http')) {
                                  // URL completa (Cloudinary o externa)
                                  fileUrl = value;
                                } else if (value.startsWith('/api/')) {
                                  fileUrl = `${process.env.REACT_APP_BACKEND_URL}${value}`;
                                } else if (value.includes('clinical_files/')) {
                                  // Extraer solo el nombre del archivo si viene con la ruta
                                  const fileName = value.split('clinical_files/').pop();
                                  fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/clinical_files/${fileName}`;
                                } else {
                                  // Asumir que es solo el nombre del archivo
                                  fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/clinical_files/${value}`;
                                }
                              }
                              
                              return (
                                <div key={key} className="p-2 rounded bg-gray-900/50">
                                  <p className="text-gray-400 text-xs">{fieldLabel}</p>
                                  {isFile ? (
                                    <a 
                                      href={fileUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1 underline"
                                      download={displayValue}
                                    >
                                      📎 {displayValue}
                                      <span className="text-xs">(Descargar)</span>
                                    </a>
                                  ) : (
                                    <p className="text-white text-sm">{displayValue}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Señales de alarma */}
                          {form.red_flag_items && form.red_flag_items.length > 0 && (
                            <div className="mt-3 p-3 bg-red-500/20 border border-red-500/40 rounded">
                              <p className="text-red-400 font-bold mb-2">⚠️ SEÑALES DE ALARMA:</p>
                              <ul className="text-sm text-red-300 list-disc list-inside">
                                {form.red_flag_items.map((item, idx) => (
                                  <li key={idx}>{item}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {form.status === 'pending' && (
                        <p className="text-xs text-gray-500 italic mt-2">
                          El usuario aún no ha completado este formulario
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <div style={{ order: 10 }} className="space-y-3" data-testid="section-datos-basicos">
            <h3 className="text-sm font-bold uppercase tracking-wider text-purple-300 border-b border-purple-500/30 pb-1">📋 Datos Básicos</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Nombre *</label>
                <Input
                  placeholder="Nombre completo"
                  value={clientData.name}
                  onChange={(e) => {
                    setClientData(prev => ({ ...prev, name: e.target.value }));
                    findPlanByClientName(e.target.value);
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Edad</label>
                <Input
                  type="number"
                  placeholder="Años"
                  value={clientData.age}
                  onChange={(e) => setClientData(prev => ({ ...prev, age: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Género</label>
                <select
                  value={clientData.gender || 'femenino'}
                  onChange={(e) => setClientData(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-white text-sm"
                  data-testid="offline-generator-gender"
                >
                  <option value="femenino">Femenino</option>
                  <option value="masculino">Masculino</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Peso (kg)</label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Kg"
                  value={clientData.weight}
                  onChange={(e) => setClientData(prev => ({ ...prev, weight: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Altura (cm)</label>
                <Input
                  type="number"
                  placeholder="Cm"
                  value={clientData.height}
                  onChange={(e) => setClientData(prev => ({ ...prev, height: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">IMC <span className="text-xs text-gray-500">(kg/m²)</span></label>
                <Input
                  type="text"
                  readOnly
                  value={(() => {
                    const bc = computeBodyComposition(clientData);
                    return bc.bmi != null ? bc.bmi : '';
                  })()}
                  placeholder="—"
                  className="bg-slate-800/60 text-white font-semibold cursor-not-allowed"
                  data-testid="offline-generator-bmi"
                />
                {(() => {
                  const bc = computeBodyComposition(clientData);
                  if (!bc.bmi_category) return <p className="text-[10px] text-gray-500 mt-1">Peso ÷ (Altura m)²</p>;
                  return (
                    <span
                      className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: `${bc.bmi_category_color}22`, color: bc.bmi_category_color, border: `1px solid ${bc.bmi_category_color}55` }}
                      data-testid="offline-generator-bmi-category"
                    >
                      {bc.bmi_category}
                    </span>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  % Grasa Actual <span className="text-xs text-gray-500">(opcional)</span>
                </label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Ej. 25"
                  value={clientData.body_fat_pct}
                  onChange={(e) => setClientData(prev => ({ ...prev, body_fat_pct: e.target.value }))}
                  data-testid="offline-generator-body-fat-pct"
                />
                {(() => {
                  const bc = computeBodyComposition(clientData);
                  if (!bc.bf_interpretation) return <p className="text-[10px] text-gray-500 mt-1">Si se completa, el BMR usa Katch-McArdle.</p>;
                  return (
                    <span
                      className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
                      style={{ backgroundColor: `${bc.bf_interpretation_color}22`, color: bc.bf_interpretation_color, border: `1px solid ${bc.bf_interpretation_color}55` }}
                      data-testid="offline-generator-bf-interpretation"
                    >
                      {bc.bf_interpretation}
                    </span>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  % Grasa Objetivo <span className="text-xs text-gray-500">(opcional)</span>
                </label>
                <Input
                  type="number"
                  step="0.1"
                  placeholder="Ej. 20"
                  value={clientData.body_fat_pct_target}
                  onChange={(e) => setClientData(prev => ({ ...prev, body_fat_pct_target: e.target.value }))}
                  data-testid="offline-generator-body-fat-pct-target"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Masa Libre de Grasa <span className="text-xs text-gray-500">(kg)</span>
                </label>
                <Input
                  type="text"
                  readOnly
                  value={(() => {
                    const w = parseFloat(clientData.weight) || 0;
                    const bf = parseFloat(clientData.body_fat_pct) || 0;
                    if (w > 0 && bf > 0) return (w * (1 - bf / 100)).toFixed(1);
                    return '';
                  })()}
                  placeholder="—"
                  className="bg-slate-800/60 text-emerald-300 font-semibold cursor-not-allowed"
                  data-testid="offline-generator-lean-mass"
                />
                <p className="text-[10px] text-gray-500 mt-1">Peso × (1 − %grasa)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Masa Grasa <span className="text-xs text-gray-500">(kg)</span>
                </label>
                <Input
                  type="text"
                  readOnly
                  value={(() => {
                    const w = parseFloat(clientData.weight) || 0;
                    const bf = parseFloat(clientData.body_fat_pct) || 0;
                    if (w > 0 && bf > 0) return (w * (bf / 100)).toFixed(1);
                    return '';
                  })()}
                  placeholder="—"
                  className="bg-slate-800/60 text-amber-300 font-semibold cursor-not-allowed"
                  data-testid="offline-generator-fat-mass"
                />
                <p className="text-[10px] text-gray-500 mt-1">Peso × %grasa</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Masa Grasa Objetivo <span className="text-xs text-gray-500">(kg)</span>
                </label>
                <Input
                  type="text"
                  readOnly
                  value={(() => {
                    const bc = computeBodyComposition(clientData);
                    return bc.fat_mass_target_kg != null ? bc.fat_mass_target_kg : '';
                  })()}
                  placeholder="—"
                  className="bg-slate-800/60 text-cyan-300 font-semibold cursor-not-allowed"
                  data-testid="offline-generator-fat-mass-target"
                />
                <p className="text-[10px] text-gray-500 mt-1">Peso × %grasa objetivo</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Pérdida Grasa <span className="text-xs text-gray-500">(kg/semana)</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={clientData.manual_weekly_fat_loss_kg}
                  onChange={(e) => setClientData(prev => ({ ...prev, manual_weekly_fat_loss_kg: e.target.value }))}
                  placeholder={(() => {
                    const bc = computeBodyComposition({ ...clientData, manual_weekly_fat_loss_kg: '', manual_weeks_to_target: '' });
                    return bc.weekly_fat_loss_kg > 0 ? String(bc.weekly_fat_loss_kg) : '—';
                  })()}
                  className="bg-slate-800/60 text-pink-300 font-semibold"
                  data-testid="offline-generator-weekly-fat-loss"
                />
                <p className="text-[10px] text-gray-500 mt-1">Auto: déficit diario × 7 ÷ 7700 · editable</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Semanas al Objetivo
                </label>
                <Input
                  type="number"
                  step="1"
                  value={clientData.manual_weeks_to_target}
                  onChange={(e) => setClientData(prev => ({ ...prev, manual_weeks_to_target: e.target.value }))}
                  placeholder={(() => {
                    const bc = computeBodyComposition({ ...clientData, manual_weeks_to_target: '' });
                    return bc.weeks_to_target != null ? String(bc.weeks_to_target) : '—';
                  })()}
                  className="bg-slate-800/60 text-yellow-300 font-semibold"
                  data-testid="offline-generator-weeks-to-target"
                />
                <p className="text-[10px] text-gray-500 mt-1">Auto: (masa grasa − objetivo) ÷ pérdida sem. · editable</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Aumento Muscular <span className="text-xs text-gray-500">(kg/semana)</span>
                </label>
                <Input
                  type="text"
                  readOnly
                  value={(() => {
                    const bc = computeBodyComposition(clientData);
                    return bc.weekly_muscle_gain_kg > 0 ? bc.weekly_muscle_gain_kg : '';
                  })()}
                  placeholder="—"
                  className="bg-slate-800/60 text-fuchsia-300 font-semibold cursor-not-allowed"
                  data-testid="offline-generator-weekly-muscle-gain"
                />
                <p className="text-[10px] text-gray-500 mt-1">0.1 si ganancia muscular, 0 en pérdida</p>
              </div>
            </div>

            {/* 2026-02 · Cálculos Metabólicos y Proyecciones (admin-only, editables con cascada) */}
            {(() => {
              const mp = computeMetabolicProjections(clientData, metabolicOverrides, projWeeks);
              if (!mp) return null;
              const setOv = (k, v) => setMetabolicOverrides(prev => ({ ...prev, [k]: v }));
              const editField = (label, key, value, hint, color, testid) => (
                <div key={testid}>
                  <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={metabolicOverrides[key] !== undefined && metabolicOverrides[key] !== '' ? metabolicOverrides[key] : (value ?? '')}
                    onChange={(e) => setOv(key, e.target.value)}
                    placeholder="—"
                    className="bg-slate-900/60 font-semibold text-sm"
                    style={{ color: color || '#E5E7EB' }}
                    data-testid={testid}
                  />
                  {hint && <p className="text-[10px] text-gray-500 mt-1">{hint}</p>}
                </div>
              );
              const showRange = (label, low, high, hint, color, testid) => (
                <div key={testid}>
                  <label className="block text-xs font-medium text-gray-400 mb-1">{label}</label>
                  <Input
                    type="text"
                    readOnly
                    value={`${low} – ${high} kg`}
                    className="bg-slate-900/60 font-semibold cursor-not-allowed text-sm"
                    style={{ color: color || '#E5E7EB' }}
                    data-testid={testid}
                  />
                  {hint && <p className="text-[10px] text-gray-500 mt-1">{hint}</p>}
                </div>
              );
              return (
                <div className="space-y-4 pt-4 mt-2 border-t border-gray-700/60" data-testid="metabolic-projections">
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-cyan-300 mb-2">🧮 Cálculos Base</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {editField('BMR (kcal)', 'bmr', mp.bmr, 'Katch-McArdle o Mifflin-St Jeor', '#60A5FA', 'mp-bmr')}
                      {editField('TDEE Mantenimiento (kcal)', 'tdee', mp.tdee, 'BMR × actividad', '#34D399', 'mp-tdee')}
                      {editField('% Ajuste Calórico', 'adjustmentPct', mp.adjustmentPct, 'Ajuste ÷ TDEE × 100', '#FBBF24', 'mp-adjustment-pct')}
                      {editField('Calorías Ajustadas (kcal)', 'adjustedKcal', mp.adjustedKcal, 'TDEE × (1 + %/100)', '#FFFFFF', 'mp-adjusted')}
                      {editField('Déficit Diario (kcal)', 'dailyDeficit', mp.dailyDeficit, 'TDEE − Ajustadas', '#FF6B9D', 'mp-daily-deficit')}
                      {editField('Cal. Semanales (kcal)', 'weeklyAdjustedKcal', mp.weeklyAdjustedKcal, 'Ajustadas × 7', '#E5E7EB', 'mp-weekly-adjusted')}
                      {editField('Ajuste Semanal (kcal)', 'weeklyAdjustmentKcal', mp.weeklyAdjustmentKcal, 'Déficit × 7', '#FF6B9D', 'mp-weekly-adjustment')}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-bold uppercase tracking-wider text-pink-300 mb-2">🔻 Proyección · Pérdida de Grasa</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {editField('Kg a Bajar', 'kgToLose', mp.kgToLose, 'Masa grasa − objetivo', '#F59E0B', 'mp-kg-to-lose')}
                      {showRange('Reducción Semanal Rango', mp.weeklyLossLow, mp.weeklyLossHigh, '0.5% – 1% del peso', '#34D399', 'mp-weekly-range')}
                      {editField('Pérdida Semanal · Déficit (kg)', 'weeklyLossFromDeficit', mp.weeklyLossFromDeficit, 'Déficit × 7 ÷ 7700', '#FF6B9D', 'mp-weekly-loss-deficit')}
                      {editField('% Pérdida Semanal', 'weeklyLossPctFromDeficit', mp.weeklyLossPctFromDeficit, 'Pérdida ÷ peso × 100', '#FBBF24', 'mp-weekly-loss-pct')}
                      {editField('Días al Objetivo', 'daysToTargetFat', mp.daysToTargetFat, 'Kg a bajar ÷ pérdida sem × 7', '#FBBF24', 'mp-days-to-target-fat')}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-fuchsia-300">💪 Proyección · Aumento de Músculo</h4>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-400">Semanas:</label>
                        <Input
                          type="number"
                          step="1"
                          value={projWeeks}
                          onChange={(e) => setProjWeeks(parseInt(e.target.value) || 20)}
                          className="bg-slate-900/60 w-20 h-7 text-sm text-fuchsia-300 font-semibold"
                          data-testid="mp-proj-weeks"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {editField('Aumento Semanal (kg)', 'weeklyGain', mp.weeklyGain, '0.25 si ganancia · 0 si pérdida', '#E879F9', 'mp-weekly-gain')}
                      {editField('Músculo 2:1 / sem (kg)', 'muscleGain21', mp.muscleGain21, '2 músculo : 1 grasa', '#34D399', 'mp-muscle-21')}
                      {editField('Músculo 1:1 / sem (kg)', 'muscleGain11', mp.muscleGain11, '1 músculo : 1 grasa', '#34D399', 'mp-muscle-11')}
                      {editField('% Aumento Mensual', 'monthlyGainPct', mp.monthlyGainPct, '(Sem × 4) ÷ peso × 100', '#FBBF24', 'mp-monthly-gain-pct')}
                      {editField(`Aumento Total · ${projWeeks} sem (kg)`, 'totalWeightGain', mp.totalWeightGain, 'Sem × semanas', '#E879F9', 'mp-total-gain')}
                      {editField(`Músculo Total 2:1 · ${projWeeks} sem (kg)`, 'totalMuscle21', mp.totalMuscle21, null, '#34D399', 'mp-total-muscle-21')}
                      {editField(`Músculo Total 1:1 · ${projWeeks} sem (kg)`, 'totalMuscle11', mp.totalMuscle11, null, '#34D399', 'mp-total-muscle-11')}
                      {editField(`Grasa Total 2:1 · ${projWeeks} sem (kg)`, 'totalFat21', mp.totalFat21, null, '#F59E0B', 'mp-total-fat-21')}
                      {editField(`Grasa Total 1:1 · ${projWeeks} sem (kg)`, 'totalFat11', mp.totalFat11, null, '#F59E0B', 'mp-total-fat-11')}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Medidas corporales del cliente (asesoría 1 a 1) */}
          {(clientData.additional_info?.waist_cm != null ||
            clientData.additional_info?.hip_cm != null ||
            clientData.additional_info?.chest_cm != null ||
            clientData.additional_info?.arm_cm != null ||
            clientData.additional_info?.thigh_cm != null ||
            clientData.additional_info?.calf_cm != null ||
            clientData.additional_info?.body_fat_percentage != null) && (
            <div
              className="p-3 rounded-lg border-2 border-dashed border-pink-500/40 bg-pink-900/10"
              data-testid="client-measurements-block"
              style={{ order: 11 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <FaRuler className="text-pink-400" />
                <span className="text-sm font-semibold text-pink-300">Medidas corporales del cliente</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {[
                  ['waist_cm', 'Cintura'],
                  ['hip_cm', 'Cadera'],
                  ['chest_cm', 'Pecho'],
                  ['arm_cm', 'Brazo'],
                  ['thigh_cm', 'Muslo'],
                  ['calf_cm', 'Pantorrilla']
                ].map(([key, label]) => {
                  const v = clientData.additional_info?.[key];
                  if (v == null) return null;
                  return (
                    <div key={key} className="p-2 rounded bg-gray-800/70">
                      <div className="text-gray-400">{label}</div>
                      <div className="text-white font-semibold">{v} cm</div>
                    </div>
                  );
                })}
                {clientData.additional_info?.body_fat_percentage != null && (
                  <div className="p-2 rounded bg-gray-800/70">
                    <div className="text-gray-400">% Grasa</div>
                    <div className="text-white font-semibold">{clientData.additional_info.body_fat_percentage}%</div>
                  </div>
                )}
              </div>
              {clientData.additional_info?.measurement_updates?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-xs text-pink-300 cursor-pointer hover:text-pink-200">
                    📊 Ver histórico de actualizaciones ({clientData.additional_info.measurement_updates.length})
                  </summary>
                  <div className="mt-2 space-y-1 pl-3 border-l border-pink-500/30">
                    {clientData.additional_info.measurement_updates.slice().reverse().map((u, idx) => (
                      <div key={u.id || idx} className="text-xs text-gray-300">
                        <span className="text-gray-500">
                          {u.submitted_at ? new Date(u.submitted_at).toLocaleDateString('es-CO') : '—'}
                        </span>
                        {' · '}
                        {['waist_cm','hip_cm','chest_cm','arm_cm','thigh_cm','calf_cm','body_fat_percentage']
                          .filter(k => u[k] != null)
                          .map(k => `${k.replace('_cm','').replace('body_fat_percentage','%grasa')}: ${u[k]}`)
                          .join(' | ')}
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}

          <h3 style={{ order: 30 }} className="text-sm font-bold uppercase tracking-wider text-cyan-300 border-b border-cyan-500/30 pb-1" data-testid="section-configuracion-header">⚙️ Configuración</h3>
          {/* Email del cliente - para poder enviar el plan */}
          <div className="p-3 rounded-lg border-2 border-dashed border-cyan-500/50 bg-cyan-900/20" style={{ order: 31 }}>
            <label className="block text-sm font-medium text-cyan-400 mb-1">📧 Email del Cliente (para enviar el plan)</label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="email@cliente.com"
                value={clientData.email || ''}
                onChange={(e) => setClientData(prev => ({ ...prev, email: e.target.value }))}
                className="flex-1 bg-gray-800 border-cyan-500/30"
              />
              {clientData.email && (
                <Badge className="bg-green-500 text-white self-center">
                  ✓ Listo para enviar
                </Badge>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">Ingresa el email del cliente para poder enviarle la dieta/rutina por correo</p>
          </div>

          {/* Frecuencia de Seguimiento de Patologías */}
          {clientData.email && (
            <div className="p-3 rounded-lg border-2 border-dashed border-pink-500/50 bg-pink-900/20" style={{ order: 32 }}>
              <label className="block text-sm font-medium text-pink-400 mb-1">Seguimiento de Salud (cada cuántas semanas)</label>
              <div className="flex gap-2 items-center">
                <select
                  value={clientData.followup_frequency_weeks || '0'}
                  onChange={async (e) => {
                    const weeks = parseInt(e.target.value);
                    setClientData(prev => ({ ...prev, followup_frequency_weeks: weeks }));
                    try {
                      await axios.post(`${API}/followup/config`, {
                        user_email: clientData.email,
                        frequency_weeks: weeks
                      }, { withCredentials: false });
                      toast.success(`Seguimiento configurado: cada ${weeks === 0 ? 'desactivado' : weeks + ' semanas'}`);
                    } catch (err) {
                      console.error('Error followup config:', err?.response?.data || err);
                      toast.error(err?.response?.data?.detail || 'Error al configurar seguimiento');
                    }
                  }}
                  className="bg-gray-800 border border-pink-500/30 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="followup-frequency-select"
                >
                  <option value="0">Desactivado</option>
                  <option value="2">Cada 2 semanas</option>
                  <option value="4">Cada 4 semanas</option>
                  <option value="6">Cada 6 semanas</option>
                  <option value="8">Cada 8 semanas</option>
                  <option value="12">Cada 12 semanas</option>
                </select>
              </div>
              <p className="text-xs text-gray-400 mt-1">El formulario de seguimiento de salud le aparecerá automáticamente al cliente</p>
            </div>
          )}

          {/* ========== SECCIÓN: FORMULARIOS DE ACTUALIZACIÓN (RENOVACIONES) ========== */}
          {clientData.email && updateForms && (
            <div className="p-3 rounded-lg border-2 border-orange-500/50 bg-orange-900/20" style={{ order: 33 }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FaClipboardList className="text-orange-400" />
                  <span className="text-sm font-medium text-orange-400">
                    Formularios de Actualización ({updateForms.continuation_forms?.length || 0})
                  </span>
                  {loadingUpdateForms && <FaSpinner className="animate-spin text-orange-400" />}
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowUpdateForms(!showUpdateForms)}
                  className="text-orange-400 hover:text-orange-300"
                >
                  {showUpdateForms ? '▼ Ocultar' : '▶ Ver Formularios'}
                </Button>
              </div>
              
              {showUpdateForms && (
                <div className="space-y-3 mt-3">
                  {/* Formularios de actualización (renovaciones) */}
                  {updateForms.continuation_forms?.length > 0 ? (
                    <>
                      <p className="text-xs text-gray-400 mb-2">
                        📝 Estos son los formularios de actualización enviados por el cliente al renovar su plan:
                      </p>
                      {updateForms.continuation_forms.map((form, idx) => (
                        <div 
                          key={form.id || idx} 
                          className="p-3 rounded bg-gray-800/70 border border-orange-500/30"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-white">
                                {form.form_type === 'diet_continuation' ? '🥗 Actualización Dieta' : 
                                 form.form_type === 'workout_continuation' ? '💪 Actualización Rutina' : 
                                 '📋 Formulario de Actualización'}
                              </span>
                              <Badge className={form.status === 'released' ? 'bg-green-500' : 'bg-yellow-500'}>
                                {form.status === 'released' ? '✅ Atendido' : '⏳ Pendiente'}
                              </Badge>
                            </div>
                            <span className="text-xs text-gray-500">
                              {form.created_at ? new Date(form.created_at).toLocaleDateString('es-CO') : 'Sin fecha'}
                            </span>
                          </div>
                          
                          {/* Datos del formulario */}
                          <div className="text-xs text-gray-300 space-y-1">
                            {form.form_data?.newWeight && (
                              <p>⚖️ Nuevo peso: <span className="text-white font-medium">{form.form_data.newWeight} kg</span></p>
                            )}
                            {form.form_data?.newGoal && (
                              <p>🎯 Nuevo objetivo: <span className="text-white font-medium">{form.form_data.newGoal}</span></p>
                            )}
                            {form.form_data?.experienceWithPlan && (
                              <p>💬 Experiencia: <span className="text-gray-400">{form.form_data.experienceWithPlan}</span></p>
                            )}
                            {form.form_data?.newAllergies && (
                              <p>🚫 Nuevas alergias: <span className="text-red-400">{form.form_data.newAllergies}</span></p>
                            )}
                            {form.form_data?.mealsPerDay && (
                              <p>🍽️ Comidas/día: <span className="text-white font-medium">{form.form_data.mealsPerDay}</span></p>
                            )}
                          </div>
                          
                          {/* Botón para aplicar datos */}
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="mt-2 text-orange-400 border-orange-500/50 hover:bg-orange-500/20"
                            onClick={() => applyUpdateFormData(form)}
                          >
                            <FaSync className="mr-1" /> Aplicar datos al cliente
                          </Button>
                        </div>
                      ))}
                    </>
                  ) : (
                    <p className="text-xs text-gray-500 italic">
                      ℹ️ Este cliente no tiene formularios de actualización pendientes
                    </p>
                  )}
                  
                  {/* Formularios iniciales (referencia) */}
                  {updateForms.initial_forms?.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">
                        📂 Ver formularios iniciales ({updateForms.initial_forms.length})
                      </summary>
                      <div className="mt-2 space-y-2 pl-3 border-l border-gray-700">
                        {updateForms.initial_forms.slice(0, 3).map((form, idx) => (
                          <div key={form.id || idx} className="text-xs text-gray-400">
                            <span className="text-gray-500">{new Date(form.created_at).toLocaleDateString('es-CO')}</span>
                            {' - '}
                            <span>{form.form_type === 'diet' ? '🥗 Dieta' : form.form_type === 'workout' ? '💪 Rutina' : '📋 Formulario'}</span>
                            {' - '}
                            <span className={form.status === 'released' ? 'text-green-400' : 'text-yellow-400'}>
                              {form.status === 'released' ? 'Atendido' : 'Pendiente'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}
          {/* ========== FIN SECCIÓN FORMULARIOS DE ACTUALIZACIÓN ========== */}

          <h3 style={{ order: 20 }} className="text-sm font-bold uppercase tracking-wider text-pink-300 border-b border-pink-500/30 pb-1" data-testid="section-plan-header">🎯 Plan</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4" style={{ order: 21 }} data-testid="section-plan-grid">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Objetivo</label>
              <Select value={clientData.goal} onValueChange={(v) => setClientData(prev => ({ ...prev, goal: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOALS.map(g => (
                    <SelectItem key={g.id} value={g.id}>{g.icon} {g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Nivel de Actividad</label>
              <Select value={clientData.activity_level} onValueChange={(v) => setClientData(prev => ({ ...prev, activity_level: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_LEVELS.map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Tipo de Plan *</label>
              <Select value={clientData.plan_type} onValueChange={(v) => setClientData(prev => ({ ...prev, plan_type: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availablePlanTypes.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.icon} {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Checkbox para branding */}
              <div className="mt-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showBranding"
                  checked={showBranding}
                  onChange={(e) => setShowBranding(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-pink-500 focus:ring-pink-500"
                />
                <label htmlFor="showBranding" className="text-xs text-gray-400 cursor-pointer">
                  Incluir logo DeisyCFit en PDFs
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">🍽️ Comidas/Día * <span className="text-xs text-cyan-400">(por dieta)</span></label>
              <Select 
                value={(dietData.num_meals || clientData.num_meals)?.toString() || '5'} 
                onValueChange={(v) => {
                  const n = parseInt(v);
                  // Guardar en la dieta activa (per-tab) y también como default global
                  setDietData(prev => ({ ...prev, num_meals: n }));
                  setClientData(prev => ({ ...prev, num_meals: n }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 comidas</SelectItem>
                  <SelectItem value="4">4 comidas</SelectItem>
                  <SelectItem value="5">5 comidas</SelectItem>
                  <SelectItem value="6">6 comidas</SelectItem>
                  <SelectItem value="7">7 comidas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">💪 Comida Post-Entreno</label>
              <Select 
                value={dietData.post_workout_meal || 'none'} 
                onValueChange={(v) => setDietData(prev => ({ ...prev, post_workout_meal: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">🛋️ Sin post-entreno (día descanso)</SelectItem>
                  {Array.from({ length: dietData.num_meals || clientData.num_meals || 5 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      Comida {i + 1} es post-entreno
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">La comida post-entreno no tendrá grasas añadidas</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">🥗 Preferencia de Dieta <span className="text-xs text-cyan-400">(por dieta)</span></label>
              <Select 
                value={dietData.diet_preference || clientData.diet_preference || 'equilibrada'} 
                onValueChange={(v) => {
                  // Guardar en la dieta activa (per-tab) y también como default global
                  setDietData(prev => ({ ...prev, diet_preference: v }));
                  setClientData(prev => ({ ...prev, diet_preference: v }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIET_PREFERENCES.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.icon} {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {DIET_PREFERENCES.find(d => d.id === (dietData.diet_preference || clientData.diet_preference))?.description || ''}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" style={{ order: 22 }}>
            <Badge className={`${selectedPlan?.color} text-white px-4 py-2`}>
              {selectedPlan?.icon} {selectedPlan?.name}
            </Badge>
            <Button 
              onClick={() => calculateMacros(false)} 
              className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white"
              data-testid="recalculate-macros-only-btn"
              title="Actualiza solo los macros objetivo manteniendo las comidas que ya armaste"
            >
              🔄 Solo recalcular macros
            </Button>
            <Button 
              onClick={() => {
                if ((dietData.meals || []).some(m => (m.items || []).length > 0)) {
                  if (!window.confirm('Esto borrará todas las comidas que ya armaste. ¿Estás segura?')) return;
                }
                calculateMacros(true);
              }} 
              className="bg-gradient-to-r from-pink-500 to-purple-500 text-white"
              data-testid="reset-diet-btn"
              title="Reinicia toda la dieta y crea comidas vacías nuevas"
            >
              🧮 Reiniciar dieta y crear comidas
            </Button>
            {dietData.target_calories > 0 && (
              <div className="text-sm text-gray-300 w-full">
                <span className="font-semibold text-white">{dietData.target_calories} kcal</span> totales | 
                <span className="text-cyan-400 ml-1">{Math.round(dietData.target_calories / (dietData.num_meals || clientData.num_meals || 5))} kcal/comida</span>
              </div>
            )}
          </div>

          {/* Ciclo Hormonal (colapsable, cerrado por defecto) */}
          {clientData.email && (
            <div style={{ order: 50 }} className="rounded-lg overflow-hidden border-2 border-dashed border-pink-500/50 bg-pink-900/10" data-testid="section-ciclo-hormonal">
              <button
                type="button"
                onClick={() => toggleSection('hormonalCycle')}
                className="w-full p-3 flex items-center justify-between text-pink-300 hover:text-pink-200 hover:bg-pink-500/10 transition-colors"
                data-testid="toggle-ciclo-hormonal"
              >
                <span className="font-bold flex items-center gap-2">🩸 Mi Ciclo Hormonal</span>
                <span className="text-sm">{collapsedSections.hormonalCycle ? '▼ Expandir' : '▲ Colapsar'}</span>
              </button>
              {!collapsedSections.hormonalCycle && (
                <div className="px-3 pb-3">
                  <HormonalCycleAdminPanel email={clientData.email} />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs para Dieta, Rutina y Ciclo de Farmacología */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 bg-gray-800">
          <TabsTrigger value="diet" className="flex items-center gap-2 data-[state=active]:bg-pink-600 data-[state=active]:text-white">
            <FaUtensils /> Crear Dieta
          </TabsTrigger>
          <TabsTrigger value="workout" className="flex items-center gap-2 data-[state=active]:bg-cyan-600 data-[state=active]:text-white">
            <FaDumbbell /> Crear Rutina
          </TabsTrigger>
          <TabsTrigger value="pharmacology" className="flex items-center gap-2 data-[state=active]:bg-pink-500 data-[state=active]:text-white" data-testid="tab-pharmacology">
            <FaPills /> Ciclo Farmacología
          </TabsTrigger>
        </TabsList>

        {/* Tab Dieta */}
        <TabsContent value="diet" className="space-y-4">
          {/* Botón Generar Automáticamente Dieta */}
          <Card className="border-2 border-emerald-500/50 bg-gradient-to-r from-emerald-900/30 to-green-900/30">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-emerald-500 text-white">
                    <FaMagic />
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-400">Generación Automática de Dieta</h3>
                    <p className="text-sm text-emerald-300/70">Genera una dieta base según los macros objetivo</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowAutoDietGenerator(!showAutoDietGenerator)}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  <FaMagic className="mr-2" /> {showAutoDietGenerator ? 'Cerrar' : 'Generar Automáticamente'}
                </Button>
              </div>
              
              {showAutoDietGenerator && (
                <div className="mt-4 p-4 bg-gray-900/50 rounded-lg border-2 border-emerald-500/30">
                  {clientData.plan_type === 'basic' ? (
                    <>
                      {/* PLAN BÁSICO: Calculador inteligente de macros */}
                      <MacroCalculator 
                        clientData={clientData}
                        onMacrosCalculated={handleMacrosCalculated}
                        onClientDataChange={(changes) => setClientData(prev => ({ ...prev, ...changes }))}
                      />
                      
                      <div className="flex justify-end mt-4">
                        <Button 
                          onClick={handleGenerateSmartDiet}
                          disabled={autoDietGenerating || (!calculatedMacros && !dietData.target_calories)}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="generate-smart-diet-btn"
                        >
                          {autoDietGenerating ? (
                            <>Generando...</>
                          ) : (
                            <><FaMagic className="mr-2" /> Generar Dieta Inteligente</>
                          )}
                        </Button>
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        Se calcularán los gramos exactos de cada alimento para cumplir los macros. 3 opciones por comida. Grasa inteligente. Fruta solo en desayuno y post-entreno.
                      </p>
                    </>
                  ) : (
                    <>
                      {/* OTROS PLANES: Input manual de macros (como antes) */}
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Calorías *</label>
                          <Input type="number" value={dietData.target_calories}
                            onChange={(e) => setDietData(prev => ({ ...prev, target_calories: parseInt(e.target.value) || 0 }))}
                            placeholder="2000" className="bg-gray-800 border-gray-600 text-white" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Proteína (g) *</label>
                          <Input type="number" value={dietData.target_protein}
                            onChange={(e) => setDietData(prev => ({ ...prev, target_protein: parseInt(e.target.value) || 0 }))}
                            placeholder="150" className="bg-gray-800 border-gray-600 text-white" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Carbos (g)</label>
                          <Input type="number" value={dietData.target_carbs}
                            onChange={(e) => setDietData(prev => ({ ...prev, target_carbs: parseInt(e.target.value) || 0 }))}
                            placeholder="200" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Grasas (g)</label>
                          <Input type="number" value={dietData.target_fat}
                            onChange={(e) => setDietData(prev => ({ ...prev, target_fat: parseInt(e.target.value) || 0 }))}
                            placeholder="60" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-1">Nº Comidas</label>
                          <Select value={clientData.num_meals?.toString() || '5'} 
                            onValueChange={(v) => setClientData(prev => ({ ...prev, num_meals: parseInt(v) }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="3">3 comidas</SelectItem>
                              <SelectItem value="4">4 comidas</SelectItem>
                              <SelectItem value="5">5 comidas</SelectItem>
                              <SelectItem value="6">6 comidas</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="flex justify-end">
                        <Button onClick={handleGenerateAutoDiet}
                          disabled={autoDietGenerating || !dietData.target_calories || !dietData.target_protein}
                          className="bg-emerald-600 hover:bg-emerald-700">
                          {autoDietGenerating ? <>Generando...</> : <><FaMagic className="mr-2" /> Generar Dieta</>}
                        </Button>
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-2">
                        Se generará una dieta base que puedes personalizar. Los alimentos se seleccionan de tu base de datos.
                      </p>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Páginas Personalizadas */}
          <Card className="border-2 border-orange-200">
            <CardHeader className="bg-gradient-to-r from-orange-50 to-yellow-50 py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FaFileAlt className="text-orange-500" /> Páginas Personalizadas ({dietData.custom_pages.length})
                </CardTitle>
                <Button size="sm" variant="outline" onClick={handleAddCustomPage}>
                  <FaPlus className="mr-1" /> Añadir Página
                </Button>
              </div>
              <CardDescription>Agrega mensajes, notas o introducciones al PDF. Elige en qué posición aparece cada una.</CardDescription>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              {dietData.custom_pages.length === 0 ? (
                <p className="text-center text-gray-500 py-4">No hay páginas personalizadas. Haz clic en &quot;Añadir Página&quot; para agregar una.</p>
              ) : (
                dietData.custom_pages.map((page) => (
                  <div key={page.id} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center gap-2 mb-2">
                      <Input
                        placeholder="Título de la página"
                        value={page.title}
                        onChange={(e) => handleUpdateCustomPage(page.id, 'title', e.target.value)}
                        className="flex-1"
                      />
                      <Select 
                        value={page.page_position?.toString() || '1'} 
                        onValueChange={(v) => handleUpdateCustomPage(page.id, 'page_position', parseInt(v))}
                      >
                        <SelectTrigger className="w-[220px]">
                          <SelectValue placeholder="Posición" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGE_POSITIONS.map(pos => (
                            <SelectItem key={pos.id} value={pos.id.toString()}>{pos.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveCustomPage(page.id)}>
                        <FaTrash />
                      </Button>
                    </div>
                    {/* Barra de herramientas de formato mejorada */}
                    <RichTextToolbar 
                      pageId={page.id}
                      content={page.content}
                      onUpdate={(newContent) => handleUpdateCustomPage(page.id, 'content', newContent)}
                    />
                    <Textarea
                      id={`page-content-${page.id}`}
                      placeholder="Contenido de la página. Usa **texto** para negrita, *texto* para cursiva"
                      value={page.content}
                      onChange={(e) => handleUpdateCustomPage(page.id, 'content', e.target.value)}
                      rows={5}
                      className="rounded-t-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Formato: **negrita**, *cursiva*, __subrayado__ | Los emojis se convertirán en símbolos con color en el PDF
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Pestañas para múltiples dietas (Inferior, Superior, Descanso) */}
          <Card className="border-2 border-purple-500/50 bg-gray-900">
            <CardHeader className="py-3 bg-gradient-to-r from-purple-900/30 to-pink-900/30">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-purple-300">
                  📋 Dietas por Tipo de Día
                </CardTitle>
                <div className="flex items-center gap-2">
                  {multiDiets.training_inferior && <Badge variant="outline" className="bg-blue-900/30 text-blue-400 border-blue-500/50">🦵 Inferior ✓</Badge>}
                  {multiDiets.training_superior && <Badge variant="outline" className="bg-green-900/30 text-green-400 border-green-500/50">💪 Superior ✓</Badge>}
                  {multiDiets.descanso && <Badge variant="outline" className="bg-orange-900/30 text-orange-400 border-orange-500/50">😴 Descanso ✓</Badge>}
                  {multiDiets.lineal && <Badge variant="outline" className="bg-purple-900/30 text-purple-400 border-purple-500/50">📊 Lineal ✓</Badge>}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="flex border-b border-gray-700">
                <button
                  onClick={() => handleDietTabChange('training_inferior')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 relative ${
                    activeDietTab === 'training_inferior' 
                      ? 'bg-blue-500 text-white border-b-2 border-blue-700' 
                      : 'bg-gray-800 hover:bg-blue-900/30 text-gray-400'
                  }`}
                >
                  🦵 Tren Inferior
                  {multiDiets.training_inferior && <span className="text-xs bg-white/20 px-1.5 rounded">✓</span>}
                  {multiDiets.training_inferior && (
                    <span 
                      onClick={(e) => { e.stopPropagation(); handleDeleteDiet('training_inferior'); }}
                      className="absolute right-1 top-1 text-red-400 hover:text-red-300 text-xs bg-red-900/50 rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer"
                      title="Eliminar esta dieta"
                    >✕</span>
                  )}
                </button>
                <button
                  onClick={() => handleDietTabChange('training_superior')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 relative ${
                    activeDietTab === 'training_superior' 
                      ? 'bg-green-500 text-white border-b-2 border-green-700' 
                      : 'bg-gray-800 hover:bg-green-900/30 text-gray-400'
                  }`}
                >
                  💪 Tren Superior
                  {multiDiets.training_superior && <span className="text-xs bg-white/20 px-1.5 rounded">✓</span>}
                  {multiDiets.training_superior && (
                    <span 
                      onClick={(e) => { e.stopPropagation(); handleDeleteDiet('training_superior'); }}
                      className="absolute right-1 top-1 text-red-400 hover:text-red-300 text-xs bg-red-900/50 rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer"
                      title="Eliminar esta dieta"
                    >✕</span>
                  )}
                </button>
                <button
                  onClick={() => handleDietTabChange('descanso')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 relative ${
                    activeDietTab === 'descanso' 
                      ? 'bg-orange-500 text-white border-b-2 border-orange-700' 
                      : 'bg-gray-800 hover:bg-orange-900/30 text-gray-400'
                  }`}
                >
                  😴 Descanso
                  {multiDiets.descanso && <span className="text-xs bg-white/20 px-1.5 rounded">✓</span>}
                  {multiDiets.descanso && (
                    <span 
                      onClick={(e) => { e.stopPropagation(); handleDeleteDiet('descanso'); }}
                      className="absolute right-1 top-1 text-red-400 hover:text-red-300 text-xs bg-red-900/50 rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer"
                      title="Eliminar esta dieta"
                    >✕</span>
                  )}
                </button>
                <button
                  onClick={() => handleDietTabChange('lineal')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-all flex items-center justify-center gap-2 relative ${
                    activeDietTab === 'lineal' 
                      ? 'bg-purple-500 text-white border-b-2 border-purple-700' 
                      : 'bg-gray-800 hover:bg-purple-900/30 text-gray-400'
                  }`}
                >
                  📊 Dieta Lineal
                  {multiDiets.lineal && <span className="text-xs bg-white/20 px-1.5 rounded">✓</span>}
                  {multiDiets.lineal && (
                    <span 
                      onClick={(e) => { e.stopPropagation(); handleDeleteDiet('lineal'); }}
                      className="absolute right-1 top-1 text-red-400 hover:text-red-300 text-xs bg-red-900/50 rounded-full w-4 h-4 flex items-center justify-center hover:bg-red-700 transition-colors cursor-pointer"
                      title="Eliminar esta dieta"
                    >✕</span>
                  )}
                </button>
              </div>
              <div className="p-4 bg-gradient-to-b from-gray-900 to-gray-800">
                <p className="text-xs text-gray-500 mb-2">
                  💡 Edita la dieta de cada tipo de día. Los cambios se guardan automáticamente al cambiar de pestaña.
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-gray-300">Editando:</span>
                    <Badge className={`${
                      activeDietTab === 'training_inferior' ? 'bg-blue-500' :
                      activeDietTab === 'training_superior' ? 'bg-green-500' : 
                      activeDietTab === 'lineal' ? 'bg-purple-500' : 'bg-orange-500'
                    }`}>
                      {activeDietTab === 'training_inferior' ? '🦵 Día Tren Inferior' :
                       activeDietTab === 'training_superior' ? '💪 Día Tren Superior' : 
                       activeDietTab === 'lineal' ? '📊 Dieta Lineal' : '😴 Día de Descanso'}
                    </Badge>
                  </div>
                  
                  {/* Botones de Copiar/Mover Dieta */}
                  {(dietData.meals && dietData.meals.length > 0 && (
                    dietData.meals.some(m => m.items?.length > 0) || 
                    dietData.target_calories > 0 ||
                    dietData.diet_name
                  )) && (
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20 text-xs"
                          onClick={() => {
                            setShowCopyDietDropdown(!showCopyDietDropdown);
                            setShowMoveDietDropdown(false);
                          }}
                        >
                          <FaCopy className="mr-1" /> Copiar a...
                        </Button>
                        {showCopyDietDropdown && (
                          <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 min-w-[160px]">
                            {getOtherDietTypes().map(type => (
                              <button
                                key={type}
                                onClick={() => copyDietTo(type)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-700 text-gray-300 hover:text-white flex items-center gap-2"
                              >
                                {dietTypeNames[type]}
                                {multiDiets[type] && <span className="text-xs text-amber-400">(reemplazar)</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="relative">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20 text-xs"
                          onClick={() => {
                            setShowMoveDietDropdown(!showMoveDietDropdown);
                            setShowCopyDietDropdown(false);
                          }}
                        >
                          <FaExchangeAlt className="mr-1" /> Mover a...
                        </Button>
                        {showMoveDietDropdown && (
                          <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50 min-w-[160px]">
                            {getOtherDietTypes().map(type => (
                              <button
                                key={type}
                                onClick={() => moveDietTo(type)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-700 text-gray-300 hover:text-white flex items-center gap-2"
                              >
                                {dietTypeNames[type]}
                                {multiDiets[type] && <span className="text-xs text-cyan-400">(intercambiar)</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuración y Macros */}
          <Card className="bg-gray-900 border-gray-700">
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Nombre de la Dieta *</label>
                  <Input
                    placeholder={`Ej: Dieta ${activeDietTab === 'training_inferior' ? 'Tren Inferior' : activeDietTab === 'training_superior' ? 'Tren Superior' : activeDietTab === 'lineal' ? 'Lineal' : 'Día Descanso'}`}
                    value={dietData.diet_name}
                    onChange={(e) => setDietData({ ...dietData, diet_name: e.target.value })}
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Tipo de Día</label>
                  <div className={`px-4 py-2 rounded-lg font-medium ${
                    activeDietTab === 'training_inferior' ? 'bg-blue-100 text-blue-700' :
                    activeDietTab === 'training_superior' ? 'bg-green-100 text-green-700' : 
                    activeDietTab === 'lineal' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                  }`}>
                    {activeDietTab === 'training_inferior' ? '🦵 Día Tren Inferior' :
                     activeDietTab === 'training_superior' ? '💪 Día Tren Superior' : 
                     activeDietTab === 'lineal' ? '📊 Dieta Lineal' : '😴 Día de Descanso'}
                  </div>
                </div>
              </div>

              {/* Macros Target con botón de progresión y redistribuir */}
              <div className="flex items-end gap-3 mb-2 flex-wrap">
                <div className="flex-1 text-sm font-medium text-gray-300">Macros Objetivo</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openRedistributeModal}
                  className="border-green-500 text-green-400 hover:bg-green-500/20"
                  title="Redistribuir los macros objetivo entre todas las comidas"
                  data-testid="redistribute-macros-btn"
                >
                  <FaBalanceScale className="mr-1" /> Redistribuir
                </Button>
                {redistributeHistory.length > 0 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={undoLastRedistribution}
                    className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                    title={`Restaura los macros que tenías antes (${redistributeHistory.length} redistribución${redistributeHistory.length === 1 ? '' : 'es'} guardada${redistributeHistory.length === 1 ? '' : 's'})`}
                    data-testid="undo-redistribute-btn"
                  >
                    ↶ Deshacer ({redistributeHistory.length})
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCalorieProgressionModal(true)}
                  className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
                >
                  <FaSync className="mr-1" /> Progresión
                </Button>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Calorías</label>
                  <Input
                    type="number"
                    value={dietData.target_calories}
                    onChange={(e) => setDietData({ ...dietData, target_calories: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Proteína (g)</label>
                  <Input
                    type="number"
                    value={dietData.target_protein}
                    onChange={(e) => setDietData({ ...dietData, target_protein: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Carbos (g)</label>
                  <Input
                    type="number"
                    value={dietData.target_carbs}
                    onChange={(e) => setDietData({ ...dietData, target_carbs: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Grasas (g)</label>
                  <Input
                    type="number"
                    value={dietData.target_fat}
                    onChange={(e) => setDietData({ ...dietData, target_fat: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {/* 2026-02 · Porcentaje de Distribución de Macros (admin-only, justo debajo de Macros Objetivo) */}
              {(() => {
                const kcal = parseInt(dietData.target_calories) || 0;
                const mpBase = computeMetabolicProjections(clientData, metabolicOverrides, projWeeks);
                const mlg = mpBase && mpBase.fatMass != null ? (parseFloat(clientData.weight) - mpBase.fatMass) : null;
                const sum = (macroSplitPct.carbs || 0) + (macroSplitPct.protein || 0) + (macroSplitPct.fats || 0);
                const sumOk = Math.abs(sum - 100) < 0.01;
                const gC = kcal > 0 ? Math.round((kcal * macroSplitPct.carbs / 100) / 4) : 0;
                const gP = kcal > 0 ? Math.round((kcal * macroSplitPct.protein / 100) / 4) : 0;
                const gF = kcal > 0 ? Math.round((kcal * macroSplitPct.fats / 100) / 9) : 0;
                const proteinPerMlg = mlg && mlg > 0 ? +(gP / mlg).toFixed(2) : null;
                const fatPerMlg = mlg && mlg > 0 ? +(gF / mlg).toFixed(2) : null;
                const setPct = (k, v) => {
                  const n = parseFloat(v);
                  const val = isNaN(n) ? 0 : Math.max(0, Math.min(100, n));
                  setMacroSplitPct(prev => ({ ...prev, [k]: val }));
                };
                const setGrams = (k, v, kcalPerGram) => {
                  const g = parseFloat(v);
                  if (isNaN(g) || kcal <= 0) return;
                  const newPct = +((g * kcalPerGram) / kcal * 100).toFixed(1);
                  setPct(k, newPct);
                };
                return (
                  <div className="mt-6 pt-4 border-t border-gray-700/60" data-testid="macro-distribution-section">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <h4 className="text-sm font-bold uppercase tracking-wider text-cyan-300">📊 Porcentaje de Distribución de Macros</h4>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            if (kcal <= 0) {
                              toast.error('Ingresa primero las calorías objetivo');
                              return;
                            }
                            setDietData(prev => {
                              // Repartir el objetivo de forma uniforme entre las comidas para que
                              // el "Total del día" quede exactamente igual al objetivo.
                              const meals = prev.meals || [];
                              const n = meals.length || 1;
                              const part = (tot, i) => {
                                const per = Math.floor(tot / n);
                                return i === n - 1 ? tot - per * (n - 1) : per;
                              };
                              const newMeals = meals.map((m, i) => {
                                const mp = part(gP, i), mc = part(gC, i), mf = part(gF, i);
                                return { ...m, target_protein: mp, target_carbs: mc, target_fat: mf, target_calories: Math.round(mp * 4 + mc * 4 + mf * 9) };
                              });
                              return {
                                ...prev,
                                target_calories: kcal,
                                target_protein: gP,
                                target_carbs: gC,
                                target_fat: gF,
                                meals: newMeals,
                                distribution_mode: 'igualitaria'
                              };
                            });
                            toast.success(`Macros objetivo actualizados y repartidos entre las comidas: ${gP}g P · ${gC}g C · ${gF}g G`);
                          }}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs h-7"
                          data-testid="apply-macros-to-target-btn"
                        >
                          ✓ Aplicar a Macros Objetivo
                        </Button>
                        <span
                          className="text-xs font-bold px-2 py-1 rounded bg-slate-700/50 text-gray-200"
                          data-testid="macro-split-sum"
                        >
                          Total: {sum.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">% Carbohidratos</label>
                        <Input type="number" step="0.1" value={macroSplitPct.carbs}
                          onChange={(e) => setPct('carbs', e.target.value)}
                          className="bg-slate-900/60 text-cyan-300 font-semibold text-sm"
                          data-testid="macro-pct-carbs" />
                        <p className="text-[10px] text-gray-500 mt-1">Default 43%</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">% Proteínas</label>
                        <Input type="number" step="0.1" value={macroSplitPct.protein}
                          onChange={(e) => setPct('protein', e.target.value)}
                          className="bg-slate-900/60 text-emerald-300 font-semibold text-sm"
                          data-testid="macro-pct-protein" />
                        <p className="text-[10px] text-gray-500 mt-1">Default 30%</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">% Grasas</label>
                        <Input type="number" step="0.1" value={macroSplitPct.fats}
                          onChange={(e) => setPct('fats', e.target.value)}
                          className="bg-slate-900/60 text-amber-300 font-semibold text-sm"
                          data-testid="macro-pct-fats" />
                        <p className="text-[10px] text-gray-500 mt-1">Default 27%</p>
                      </div>
                      <div className="hidden md:block"></div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Carbos (g)</label>
                        <Input type="number" step="1" value={gC}
                          onChange={(e) => setGrams('carbs', e.target.value, 4)}
                          className="bg-slate-900/60 text-cyan-300 font-semibold text-sm"
                          data-testid="macro-g-carbs" />
                        <p className="text-[10px] text-gray-500 mt-1">kcal × % ÷ 4</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Proteínas (g)</label>
                        <Input type="number" step="1" value={gP}
                          onChange={(e) => setGrams('protein', e.target.value, 4)}
                          className="bg-slate-900/60 text-emerald-300 font-semibold text-sm"
                          data-testid="macro-g-protein" />
                        <p className="text-[10px] text-gray-500 mt-1">kcal × % ÷ 4</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Grasas (g)</label>
                        <Input type="number" step="1" value={gF}
                          onChange={(e) => setGrams('fats', e.target.value, 9)}
                          className="bg-slate-900/60 text-amber-300 font-semibold text-sm"
                          data-testid="macro-g-fats" />
                        <p className="text-[10px] text-gray-500 mt-1">kcal × % ÷ 9</p>
                      </div>
                      <div className="hidden md:block"></div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Proteína · g/kg MLG</label>
                        <Input type="text" readOnly value={proteinPerMlg != null ? proteinPerMlg : ''} placeholder="—"
                          className="bg-slate-900/60 text-emerald-300 font-semibold text-sm cursor-not-allowed"
                          data-testid="macro-protein-per-mlg" />
                        <p className="text-[10px] text-gray-500 mt-1">Rango 1.6 – 2.5 · ideal &gt;2.0</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">Grasas · g/kg MLG</label>
                        <Input type="text" readOnly value={fatPerMlg != null ? fatPerMlg : ''} placeholder="—"
                          className="bg-slate-900/60 text-amber-300 font-semibold text-sm cursor-not-allowed"
                          data-testid="macro-fat-per-mlg" />
                        <p className="text-[10px] text-gray-500 mt-1">Mínimo &gt;0.6</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Comidas */}
          
          {/* ===== PANEL CLÍNICO EDITABLE ===== */}
          {clinicalResult && (
            <ClinicalEditPanel
              clinicalResult={clinicalResult}
              clinicalNotes={clinicalNotes}
              setClinicalNotes={setClinicalNotes}
              adminBlockedFoodsText={adminBlockedFoodsText}
              setAdminBlockedFoodsText={setAdminBlockedFoodsTextByUser}
              editingPlanId={editingPlanId}
              protocolStatus={protocolStatus}
              collapsedSections={collapsedSections}
              toggleSection={toggleSection}
              loadedEditedData={clinicalEditedData}
              loadedAutoSummary={clinicalAutoSummary}
              onEditsSaved={(edits) => {
                setClinicalEditedData(edits);
              }}
              onFoodReauthorized={(foodName) => {
                setClinicalResult(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    blocked_foods: (prev.blocked_foods || []).filter(f => f !== foodName)
                  };
                });
              }}
              onFoodReblocked={(foodName) => {
                setClinicalResult(prev => {
                  if (!prev) return prev;
                  const current = prev.blocked_foods || [];
                  if (current.includes(foodName)) return prev;
                  return {
                    ...prev,
                    blocked_foods: [...current, foodName]
                  };
                });
              }}
            />
          )}

          {/* ===== ALIMENTOS PROHIBIDOS MANUALMENTE (SIEMPRE VISIBLE) =====
              Independiente del módulo clínico. Aplica a cualquier cliente,
              tenga o no patología/alergia. El gusto en alimentos no depende
              de la salud, así que esta caja siempre debe estar accesible. */}
          <div
            className="rounded-lg p-3"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              border: '1.5px solid rgba(239, 68, 68, 0.35)'
            }}
          >
            <p className="text-red-400 font-bold text-sm mb-1 flex items-center gap-2">
              🚫 ALIMENTOS PROHIBIDOS MANUALMENTE
            </p>
            <p className="text-red-200/70 text-xs mb-2 leading-relaxed">
              Escribe los alimentos que NO deben aparecer en este plan, separados por comas.
              Se filtran automáticamente de todas las listas de comidas (también funciona por
              palabra clave, ej: <em>&quot;cerdo&quot;</em> bloquea &quot;Lomo de cerdo&quot;,
              &quot;Chuleta de cerdo&quot;…).
            </p>
            <Textarea
              value={adminBlockedFoodsText || ''}
              onChange={(e) => setAdminBlockedFoodsTextByUser(e.target.value)}
              placeholder="Ej: cerdo, atún, brócoli, leche entera, plátano verde"
              className="bg-gray-900/50 border-red-500/30 text-white text-sm min-h-[80px] placeholder:text-red-300/40"
              data-testid="admin-blocked-foods-input-global"
            />
            {adminBlockedFoodsText && adminBlockedFoodsText.trim() && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {adminBlockedFoodsText
                  .split(/[,;\n]+/)
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map((term, i) => (
                    <span
                      key={i}
                      className="text-[11px] px-2.5 py-1 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 inline-flex items-center gap-1"
                    >
                      🚫 {term}
                    </span>
                  ))}
              </div>
            )}
          </div>
          
          {/* Suplementos en Ayunas - Antes de la primera comida */}
          <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm font-bold text-amber-700">
                  <FaPills className="text-amber-500" /> 
                  <span>🌅 SUPLEMENTOS EN AYUNAS</span>
                  <span className="text-xs font-normal text-amber-600">(antes del desayuno)</span>
                </label>
                <div className="flex items-center gap-2">
                  {/* Botones de copiar/mover suplementos */}
                  {((dietData.fasting_supplements?.length > 0) || 
                    (dietData.pre_workout_supplements?.length > 0) ||
                    (dietData.meals?.some(meal => meal.supplements?.length > 0))) && (
                    <>
                      <div className="relative">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-cyan-600 border-cyan-400 hover:bg-cyan-100 text-xs"
                          onClick={() => {
                            setShowCopySuppsDropdown(!showCopySuppsDropdown);
                            setShowMoveSuppsDropdown(false);
                          }}
                        >
                          <FaCopy className="mr-1" /> Copiar supps
                        </Button>
                        {showCopySuppsDropdown && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-[150px]">
                            {getOtherDietTypes().map(type => (
                              <button
                                key={type}
                                onClick={() => copySupplementsTo(type)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2"
                              >
                                {dietTypeNames[type]}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-purple-600 border-purple-400 hover:bg-purple-100 text-xs"
                          onClick={() => {
                            setShowMoveSuppsDropdown(!showMoveSuppsDropdown);
                            setShowCopySuppsDropdown(false);
                          }}
                        >
                          <FaExchangeAlt className="mr-1" /> Mover
                        </Button>
                        {showMoveSuppsDropdown && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl z-50 min-w-[150px]">
                            {getOtherDietTypes().map(type => (
                              <button
                                key={type}
                                onClick={() => moveSupplementsTo(type)}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 text-gray-700 flex items-center gap-2"
                              >
                                {dietTypeNames[type]}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-amber-600 border-amber-400 hover:bg-amber-100"
                    onClick={handleAddFastingSupplement}
                  >
                    <FaPlus className="mr-1" /> Añadir
                  </Button>
                </div>
              </div>
              
              {(!dietData.fasting_supplements || dietData.fasting_supplements.length === 0) ? (
                <p className="text-sm italic text-amber-600/70">Sin suplementos en ayunas. Haz clic en &quot;Añadir&quot; para agregar.</p>
              ) : (
                <div className="space-y-2">
                  {dietData.fasting_supplements.map((supp, suppIndex) => (
                    <div key={suppIndex} className="flex items-center gap-2">
                      <Input
                        placeholder="Ej: L-Carnitina 1g, Cafeína 200mg, ACV..."
                        value={supp}
                        onChange={(e) => handleUpdateFastingSupplement(suppIndex, e.target.value)}
                        className="flex-1 bg-white border-amber-300"
                      />
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveFastingSupplement(suppIndex)}>
                        <FaTrash />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 💧 HIDRATACIÓN - Litros de agua diarios */}
          <Card className="border-l-4 border-l-cyan-500 bg-gradient-to-r from-cyan-50 to-sky-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-sm font-bold text-cyan-700">
                  <span className="text-lg">💧</span>
                  <span>HIDRATACIÓN DIARIA</span>
                  <span className="text-xs font-normal text-cyan-600">(litros de agua recomendados)</span>
                </label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-cyan-700 mb-1 block">Litros al día</label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="Ej: 2.5"
                    value={dietData.water_liters ?? ''}
                    onChange={(e) => setDietData({ ...dietData, water_liters: e.target.value })}
                    className="bg-white border-cyan-300"
                    data-testid="water-liters-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-cyan-700 mb-1 block">Nota (opcional)</label>
                  <Input
                    placeholder="Ej: Distribuir durante el día, más en entreno..."
                    value={dietData.water_note ?? ''}
                    onChange={(e) => setDietData({ ...dietData, water_note: e.target.value })}
                    className="bg-white border-cyan-300"
                    data-testid="water-note-input"
                  />
                </div>
              </div>
              <p className="text-[11px] text-cyan-700/70 mt-2 italic">
                Se mostrará como un recordatorio en el dashboard de la clienta.
              </p>
            </CardContent>
          </Card>
          
          {/* ═══════════════════════════════════════════════════════════
              RESUMEN DE MACROS DEL DÍA — se actualiza en tiempo real
              conforme el admin edita macros o asigna recetas por comida.
              ═══════════════════════════════════════════════════════════ */}
          <DailyMacrosSummary meals={dietData.meals} target={{
            protein: dietData.target_protein,
            carbs: dietData.target_carbs,
            fat: dietData.target_fat,
            calories: dietData.target_calories,
          }} />

          {dietData.meals.map((meal, mealIndex) => {
            const isPostWorkout = dietData.post_workout_meal === (mealIndex + 1).toString();
            const postWorkoutIndex = parseInt(dietData.post_workout_meal);
            const showPreWorkoutSupplements = isPostWorkout && dietData.post_workout_meal !== 'none' && dietData.day_type !== 'descanso';
            
            return (
            <React.Fragment key={mealIndex}>
              {/* Suplementos Pre-Entreno - Solo aparece antes de comida post-entreno en días de entrenamiento */}
              {showPreWorkoutSupplements && (
                <Card className="border-l-4 border-l-red-500 bg-gradient-to-r from-red-50 to-orange-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="flex items-center gap-2 text-sm font-bold text-red-700">
                        <FaPills className="text-red-500" /> 
                        <span>💪 SUPLEMENTOS PRE-ENTRENO</span>
                        <span className="text-xs font-normal text-red-600">(antes del entrenamiento)</span>
                      </label>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 border-red-400 hover:bg-red-100"
                        onClick={handleAddPreWorkoutSupplement}
                      >
                        <FaPlus className="mr-1" /> Añadir
                      </Button>
                    </div>
                    
                    {(!dietData.pre_workout_supplements || dietData.pre_workout_supplements.length === 0) ? (
                      <p className="text-sm italic text-red-600/70">Sin suplementos pre-entreno. Haz clic en &quot;Añadir&quot; para agregar.</p>
                    ) : (
                      <div className="space-y-2">
                        {dietData.pre_workout_supplements.map((supp, suppIndex) => (
                          <div key={suppIndex} className="flex items-center gap-2">
                            <Input
                              placeholder="Ej: Creatina 5g, Beta-Alanina 3g, Pre-Workout..."
                              value={supp}
                              onChange={(e) => handleUpdatePreWorkoutSupplement(suppIndex, e.target.value)}
                              className="flex-1 bg-white border-red-300"
                            />
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemovePreWorkoutSupplement(suppIndex)}>
                              <FaTrash />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
            <Card className={`border-l-4 ${isPostWorkout ? 'border-l-orange-500 bg-gray-900' : 'border-l-purple-400'}`}>
              <CardHeader className={`py-3 ${isPostWorkout ? 'bg-gradient-to-r from-orange-900/40 to-orange-800/30' : 'bg-gradient-to-r from-purple-50 to-pink-50'}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    {/* Botones para mover comida arriba/abajo */}
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleMoveMealUp(mealIndex)}
                        disabled={mealIndex === 0}
                        className={`p-1 rounded transition-all ${
                          mealIndex === 0 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : isPostWorkout ? 'text-orange-400 hover:bg-orange-900/50 hover:text-orange-300' : 'text-purple-500 hover:bg-purple-100 hover:text-purple-700'
                        }`}
                        title="Mover arriba"
                      >
                        <FaArrowUp size={12} />
                      </button>
                      <button
                        onClick={() => handleMoveMealDown(mealIndex)}
                        disabled={mealIndex === dietData.meals.length - 1}
                        className={`p-1 rounded transition-all ${
                          mealIndex === dietData.meals.length - 1 
                            ? 'text-gray-300 cursor-not-allowed' 
                            : 'text-purple-500 hover:bg-purple-100 hover:text-purple-700'
                        }`}
                        title="Mover abajo"
                      >
                        <FaArrowDown size={12} />
                      </button>
                    </div>
                    <Input
                      className={`w-40 font-semibold ${isPostWorkout ? 'text-white bg-gray-800 border-orange-500/50' : ''}`}
                      value={meal.meal_name}
                      onChange={(e) => {
                        const newMeals = [...dietData.meals];
                        newMeals[mealIndex].meal_name = e.target.value;
                        setDietData({ ...dietData, meals: newMeals });
                      }}
                    />
                    {isPostWorkout && (
                      <span className="bg-orange-500 text-black text-xs px-2 py-1 rounded-full font-bold animate-pulse">
                        💪 POST-ENTRENO
                      </span>
                    )}
                    <Input
                      type="time"
                      className={`w-28 ${isPostWorkout ? 'text-white bg-gray-800 border-orange-500/50' : ''}`}
                      value={meal.meal_time}
                      onChange={(e) => {
                        const newMeals = [...dietData.meals];
                        newMeals[mealIndex].meal_time = e.target.value;
                        setDietData({ ...dietData, meals: newMeals });
                      }}
                    />
                    
                    {/* 2026-02 · Sin límite fijo de opciones. Muestra tantas
                        pestañas como opciones tenga la comida + botón "+" para
                        agregar. Cada pestaña tiene su propia "×" para eliminar. */}
                    <div className={`flex items-center gap-1 rounded-lg px-2 py-1 border flex-wrap ${isPostWorkout ? 'bg-gray-800 border-orange-600' : 'bg-blue-50 border-blue-200'}`}>
                      <span className={`text-xs mr-1 ${isPostWorkout ? 'text-orange-400' : 'text-blue-600'}`}>Opciones:</span>
                      {Array.from({ length: meal.num_options || 1 }, (_, i) => {
                        const isActive = (meal.active_option || 0) === i;
                        return (
                          <div key={i} className="relative inline-flex" data-testid={`meal-option-${mealIndex}-${i}`}>
                            <button
                              className={`px-2 py-1 text-xs font-semibold rounded-l transition-all ${
                                isActive
                                  ? isPostWorkout ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'
                                  : isPostWorkout ? 'bg-gray-700 text-orange-300 border border-orange-600 hover:bg-orange-900' : 'bg-white text-blue-600 border border-blue-300 hover:bg-blue-100'
                              }`}
                              onClick={() => {
                                if (!isActive) {
                                  handleSaveCurrentOption(mealIndex);
                                  handleChangeActiveOption(mealIndex, i);
                                }
                              }}
                              data-testid={`meal-option-tab-${mealIndex}-${i}`}
                            >
                              Opción {i + 1}
                            </button>
                            {(meal.num_options || 1) > 1 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`¿Eliminar la Opción ${i + 1} de esta comida?`)) {
                                    handleRemoveOption(mealIndex, i);
                                  }
                                }}
                                title="Eliminar esta opción"
                                className={`px-1.5 text-xs font-bold rounded-r transition-all ${
                                  isPostWorkout ? 'bg-gray-700 text-red-400 border border-orange-600 hover:bg-red-900' : 'bg-white text-red-500 border border-blue-300 hover:bg-red-50'
                                }`}
                                data-testid={`meal-option-remove-${mealIndex}-${i}`}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        );
                      })}
                      <button
                        onClick={() => handleChangeNumOptions(mealIndex, (meal.num_options || 1) + 1)}
                        className={`px-2 py-1 text-xs font-bold rounded transition-all ${
                          isPostWorkout ? 'bg-orange-600 text-white hover:bg-orange-500' : 'bg-blue-600 text-white hover:bg-blue-500'
                        }`}
                        title="Agregar otra opción"
                        data-testid={`meal-option-add-${mealIndex}`}
                      >
                        + Opción
                      </button>
                    </div>
                  </div>
                  
                  {/* Espacio reservado del selector fijo eliminado */}
                  {false && (
                    <div>
                      {Array.from({ length: meal.num_options || 1 }, (_, i) => (
                        <button
                          key={i}
                          onClick={() => {
                            handleSaveCurrentOption(mealIndex);
                            handleChangeActiveOption(mealIndex, i);
                          }}
                        >
                          Opción {i + 1}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Macros objetivo de esta comida — EDITABLES */}
                  <div className={`flex items-center gap-2 text-xs flex-wrap ${isPostWorkout ? 'text-white' : ''}`} data-testid={`meal-macros-editor-${mealIndex}`}>
                    <span className={`text-[10px] uppercase tracking-widest font-bold ${isPostWorkout ? 'text-orange-300' : 'text-gray-500'}`}>🎯 Macros</span>
                    {[
                      { key: 'target_protein', label: 'P', unit: 'g', bg: isPostWorkout ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-800' },
                      { key: 'target_carbs',   label: 'C', unit: 'g', bg: isPostWorkout ? 'bg-yellow-900/60 text-yellow-300' : 'bg-yellow-100 text-yellow-800' },
                      { key: 'target_fat',     label: 'G', unit: 'g', bg: isPostWorkout ? 'bg-green-900/60 text-green-300' : 'bg-green-100 text-green-800' },
                    ].map((cfg) => (
                      <label key={cfg.key} className={`flex items-center gap-1 px-2 py-1 rounded font-semibold ${cfg.bg}`}>
                        {cfg.label}:
                        <input
                          type="number"
                          min="0"
                          value={meal[cfg.key] ?? ''}
                          onChange={(e) => {
                            const val = e.target.value === '' ? 0 : Number(e.target.value);
                            const newMeals = [...dietData.meals];
                            newMeals[mealIndex] = { ...newMeals[mealIndex], [cfg.key]: val };
                            // Auto-recalcular kcal = P*4 + C*4 + G*9
                            const p = cfg.key === 'target_protein' ? val : (newMeals[mealIndex].target_protein || 0);
                            const c = cfg.key === 'target_carbs'   ? val : (newMeals[mealIndex].target_carbs   || 0);
                            const g = cfg.key === 'target_fat'     ? val : (newMeals[mealIndex].target_fat     || 0);
                            newMeals[mealIndex].target_calories = Math.round(p * 4 + c * 4 + g * 9);
                            setDietData({ ...dietData, meals: newMeals });
                          }}
                          data-testid={`meal-${cfg.key}-input-${mealIndex}`}
                          className={`w-12 bg-transparent border-0 border-b border-current focus:outline-none text-xs font-bold text-right`}
                        />
                        {cfg.unit}
                      </label>
                    ))}
                    <span className={`px-2 py-1 rounded font-bold ${isPostWorkout ? 'bg-purple-900/60 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                      = {meal.target_calories || 0} kcal
                    </span>
                  </div>

                  {/* Botón: Asignar receta de la biblioteca */}
                  {meal.recipe_from_library?.name ? (
                    <div className={`mt-2 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${isPostWorkout ? 'bg-emerald-900/40 border-emerald-700 text-emerald-200' : 'bg-emerald-50 border-emerald-300 text-emerald-800'}`}
                      data-testid={`meal-recipe-badge-${mealIndex}`}>
                      <span className="text-base">📖</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{meal.recipe_from_library.name}</div>
                        <div className={`text-[10px] ${isPostWorkout ? 'text-emerald-300/70' : 'text-emerald-700/70'}`}>
                          Receta asignada · P {meal.recipe_from_library.protein || 0}g · C {meal.recipe_from_library.carbs || 0}g · G {meal.recipe_from_library.fat || 0}g
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-6 px-2 text-xs ${isPostWorkout ? 'text-emerald-300 hover:bg-emerald-800' : 'text-emerald-700 hover:bg-emerald-100'}`}
                        onClick={() => openRecipePicker(mealIndex)}
                        data-testid={`meal-change-recipe-${mealIndex}`}
                      >Cambiar</Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs text-red-400 hover:bg-red-500/10"
                        onClick={() => {
                          const newMeals = [...dietData.meals];
                          newMeals[mealIndex] = { ...newMeals[mealIndex], recipe_from_library: null };
                          setDietData({ ...dietData, meals: newMeals });
                        }}
                        data-testid={`meal-clear-recipe-${mealIndex}`}
                      >✕</Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className={`mt-2 text-xs ${isPostWorkout ? 'border-emerald-500 text-emerald-300 hover:bg-emerald-900/40' : 'border-emerald-400 text-emerald-700 hover:bg-emerald-50'}`}
                      onClick={() => openRecipePicker(mealIndex)}
                      data-testid={`meal-assign-recipe-btn-${mealIndex}`}
                    >
                      📖 Asignar receta de la biblioteca
                    </Button>
                  )}
                  
                  <div className="flex items-center gap-2">
                    {/* Botón Regenerar Comida */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      onClick={() => handleRegenerateMeal(mealIndex)}
                      title="Regenerar esta comida con nuevos alimentos"
                    >
                      <FaSync className="mr-1" /> Regenerar
                    </Button>
                    
                    {dietData.meals.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:bg-red-50"
                        onClick={() => {
                          const newMeals = dietData.meals.filter((_, i) => i !== mealIndex);
                          setDietData({ ...dietData, meals: newMeals });
                        }}
                      >
                        <FaTrash className="mr-1" /> Eliminar
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Macros: Total agregado y Restante */}
                {mealMacrosRemaining[mealIndex] && meal.items.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {/* Total agregado */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-medium w-16 ${isPostWorkout ? 'text-gray-400' : 'text-gray-500'}`}>Agregado:</span>
                      <span className={`px-2 py-0.5 rounded font-semibold ${isPostWorkout ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                        P: {Math.round(mealMacrosRemaining[mealIndex].usedProtein || 0)}g
                      </span>
                      <span className={`px-2 py-0.5 rounded font-semibold ${isPostWorkout ? 'bg-yellow-900/60 text-yellow-300' : 'bg-yellow-100 text-yellow-700'}`}>
                        C: {Math.round(mealMacrosRemaining[mealIndex].usedCarbs || 0)}g
                      </span>
                      <span className={`px-2 py-0.5 rounded font-semibold ${isPostWorkout ? 'bg-orange-900/60 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                        G: {Math.round(mealMacrosRemaining[mealIndex].usedFat || 0)}g
                      </span>
                      <span className={`px-2 py-0.5 rounded font-semibold ${isPostWorkout ? 'bg-purple-900/60 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                        {Math.round(mealMacrosRemaining[mealIndex].usedCalories || 0)} kcal
                      </span>
                    </div>
                    {/* Restante con colores: verde = exacto, rojo = pasado */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`font-medium w-16 ${isPostWorkout ? 'text-gray-400' : 'text-gray-500'}`}>Restante:</span>
                      {(() => {
                        const remaining = mealMacrosRemaining[mealIndex];
                        const getStyle = (value) => {
                          if (isPostWorkout) {
                            if (value < -2) return 'bg-red-900/70 text-red-300 font-bold'; // Te pasaste
                            if (value <= 2 && value >= -2) return 'bg-green-900/70 text-green-300 font-bold'; // Exacto
                            return 'bg-gray-800 text-gray-300'; // Falta
                          }
                          if (value < -2) return 'bg-red-100 text-red-700 font-bold'; // Te pasaste
                          if (value <= 2 && value >= -2) return 'bg-green-100 text-green-700 font-bold'; // Exacto
                          return 'bg-gray-100 text-gray-600'; // Falta
                        };
                        const formatValue = (value) => {
                          const rounded = Math.round(value);
                          if (rounded < 0) return `${rounded}g`; // Muestra negativo
                          if (rounded === 0) return '✓';
                          return `${rounded}g`;
                        };
                        return (
                          <>
                            <span className={`px-2 py-0.5 rounded ${getStyle(remaining.protein)}`}>
                              P: {formatValue(remaining.protein)}
                            </span>
                            <span className={`px-2 py-0.5 rounded ${getStyle(remaining.carbs)}`}>
                              C: {formatValue(remaining.carbs)}
                            </span>
                            <span className={`px-2 py-0.5 rounded ${getStyle(remaining.fat)}`}>
                              G: {formatValue(remaining.fat)}
                            </span>
                            <span className={`px-2 py-0.5 rounded ${getStyle(remaining.calories)}`}>
                              {formatValue(remaining.calories).replace('g', '')} kcal
                            </span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent className={`space-y-3 ${isPostWorkout ? 'bg-gray-900' : ''}`}>
                {meal.items.length > 0 && (
                  <div className="space-y-2">
                    {meal.items.map((item, itemIndex) => {
                      // Calcular macros actuales basados en los gramos
                      const currentGrams = item.grams || Math.round((item.multiplier || 1) * 100);
                      const currentMult = currentGrams / 100;
                      const currentProtein = Math.round((item.protein || 0) * currentMult);
                      const currentCarbs = Math.round((item.carbs || 0) * currentMult);
                      const currentFat = Math.round(((item.fat || item.fats) || 0) * currentMult);
                      
                      return (
                        <div key={itemIndex} className={`flex items-center gap-3 p-3 rounded-lg border ${
                          item.skip 
                            ? isPostWorkout ? 'bg-gray-800 opacity-50 border-gray-700' : 'bg-gray-100 opacity-50 border-gray-200' 
                            : isPostWorkout ? 'bg-gradient-to-r from-orange-900/50 to-amber-900/40 border-orange-700' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
                        }`}>
                          {/* Flechas para mover arriba/abajo */}
                          <div className="flex flex-col gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-5 w-5 p-0 disabled:opacity-30 ${isPostWorkout ? 'hover:bg-orange-800' : 'hover:bg-purple-200'}`}
                              onClick={() => handleMoveItem(mealIndex, itemIndex, 'up')}
                              disabled={itemIndex === 0}
                              title="Mover arriba"
                            >
                              <FaArrowUp className={`text-xs ${isPostWorkout ? 'text-orange-400' : 'text-purple-600'}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-5 w-5 p-0 disabled:opacity-30 ${isPostWorkout ? 'hover:bg-orange-800' : 'hover:bg-purple-200'}`}
                              onClick={() => handleMoveItem(mealIndex, itemIndex, 'down')}
                              disabled={itemIndex === meal.items.length - 1}
                              title="Mover abajo"
                            >
                              <FaArrowDown className={`text-xs ${isPostWorkout ? 'text-orange-400' : 'text-purple-600'}`} />
                            </Button>
                          </div>
                          {/* Nombre del alimento con botón de intercambio */}
                          <div className="flex-1 flex items-center gap-2">
                            <span className={`text-sm font-semibold ${isPostWorkout ? 'text-white' : 'text-gray-800'}`}>{item.food_name}</span>
                            {item.measurement_note && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${isPostWorkout ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                                {item.measurement_note}
                              </span>
                            )}
                            {!item.skip && getFilteredFoodDatabase()[item.category] && (
                              <Select
                                value=""
                                onValueChange={(foodName) => {
                                  const newFood = getFilteredFoodDatabase()[item.category].find(f => f.name === foodName);
                                  if (newFood) {
                                    handleSwapFoodItem(mealIndex, itemIndex, newFood);
                                  }
                                }}
                              >
                                <SelectTrigger className={`w-8 h-8 p-0 border-0 bg-transparent rounded-full ${isPostWorkout ? 'hover:bg-orange-800' : 'hover:bg-purple-100'}`}>
                                  <FaExchangeAlt className={`text-xs mx-auto ${isPostWorkout ? 'text-orange-400' : 'text-purple-500'}`} />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {getFilteredFoodDatabase()[item.category]
                                    ?.filter(f => f.name !== item.food_name)
                                    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
                                    .map(food => (
                                      <SelectItem key={food.name} value={food.name}>
                                        {food._caution ? '⚠️ ' : ''}{food.name} <span className="text-xs text-gray-400">({food.protein}P/{food.carbs}C/{food.fat}G)</span>
                                      </SelectItem>
                                    ))
                                  }
                                </SelectContent>
                              </Select>
                            )}
                            {/* 2026-02 · Botón de intercambio UNIVERSAL: siempre
                                visible en ingredientes de recetas importadas
                                (donde la categoría no matchea con foodDatabase).
                                Abre un modal con toda la biblioteca de alimentos
                                — se conservan los gramos, se recalculan macros
                                y totales de Agregado/Restante/Total del día. */}
                            {!item.skip && item.from_recipe_library_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`w-8 h-8 p-0 rounded-full ${isPostWorkout ? 'hover:bg-orange-800' : 'hover:bg-emerald-100'}`}
                                onClick={() => {
                                  setSwapPickerCtx({ mealIndex, itemIndex, currentName: item.food_name });
                                  setSwapPickerSearch('');
                                  setSwapPickerOpen(true);
                                }}
                                title="Buscar reemplazo en toda la biblioteca"
                                data-testid={`swap-food-btn-${mealIndex}-${itemIndex}`}
                              >
                                <FaExchangeAlt className={`text-xs ${isPostWorkout ? 'text-emerald-400' : 'text-emerald-600'}`} />
                              </Button>
                            )}
                          </div>
                          {!item.skip && (
                            <>
                              <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border shadow-sm ${isPostWorkout ? 'bg-gray-800 border-orange-600' : 'bg-white border-purple-300'}`}>
                                <Input
                                  type="number"
                                  step="5"
                                  min="10"
                                  className={`w-16 text-center text-base font-bold border-0 p-0 focus:ring-0 ${isPostWorkout ? 'bg-transparent text-white' : ''}`}
                                  value={currentGrams}
                                  onChange={(e) => {
                                    const newGrams = parseFloat(e.target.value) || 100;
                                    const newMultiplier = newGrams / 100;
                                    const newMeals = [...dietData.meals];
                                    newMeals[mealIndex] = {
                                      ...newMeals[mealIndex],
                                      items: newMeals[mealIndex].items.map((it, idx) => 
                                        idx === itemIndex 
                                          ? { ...it, grams: newGrams, multiplier: newMultiplier, quantity: newGrams }
                                          : it
                                      )
                                    };
                                    setDietData({ ...dietData, meals: newMeals });
                                    // Recalcular macros restantes inmediatamente
                                    recalculateMealRemaining(mealIndex, newMeals);
                                  }}
                                />
                                <span className={`text-sm font-bold ${isPostWorkout ? 'text-orange-400' : 'text-purple-700'}`}>g</span>
                              </div>
                              <div className={`text-sm font-medium px-3 py-1.5 rounded-lg border ${isPostWorkout ? 'bg-gray-800 border-orange-600' : 'bg-gray-100 border-gray-200'}`}>
                                <span className={isPostWorkout ? 'text-blue-400' : 'text-blue-600'}>{currentProtein}P</span>
                                <span className={isPostWorkout ? 'text-gray-500' : 'text-gray-400'} > / </span>
                                <span className={isPostWorkout ? 'text-yellow-400' : 'text-yellow-600'}>{currentCarbs}C</span>
                                <span className={isPostWorkout ? 'text-gray-500' : 'text-gray-400'}> / </span>
                                <span className={isPostWorkout ? 'text-green-400' : 'text-green-600'}>{currentFat}G</span>
                              </div>
                            </>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveFoodItem(mealIndex, itemIndex)}>
                            <FaTrash className="text-red-500" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="grid grid-cols-6 gap-2">
                  {Object.entries(getFilteredFoodDatabase()).map(([category, foods]) => {
                    // Verificar si esta comida es post-entreno
                    const isPostWorkout = dietData.post_workout_meal === (mealIndex + 1).toString();
                    
                    // Si es post-entreno y es la categoría de grasas, no mostrar el dropdown
                    if (isPostWorkout && category === 'grasas') {
                      return (
                        <div key={category} className="flex items-center justify-center bg-orange-900/40 rounded-lg border-2 border-dashed border-orange-600 text-xs text-orange-300 p-2">
                          🚫 Sin grasas (Post-entreno)
                        </div>
                      );
                    }
                    
                    // Calcular el objetivo de macro para esta comida
                    const mealTargets = {
                      protein: meal.target_protein || 30,
                      carbs: meal.target_carbs || 30,
                      fat: meal.target_fat || 10
                    };
                    const remaining = mealMacrosRemaining[mealIndex] || mealTargets;
                    
                    // Determinar qué macro usar para calcular los gramos
                    let targetMacro = remaining.protein;
                    let macroKey = 'protein';
                    if (category === 'carbohidratos' || category === 'carbohidratos_simples') {
                      targetMacro = remaining.carbs;
                      macroKey = 'carbs';
                    } else if (category === 'grasas') {
                      targetMacro = remaining.fat;
                      macroKey = 'fat';
                    } else if (category === 'frutas') {
                      targetMacro = Math.round(remaining.carbs * 0.3);
                      macroKey = 'carbs';
                    }
                    
                    // Ordenar alimentos alfabéticamente y calcular gramos
                    const sortedFoods = [...foods]
                      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
                      .map(food => {
                        // Calcular gramos para alcanzar el objetivo
                        const macroPer100g = food[macroKey] || 0;
                        let calculatedGrams = 100;
                        if (macroPer100g > 0 && targetMacro > 0) {
                          calculatedGrams = Math.round((targetMacro / macroPer100g) * 100);
                          // Limitar a rangos razonables según categoría
                          if (category === 'grasas') {
                            calculatedGrams = Math.min(calculatedGrams, 80);
                            calculatedGrams = Math.max(calculatedGrams, 5);
                          } else if (category === 'carbohidratos_simples') {
                            // Carbohidratos simples: porciones más pequeñas
                            calculatedGrams = Math.min(calculatedGrams, 60);
                            calculatedGrams = Math.max(calculatedGrams, 10);
                          } else if (category === 'frutas') {
                            calculatedGrams = Math.min(calculatedGrams, 300);
                            calculatedGrams = Math.max(calculatedGrams, 50);
                          } else if (category === 'vegetales') {
                            calculatedGrams = 100; // Vegetales siempre 100g (libre elección)
                          } else {
                            calculatedGrams = Math.min(calculatedGrams, 400);
                            calculatedGrams = Math.max(calculatedGrams, 10);
                          }
                        }
                        return { ...food, calculatedGrams };
                      });
                    
                    // Para vegetales, agregar opción especial al inicio
                    if (category === 'vegetales') {
                      sortedFoods.unshift({
                        name: '🥗 Libre elección (elige los que más te gusten)',
                        calories: 25,
                        protein: 2,
                        carbs: 5,
                        fat: 0,
                        calculatedGrams: 100,
                        isSpecial: true
                      });
                    }
                    
                    // Agregar opción "NO AGREGAR" al inicio de TODAS las categorías
                    sortedFoods.unshift({
                      name: '❌ NO AGREGAR',
                      skip: true,
                      calculatedGrams: 0
                    });
                    
                    return (
                      <Select key={category} onValueChange={(foodName) => {
                        const food = sortedFoods.find(f => f.name === foodName);
                        if (food) handleAddFoodItem(mealIndex, category, food);
                      }}>
                        <SelectTrigger className={`text-xs ${isPostWorkout ? 'bg-gray-800 border-orange-600 text-white' : ''}`}>
                          <SelectValue placeholder={
                            category === 'proteinas' ? '🍖 Prot' :
                            category === 'carbohidratos' ? '🍞 Carb' :
                            category === 'carbohidratos_simples' ? '🍯 C.Simp' :
                            category === 'grasas' ? '🥑 Gras' :
                            category === 'vegetales' ? '🥬 Veg' : '🍎 Frut'
                          } />
                        </SelectTrigger>
                        <SelectContent className="max-h-80">
                          {sortedFoods.map((food) => (
                            <SelectItem 
                              key={food.name} 
                              value={food.name} 
                              className={`${food.skip ? 'text-gray-400' : ''} ${food.isSpecial ? 'bg-green-50 font-semibold' : ''} ${food._caution ? 'text-amber-600' : ''} py-2`}
                            >
                              <span className="font-medium">{food._caution ? '⚠️ ' : ''}{food.name}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    );
                  })}
                  {/* ACOMP - Acompañamientos opcionales (multi-select) */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`acomp-btn-meal-${mealIndex}`}
                        className={`text-xs justify-start ${
                          (meal.acompanamientos || []).length > 0
                            ? (isPostWorkout ? 'bg-teal-900 border-teal-500 text-teal-300' : 'bg-teal-900/40 border-teal-500 text-teal-300')
                            : (isPostWorkout ? 'bg-gray-800 border-gray-600 text-gray-300' : '')
                        }`}
                      >
                        {(meal.acompanamientos || []).length > 0
                          ? `ACOMP (${(meal.acompanamientos || []).length})`
                          : 'ACOMP'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 max-h-80 overflow-y-auto p-3" align="start">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-gray-700">Acompañamiento (opcional)</p>
                        {(meal.acompanamientos || []).length > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs text-red-500 h-6 px-2"
                            data-testid={`acomp-clear-meal-${mealIndex}`}
                            onClick={() => handleClearAcomp(mealIndex)}
                          >
                            NO AGREGAR
                          </Button>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-400 mb-3">No reemplaza macronutrientes principales.</p>
                      {ACOMP_OPTIONS.map((group) => (
                        <div key={group.group} className="mb-2">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{group.group}</p>
                          {group.items.map((item) => {
                            const isChecked = (meal.acompanamientos || []).some(a => a.id === item.id);
                            return (
                              <label
                                key={item.id}
                                className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-gray-50 cursor-pointer"
                              >
                                <Checkbox
                                  checked={isChecked}
                                  data-testid={`acomp-check-${item.id}-meal-${mealIndex}`}
                                  onCheckedChange={() => handleToggleAcomp(mealIndex, item.id, item.label)}
                                />
                                <span className="text-sm">{item.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      ))}
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Badges de acompañamientos seleccionados */}
                {(meal.acompanamientos || []).length > 0 && (
                  <div className={`mt-2 flex flex-wrap gap-1.5 p-2 rounded-lg border ${isPostWorkout ? 'bg-teal-900/20 border-teal-800' : 'bg-teal-900/30 border-teal-700'}`}>
                    <span className={`text-xs font-medium mr-1 ${isPostWorkout ? 'text-teal-400' : 'text-teal-400'}`}>ACOMP:</span>
                    {(meal.acompanamientos || []).map((a) => (
                      <Badge
                        key={a.id}
                        variant="outline"
                        data-testid={`acomp-badge-${a.id}-meal-${mealIndex}`}
                        className={`text-xs cursor-pointer ${isPostWorkout ? 'border-teal-600 text-teal-300 hover:bg-teal-900' : 'border-teal-600 text-teal-300 hover:bg-teal-900/50'}`}
                        onClick={() => handleToggleAcomp(mealIndex, a.id, a.label)}
                      >
                        {a.label} <FaTimes className="ml-1 text-[10px]" />
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Suplementos múltiples */}
                <div className={`mt-3 p-3 rounded-lg border ${isPostWorkout ? 'bg-purple-900/30 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className={`flex items-center gap-2 text-sm font-medium ${isPostWorkout ? 'text-purple-300' : 'text-purple-700'}`}>
                      <FaPills className={isPostWorkout ? 'text-purple-400' : 'text-purple-500'} /> Suplementos / Vitaminas
                    </label>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className={isPostWorkout ? 'text-purple-300 border-purple-600 hover:bg-purple-900' : 'text-purple-600 border-purple-300'}
                      onClick={() => handleAddSupplement(mealIndex)}
                    >
                      <FaPlus className="mr-1" /> Añadir Suplemento
                    </Button>
                  </div>
                  
                  {(!meal.supplements || meal.supplements.length === 0) ? (
                    <p className={`text-sm italic ${isPostWorkout ? 'text-gray-400' : 'text-gray-500'}`}>Sin suplementos. Haz clic en &quot;Añadir Suplemento&quot; para agregar.</p>
                  ) : (
                    <div className="space-y-2">
                      {meal.supplements.map((supp, suppIndex) => (
                        <div key={suppIndex} className="flex items-center gap-2">
                          <Input
                            placeholder="Ej: Creatina 5g, Vitamina D 1000UI..."
                            value={supp}
                            onChange={(e) => handleUpdateSupplement(mealIndex, suppIndex, e.target.value)}
                            className={`flex-1 ${isPostWorkout ? 'bg-gray-800 border-purple-600 text-white placeholder:text-gray-500' : 'bg-white'}`}
                          />
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleRemoveSupplement(mealIndex, suppIndex)}>
                            <FaTrash />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            </React.Fragment>
          );
          })}

          {/* Botón Añadir Comida */}
          <Button
            variant="outline"
            className="w-full border-dashed border-2 border-purple-300 text-purple-600 hover:bg-purple-50"
            onClick={() => {
              const newMealNumber = dietData.meals.length + 1;
              setDietData({
                ...dietData,
                meals: [...dietData.meals, { 
                  meal_name: `Comida ${newMealNumber}`, 
                  meal_time: '', 
                  items: [],
                  supplements: []
                }]
              });
            }}
          >
            <FaPlus className="mr-2" /> Añadir Comida
          </Button>

          {/* Notas y Descarga */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <Textarea
                placeholder="Notas adicionales..."
                value={dietData.notes}
                onChange={(e) => setDietData({ ...dietData, notes: e.target.value })}
                rows={2}
              />
              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={handleDownloadDietPDF}
                  disabled={generating}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600"
                >
                  {generating ? 'Generando...' : <><FaDownload className="mr-2" /> Descargar PDF</>}
                </Button>
                {/* Botón SIEMPRE visible - si no hay email, pide ingresarlo primero */}
                <Button
                  onClick={async () => {
                    // Verificar si hay email
                    if (!clientData.email || !clientData.email.trim()) {
                      toast.error('Por favor ingresa el email del cliente en la sección de datos básicos');
                      return;
                    }
                    // Si no hay plan guardado, guardarlo primero
                    if (!editingPlanId) {
                      toast.info('Guardando plan antes de enviar...');
                      await handleSavePlan();
                    }
                    // Luego enviar
                    handleSendDietToUser();
                  }}
                  disabled={sendingDiet}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                >
                  {sendingDiet ? (
                    <><FaSpinner className="mr-2 animate-spin" /> Enviando...</>
                  ) : (
                    <><FaPaperPlane className="mr-2" /> Enviar Dieta al Usuario</>
                  )}
                </Button>
              </div>
              {!clientData.email && (
                <p className="text-sm text-amber-400 text-center">
                  ⚠️ Ingresa el email del cliente arriba para poder enviarle la dieta
                </p>
              )}
              {clientData.email && !editingPlanId && (
                <p className="text-sm text-amber-400 text-center">
                  💡 Al enviar, el plan se guardará automáticamente
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Rutina */}
        <TabsContent value="workout" className="space-y-4">
          {/* 2026-02 · Progresión de Entrenamiento (Data Progresión) de la clienta
              — se muestra al tope de la pestaña de Rutina para que la admin la
              consulte mientras arma el plan. */}
          {clientData.email && (
            <>
              <div className="flex justify-end">
                <Button
                  onClick={() => setAdminDataProgOpen(true)}
                  className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
                  data-testid="admin-open-data-progresion"
                >
                  🏋️ Abrir Data Progresión de la clienta
                </Button>
              </div>
              <WorkoutProgressionSection email={clientData.email} />
            </>
          )}
          <AdminDataProgresionModal
            email={clientData.email}
            open={adminDataProgOpen}
            onClose={() => setAdminDataProgOpen(false)}
          />
          {/* Datos del formulario personalizado para rutina */}
          {clientData.additional_info && Object.keys(clientData.additional_info).length > 0 && (
            <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-pink-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
                  <FaClipboardList /> Información del Cliente para Rutina
                  {clientData.email && (
                    <span className="text-sm font-normal text-blue-600 ml-2">({clientData.email})</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Datos básicos del cliente */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                  <div className="bg-white rounded p-2 text-center border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Nombre</p>
                    <p className="font-bold text-purple-700">{clientData.name || '-'}</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Edad</p>
                    <p className="font-bold text-purple-700">{clientData.age || '-'} años</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Peso</p>
                    <p className="font-bold text-purple-700">{clientData.weight || '-'} kg</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Altura</p>
                    <p className="font-bold text-purple-700">{clientData.height || '-'} cm</p>
                  </div>
                  <div className="bg-white rounded p-2 text-center border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Objetivo</p>
                    <p className="font-bold text-purple-700 capitalize">{clientData.goal?.replace(/_/g, ' ') || '-'}</p>
                  </div>
                </div>

                {/* Info de actividad y trabajo */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div className="bg-white rounded p-2 border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Nivel de Actividad</p>
                    <p className="font-semibold capitalize text-gray-900">{clientData.activity_level?.replace(/_/g, ' ') || '-'}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Tipo de Trabajo</p>
                    <p className="font-semibold text-gray-900">{clientData.additional_info.work_type === 'sedentario' ? '💻 Sedentario' : '🏃 Activo'}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Horario</p>
                    <p className="font-semibold text-gray-900">{clientData.additional_info.wake_up_time || '?'} - {clientData.additional_info.sleep_time || '?'}</p>
                  </div>
                  <div className="bg-white rounded p-2 border border-gray-200">
                    <p className="text-gray-600 text-xs font-medium">Plan</p>
                    <p className="font-semibold uppercase text-pink-600">{clientData.plan_type || '-'}</p>
                  </div>
                </div>
                
                {/* Patologías importantes para el entrenamiento */}
                {clientData.additional_info.pathologies?.length > 0 && clientData.additional_info.pathologies[0] !== 'ninguna' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-red-700 font-semibold text-sm mb-2">⚠️ PATOLOGÍAS A CONSIDERAR EN LA RUTINA:</p>
                    <div className="flex flex-wrap gap-1">
                      {clientData.additional_info.pathologies.map((p, i) => (
                        <span key={i} className="bg-red-200 text-red-800 px-2 py-1 rounded text-xs font-medium capitalize">
                          {p.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Limitaciones físicas */}
                {clientData.additional_info.physical_limitations && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <p className="text-orange-700 font-semibold text-sm mb-1">⚠️ LIMITACIONES FÍSICAS:</p>
                    <p className="text-orange-800 text-sm">{clientData.additional_info.physical_limitations}</p>
                  </div>
                )}

                {/* INFO DE ENTRENAMIENTO - NUEVA SECCIÓN */}
                {(clientData.additional_info.training_location || clientData.additional_info.training_experience || 
                  clientData.additional_info.training_time || clientData.additional_info.training_days_per_week) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-blue-700 font-semibold text-sm mb-2">🏋️ INFORMACIÓN DE ENTRENAMIENTO:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                      {clientData.additional_info.training_location && (
                        <div className="bg-blue-100 rounded p-2">
                          <p className="text-blue-600 text-xs">Lugar de entreno</p>
                          <p className="text-blue-900 font-semibold capitalize">
                            {clientData.additional_info.training_location === 'gym' ? '🏢 Gimnasio' : 
                             clientData.additional_info.training_location === 'home' ? '🏠 Casa' : 
                             clientData.additional_info.training_location === 'both' ? '🏢🏠 Ambos' :
                             clientData.additional_info.training_location}
                          </p>
                        </div>
                      )}
                      {clientData.additional_info.training_experience && (
                        <div className="bg-blue-100 rounded p-2">
                          <p className="text-blue-600 text-xs">Experiencia</p>
                          <p className="text-blue-900 font-semibold capitalize">
                            {clientData.additional_info.training_experience === 'principiante' ? '🌱 Principiante' :
                             clientData.additional_info.training_experience === 'intermedio' ? '💪 Intermedio' :
                             clientData.additional_info.training_experience === 'avanzado' ? '🔥 Avanzado' :
                             clientData.additional_info.training_experience}
                          </p>
                        </div>
                      )}
                      {clientData.additional_info.training_time && (
                        <div className="bg-blue-100 rounded p-2">
                          <p className="text-blue-600 text-xs">Tiempo disponible</p>
                          <p className="text-blue-900 font-semibold">
                            ⏱️ {clientData.additional_info.training_time}
                          </p>
                        </div>
                      )}
                      {clientData.additional_info.training_days_per_week && (
                        <div className="bg-blue-100 rounded p-2">
                          <p className="text-blue-600 text-xs">Días por semana</p>
                          <p className="text-blue-900 font-semibold">
                            📅 {clientData.additional_info.training_days_per_week} días
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Equipos disponibles */}
                    {clientData.additional_info.available_equipment?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-blue-600 text-xs mb-1">Equipos disponibles:</p>
                        <div className="flex flex-wrap gap-1">
                          {clientData.additional_info.available_equipment.map((eq, i) => (
                            <span key={i} className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-medium capitalize">
                              {eq.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Preferencias de entrenamiento */}
                    {clientData.additional_info.training_preferences && (
                      <div className="mt-2">
                        <p className="text-blue-600 text-xs mb-1">Preferencias:</p>
                        <p className="text-blue-800 text-sm">{clientData.additional_info.training_preferences}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Nota sobre videollamada */}
                {clientData.additional_info.eating_disorder_option === 'videollamada' && (
                  <div className="bg-purple-100 border border-purple-300 rounded-lg p-3">
                    <p className="text-purple-700 font-semibold text-sm">
                      📞 Este cliente solicitó VIDEOLLAMADA para hablar sobre temas de conducta alimentaria
                    </p>
                  </div>
                )}
                
                {/* PREGUNTAS PERSONALIZADAS / SALUD HORMONAL - COLAPSABLE */}
                {clientData.additional_info.custom_fields && Object.keys(clientData.additional_info.custom_fields).length > 0 && (
                  <div className="bg-pink-50 border-2 border-pink-300 rounded-lg overflow-hidden">
                    <button 
                      onClick={() => toggleSection('hormonalQuestions')}
                      className="w-full p-3 flex items-center justify-between hover:bg-pink-100 transition-colors"
                    >
                      <p className="text-pink-700 font-bold text-sm flex items-center gap-2">
                        🩺 SALUD HORMONAL Y PREGUNTAS PERSONALIZADAS
                        <span className="bg-pink-200 text-pink-800 px-2 py-0.5 rounded text-xs">
                          {Object.keys(clientData.additional_info.custom_fields).length} respuestas
                        </span>
                      </p>
                      <span className="text-pink-600 text-sm font-medium">
                        {collapsedSections.hormonalQuestions ? '▼ Expandir' : '▲ Colapsar'}
                      </span>
                    </button>
                    {!collapsedSections.hormonalQuestions && (
                      <div className="px-3 pb-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {Object.entries(clientData.additional_info.custom_fields).map(([fieldId, value], i) => {
                            // Mapeo completo de etiquetas de todos los templates
                            const staticLabels = {
                              // Salud Hormonal (hm)
                              'hm1': '¿Tu ciclo menstrual es regular?',
                              'hm2': '¿Cada cuántos días ocurre tu ciclo?',
                              'hm3': '¿Síntomas antes de tu menstruación?',
                              'hm4': '¿Usas anticonceptivo hormonal?',
                              'hm5': 'Tiempo usando anticonceptivos',
                              'hm6': 'Nivel de estrés (1-10)',
                              'hm7': '¿Duermes al menos 7 horas?',
                              'hm8': '¿Te despiertas descansada?',
                              'hm9': '¿Agotada antes de entrenar?',
                              // Campos Competitivo Rutina (cf y Cf)
                              'cf1': 'Nombre completo', 'Cf1': 'Nombre completo',
                              'cf2': 'Edad', 'Cf2': 'Edad',
                              'cf3': 'Género', 'Cf3': 'Género',
                              'cf4': 'WhatsApp', 'Cf4': 'WhatsApp',
                              'cf5': 'Peso actual (kg)', 'Cf5': 'Peso actual (kg)',
                              'cf6': 'Altura (cm)', 'Cf6': 'Altura (cm)',
                              'cf7': '% Grasa corporal actual', 'Cf7': '% Grasa corporal actual',
                              'cf8': 'Medidas: Pecho (cm)', 'Cf8': 'Medidas: Pecho (cm)',
                              'cf9': 'Medidas: Cintura (cm)', 'Cf9': 'Medidas: Cintura (cm)',
                              'cf10': 'Medidas: Cadera (cm)', 'Cf10': 'Medidas: Cadera (cm)',
                              'cf11': 'Medidas: Brazo (cm)', 'Cf11': 'Medidas: Brazo (cm)',
                              'cf12': 'Medidas: Muslo (cm)', 'Cf12': 'Medidas: Muslo (cm)',
                              'cf13': '¿Has competido antes?', 'Cf13': '¿Has competido antes?',
                              'cf14': 'Categoría de competencia', 'Cf14': 'Categoría de competencia',
                              'cf15': 'Logros previos en competencias', 'Cf15': 'Logros previos en competencias',
                              'cf16': '¿En qué fase de preparación estás?', 'Cf16': '¿En qué fase de preparación estás?',
                              'cf17': 'Peso objetivo para competir (kg)', 'Cf17': 'Peso objetivo para competir (kg)',
                              'cf18': 'Días de entrenamiento disponibles', 'Cf18': 'Días de entrenamiento disponibles',
                              'cf19': 'Describe tu rutina actual', 'Cf19': 'Describe tu rutina actual',
                              'cf20': 'Fecha de la competencia', 'Cf20': 'Fecha de la competencia',
                              'cf21': 'Nombre del evento', 'Cf21': 'Nombre del evento',
                              'cf22': 'Ubicación del evento', 'Cf22': 'Ubicación del evento',
                              'cf23': 'Notas adicionales', 'Cf23': 'Notas adicionales',
                              // Campos Competitivo Dieta (dcf y Dcf)
                              'dcf1': 'Nombre completo', 'Dcf1': 'Nombre completo',
                              'dcf2': 'Género', 'Dcf2': 'Género',
                              'dcf3': 'Edad', 'Dcf3': 'Edad',
                              'dcf4': 'Peso actual (kg)', 'Dcf4': 'Peso actual (kg)',
                              'dcf5': 'Peso de competencia deseado (kg)', 'Dcf5': 'Peso de competencia deseado (kg)',
                              'dcf6': '% Grasa actual', 'Dcf6': '% Grasa actual',
                              'dcf7': '% Grasa objetivo', 'Dcf7': '% Grasa objetivo',
                              'dcf8': 'Semanas hasta la competencia', 'Dcf8': 'Semanas hasta la competencia',
                              'dcf9': 'Describe tu dieta actual', 'Dcf9': 'Describe tu dieta actual',
                              'dcf10': '¿Has hecho prep de competencia antes?', 'Dcf10': '¿Has hecho prep de competencia antes?',
                              'dcf11': '¿Cómo respondes a los carbos?', 'Dcf11': '¿Cómo respondes a los carbos?',
                              'dcf12': 'Fase actual de prep', 'Dcf12': 'Fase actual de prep',
                              'dcf13': 'Restricciones alimentarias', 'Dcf13': 'Restricciones alimentarias',
                              'dcf14': 'Suplementos que usas actualmente', 'Dcf14': 'Suplementos que usas actualmente',
                              'dcf15': 'Alimentos que toleras mejor en prep', 'Dcf15': 'Alimentos que toleras mejor en prep',
                              'dcf16': 'Notas adicionales para el coach', 'Dcf16': 'Notas adicionales para el coach',
                              // Campos de dieta básica (dpf)
                              'dpf1': 'Género', 'dpf2': 'Edad', 'dpf3': 'Peso actual (kg)',
                              'dpf4': 'Altura (cm)', 'dpf5': '% Grasa corporal',
                              'dpf6': 'Nivel de actividad', 'dpf7': 'Comidas al día',
                              'dpf8': '¿Cuándo entrenas?', 'dpf9': 'Objetivo nutricional',
                              'dpf10': 'Restricciones alimenticias', 'dpf11': 'Alimentos favoritos',
                              'dpf12': 'Alimentos que NO te gustan', 'dpf13': 'Alergias alimentarias'
                            };
                            
                            const label = fieldLabelsMap[fieldId] || staticLabels[fieldId] || fieldId
                              .replace(/_/g, ' ')
                              .replace(/^\d+/, '')
                              .replace(/^[a-z]/, (c) => c.toUpperCase())
                              .trim() || 'Pregunta';
                            
                            let displayValue = value;
                            if (Array.isArray(value)) {
                              const symptomLabels = {
                                'dolor_abdominal': 'Dolor abdominal',
                                'hinchazon': 'Hinchazón',
                                'irritabilidad': 'Irritabilidad',
                                'fatiga': 'Fatiga',
                                'antojos': 'Antojos',
                                'ninguno': 'Ninguno',
                                'retencion_de_liquidos': 'Retención de líquidos',
                                'ntojos_intensos': 'Antojos intensos',
                                'dolor_de_cabeza': 'Dolor de cabeza',
                                'ansiedad': 'Ansiedad',
                                'inflamacion_abdominal': 'Inflamación abdominal',
                                'cambios_de_humor': 'Cambios de humor',
                                'sensibilidad_mamaria': 'Sensibilidad mamaria'
                              };
                              displayValue = value.map(v => symptomLabels[v] || v.replace(/_/g, ' ')).join(', ');
                            } else if (typeof value === 'boolean') {
                              displayValue = value ? 'Sí' : 'No';
                            } else if (typeof value === 'string') {
                              // Mapear valores comunes
                              const valueMap = {
                                'si': 'Sí', 'yes': 'Sí', 's': 'Sí', 'S': 'Sí', 'v': 'Sí', 'V': 'Sí',
                                'no': 'No', 'n': 'No', 'N': 'No',
                                'true': 'Sí', 'false': 'No',
                                'ninguno': 'Ninguno', 'ninguna': 'Ninguna',
                                'multiple': 'Múltiple', 'normal': 'Normal',
                                'wellness': 'Wellness', 'bikini': 'Bikini', 'physique': 'Physique',
                                'retomando': 'Retomando', 'novato': 'Novato', 'avanzado': 'Avanzado'
                              };
                              
                              const lowerValue = value.toLowerCase();
                              if (valueMap[value] || valueMap[lowerValue]) {
                                displayValue = valueMap[value] || valueMap[lowerValue];
                              } else {
                                displayValue = value.replace(/_/g, ' ').replace(/^[a-z]/, c => c.toUpperCase());
                              }
                            }
                            
                            return (
                              <div key={i} className="bg-white rounded p-2 border border-pink-200">
                                <p className="text-pink-600 text-xs font-medium">{label}</p>
                                <p className="text-gray-900 text-sm mt-0.5">{displayValue || '-'}</p>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Nuevo Generador de Rutinas */}
          <WorkoutGeneratorOffline 
            clientData={clientData}
            initialWorkoutData={workoutData}
            onWorkoutDataChange={(data) => setWorkoutData(data)}
          />
          
          {/* Botones de Acción */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex gap-3 flex-wrap">
                <Button
                  onClick={handleDownloadWorkoutPDF}
                  disabled={generating || !workoutData.workout_name}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  {generating ? 'Generando...' : <><FaDownload className="mr-2" /> Descargar PDF</>}
                </Button>
                <Button
                  onClick={handleSavePlan}
                  disabled={generating || !workoutData.workout_name}
                  variant="outline"
                  className="border-purple-300 text-purple-700"
                >
                  <FaSave className="mr-2" /> Guardar
                </Button>
              </div>
              {/* Botón SIEMPRE visible - si no hay email, pide ingresarlo primero */}
              <Button
                onClick={async () => {
                  // Verificar si hay email
                  if (!clientData.email || !clientData.email.trim()) {
                    toast.error('Por favor ingresa el email del cliente en la sección de datos básicos');
                    return;
                  }
                  // Si no hay plan guardado, guardarlo primero
                  if (!editingPlanId) {
                    toast.info('Guardando plan antes de enviar...');
                    await handleSavePlan();
                  }
                  // Luego enviar
                  handleSendWorkoutToUser();
                }}
                disabled={sendingWorkout || !workoutData.workout_name}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              >
                {sendingWorkout ? (
                  <><FaSpinner className="mr-2 animate-spin" /> Enviando...</>
                ) : (
                  <><FaPaperPlane className="mr-2" /> Enviar Rutina al Usuario</>
                )}
              </Button>
              {!clientData.email && (
                <p className="text-sm text-amber-400 text-center">
                  ⚠️ Ingresa el email del cliente en la pestaña de Dieta para poder enviarle la rutina
                </p>
              )}
              {clientData.email && !editingPlanId && (
                <p className="text-sm text-amber-400 text-center">
                  💡 Al enviar, el plan se guardará automáticamente
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Ciclo de Farmacología */}
        <TabsContent value="pharmacology" className="space-y-4">
          <PharmacologyCycleTab />
        </TabsContent>
      </Tabs>
      
      {/* Modal de Progresión de Macros */}
      {showCalorieProgressionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border-2 border-cyan-500/50 rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <FaSync className="text-cyan-400" />
              Progresión de Macros
            </h3>
            
            <p className="text-gray-400 text-sm mb-4">
              Ajusta cada macronutriente de forma independiente. Los gramos de los alimentos se ajustarán según la composición de cada uno. Las calorías se calculan automáticamente.
            </p>
            
            {/* Presets de progresión */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={applyPresetVolumen}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-green-600/30 to-emerald-600/30 border border-green-500/50 hover:from-green-600/50 hover:to-emerald-600/50 transition-all"
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-xl">💪</span>
                  <span className="font-bold text-green-400">VOLUMEN</span>
                </div>
                <div className="text-xs text-gray-400">
                  +10% Carbos | +5% Proteína | +5% Grasas
                </div>
              </button>
              <button
                onClick={applyPresetDefinicion}
                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-red-600/30 to-orange-600/30 border border-red-500/50 hover:from-red-600/50 hover:to-orange-600/50 transition-all"
              >
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-xl">🔥</span>
                  <span className="font-bold text-red-400">DEFINICIÓN</span>
                </div>
                <div className="text-xs text-gray-400">
                  -15% Carbos | -10% Grasas | +5% Proteína
                </div>
              </button>
            </div>
            
            {/* Controles de macros individuales */}
            <div className="space-y-4 mb-6">
              {/* Proteína */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2">
                    <span className="text-2xl">🥩</span>
                    <span className="font-semibold text-pink-400">Proteína</span>
                  </label>
                  <span className="text-gray-400 text-sm">Actual: {dietData.target_protein}g</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[-20, -10, -5].map(val => (
                      <button
                        key={val}
                        onClick={() => setMacroProgressionPercent(prev => ({ ...prev, protein: val }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          macroProgressionPercent.protein === val 
                            ? 'bg-red-500 text-white' 
                            : 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                        }`}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    value={macroProgressionPercent.protein}
                    onChange={(e) => setMacroProgressionPercent(prev => ({ ...prev, protein: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-gray-700 border-gray-600 text-white text-center font-bold"
                    placeholder="%"
                  />
                  <div className="flex gap-1">
                    {[5, 10, 20].map(val => (
                      <button
                        key={val}
                        onClick={() => setMacroProgressionPercent(prev => ({ ...prev, protein: val }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          macroProgressionPercent.protein === val 
                            ? 'bg-green-500 text-white' 
                            : 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                        }`}
                      >
                        +{val}%
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 text-right">
                  <span className={`text-sm font-medium ${macroProgressionPercent.protein > 0 ? 'text-green-400' : macroProgressionPercent.protein < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                    → {Math.round(dietData.target_protein * (1 + macroProgressionPercent.protein / 100))}g
                  </span>
                </div>
              </div>

              {/* Carbohidratos */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2">
                    <span className="text-2xl">🍚</span>
                    <span className="font-semibold text-orange-400">Carbohidratos</span>
                  </label>
                  <span className="text-gray-400 text-sm">Actual: {dietData.target_carbs}g</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[-20, -10, -5].map(val => (
                      <button
                        key={val}
                        onClick={() => setMacroProgressionPercent(prev => ({ ...prev, carbs: val }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          macroProgressionPercent.carbs === val 
                            ? 'bg-red-500 text-white' 
                            : 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                        }`}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    value={macroProgressionPercent.carbs}
                    onChange={(e) => setMacroProgressionPercent(prev => ({ ...prev, carbs: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-gray-700 border-gray-600 text-white text-center font-bold"
                    placeholder="%"
                  />
                  <div className="flex gap-1">
                    {[5, 10, 20].map(val => (
                      <button
                        key={val}
                        onClick={() => setMacroProgressionPercent(prev => ({ ...prev, carbs: val }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          macroProgressionPercent.carbs === val 
                            ? 'bg-green-500 text-white' 
                            : 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                        }`}
                      >
                        +{val}%
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 text-right">
                  <span className={`text-sm font-medium ${macroProgressionPercent.carbs > 0 ? 'text-green-400' : macroProgressionPercent.carbs < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                    → {Math.round(dietData.target_carbs * (1 + macroProgressionPercent.carbs / 100))}g
                  </span>
                </div>
              </div>

              {/* Grasas */}
              <div className="bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center gap-2">
                    <span className="text-2xl">🥑</span>
                    <span className="font-semibold text-yellow-400">Grasas</span>
                  </label>
                  <span className="text-gray-400 text-sm">Actual: {dietData.target_fat}g</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[-20, -10, -5].map(val => (
                      <button
                        key={val}
                        onClick={() => setMacroProgressionPercent(prev => ({ ...prev, fat: val }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          macroProgressionPercent.fat === val 
                            ? 'bg-red-500 text-white' 
                            : 'bg-red-900/30 text-red-400 hover:bg-red-900/50'
                        }`}
                      >
                        {val}%
                      </button>
                    ))}
                  </div>
                  <Input
                    type="number"
                    value={macroProgressionPercent.fat}
                    onChange={(e) => setMacroProgressionPercent(prev => ({ ...prev, fat: parseInt(e.target.value) || 0 }))}
                    className="w-20 bg-gray-700 border-gray-600 text-white text-center font-bold"
                    placeholder="%"
                  />
                  <div className="flex gap-1">
                    {[5, 10, 20].map(val => (
                      <button
                        key={val}
                        onClick={() => setMacroProgressionPercent(prev => ({ ...prev, fat: val }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                          macroProgressionPercent.fat === val 
                            ? 'bg-green-500 text-white' 
                            : 'bg-green-900/30 text-green-400 hover:bg-green-900/50'
                        }`}
                      >
                        +{val}%
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 text-right">
                  <span className={`text-sm font-medium ${macroProgressionPercent.fat > 0 ? 'text-green-400' : macroProgressionPercent.fat < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                    → {Math.round(dietData.target_fat * (1 + macroProgressionPercent.fat / 100))}g
                  </span>
                </div>
              </div>
            </div>

            {/* Botón para resetear */}
            <button
              onClick={() => setMacroProgressionPercent({ protein: 0, carbs: 0, fat: 0 })}
              className="w-full text-center text-gray-400 text-sm hover:text-white mb-4"
            >
              🔄 Resetear todos a 0%
            </button>
            
            {/* Opción de aplicar a todas */}
            <div className="mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyToAllDiets}
                  onChange={(e) => setApplyToAllDiets(e.target.checked)}
                  className="w-5 h-5 rounded border-gray-600 bg-gray-800 text-cyan-500 focus:ring-cyan-500"
                />
                <span className="text-gray-300">
                  Aplicar a <strong>todas</strong> las dietas (Inferior, Superior, Descanso, Lineal)
                </span>
              </label>
              {!applyToAllDiets && (
                <p className="text-xs text-amber-400 mt-2 ml-8">
                  Solo se aplicará a la dieta actual: <strong>
                    {activeDietTab === 'training_inferior' ? 'Tren Inferior' :
                     activeDietTab === 'training_superior' ? 'Tren Superior' : 
                     activeDietTab === 'lineal' ? 'Lineal' : 'Descanso'}
                  </strong>
                </p>
              )}
            </div>
            
            {/* Preview del cambio */}
            <div className="bg-gradient-to-r from-cyan-900/30 to-purple-900/30 rounded-lg p-4 mb-6 border border-cyan-500/30">
              <div className="text-sm text-cyan-400 mb-3 font-semibold">📊 Vista previa del resultado:</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2 flex justify-between items-center bg-gray-800/50 rounded p-2">
                  <span className="text-gray-400">Calorías totales:</span>
                  <div>
                    <span className="text-gray-500">{dietData.target_calories} kcal</span>
                    <span className="mx-2 text-gray-600">→</span>
                    <span className={`font-bold ${
                      calculateNewCaloriesPreview() > dietData.target_calories ? 'text-green-400' : 
                      calculateNewCaloriesPreview() < dietData.target_calories ? 'text-red-400' : 'text-white'
                    }`}>
                      {calculateNewCaloriesPreview()} kcal
                    </span>
                    {calculateNewCaloriesPreview() !== dietData.target_calories && (
                      <span className={`ml-2 text-xs ${calculateNewCaloriesPreview() > dietData.target_calories ? 'text-green-400' : 'text-red-400'}`}>
                        ({calculateNewCaloriesPreview() > dietData.target_calories ? '+' : ''}{calculateNewCaloriesPreview() - dietData.target_calories})
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-pink-400">🥩 Proteína:</span>
                  <span className="text-white">{Math.round(dietData.target_protein * (1 + macroProgressionPercent.protein / 100))}g</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-orange-400">🍚 Carbos:</span>
                  <span className="text-white">{Math.round(dietData.target_carbs * (1 + macroProgressionPercent.carbs / 100))}g</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-yellow-400">🥑 Grasas:</span>
                  <span className="text-white">{Math.round(dietData.target_fat * (1 + macroProgressionPercent.fat / 100))}g</span>
                </div>
              </div>
            </div>
            
            {/* Botones de acción */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCalorieProgressionModal(false);
                  setMacroProgressionPercent({ protein: 0, carbs: 0, fat: 0 });
                }}
                className="flex-1 border-gray-600 text-gray-400 hover:bg-gray-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => applyMacroProgression(macroProgressionPercent, applyToAllDiets)}
                disabled={macroProgressionPercent.protein === 0 && macroProgressionPercent.carbs === 0 && macroProgressionPercent.fat === 0}
                className={`flex-1 ${
                  (macroProgressionPercent.protein !== 0 || macroProgressionPercent.carbs !== 0 || macroProgressionPercent.fat !== 0)
                    ? 'bg-gradient-to-r from-cyan-600 to-purple-600 hover:from-cyan-500 hover:to-purple-500' 
                    : 'bg-gray-600 cursor-not-allowed'
                }`}
              >
                <FaSync className="mr-2" />
                Aplicar Progresión
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DE REDISTRIBUCIÓN DE MACROS ===== */}
      {showRedistributeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border-2 border-green-500/50 rounded-2xl p-6 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="redistribute-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <FaBalanceScale className="text-green-400" />
                Redistribuir Macros
              </h3>
              <button onClick={() => setShowRedistributeModal(false)} className="text-gray-400 hover:text-white p-2">
                <FaTimes size={18} />
              </button>
            </div>

            <p className="text-gray-400 text-sm mb-4">
              Totales: <span className="text-pink-400 font-semibold">{dietData.target_protein}g P</span> | <span className="text-orange-400 font-semibold">{dietData.target_carbs}g C</span> | <span className="text-yellow-400 font-semibold">{dietData.target_fat}g G</span> | <span className="text-white font-semibold">{dietData.target_calories} kcal</span>
            </p>

            {/* Selector de modo */}
            <div className="grid grid-cols-3 gap-2 mb-5">
              {[
                { id: 'igualitaria', label: 'Igualitaria', icon: '⚖️', desc: 'Reparto igual entre comidas' },
                { id: 'peri_entreno', label: 'Peri-Entreno', icon: '💪', desc: 'Carbos concentrados pre/post entreno' },
                { id: 'personalizada', label: 'Personalizada', icon: '🎛️', desc: 'Ajusta % por comida manualmente' }
              ].map(mode => (
                <button
                  key={mode.id}
                  data-testid={`mode-${mode.id}`}
                  onClick={() => {
                    setRedistributionMode(mode.id);
                    if (mode.id === 'personalizada') {
                      // Inicializar con igualitaria
                      const n = dietData.meals.length;
                      const eq = Math.floor(100 / n);
                      const rem = 100 - eq * n;
                      const init = {};
                      dietData.meals.forEach((_, i) => {
                        init[i] = { protein: i === n - 1 ? eq + rem : eq, carbs: i === n - 1 ? eq + rem : eq, fat: i === n - 1 ? eq + rem : eq };
                      });
                      setCustomMealPercents(init);
                    }
                  }}
                  className={`py-3 px-3 rounded-xl border transition-all text-left ${
                    redistributionMode === mode.id
                      ? 'border-green-500 bg-green-500/20 shadow-lg shadow-green-500/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{mode.icon}</span>
                    <span className={`font-bold text-sm ${redistributionMode === mode.id ? 'text-green-400' : 'text-gray-300'}`}>{mode.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{mode.desc}</p>
                </button>
              ))}
            </div>

            {/* Advertencia para peri-entreno sin post-workout seleccionado */}
            {redistributionMode === 'peri_entreno' && (!dietData.post_workout_meal || dietData.post_workout_meal === 'none') && (
              <div className="bg-orange-900/30 border border-orange-500/50 rounded-lg p-3 mb-4 text-sm text-orange-300">
                Debes seleccionar una comida post-entreno en la configuración de arriba para usar este modo.
              </div>
            )}

            {/* Info de peri-entreno */}
            {redistributionMode === 'peri_entreno' && dietData.post_workout_meal && dietData.post_workout_meal !== 'none' && (
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mb-4 text-sm space-y-1">
                <p className="text-green-400 font-semibold">Distribución Peri-Entreno:</p>
                <p className="text-gray-400">Comida <span className="text-orange-400 font-bold">post-entreno</span> (Comida {dietData.post_workout_meal}): <span className="text-orange-300 font-semibold">35% carbos</span>, grasa reducida</p>
                {parseInt(dietData.post_workout_meal) > 1 && (
                  <p className="text-gray-400">Comida <span className="text-cyan-400 font-bold">pre-entreno</span> (Comida {parseInt(dietData.post_workout_meal) - 1}): <span className="text-cyan-300 font-semibold">25% carbos</span>, grasa reducida</p>
                )}
                <p className="text-gray-400">Resto de comidas: <span className="text-gray-300 font-semibold">40% carbos</span> repartidos, grasa compensada</p>
                <p className="text-gray-400">Proteína: <span className="text-pink-300 font-semibold">repartida equitativamente</span></p>
                {redistributionMode === 'peri_entreno' && (
                  <button
                    onClick={applyPeriWorkoutPresetToCustom}
                    className="mt-1 text-xs text-green-400 hover:text-green-300 underline"
                  >
                    Usar como base en modo Personalizada
                  </button>
                )}
              </div>
            )}

            {/* Modo personalizado: inputs de porcentaje por comida */}
            {redistributionMode === 'personalizada' && (
              <div className="mb-4 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Ajusta el % de cada macro por comida (debe sumar 100%)</span>
                  <button
                    onClick={() => {
                      if (dietData.post_workout_meal && dietData.post_workout_meal !== 'none') {
                        applyPeriWorkoutPresetToCustom();
                        toast.success('Preset peri-entreno aplicado');
                      } else {
                        // Reset a igualitario
                        const n = dietData.meals.length;
                        const eq = Math.floor(100 / n);
                        const rem = 100 - eq * n;
                        const init = {};
                        dietData.meals.forEach((_, i) => { init[i] = { protein: i === n - 1 ? eq + rem : eq, carbs: i === n - 1 ? eq + rem : eq, fat: i === n - 1 ? eq + rem : eq }; });
                        setCustomMealPercents(init);
                        toast.success('Valores igualitarios aplicados');
                      }
                    }}
                    className="text-xs text-green-400 hover:text-green-300 border border-green-500/30 rounded px-2 py-1"
                  >
                    {dietData.post_workout_meal && dietData.post_workout_meal !== 'none' ? 'Preset Peri-Entreno' : 'Resetear'}
                  </button>
                </div>

                {/* Header */}
                <div className="grid grid-cols-[1fr_80px_80px_80px] gap-2 text-xs font-semibold text-gray-500 px-1">
                  <span>Comida</span>
                  <span className="text-center text-pink-400">Prot %</span>
                  <span className="text-center text-orange-400">Carbs %</span>
                  <span className="text-center text-yellow-400">Grasas %</span>
                </div>

                {dietData.meals.map((meal, i) => {
                  const isPost = dietData.post_workout_meal === (i + 1).toString();
                  const isPre = parseInt(dietData.post_workout_meal) - 2 === i && parseInt(dietData.post_workout_meal) > 1;
                  return (
                    <div key={i} className={`grid grid-cols-[1fr_80px_80px_80px] gap-2 items-center rounded-lg px-2 py-2 ${
                      isPost ? 'bg-orange-900/20 border border-orange-500/30' : isPre ? 'bg-cyan-900/20 border border-cyan-500/30' : 'bg-gray-800/40'
                    }`}>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-300 font-medium truncate">{meal.meal_name}</span>
                        {isPost && <span className="text-orange-400 text-xs font-bold">POST</span>}
                        {isPre && <span className="text-cyan-400 text-xs font-bold">PRE</span>}
                      </div>
                      {['protein', 'carbs', 'fat'].map(macro => (
                        <Input
                          key={macro}
                          type="number"
                          min="0"
                          max="100"
                          value={customMealPercents[i]?.[macro] ?? 0}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            setCustomMealPercents(prev => ({ ...prev, [i]: { ...prev[i], [macro]: val } }));
                          }}
                          className={`text-center text-sm h-8 bg-gray-800 border-gray-600 ${
                            macro === 'protein' ? 'text-pink-300' : macro === 'carbs' ? 'text-orange-300' : 'text-yellow-300'
                          }`}
                        />
                      ))}
                    </div>
                  );
                })}

                {/* Totales */}
                {(() => {
                  const numMeals = dietData.meals.length;
                  let sumP = 0, sumC = 0, sumF = 0;
                  for (let i = 0; i < numMeals; i++) {
                    sumP += customMealPercents[i]?.protein || 0;
                    sumC += customMealPercents[i]?.carbs || 0;
                    sumF += customMealPercents[i]?.fat || 0;
                  }
                  const isValidP = Math.abs(sumP - 100) <= 1;
                  const isValidC = Math.abs(sumC - 100) <= 1;
                  const isValidF = Math.abs(sumF - 100) <= 1;
                  return (
                    <div className="grid grid-cols-[1fr_80px_80px_80px] gap-2 px-2 py-2 border-t border-gray-700 mt-1">
                      <span className="text-sm font-semibold text-gray-400">Total:</span>
                      <span className={`text-center text-sm font-bold ${isValidP ? 'text-green-400' : 'text-red-400'}`}>{sumP}%</span>
                      <span className={`text-center text-sm font-bold ${isValidC ? 'text-green-400' : 'text-red-400'}`}>{sumC}%</span>
                      <span className={`text-center text-sm font-bold ${isValidF ? 'text-green-400' : 'text-red-400'}`}>{sumF}%</span>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Vista previa de distribución */}
            <div className="bg-gray-800/50 rounded-lg p-4 mb-5 border border-gray-700">
              <p className="text-sm text-gray-400 mb-3 font-semibold">Vista previa (gramos por comida):</p>
              <div className="space-y-2">
                {getDistributionPreview().map((meal, i) => {
                  const kcal = Math.round((meal.protein * 4) + (meal.carbs * 4) + (meal.fat * 9));
                  const maxCarbs = Math.max(...getDistributionPreview().map(m => m.carbs), 1);
                  const carbBarWidth = (meal.carbs / maxCarbs) * 100;
                  const isPost = dietData.post_workout_meal === (i + 1).toString();
                  const isPre = parseInt(dietData.post_workout_meal) - 2 === i && parseInt(dietData.post_workout_meal) > 1;
                  return (
                    <div key={i} className={`flex items-center gap-3 text-sm rounded-lg px-3 py-2 ${
                      isPost ? 'bg-orange-900/20' : isPre ? 'bg-cyan-900/20' : ''
                    }`}>
                      <span className="w-24 text-gray-300 font-medium truncate flex items-center gap-1">
                        {meal.name}
                        {isPost && <span className="text-orange-400 text-[10px]">POST</span>}
                        {isPre && <span className="text-cyan-400 text-[10px]">PRE</span>}
                      </span>
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-3 overflow-hidden" title={`Carbos: ${meal.carbs}g`}>
                          <div
                            className={`h-full rounded-full transition-all ${isPost ? 'bg-orange-500' : isPre ? 'bg-cyan-500' : 'bg-green-500'}`}
                            style={{ width: `${carbBarWidth}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 text-xs whitespace-nowrap">
                        <span className="text-pink-400">{meal.protein}g P</span>
                        <span className={`font-semibold ${isPost ? 'text-orange-400' : isPre ? 'text-cyan-400' : 'text-orange-300'}`}>{meal.carbs}g C</span>
                        <span className="text-yellow-400">{meal.fat}g G</span>
                        <span className="text-gray-400">{kcal} kcal</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRedistributeModal(false)}
                className="flex-1 border-gray-600 text-gray-400 hover:bg-gray-800"
                data-testid="redistribute-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={applyRedistribution}
                disabled={
                  (redistributionMode === 'peri_entreno' && (!dietData.post_workout_meal || dietData.post_workout_meal === 'none'))
                }
                className={`flex-1 ${
                  redistributionMode === 'peri_entreno' && (!dietData.post_workout_meal || dietData.post_workout_meal === 'none')
                    ? 'bg-gray-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
                }`}
                data-testid="redistribute-apply"
              >
                <FaBalanceScale className="mr-2" />
                Aplicar Distribución
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL DE DIAGNÓSTICO POR PATOLOGÍAS ===== */}
      {showDiagnosisModal && selectedPathologyCase && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl border border-teal-500/50 w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-600/30 to-emerald-600/30 px-6 py-4 border-b border-teal-500/30 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <FaEnvelope className="text-teal-400" />
                  Motor Narrativo Premium - Diagnóstico
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Para: <span className="text-teal-300">{selectedPathologyCase.user_name}</span> ({selectedPathologyCase.user_email})
                </p>
              </div>
              <button 
                onClick={() => setShowDiagnosisModal(false)}
                className="text-gray-400 hover:text-white p-2"
              >
                <FaTimes size={20} />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Info del caso con severidad y dominante */}
              <div className="grid grid-cols-2 gap-4">
                {/* Patologías y Tags */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-300 mb-2">Patologías detectadas:</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(selectedPathologyCase.pathologies_selected || selectedPathologyCase.pathologies || []).map((p, i) => (
                      <Badge key={i} className="bg-pink-500/30 text-pink-300 border-pink-500/50">
                        {p}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-gray-500">Tags:</span>
                    {selectedPathologyCase.tags?.map((t, i) => (
                      <Badge key={i} variant="outline" className="text-xs text-cyan-400 border-cyan-500/50">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Severidad y Dominante */}
                <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-400">Severidad Global:</span>
                    <Badge className={`${
                      selectedPathologyCase.severity_global === 'RED' ? 'bg-red-500/30 text-red-300' :
                      selectedPathologyCase.severity_global === 'YELLOW' ? 'bg-yellow-500/30 text-yellow-300' :
                      selectedPathologyCase.severity_global === 'GREEN' ? 'bg-green-500/30 text-green-300' :
                      'bg-blue-500/30 text-blue-300'
                    }`}>
                      {selectedPathologyCase.severity_global || 'YELLOW'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Patología Dominante:</span>
                    <Badge className="bg-purple-500/30 text-purple-300">
                      {selectedPathologyCase.dominant_pathology || 'N/A'}
                    </Badge>
                  </div>
                  {selectedPathologyCase.active_pathologies?.length > 1 && (
                    <div className="mt-2 text-xs text-gray-500">
                      Secundarias: {selectedPathologyCase.active_pathologies.filter(p => p !== selectedPathologyCase.dominant_pathology).join(', ')}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botón Generar Email Clínico - Siempre visible */}
              <div className="bg-gradient-to-r from-emerald-900/30 to-teal-900/30 rounded-lg p-3 border border-emerald-500/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-emerald-300 font-medium">Motor Narrativo</p>
                      <p className="text-xs text-gray-400">Generar o regenerar mensaje</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={generateClinicalEmail}
                        disabled={generatingClinicalEmail}
                        className="bg-gray-700 hover:bg-gray-600 text-white text-xs"
                        data-testid="generate-clinical-email-btn"
                      >
                        {generatingClinicalEmail ? (
                          <FaSpinner className="mr-1 animate-spin" />
                        ) : (
                          <FaClipboardList className="mr-1" />
                        )}
                        Plantilla
                      </Button>
                      <Button
                        onClick={generateAIClinicalEmail}
                        disabled={generatingClinicalEmail}
                        className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-xs"
                        data-testid="generate-ai-email-btn"
                      >
                        {generatingClinicalEmail ? (
                          <FaSpinner className="mr-1 animate-spin" />
                        ) : (
                          <FaBrain className="mr-1" />
                        )}
                        Generar con IA
                      </Button>
                    </div>
                  </div>
                </div>
              
              {/* Editor de Subject */}
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1 block">Asunto del email:</label>
                <Input
                  value={editingDiagnosis.subject}
                  onChange={(e) => setEditingDiagnosis(prev => ({ ...prev, subject: e.target.value }))}
                  className="bg-gray-800 border-gray-600 text-white"
                  placeholder="Asunto del correo..."
                />
              </div>
              
              {/* Editor de Body - Mensaje al Cliente */}
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1 block">
                  📝 Mensaje al Cliente:
                  <span className="text-xs text-gray-500 ml-2">(Generado por Motor Narrativo Premium)</span>
                </label>
                <Textarea
                  value={editingDiagnosis.body}
                  onChange={(e) => setEditingDiagnosis(prev => ({ ...prev, body: e.target.value }))}
                  className="bg-gray-800 border-gray-600 text-white min-h-[200px] font-mono text-sm"
                  placeholder="Contenido del correo de diagnóstico..."
                />
              </div>
              
              {/* Editor de Plan de Acción */}
              <div className="border-t border-gray-700 pt-4">
                <label className="text-sm font-medium text-teal-300 mb-1 block">
                  📋 Plan de Acción (se adjunta al mensaje):
                </label>
                <Input
                  value={editingDiagnosis.actionPlanTitle || selectedPathologyCase.action_plan_title_auto || 'Plan de Acción Personalizado – Próximos 30 días'}
                  onChange={(e) => setEditingDiagnosis(prev => ({ ...prev, actionPlanTitle: e.target.value }))}
                  className="bg-gray-800 border-gray-600 text-white mb-2"
                  placeholder="Título del plan de acción..."
                />
                <Textarea
                  value={editingDiagnosis.actionPlanBody || selectedPathologyCase.action_plan_body_auto || ''}
                  onChange={(e) => setEditingDiagnosis(prev => ({ ...prev, actionPlanBody: e.target.value }))}
                  className="bg-gray-800 border-gray-600 text-white min-h-[150px] font-mono text-sm"
                  placeholder="Contenido del plan de acción..."
                />
              </div>
              
              {/* Adjuntar Imágenes */}
              <div className="border-t border-gray-700 pt-4" onPaste={handleEmailPaste}>
                <label className="text-sm font-medium text-orange-300 mb-2 block">
                  <FaImage className="inline mr-2" />
                  Imágenes Adjuntas:
                  <span className="text-xs text-gray-500 ml-2">(Sube o pega imágenes desde el portapapeles)</span>
                </label>
                
                {/* Imágenes adjuntas preview */}
                {emailImageUrls.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    {emailImageUrls.map((url, idx) => (
                      <div key={idx} className="relative group rounded-lg overflow-hidden border border-gray-600">
                        <img src={url} alt={`Adjunto ${idx + 1}`} className="w-full h-24 object-cover" />
                        <button
                          onClick={() => removeEmailImage(idx)}
                          className="absolute top-1 right-1 bg-red-600 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          data-testid={`remove-email-image-${idx}`}
                        >
                          <FaTimes className="text-white text-xs" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload button */}
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files?.[0]) uploadEmailImage(e.target.files[0]);
                        e.target.value = '';
                      }}
                      data-testid="email-image-upload-input"
                    />
                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-gray-300 text-sm hover:bg-gray-600 transition-colors">
                      {uploadingEmailImage ? <FaSpinner className="animate-spin" /> : <FaImage />}
                      {uploadingEmailImage ? 'Subiendo...' : 'Subir Imagen'}
                    </span>
                  </label>
                  <span className="text-xs text-gray-500 self-center">
                    o pega (Ctrl+V) una captura de pantalla aquí
                  </span>
                </div>
              </div>
              
              {/* Status */}
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>Estado: 
                  <Badge className={`ml-2 ${
                    selectedPathologyCase.status === 'draft' ? 'bg-yellow-500/30 text-yellow-300' :
                    selectedPathologyCase.status === 'sent' ? 'bg-green-500/30 text-green-300' :
                    'bg-gray-500/30 text-gray-300'
                  }`}>
                    {selectedPathologyCase.status === 'draft' ? 'Borrador' :
                     selectedPathologyCase.status === 'sent' ? 'Enviado' : selectedPathologyCase.status}
                  </Badge>
                </span>
                {selectedPathologyCase.sent_at && (
                  <span>Enviado: {new Date(selectedPathologyCase.sent_at).toLocaleString('es-CO')}</span>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t border-gray-700 px-6 py-4 bg-gray-800/50 flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={saveDiagnosisDraft}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <FaSave className="mr-2" />
                  Guardar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => deletePathologyCase(selectedPathologyCase.id)}
                  className="border-red-600 text-red-400 hover:bg-red-900/30 hover:text-red-300"
                  title="Eliminar este caso de diagnóstico"
                >
                  <FaTrash className="mr-2" />
                  Eliminar
                </Button>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDiagnosisModal(false)}
                  className="border-gray-600 text-gray-400 hover:bg-gray-700"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={sendDiagnosisEmail}
                  disabled={sendingDiagnosis || selectedPathologyCase.status === 'sent'}
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                >
                  {sendingDiagnosis ? (
                    <><FaSpinner className="mr-2 animate-spin" /> Enviando...</>
                  ) : selectedPathologyCase.status === 'sent' ? (
                    <><FaEnvelope className="mr-2" /> Ya enviado</>
                  ) : (
                    <><FaPaperPlane className="mr-2" /> Enviar Email</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal para Asignar Patologías Manualmente */}
      {showManualPathologyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowManualPathologyModal(false)}>
          <div 
            className="bg-gray-900 border border-purple-500/50 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <FaPlus className="text-purple-400" /> Asignar Patologías Manualmente
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowManualPathologyModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <FaTimes />
                </Button>
              </div>
              {clientData.email && (
                <p className="text-gray-400 text-sm mt-2">
                  Asignando a: <span className="text-purple-300">{clientData.name || clientData.email}</span>
                </p>
              )}
            </div>
            
            <div className="p-6 space-y-4">
              {/* Patologías actuales */}
              {clientData.additional_info?.pathologies?.length > 0 && (
                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                  <p className="text-purple-400 font-semibold text-sm mb-2">📋 Patologías actuales:</p>
                  <div className="flex flex-wrap gap-1">
                    {clientData.additional_info.pathologies
                      .filter(p => p && !['ninguna', 'ninguna_patologia', 'ninguna patología'].includes(p.toLowerCase()))
                      .map((p, i) => (
                        <span key={i} className="bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded text-xs">
                          {p.replace(/_/g, ' ')}
                        </span>
                      ))}
                  </div>
                </div>
              )}
              
              {/* Seleccionar nuevas patologías */}
              <div>
                <p className="text-white font-semibold mb-3">Selecciona las patologías a agregar:</p>
                
                {/* Agrupar por categoría */}
                {['RENAL', 'HEPATIC', 'THYROID', 'GLUCOSE', 'DIGESTIVE', 'LIPIDS', 'CARDIO', 'HEMATO', 'BONE', 'INFLAMMATORY', 'GYNECO', 'HORMONAL'].map(category => {
                  const categoryPathologies = AVAILABLE_PATHOLOGIES.filter(p => p.category === category);
                  const categoryLabels = {
                    'RENAL': '🔬 Renal',
                    'HEPATIC': '🔬 Hepático',
                    'THYROID': '🦋 Tiroides',
                    'GLUCOSE': '📊 Glucosa/Metabólico',
                    'DIGESTIVE': '🍽️ Digestivo',
                    'LIPIDS': '💧 Lípidos',
                    'CARDIO': '❤️ Cardiovascular',
                    'HEMATO': '🩸 Hematológico',
                    'BONE': '🦴 Óseo',
                    'INFLAMMATORY': '🔥 Inflamatorio',
                    'GYNECO': '🌸 Ginecológico',
                    'HORMONAL': '⚡ Hormonal'
                  };
                  
                  return (
                    <div key={category} className="mb-3">
                      <p className="text-gray-400 text-xs font-semibold mb-1">{categoryLabels[category]}</p>
                      <div className="flex flex-wrap gap-2">
                        {categoryPathologies.map(path => (
                          <button
                            key={path.id}
                            onClick={() => toggleManualPathology(path.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                              selectedManualPathologies.includes(path.id)
                                ? 'bg-purple-500 text-white'
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                            }`}
                          >
                            {selectedManualPathologies.includes(path.id) && '✓ '}
                            {path.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Resumen de selección */}
              {selectedManualPathologies.length > 0 && (
                <div className="p-3 rounded-lg bg-teal-500/10 border border-teal-500/30">
                  <p className="text-teal-400 font-semibold text-sm mb-2">
                    ✓ {selectedManualPathologies.length} patología(s) seleccionada(s):
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {selectedManualPathologies.map(id => {
                      const path = AVAILABLE_PATHOLOGIES.find(p => p.id === id);
                      return (
                        <span key={id} className="bg-teal-500/30 text-teal-300 px-2 py-0.5 rounded text-xs">
                          {path?.name || id}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-gray-800/50 flex justify-end gap-3 border-t border-gray-700">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowManualPathologyModal(false);
                  setSelectedManualPathologies([]);
                }}
                className="text-gray-400 hover:text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={assignManualPathologies}
                disabled={processingPathologies || selectedManualPathologies.length === 0}
                className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white"
              >
                {processingPathologies ? (
                  <><FaSpinner className="mr-2 animate-spin" /> Procesando...</>
                ) : (
                  <><FaPlus className="mr-2" /> Asignar y Procesar</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modal: Historial de versiones del plan ===== */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-[120] bg-black/70 flex items-center justify-center p-4" onClick={() => setShowHistoryModal(false)} data-testid="history-modal">
          <div
            className="bg-gray-900 border border-amber-500/30 rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-amber-500/30 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15), rgba(217, 119, 6, 0.15))' }}>
              <div>
                <h3 className="text-white font-bold flex items-center gap-2"><FaHistory className="text-amber-400" /> Historial del plan</h3>
                <p className="text-amber-200/60 text-xs">Últimas 10 versiones · auto-snapshot en cada cambio total + guardar versión manualmente</p>
              </div>
              <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-white" data-testid="close-history-modal-btn">
                <FaTimes />
              </button>
            </div>

            {/* Crear snapshot manual */}
            <div className="px-5 py-3 border-b border-gray-700 bg-gray-900/60">
              <div className="text-xs text-amber-300 font-semibold mb-2 uppercase">📸 Guardar versión actual</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  placeholder="Nombre (ej: Pre-cambio total semana 3)"
                  value={manualSnapName}
                  onChange={(e) => setManualSnapName(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white text-sm"
                  data-testid="manual-snap-name-input"
                />
                <Input
                  placeholder="Nota opcional"
                  value={manualSnapNote}
                  onChange={(e) => setManualSnapNote(e.target.value)}
                  className="bg-gray-800 border-gray-600 text-white text-sm"
                  data-testid="manual-snap-note-input"
                />
                <Button
                  onClick={createManualSnapshot}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-sm whitespace-nowrap"
                  data-testid="save-manual-snap-btn"
                >
                  <FaSave className="mr-2" /> Guardar versión
                </Button>
              </div>
              <p className="text-[10px] text-gray-500 mt-2">Antes de guardar la versión se persiste el plan actual para no perder lo que tengas sin guardar.</p>
            </div>

            {/* Listado */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {historyLoading ? (
                <div className="text-center py-8 text-gray-400">Cargando historial...</div>
              ) : historyList.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  <FaHistory className="mx-auto text-3xl mb-2 opacity-50" />
                  <p className="text-sm">Aún no hay versiones guardadas para este plan.</p>
                  <p className="text-xs opacity-70 mt-1">Cuando hagas un cambio total se creará una automáticamente, o usa el botón de arriba para guardar una versión ahora.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {historyList.map((snap) => (
                    <div key={snap.id} className="bg-gray-800/70 border border-gray-700 rounded-lg p-3 hover:border-amber-500/40 transition" data-testid={`history-row-${snap.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase font-semibold ${snap.source === 'manual' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40' : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'}`}>
                              {snap.source === 'manual' ? '📸 Manual' : '⚡ Automática'}
                            </span>
                            <span className="text-white text-sm font-semibold truncate">{snap.name}</span>
                          </div>
                          {snap.note && <p className="text-gray-400 text-xs mt-1 italic truncate">{snap.note}</p>}
                          <p className="text-gray-500 text-[11px] mt-1">
                            {snap.created_at ? new Date(snap.created_at).toLocaleString('es-CO') : ''}
                            {snap.plan_name ? <span className="ml-2 text-gray-600">· {snap.plan_name}</span> : null}
                          </p>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-1 shrink-0">
                          <Button
                            size="sm"
                            onClick={() => restoreSnapshot(snap.id, snap.name)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            data-testid={`restore-snap-${snap.id}`}
                          >
                            <FaUndo className="mr-1" /> Restaurar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteSnapshot(snap.id)}
                            className="border-red-500/40 text-red-400 hover:bg-red-500/10 text-xs"
                            data-testid={`delete-snap-${snap.id}`}
                          >
                            <FaTrash className="mr-1" /> Eliminar
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ═══════════════════════════════════════════════════════════
          MODAL: Asignar receta de la biblioteca a una comida
          ═══════════════════════════════════════════════════════════ */}
      {swapPickerOpen && swapPickerCtx && (() => {
        // Aplanamos toda la biblioteca (todas las categorías) en un solo
        // array searchable. Se muestra la categoría como badge para orientar.
        const flat = Object.entries(foodDatabase || {}).flatMap(([cat, foods]) =>
          (foods || []).map((f) => ({ ...f, __category: cat }))
        );
        const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const q = norm(swapPickerSearch.trim());
        const results = flat
          .filter((f) => f && f.name && norm(f.name) !== norm(swapPickerCtx.currentName))
          .filter((f) => !q || norm(f.name).includes(q) || norm(f.__category).includes(q))
          .sort((a, b) => a.name.localeCompare(b.name, 'es'))
          .slice(0, 200);
        return (
          <div
            className="fixed inset-0 z-[310] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
            onClick={() => setSwapPickerOpen(false)}
            data-testid="swap-food-modal"
          >
            <div
              className="w-full max-w-2xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                    🔀 Intercambiar ingrediente
                  </h3>
                  <p className="text-xs text-emerald-700/70 mt-0.5">
                    Reemplazando: <b>{swapPickerCtx.currentName}</b> · Se conservan los gramos y se recalculan los macros al instante.
                  </p>
                </div>
                <button
                  onClick={() => setSwapPickerOpen(false)}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-emerald-100 text-emerald-800"
                  data-testid="swap-food-close"
                >✕</button>
              </div>

              <div className="p-3 border-b">
                <Input
                  type="text"
                  value={swapPickerSearch}
                  onChange={(e) => setSwapPickerSearch(e.target.value)}
                  placeholder="Buscar alimento (por nombre o categoría)…"
                  autoFocus
                  data-testid="swap-food-search"
                />
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                {results.length === 0 ? (
                  <p className="text-center text-gray-500 py-8 text-sm">
                    Sin resultados. Probá con otra búsqueda.
                  </p>
                ) : (
                  <ul className="space-y-1" data-testid="swap-food-list">
                    {results.map((f) => (
                      <li
                        key={`${f.__category}-${f.name}`}
                        className="rounded-lg px-3 py-2 border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/60 transition-all cursor-pointer flex items-center justify-between gap-3"
                        onClick={() => {
                          handleSwapFoodItem(swapPickerCtx.mealIndex, swapPickerCtx.itemIndex, f);
                          setSwapPickerOpen(false);
                          setSwapPickerCtx(null);
                          toast.success(`✅ "${swapPickerCtx.currentName}" reemplazado por "${f.name}"`);
                        }}
                        data-testid={`swap-food-item-${f.name}`}
                      >
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-800">
                            {f._caution ? '⚠️ ' : ''}{f.name}
                          </div>
                          <div className="text-[11px] text-gray-500 capitalize">{f.__category?.replace(/_/g, ' ')}</div>
                        </div>
                        <div className="text-xs font-mono whitespace-nowrap">
                          <span className="text-blue-600">{f.protein || 0}P</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-yellow-600">{f.carbs || 0}C</span>
                          <span className="text-gray-400"> / </span>
                          <span className="text-green-600">{f.fat || 0}G</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      {/* ═══════════════════════════════════════════════════════════
          MODAL: Asignar receta de la biblioteca a una comida
          ═══════════════════════════════════════════════════════════ */}
      {recipePickerOpen && (() => {
        // 2026-02 · Slots canónicos ordenados cronológicamente. Contamos las
        // recetas por slot (case-insensitive) para pintar el badge de cada chip.
        const SLOTS = ['Desayuno', 'Snack AM', 'Almuerzo', 'Snack PM', 'Cena', 'Post-Entreno'];
        const _norm = (s) => String(s || '').trim().toLowerCase();
        const countBySlot = SLOTS.reduce((acc, s) => {
          acc[s] = recipeResults.filter((r) => _norm(r.meal_slot) === _norm(s)).length;
          return acc;
        }, {});
        // Filtrado combinado: chip + texto (ambos client-side).
        // 2026-02 · La búsqueda por texto matchea nombre de receta O nombre
        // de cualquier ingrediente ("pollo" trae "Ensalada de pollo" y
        // también recetas que sólo tienen pollo en su lista de ingredientes).
        const q = _norm(recipeSearch);
        const _matchesText = (r) => {
          if (!q) return true;
          if (_norm(r.name).includes(q)) return true;
          const ings = Array.isArray(r.ingredients) ? r.ingredients : [];
          return ings.some((ing) => _norm(ing?.name || ing?.food_name || '').includes(q));
        };
        const filteredResults = recipeResults
          .filter((r) => !recipeSlotFilter || _norm(r.meal_slot) === _norm(recipeSlotFilter))
          .filter(_matchesText);
        return (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
          onClick={() => setRecipePickerOpen(false)}
          data-testid="recipe-picker-modal"
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b bg-gradient-to-r from-emerald-50 to-teal-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">📖 Biblioteca de recetas</h3>
                <p className="text-xs text-emerald-700/70 mt-0.5">
                  {recipeResults.length} receta{recipeResults.length === 1 ? '' : 's'} en el catálogo
                  {filteredResults.length !== recipeResults.length && ` · ${filteredResults.length} visible${filteredResults.length === 1 ? '' : 's'} con el filtro`}
                </p>
              </div>
              <button
                onClick={() => setRecipePickerOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-emerald-100 text-emerald-800"
                data-testid="recipe-picker-close"
              >✕</button>
            </div>

            <div className="p-3 border-b space-y-2">
              <Input
                type="text"
                value={recipeSearch}
                onChange={(e) => setRecipeSearch(e.target.value)}
                placeholder="Buscar receta por nombre…"
                className="w-full"
                autoFocus
                data-testid="recipe-picker-search"
              />
              {/* 2026-02 · Chips de filtro por meal_slot. "Todas" limpia el filtro. */}
              <div className="flex flex-wrap gap-1.5" data-testid="recipe-picker-slot-chips">
                <button
                  type="button"
                  onClick={() => setRecipeSlotFilter('')}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                    recipeSlotFilter === '' ? 'bg-emerald-500 text-white shadow' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="recipe-picker-slot-all"
                >
                  Todas <span className="opacity-70">({recipeResults.length})</span>
                </button>
                {SLOTS.map((slot) => {
                  const emoji = { 'Desayuno':'🍳', 'Snack AM':'🍎', 'Almuerzo':'🍽️', 'Snack PM':'🥜', 'Cena':'🌙', 'Post-Entreno':'💪' }[slot] || '🍽️';
                  const count = countBySlot[slot] || 0;
                  const active = _norm(recipeSlotFilter) === _norm(slot);
                  return (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setRecipeSlotFilter(active ? '' : slot)}
                      disabled={count === 0}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                        active ? 'bg-emerald-500 text-white shadow'
                        : count === 0 ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`recipe-picker-slot-${slot.replace(/\s+/g,'-')}`}
                    >
                      {emoji} {slot} {count > 0 && <span className="opacity-70">({count})</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {recipeLoading ? (
                <p className="text-center text-gray-500 py-8 text-sm">Cargando recetas…</p>
              ) : filteredResults.length === 0 ? (
                <p className="text-center text-gray-500 py-8 text-sm">
                  {recipeResults.length === 0 ? 'Sin recetas en el catálogo.' : 'Sin resultados con este filtro. Probá otro.'}
                </p>
              ) : (
                <ul className="space-y-1.5" data-testid="recipe-picker-list">
                  {filteredResults.map((r) => (
                    <li
                      key={r.id}
                      className="rounded-lg p-2.5 border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/60 transition-all flex items-center gap-2"
                      data-testid={`recipe-picker-item-${r.id}`}
                    >
                      {/* 2026-02 · Toggle favorito (estrella). Click en la estrella
                          NO asigna la receta — sólo cambia el flag. */}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const newVal = !r.is_favorite;
                            await axios.patch(`${BACKEND_URL}/api/admin/recipe-library/${r.id}/favorite`, { is_favorite: newVal });
                            setRecipeResults((prev) => prev.map((x) => x.id === r.id ? { ...x, is_favorite: newVal } : x));
                            toast.success(newVal ? '★ Marcada como favorita' : 'Quitada de favoritas');
                          } catch { toast.error('No se pudo actualizar el favorito'); }
                        }}
                        title={r.is_favorite ? 'Quitar de favoritas' : 'Marcar como favorita'}
                        className="text-lg leading-none flex-shrink-0 hover:scale-110 transition-transform"
                        data-testid={`recipe-picker-fav-${r.id}`}
                      >
                        {r.is_favorite ? <span className="text-amber-400">★</span> : <span className="text-gray-300">☆</span>}
                      </button>
                      <div
                        onClick={() => assignRecipeToMeal(r)}
                        className="min-w-0 flex-1 cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                              {r.is_favorite && <span className="text-amber-500 mr-1">★</span>}
                              {r.name}
                            </p>
                            <div className="text-[11px] text-gray-500 flex flex-wrap gap-2 mt-0.5">
                              {(r.meal_slot || r.meal_time) && <span>🕐 {r.meal_slot || r.meal_time}</span>}
                              <span className="text-blue-600">P {r.protein || 0}g</span>
                              <span className="text-yellow-600">C {r.carbs || 0}g</span>
                              <span className="text-green-600">G {r.fat || r.fats || 0}g</span>
                            <span className="text-purple-600 font-semibold">{Math.round(r.calories || 0)} kcal</span>
                          </div>
                        </div>
                      </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// DailyMacrosSummary: banner sticky con los totales del día que se
// actualizan automáticamente al agregar/editar comidas. Prioriza los
// macros target_* editados manualmente por comida.
// ═══════════════════════════════════════════════════════════════════════
const DailyMacrosSummary = ({ meals, target }) => {
  const totals = React.useMemo(() => {
    let p = 0, c = 0, f = 0, k = 0;
    (meals || []).forEach((m) => {
      // Total del día = suma de los macros PLANIFICADOS de cada comida
      // (recuadros P/C/G editables). Así "Aplicar a Macros Objetivo",
      // "Redistribuir" y los editores por comida mueven este total.
      if ((m.target_protein || 0) + (m.target_carbs || 0) + (m.target_fat || 0) > 0) {
        p += Number(m.target_protein) || 0;
        c += Number(m.target_carbs) || 0;
        f += Number(m.target_fat) || 0;
        k += Number(m.target_calories) || 0;
        return;
      }
      // Sin macros planificados: usar los de la receta asignada si existe.
      if (m.recipe_from_library?.name) {
        p += Number(m.recipe_from_library.protein) || 0;
        c += Number(m.recipe_from_library.carbs) || 0;
        f += Number(m.recipe_from_library.fat) || 0;
        k += Number(m.recipe_from_library.calories) || 0;
        return;
      }
      // Fallback: sumar los items reales de la comida.
      (m.items || []).forEach((it) => {
        if (it?.skip) return;
        const mult = (Number(it.grams) || Math.round((Number(it.multiplier) || 1) * 100)) / 100;
        p += (Number(it.protein) || 0) * mult;
        c += (Number(it.carbs) || 0) * mult;
        f += ((Number(it.fat) || Number(it.fats)) || 0) * mult;
        k += (Number(it.calories) || 0) * mult;
      });
    });
    if (k === 0) k = Math.round(p * 4 + c * 4 + f * 9);
    return { p: Math.round(p), c: Math.round(c), f: Math.round(f), k: Math.round(k) };
  }, [meals]);

  const tgt = target || {};
  const hasTarget = (Number(tgt.protein) || 0) > 0;

  const pctBar = (used, target) => {
    if (!target || target <= 0) return null;
    const pct = Math.min(100, Math.round((used / target) * 100));
    return pct;
  };

  const badge = (label, used, targetVal, colorClass) => {
    const pct = pctBar(used, targetVal);
    const over = pct !== null && used > targetVal * 1.02;
    const exact = pct !== null && used >= targetVal * 0.98 && used <= targetVal * 1.02;
    const under = pct !== null && used < targetVal * 0.98;
    const bg = over ? 'bg-red-100 border-red-400' : exact ? 'bg-emerald-100 border-emerald-500' : under ? 'bg-slate-100 border-slate-300' : 'bg-white border-slate-300';
    return (
      <div className={`flex-1 min-w-[100px] rounded-lg border-2 p-2 ${bg}`}>
        <div className="flex items-center justify-between">
          <span className={`text-[10px] font-black uppercase tracking-widest ${colorClass}`}>{label}</span>
          {pct !== null && (
            <span className={`text-[10px] font-semibold ${over ? 'text-red-700' : exact ? 'text-emerald-700' : 'text-slate-500'}`}>
              {pct}%
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className={`text-xl font-black ${colorClass}`}>{used}</span>
          {targetVal > 0 && <span className="text-[11px] text-slate-500">/ {targetVal}</span>}
        </div>
      </div>
    );
  };

  return (
    <div
      className="sticky top-2 z-20 mb-3 rounded-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 shadow-lg p-3"
      data-testid="daily-macros-summary"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-widest font-black text-indigo-700">
            📊 Total del día
          </p>
          <p className="text-[10px] text-indigo-500">
            Se recalcula automáticamente al editar macros o asignar recetas
          </p>
        </div>
        {hasTarget && (
          <span className="text-[10px] bg-white/70 text-indigo-700 px-2 py-1 rounded-full font-semibold">
            Objetivo: {tgt.calories || 0} kcal · P{tgt.protein || 0} · C{tgt.carbs || 0} · G{tgt.fat || 0}
          </span>
        )}
      </div>
      <div className="flex gap-2 flex-wrap">
        {badge('Proteína', totals.p, Number(tgt.protein) || 0, 'text-blue-700')}
        {badge('Carbohidratos', totals.c, Number(tgt.carbs) || 0, 'text-yellow-700')}
        {badge('Grasas', totals.f, Number(tgt.fat) || 0, 'text-green-700')}
        {badge('Calorías', totals.k, Number(tgt.calories) || 0, 'text-purple-700')}
      </div>
    </div>
  );
};

export default OfflineGenerator;
